// Helper to calculate consolidation settlement Sc
function calculateSc(s0, sp, e0, Cc, Cr, h, ds) {
    if (globalThis.NendatYeuCore) {
        return globalThis.NendatYeuCore.calculateConsolidationSettlement({
            initialEffectiveStress: s0,
            preconsolidationStress: sp,
            voidRatio: e0,
            compressionIndex: Cc,
            recompressionIndex: Cr,
            thickness: h,
            stressIncrement: ds
        });
    }
    if (ds <= 0) return 0;
    if (s0 <= 0) return 0;
    if (s0 >= sp) {
        // Normally Consolidated (NC)
        return (Cc / (1 + e0)) * h * Math.log10((s0 + ds) / s0);
    } else {
        // Overconsolidated (OC)
        if (s0 + ds <= sp) {
            return (Cr / (1 + e0)) * h * Math.log10((s0 + ds) / s0);
        } else {
            if (sp > 0) {
                return (Cr / (1 + e0)) * h * Math.log10(sp / s0) + (Cc / (1 + e0)) * h * Math.log10((s0 + ds) / sp);
            }
            return 0;
        }
    }
}

function parseLocalizedNumber(value) {
    if (globalThis.NendatYeuCore) return globalThis.NendatYeuCore.parseLocalizedNumber(value);
    if (typeof value === "number") return value;
    if (typeof value !== "string") return NaN;
    return Number(value.trim().replace(/s/g, "").replace(",", "."));
}

function escapeHtml(value) {
    if (globalThis.NendatYeuCore) return globalThis.NendatYeuCore.escapeHtml(value);
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

function sanitizeColor(value) {
    if (globalThis.NendatYeuCore) return globalThis.NendatYeuCore.sanitizeColor(value);
    return /^#[0-9a-f]{6}$/i.test(String(value || "")) ? value : "#6b7280";
}

function getDrainagePath(thickness) {
    const boundary = appState?.foundation?.drainage || "single";
    if (globalThis.NendatYeuCore) return globalThis.NendatYeuCore.getDrainagePath(thickness, boundary);
    return boundary === "double" ? thickness / 2 : thickness;
}

function effectiveStressIncrement(sub, fromDepth, toDepth, gwt) {
    if (globalThis.NendatYeuCore) {
        return globalThis.NendatYeuCore.effectiveStressIncrement(sub, fromDepth, toDepth, gwt);
    }
    if (toDepth <= fromDepth) return 0;
    const top = Math.max(fromDepth, sub.depthTop);
    const bottom = Math.min(toDepth, sub.depthBottom);
    if (bottom <= top) return 0;
    const dryBottom = Math.min(bottom, gwt);
    const dryThickness = Math.max(0, dryBottom - top);
    const wetTop = Math.max(top, gwt);
    const wetThickness = Math.max(0, bottom - wetTop);
    return sub.gamma * dryThickness + (sub.gammaSat - WATER_UNIT_WEIGHT) * wetThickness;
}

function getThicknessWeightedCv(layers, fallback = 1.5) {
    if (globalThis.NendatYeuCore) return globalThis.NendatYeuCore.getThicknessWeightedCv(layers, fallback);
    const cohesiveLayers = (layers || []).filter(layer =>
        layer && (layer.type === "cohesive" || layer.Cc > 0.05) &&
        Number.isFinite(layer.Cv) && Number.isFinite(layer.thickness) && layer.thickness > 0
    );
    const totalThickness = cohesiveLayers.reduce((sum, layer) => sum + layer.thickness, 0);
    if (totalThickness <= 0) return fallback;
    return cohesiveLayers.reduce((sum, layer) => sum + layer.Cv * layer.thickness, 0) / totalThickness;
}

function markAnalysisInvalid(message) {
    const banner = document.getElementById("compliance-banner");
    const text = document.getElementById("compliance-text");
    if (banner) {
        banner.style.background = "rgba(239, 68, 68, 0.15)";
        banner.style.borderColor = "rgba(239, 68, 68, 0.4)";
    }
    if (text) {
        text.textContent = "CHƯA CÓ KẾT QUẢ HỢP LỆ: " + message;
        text.style.color = "#fca5a5";
    }
    if (appState) appState.lastAnalysis = { ...(appState.lastAnalysis || {}), valid: false, error: message };
}

// Helper to check if light mode is active
function isLightMode() {
    return document.body.classList.contains("light-theme");
}

// Helper: interpolate settlement S(t) from settlementTimeData array
function getSettlementAtTime(t, data) {
    if (!data || data.length === 0) return 0;
    if (t <= data[0].t) return data[0].S;
    if (t >= data[data.length - 1].t) return data[data.length - 1].S;
    for (let i = 1; i < data.length; i++) {
        if (data[i].t >= t) {
            const t0 = data[i - 1].t, t1 = data[i].t;
            const s0 = data[i - 1].S, s1 = data[i].S;
            return s0 + (s1 - s0) * (t - t0) / (t1 - t0);
        }
    }
    return data[data.length - 1].S;
}

// Helper: interpolate consolidation degree U(t) from settlementTimeData array
function getUAtTime(t, data) {
    if (!data || data.length === 0) return 0;
    if (t <= data[0].t) return data[0].U;
    if (t >= data[data.length - 1].t) return data[data.length - 1].U;
    for (let i = 1; i < data.length; i++) {
        if (data[i].t >= t) {
            const t0 = data[i - 1].t, t1 = data[i].t;
            const u0 = data[i - 1].U, u1 = data[i].U;
            return u0 + (u1 - u0) * (t - t0) / (t1 - t0);
        }
    }
    return data[data.length - 1].U;
}

let isPrinting = false;

// Application State & Data Structure
let appState = {
    schemaVersion: 2,
        embankment: {
        height: 4.5,      // H (m)
        gamma: 18.5,      // kN/m3
        width: 24.0,      // B (m)
        slope: 1.5,       // m
        staged: false,
        stagedH1: 2.0,
        stagedT1: 6,
        stagedH2: 2.5
    },
    foundation: {
        gwt: 1.0,         // Z_w (m)
        surcharge: 20.0,  // q (kPa)
        life: 15,         // years
        allowable: 10.0,  // [S] (cm)
        mFactor: 1.3,     // m empirical factor
        tConstruction: 1.0, // time of pavement construction (years)
        drainage: "single", // conservative default: one-way vertical drainage
        shearMode: "undrained"
    },
    treatment: {
        method: 'pvd'     // 'none', 'pvd', 'cdm', 'excavation_replacement', 'bamboo_replacement'
    },
        pvd: {
        spacing: 1.2,     // S (m)
        pattern: 'triangular',
        depth: 15.0,      // m
        width: 100.0,     // mm
        thickness: 4.0,   // mm
        chRatio: 2.0,     // Ch = x * Cv
        dsDw: 2.5,        // ds/dw ratio (smear zone)
        khKs: 3.0,        // kh/ks ratio (smear permeability)
        qw: 60.0,         // PVD discharge capacity (cm3/s)
        standard: 'tccs_41', // Tiêu chuẩn tính toán: 'tcvn_9355' hoặc 'tccs_41'
        vacuumPressure: 80.0,
        vacuumEfficiency: 0.85
    },
    cdm: {
        spacing: 1.5,     // S (m)
        pattern: 'square',
        depth: 12.0,      // L_c (m)
        diameter: 0.8,    // d (m)
        qu: 1.5,          // MPa
        emodulusRatio: 150,
        tUlt: 100.0,      // kN/m
        rf: 2.0,
        phi: 30.0,        // degrees
        overWidth: 1.0    // overwidth (m)
    },
    sandDrain: {
        spacing: 1.5,
        pattern: 'triangular',
        depth: 15.0,
        diameter: 0.4,    // m
        chRatio: 2.0
    },
    sandColumn: {
        spacing: 1.8,
        pattern: 'triangular',
        depth: 12.0,
        diameter: 0.8,    // m
        modulus: 25.0,    // MPa
        chRatio: 2.0
    },
    bambooPile: {
        spacing: 0.2,
        pattern: 'square',
        depth: 4.0,
        diameter: 0.08,   // m
        modulus: 100.0,   // MPa
        phi: 30.0         // degrees
    },
    excavationReplacement: {
        depth: 2.0,      // m
        gamma: 18.0,     // kN/m3
        gammaSat: 19.0,  // kN/m3
        E: 15000.0,      // kPa
        phi: 30.0,       // degrees
        c: 0.0           // kPa
    },
    soilLayers: [
        {
            id: 1,
            name: "Sét bùn yếu (Mekong Clay)",
            type: "cohesive",
            thickness: 10.0,
            gamma: 14.5,
            gammaSat: 15.2,
            e0: 1.85,
            Cc: 0.65,
            Cr: 0.08,
            sigmaP: 35.0,     // kPa
            Cv: 1.5,          // m2/year
            Ca: 0.022,        // C_alpha
            E: 2200,          // Elastic Modulus (kPa)
            c: 12.0,          // kPa
            phi: 4.0,         // degrees
            color: "#4a3525"
        },
        {
            id: 2,
            name: "Sét dẻo mềm",
            type: "cohesive",
            thickness: 6.0,
            gamma: 17.0,
            gammaSat: 17.8,
            e0: 1.05,
            Cc: 0.28,
            Cr: 0.04,
            sigmaP: 90.0,     // kPa
            Cv: 3.2,          // m2/year
            Ca: 0.008,        // C_alpha
            E: 4800,          // Elastic Modulus (kPa)
            c: 18.0,          // kPa
            phi: 10.0,        // degrees
            color: "#6b5e53"
        },
        {
            id: 3,
            name: "Cát chặt vừa (Dưới thải nước)",
            type: "granular",
            thickness: 8.0,
            gamma: 18.5,
            gammaSat: 19.2,
            e0: 0.65,
            Cc: 0.02,
            Cr: 0.004,
            sigmaP: 220.0,    // kPa
            Cv: 60.0,         // m2/year
            Ca: 0.001,        // C_alpha
            E: 24000,         // Elastic Modulus (kPa)
            c: 2.0,           // kPa
            phi: 32.0,        // degrees
            color: "#a69575"
        }
    ]
};

// Global constants
const WATER_UNIT_WEIGHT = 9.81; // kN/m3

// Color palette for adding new layers
const SOIL_COLORS = [
    "#4a3525", "#6b5e53", "#a69575", "#515844", 
    "#5c6d70", "#48485c", "#7e6d6d", "#7a8a99"
];

// Document Elements
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initApp);
} else {
    initApp();
}

function initApp() {
    // Render layers table
    renderLayersTable();
    
    // Bind Inputs to state
    bindInputs();
    
    // Bind buttons
    document.getElementById("btn-add-layer").addEventListener("click", addNewLayer);
    document.getElementById("btn-load-sample").addEventListener("click", loadSampleProject);
    document.getElementById("btn-print").addEventListener("click", () => window.print());
    document.getElementById("btn-new").addEventListener("click", newProject);

    window.addEventListener("beforeprint", () => {
        isPrinting = true;
        runAnalysis();
    });
    window.addEventListener("afterprint", () => {
        isPrinting = false;
        runAnalysis();
    });
    
    // File Import/Export
    document.getElementById("btn-export").addEventListener("click", exportJSON);
    document.getElementById("btn-import").addEventListener("click", () => {
        document.getElementById("import-file").click();
    });
    document.getElementById("import-file").addEventListener("change", importJSON);

    // Geotechnical Data Import/Export
    document.getElementById("btn-export-excel").addEventListener("click", exportGeotechnicalCSV);
    document.getElementById("btn-import-excel").addEventListener("click", () => {
        document.getElementById("import-excel-file").click();
    });
    document.getElementById("import-excel-file").addEventListener("change", importGeotechnicalData);

    // Word Export
    document.getElementById("btn-export-word").addEventListener("click", exportWord);
    
    // Theory modal toggle
    const theoryModal = document.getElementById("theory-modal");
    const btnTheory = document.getElementById("btn-theory");
    const btnCloseTheory = document.getElementById("btn-close-theory");

    if (btnTheory && theoryModal) {
        btnTheory.addEventListener("click", () => {
            theoryModal.classList.add("active");
        });
    }
    if (btnCloseTheory && theoryModal) {
        btnCloseTheory.addEventListener("click", () => {
            theoryModal.classList.remove("active");
        });
    }
    if (theoryModal) {
        theoryModal.addEventListener("click", (e) => {
            if (e.target === theoryModal) {
                theoryModal.classList.remove("active");
            }
        });
    }
    
    // Theory Print & Word Export actions
    const btnPrintTheory = document.getElementById("btn-print-theory");
    const btnExportWordTheory = document.getElementById("btn-export-word-theory");
    
    if (btnPrintTheory) {
        btnPrintTheory.addEventListener("click", () => {
            document.body.classList.add("print-theory-only");
            window.print();
            document.body.classList.remove("print-theory-only");
        });
    }
    if (btnExportWordTheory) {
        btnExportWordTheory.addEventListener("click", exportTheoryToWord);
    }
    
    // Tab switching
    const tabBtns = document.querySelectorAll(".tab-btn");
    tabBtns.forEach(btn => {
        btn.addEventListener("click", (e) => {
            tabBtns.forEach(b => b.classList.remove("active"));
            document.querySelectorAll(".tab-content").forEach(c => c.classList.remove("active"));
            
            btn.classList.add("active");
            const tabId = btn.getAttribute("data-tab");
            document.getElementById(tabId).classList.add("active");
            
            // Redraw charts when switching tabs to ensure canvas updates properly
            runAnalysis();
        });
    });

    // Theory sub-tab switching
    const theoryBtns = document.querySelectorAll(".theory-menu-item");
    theoryBtns.forEach(btn => {
        btn.addEventListener("click", () => {
            theoryBtns.forEach(b => b.classList.remove("active"));
            document.querySelectorAll(".theory-sub-content").forEach(c => c.classList.remove("active"));
            
            btn.classList.add("active");
            const subTabId = btn.getAttribute("data-sub-tab");
            const subEl = document.getElementById(subTabId);
            if (subEl) {
                subEl.classList.add("active");
            }
        });
    });

    // Treatment Method dropdown trigger visibility
    const methodSelect = document.getElementById("treatment-method");
        methodSelect.addEventListener("change", () => {
        const method = methodSelect.value;
        
        document.getElementById("pvd-settings").style.display = (method === "pvd" || method === "vacuum_pvd") ? "block" : "none";
        document.getElementById("pvd-vacuum-settings").style.display = method === "vacuum_pvd" ? "block" : "none";
        document.getElementById("cdm-settings").style.display = method === "cdm" ? "block" : "none";
        document.getElementById("sand-drain-settings").style.display = method === "sand_drain" ? "block" : "none";
        document.getElementById("sand-column-settings").style.display = method === "sand_column" ? "block" : "none";
        document.getElementById("bamboo-pile-settings").style.display = (method === "bamboo_pile" || method === "bamboo_replacement") ? "block" : "none";
        document.getElementById("excavation-replacement-settings").style.display = (method === "excavation_replacement" || method === "bamboo_replacement") ? "block" : "none";
        
        runAnalysis();
    });

    // Theme Toggle Click Handler
    const themeToggle = document.getElementById("btn-theme-toggle");
    if (themeToggle) {
        themeToggle.addEventListener("click", () => {
            const isLight = document.body.classList.toggle("light-theme");
            localStorage.setItem("nendatyeu-theme", isLight ? "light" : "dark");
            
            // Redraw canvases
            let totalDepth = appState.soilLayers.reduce((sum, l) => sum + l.thickness, 0);
            drawGeotechnicalSection(totalDepth);
            if (window.lastSublayers) {
                drawStressChart(window.lastSublayers);
                drawDepthSettlementCurve(window.lastSublayers);
            }
            if (window.lastSettlementTimeData) {
                drawTimeCurve(window.lastSettlementTimeData, window.lastTConst, window.lastTEnd);
            }
            if (appState.lastVerification && appState.lastVerification.circular) {
                drawSlipCircleChart(appState.lastVerification.circular, getEffectiveSoilLayers());
            }
        });
    }

    // Initialize Theme from localStorage
    const savedTheme = localStorage.getItem("nendatyeu-theme");
    if (savedTheme === "light") {
        document.body.classList.add("light-theme");
    }

    // Mandrel Helper event listeners
    const btnToggleMandrel = document.getElementById("btn-toggle-mandrel-helper");
    const mandrelPanel = document.getElementById("pvd-mandrel-helper-panel");
    
    const mandrelWidthInput = document.getElementById("mandrel-width");
    const mandrelThickInput = document.getElementById("mandrel-thick");
    const mandrelRatioInput = document.getElementById("mandrel-ratio");

    function updateMandrelCalculation() {
        if (!mandrelWidthInput || !mandrelThickInput || !mandrelRatioInput) return;
        let w = parseFloat(mandrelWidthInput.value) || 80;
        let t = parseFloat(mandrelThickInput.value) || 40;
        let mult = parseFloat(mandrelRatioInput.value) || 2.5;
        let pvd_w = parseFloat(document.getElementById("pvd-width").value) || 100;
        let pvd_t = parseFloat(document.getElementById("pvd-thickness").value) || 4;
        
        let dw = (2 * (pvd_w + pvd_t)) / Math.PI; // mm
        let dm = Math.sqrt(4 * w * t / Math.PI); // mm
        let ds_dw = (mult * dm) / dw;
        
        const valDm = document.getElementById("val-dm-calc");
        const valDw = document.getElementById("val-dw-calc");
        const valDsdw = document.getElementById("val-dsdw-calc");
        
        if (valDm) valDm.innerText = (dm / 10).toFixed(2);
        if (valDw) valDw.innerText = (dw / 10).toFixed(2);
        if (valDsdw) valDsdw.innerText = ds_dw.toFixed(2);
    }

    if (btnToggleMandrel && mandrelPanel) {
        btnToggleMandrel.addEventListener("click", () => {
            const show = mandrelPanel.style.display === "none";
            mandrelPanel.style.display = show ? "block" : "none";
            if (show) {
                updateMandrelCalculation();
            }
        });
    }

    if (mandrelWidthInput) mandrelWidthInput.addEventListener("input", updateMandrelCalculation);
    if (mandrelThickInput) mandrelThickInput.addEventListener("input", updateMandrelCalculation);
    if (mandrelRatioInput) mandrelRatioInput.addEventListener("input", updateMandrelCalculation);

    const pvdWidthEl = document.getElementById("pvd-width");
    const pvdThickEl = document.getElementById("pvd-thickness");
    if (pvdWidthEl) pvdWidthEl.addEventListener("input", updateMandrelCalculation);
    if (pvdThickEl) pvdThickEl.addEventListener("input", updateMandrelCalculation);

    const btnApplyMandrel = document.getElementById("btn-apply-mandrel");
    if (btnApplyMandrel) {
        btnApplyMandrel.addEventListener("click", () => {
            if (!mandrelWidthInput || !mandrelThickInput || !mandrelRatioInput) return;
            let w = parseFloat(mandrelWidthInput.value) || 80;
            let t = parseFloat(mandrelThickInput.value) || 40;
            let mult = parseFloat(mandrelRatioInput.value) || 2.5;
            let pvd_w = parseFloat(document.getElementById("pvd-width").value) || 100;
            let pvd_t = parseFloat(document.getElementById("pvd-thickness").value) || 4;
            
            let dw = (2 * (pvd_w + pvd_t)) / Math.PI;
            let dm = Math.sqrt(4 * w * t / Math.PI);
            let ds_dw = (mult * dm) / dw;

            const dsDwInput = document.getElementById("pvd-ds-dw");
            if (dsDwInput) {
                dsDwInput.value = ds_dw.toFixed(2);
                dsDwInput.dispatchEvent(new Event("input", { bubbles: true }));
            }
            showNotification("Đã áp dụng thông số trục xuyên thành công!", "success");
        });
    }

    // PDF Modal toggle and generation
    const btnExportPdf = document.getElementById("btn-export-pdf");
    const pdfModal = document.getElementById("pdf-modal");
    const btnClosePdf = document.getElementById("btn-close-pdf");
    const btnCancelPdf = document.getElementById("btn-cancel-pdf");
    
    if (btnExportPdf && pdfModal) {
        btnExportPdf.addEventListener("click", () => {
            pdfModal.style.display = "block";
        });
    }
    
    const closePdfModal = () => {
        if (pdfModal) pdfModal.style.display = "none";
    };
    
    if (btnClosePdf) btnClosePdf.addEventListener("click", closePdfModal);
    if (btnCancelPdf) btnCancelPdf.addEventListener("click", closePdfModal);
    
    window.addEventListener("click", (e) => {
        if (e.target === pdfModal) closePdfModal();
    });

    const btnGeneratePdf = document.getElementById("btn-generate-pdf");
    if (btnGeneratePdf) {
        btnGeneratePdf.addEventListener("click", () => {
            closePdfModal();
            showNotification("Đang xuất báo cáo PDF...", "success");
            
            const projName = document.getElementById("pdf-project-name").value || "Dự án xử lý nền đất yếu";
            const itemName = document.getElementById("pdf-item-name").value || "Nền đường đầu cầu";
            const location = document.getElementById("pdf-location").value || "Km 10+000";
            const author = document.getElementById("pdf-author").value || "Kỹ sư thiết kế";
            const approver = document.getElementById("pdf-approver").value || "Phòng Kỹ thuật";
            const dateStr = new Date().toLocaleDateString("vi-VN");
            const reportMeta = globalThis.NendatYeuCore
                ? globalThis.NendatYeuCore.createReportMetadata(appState)
                : { version: "3.0.0", checksum: "N/A", standards: [], assumptions: [] };
            
            const container = document.createElement("div");
            container.style.padding = "20px";
            container.style.fontFamily = "Arial, sans-serif";
            container.style.color = "#333";
            container.style.background = "#fff";
            
            let html = `
                <div style="display: flex; justify-content: space-between; border-bottom: 2px solid #3b82f6; padding-bottom: 10px; margin-bottom: 20px; font-size: 0.8rem; color: #555;">
                    <div>ĐƠN VỊ THIẾT KẾ: PHÒNG KỸ THUẬT &amp; CÔNG NGHỆ</div>
                    <div>QUY TRÌNH KIỂM TOÁN TCCS 41:2022/TCĐBVN</div>
                </div>
                
                <div style="text-align: center; font-weight: bold; font-size: 1.4rem; color: #1e3a8a; margin-bottom: 20px; text-transform: uppercase;">
                    BÁO CÁO THUYẾT MINH KỸ THUẬT<br>
                    TÍNH TOÁN XỬ LÝ NỀN ĐẤT YẾU
                </div>
                
                <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 0.9rem;">
                    <tr>
                        <td style="width: 25%; font-weight: bold; padding: 4px 0;">Tên dự án:</td>
                        <td style="padding: 4px 0; border-bottom: 1px dotted #ccc;">${projName}</td>
                    </tr>
                    <tr>
                        <td style="font-weight: bold; padding: 4px 0;">Hạng mục:</td>
                        <td style="padding: 4px 0; border-bottom: 1px dotted #ccc;">${itemName}</td>
                    </tr>
                    <tr>
                        <td style="font-weight: bold; padding: 4px 0;">Địa điểm:</td>
                        <td style="padding: 4px 0; border-bottom: 1px dotted #ccc;">${location}</td>
                    </tr>
                    <tr>
                        <td style="font-weight: bold; padding: 4px 0;">Ngày thực hiện:</td>
                        <td style="padding: 4px 0; border-bottom: 1px dotted #ccc;">${dateStr}</td>
                    </tr>
                </table>
                <div style="border:1px solid #cbd5e1;background:#f8fafc;padding:10px;margin-bottom:18px;font-size:0.75rem;">
                    <strong>Truy xuất kết quả:</strong> Phiên bản ${reportMeta.version} · Checksum ${reportMeta.checksum}<br>
                    <strong>Tiêu chuẩn:</strong> ${reportMeta.standards.join("; ")}<br>
                    <strong>Giả định:</strong> ${reportMeta.assumptions.join(" ")}
                </div>
                
                <h3 style="color: #1e3a8a; border-bottom: 1px solid #3b82f6; padding-bottom: 4px; margin-top: 30px;">1. THÔNG SỐ ĐẤT NỀN VÀ PHÂN TẦNG ĐỊA CHẤT</h3>
            `;
            
            html += `<table style="width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 0.8rem; text-align: center;">
                <thead>
                    <tr style="background: #f1f5f9; font-weight: bold;">
                        <th style="border: 1px solid #cbd5e1; padding: 6px;">Tên lớp đất</th>
                        <th style="border: 1px solid #cbd5e1; padding: 6px;">Dày H (m)</th>
                        <th style="border: 1px solid #cbd5e1; padding: 6px;">&gamma; (kN/m³)</th>
                        <th style="border: 1px solid #cbd5e1; padding: 6px;">e<sub>0</sub></th>
                        <th style="border: 1px solid #cbd5e1; padding: 6px;">C<sub>c</sub></th>
                        <th style="border: 1px solid #cbd5e1; padding: 6px;">C<sub>v</sub> (m²/năm)</th>
                        <th style="border: 1px solid #cbd5e1; padding: 6px;">E<sub>s</sub> (kPa)</th>
                    </tr>
                </thead>
                <tbody>`;
            
            appState.soilLayers.forEach(l => {
                html += `
                    <tr>
                        <td style="border: 1px solid #cbd5e1; padding: 6px; text-align: left;">${l.name}</td>
                        <td style="border: 1px solid #cbd5e1; padding: 6px;">${l.thickness.toFixed(1)}</td>
                        <td style="border: 1px solid #cbd5e1; padding: 6px;">${l.gamma.toFixed(1)}</td>
                        <td style="border: 1px solid #cbd5e1; padding: 6px;">${l.e0.toFixed(2)}</td>
                        <td style="border: 1px solid #cbd5e1; padding: 6px;">${l.Cc.toFixed(3)}</td>
                        <td style="border: 1px solid #cbd5e1; padding: 6px;">${l.Cv.toFixed(1)}</td>
                        <td style="border: 1px solid #cbd5e1; padding: 6px;">${l.E.toFixed(0)}</td>
                    </tr>
                `;
            });
            html += `</tbody></table>`;
            
            let treatmentText = "Không xử lý (Để tự nhiên)";
            const method = appState.treatment.method;
            if (method === 'pvd') treatmentText = "Cắm bấc thấm (PVD)";
            else if (method === 'vacuum_pvd') treatmentText = "Bấc thấm + Hút chân không (Vacuum + PVD)";
            else if (method === 'cdm') treatmentText = "Cọc xi măng đất (CDM)";
            else if (method === 'sand_drain') treatmentText = "Giếng cát (Sand Drain)";
            else if (method === 'sand_column') treatmentText = "Cọc cát / Cọc vật liệu rời";
            else if (method === 'bamboo_pile') treatmentText = "Gia cố cọc tre";
            else if (method === 'excavation_replacement') treatmentText = "Đào thay đất";
            else if (method === 'bamboo_replacement') treatmentText = "Đóng cọc tre + Thay đất";

            html += `
                <h3 style="color: #1e3a8a; border-bottom: 1px solid #3b82f6; padding-bottom: 4px; margin-top: 30px; page-break-before: always;">2. THÔNG SỐ THIẾT KẾ</h3>
                <ul style="font-size: 0.9rem; line-height: 1.6;">
                    <li><strong>Chiều cao đắp H<sub>emb</sub>:</strong> ${appState.embankment.height.toFixed(2)} m</li>
                    <li><strong>Dung trọng đất đắp &gamma;<sub>emb</sub>:</strong> ${appState.embankment.gamma.toFixed(1)} kN/m³</li>
                    <li><strong>Hoạt tải xe chạy q:</strong> ${appState.foundation.surcharge.toFixed(1)} kPa</li>
                    <li><strong>Thời gian thi công mặt đường t<sub>ck</sub>:</strong> ${appState.foundation.tConstruction.toFixed(2)} năm</li>
                    <li><strong>Độ lún dư cho phép [S]:</strong> ${appState.foundation.allowable.toFixed(1)} cm</li>
                    <li><strong>Giải pháp gia cố lựa chọn:</strong> ${treatmentText}</li>
                </ul>
            `;
            
            const result = appState.lastAnalysis;
            if (!result || !result.valid) {
                showNotification("Không thể xuất PDF khi kết quả tính chưa hợp lệ.", "error");
                return;
            }
            const totalS = result.total;
            const totalSc = result.consolidation;
            const totalSi = result.immediate;
            const totalSs = result.secondary;
            const uHandover = formatDecimal(result.uHandover, 2) + "%";
            const sRemaining = result.remaining;
            
            html += `
                <h3 style="color: #1e3a8a; border-bottom: 1px solid #3b82f6; padding-bottom: 4px; margin-top: 30px;">3. KẾT QUẢ TÍNH TOÁN LÚN VÀ CỐ KẾT</h3>
                <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 0.9rem; text-align: center; border: 1px solid #cbd5e1;">
                    <tr style="background: #f1f5f9; font-weight: bold;">
                        <td style="border: 1px solid #cbd5e1; padding: 8px; text-align: left;">Chỉ tiêu kiểm toán</td>
                        <td style="border: 1px solid #cbd5e1; padding: 8px;">Kết quả tính toán</td>
                        <td style="border: 1px solid #cbd5e1; padding: 8px;">Yêu cầu kỹ thuật</td>
                    </tr>
                    <tr>
                        <td style="border: 1px solid #cbd5e1; padding: 8px; text-align: left;">Độ lún tức thời (S<sub>i</sub>)</td>
                        <td style="border: 1px solid #cbd5e1; padding: 8px;">${totalSi.toFixed(2)} cm</td>
                        <td style="border: 1px solid #cbd5e1; padding: 8px;">-</td>
                    </tr>
                    <tr>
                        <td style="border: 1px solid #cbd5e1; padding: 8px; text-align: left;">Độ lún cố kết (S<sub>c</sub>)</td>
                        <td style="border: 1px solid #cbd5e1; padding: 8px;">${totalSc.toFixed(2)} cm</td>
                        <td style="border: 1px solid #cbd5e1; padding: 8px;">-</td>
                    </tr>
                    <tr>
                        <td style="border: 1px solid #cbd5e1; padding: 8px; text-align: left;">Độ lún từ biến creep (S<sub>s</sub>)</td>
                        <td style="border: 1px solid #cbd5e1; padding: 8px;">${totalSs.toFixed(2)} cm</td>
                        <td style="border: 1px solid #cbd5e1; padding: 8px;">-</td>
                    </tr>
                    <tr style="font-weight: bold;">
                        <td style="border: 1px solid #cbd5e1; padding: 8px; text-align: left; color: #1e3a8a;">Độ lún tổng cộng (S)</td>
                        <td style="border: 1px solid #cbd5e1; padding: 8px; color: #1e3a8a;">${totalS.toFixed(2)} cm</td>
                        <td style="border: 1px solid #cbd5e1; padding: 8px;">-</td>
                    </tr>
                    <tr>
                        <td style="border: 1px solid #cbd5e1; padding: 8px; text-align: left;">Độ cố kết bàn giao U(t<sub>ck</sub>)</td>
                        <td style="border: 1px solid #cbd5e1; padding: 8px;">${uHandover}</td>
                        <td style="border: 1px solid #cbd5e1; padding: 8px;">-</td>
                    </tr>
                    <tr style="font-weight: bold; background: #fffbeb;">
                        <td style="border: 1px solid #cbd5e1; padding: 8px; text-align: left;">Độ lún dư còn lại (S<sub>dư</sub>)</td>
                        <td style="border: 1px solid #cbd5e1; padding: 8px;">${sRemaining.toFixed(2)} cm</td>
                        <td style="border: 1px solid #cbd5e1; padding: 8px; color: #047857;">&le; [S] = ${appState.foundation.allowable.toFixed(1)} cm</td>
                    </tr>
                </table>
            `;

            const canvasSketch = document.getElementById("soil-canvas");
            const canvasTime = document.getElementById("chart-time");
            const canvasStress = document.getElementById("chart-stress");
            const canvasSlip = document.getElementById("slip-circle-canvas");
            
            html += `<h3 style="color: #1e3a8a; border-bottom: 1px solid #3b82f6; padding-bottom: 4px; margin-top: 30px; page-break-before: always;">4. CÁC BIỂU ĐỒ KỸ THUẬT</h3>`;
            
            if (canvasSketch) {
                html += `
                    <div style="text-align: center; margin-bottom: 20px;">
                        <p style="font-weight: bold; font-size: 0.85rem; margin-bottom: 4px;">Hình 1. Sơ đồ mặt cắt địa chất và tải trọng đắp</p>
                        <img src="${canvasSketch.toDataURL("image/png")}" style="max-width: 100%; border: 1px solid #e2e8f0; border-radius: 6px;">
                    </div>
                `;
            }
            
            if (canvasTime) {
                html += `
                    <div style="text-align: center; margin-bottom: 20px;">
                        <p style="font-weight: bold; font-size: 0.85rem; margin-bottom: 4px;">Hình 2. Biểu đồ quan hệ giữa Độ lún - Thời gian</p>
                        <img src="${canvasTime.toDataURL("image/png")}" style="max-width: 100%; border: 1px solid #e2e8f0; border-radius: 6px;">
                    </div>
                `;
            }

            if (canvasSlip && canvasSlip.style.display !== "none") {
                html += `
                    <div style="text-align: center; margin-bottom: 20px; page-break-before: always;">
                        <p style="font-weight: bold; font-size: 0.85rem; margin-bottom: 4px;">Hình 3. Biểu đồ cung trượt ổn định trượt sâu (Fellenius)</p>
                        <img src="${canvasSlip.toDataURL("image/png")}" style="max-width: 100%; border: 1px solid #e2e8f0; border-radius: 6px;">
                    </div>
                `;
            }

            const reportContent = document.getElementById("cdm-verification-content");
            if (reportContent) {
                html += `
                    <h3 style="color: #1e3a8a; border-bottom: 1px solid #3b82f6; padding-bottom: 4px; margin-top: 30px; page-break-before: always;">5. CHI TIẾT THUYẾT MINH TÍNH TOÁN</h3>
                    <div style="font-size: 0.85rem; line-height: 1.6;">
                        ${reportContent.innerHTML}
                    </div>
                `;
            }
            
            html += `
                <div style="margin-top: 50px; display: flex; justify-content: space-between; font-size: 0.9rem; page-break-inside: avoid;">
                    <div style="text-align: center; width: 45%;">
                        <strong>NGƯỜI LẬP THUYẾT MINH</strong><br>
                        <span style="font-size: 0.75rem; color: #666; font-style: italic;">(Ký và ghi rõ họ tên)</span>
                        <br><br><br><br><br>
                        <strong>${author}</strong>
                    </div>
                    <div style="text-align: center; width: 45%;">
                        <strong>NGƯỜI DUYỆT THUYẾT MINH</strong><br>
                        <span style="font-size: 0.75rem; color: #666; font-style: italic;">(Ký và duyệt)</span>
                        <br><br><br><br><br>
                        <strong>${approver}</strong>
                    </div>
                </div>
            `;
            
            container.innerHTML = html;
            
            const opt = {
                margin:       [12, 12, 12, 12],
                filename:     'Thuyet_minh_tinh_lun_' + projName.replace(/\s+/g, '_') + '.pdf',
                image:        { type: 'jpeg', quality: 0.98 },
                html2canvas:  { scale: 2, useCORS: true, logging: false },
                jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
            };
            
            html2pdf().set(opt).from(container).save().then(() => {
                showNotification("Xuất báo cáo PDF thành công!", "success");
            }).catch(err => {
                showNotification("Lỗi xuất PDF: " + err, "error");
            });
        });
    }

    // Staged construction toggle visibility and disable controls
    function updateStagedUI() {
        const toggle = document.getElementById("emb-staged-toggle");
        const panel = document.getElementById("emb-staged-settings");
        const embHeightInput = document.getElementById("emb-height");
        
        if (toggle && panel && embHeightInput) {
            const isStaged = toggle.checked;
            panel.style.display = isStaged ? "block" : "none";
            
            if (isStaged) {
                embHeightInput.disabled = true;
                embHeightInput.style.opacity = "0.6";
                let h1 = parseFloat(document.getElementById("staged-h1").value) || 2.0;
                let h2 = parseFloat(document.getElementById("staged-h2").value) || 2.5;
                embHeightInput.value = (h1 + h2).toFixed(1);
                appState.embankment.height = h1 + h2;
            } else {
                embHeightInput.disabled = false;
                embHeightInput.style.opacity = "";
                appState.embankment.height = parseFloat(embHeightInput.value) || 4.5;
            }
        }
    }

    const stagedToggleEl = document.getElementById("emb-staged-toggle");
    if (stagedToggleEl) {
        stagedToggleEl.addEventListener("change", () => {
            updateStagedUI();
            runAnalysis();
        });
    }

    const stagedH1El = document.getElementById("staged-h1");
    const stagedH2El = document.getElementById("staged-h2");
    if (stagedH1El) stagedH1El.addEventListener("input", () => {
        updateStagedUI();
        runAnalysis();
    });
    if (stagedH2El) stagedH2El.addEventListener("input", () => {
        updateStagedUI();
        runAnalysis();
    });
    
    // Call initially
    updateStagedUI();

    // Drag-to-adjust thickness event listeners on soil canvas
    const canvasSoil = document.getElementById("soil-canvas");
    if (canvasSoil) {
        let isDragging = false;
        let dragBoundaryIndex = -1;
        let dragStartY = 0;
        let initialThickness = 0;
        let initialNextThickness = 0;
        
        canvasSoil.addEventListener("mousemove", (e) => {
            if (!window.geotechnicalDrawingInfo) return;
            
            const rect = canvasSoil.getBoundingClientRect();
            const x = (e.clientX - rect.left) * (canvasSoil.width / rect.width);
            const y = (e.clientY - rect.top) * (canvasSoil.height / rect.height);
            
            const info = window.geotechnicalDrawingInfo;
            const boundaries = info.boundaries;
            
            if (isDragging) {
                let dyPixels = y - dragStartY;
                let dyMeters = dyPixels / info.scaleY;
                
                let newThickness = initialThickness + dyMeters;
                if (newThickness < 0.1) newThickness = 0.1;
                
                const boundary = boundaries[dragBoundaryIndex];
                const layerId = boundary.id;
                const layerIdx = appState.soilLayers.findIndex(l => l.id === layerId);
                
                if (layerIdx !== -1) {
                    if (layerIdx < appState.soilLayers.length - 1) {
                        let totalTwoLayers = initialThickness + initialNextThickness;
                        let nextThickness = totalTwoLayers - newThickness;
                        if (nextThickness < 0.1) {
                            nextThickness = 0.1;
                            newThickness = totalTwoLayers - 0.1;
                        }
                        appState.soilLayers[layerIdx].thickness = Math.round(newThickness * 10) / 10;
                        appState.soilLayers[layerIdx + 1].thickness = Math.round(nextThickness * 10) / 10;
                    } else {
                        appState.soilLayers[layerIdx].thickness = Math.round(newThickness * 10) / 10;
                    }
                    
                    let totalDepth = appState.soilLayers.reduce((sum, l) => sum + l.thickness, 0);
                    drawGeotechnicalSection(totalDepth);
                    
                    const ctx = canvasSoil.getContext("2d");
                    ctx.strokeStyle = "#ef4444";
                    ctx.lineWidth = 2;
                    ctx.setLineDash([5, 5]);
                    ctx.beginPath();
                    ctx.moveTo(info.paddingLeft, y);
                    ctx.lineTo(info.paddingLeft + info.drawW, y);
                    ctx.stroke();
                    ctx.setLineDash([]);
                }
                return;
            }
            
            if (x >= info.paddingLeft && x <= info.paddingLeft + info.drawW) {
                let hoverIndex = -1;
                for (let i = 0; i < boundaries.length; i++) {
                    if (Math.abs(y - boundaries[i].y) <= 8) {
                        hoverIndex = i;
                        break;
                    }
                }
                
                if (hoverIndex !== -1) {
                    canvasSoil.style.cursor = "row-resize";
                } else {
                    canvasSoil.style.cursor = "default";
                }
            } else {
                canvasSoil.style.cursor = "default";
            }
        });
        
        canvasSoil.addEventListener("mousedown", (e) => {
            if (!window.geotechnicalDrawingInfo) return;
            
            const rect = canvasSoil.getBoundingClientRect();
            const x = (e.clientX - rect.left) * (canvasSoil.width / rect.width);
            const y = (e.clientY - rect.top) * (canvasSoil.height / rect.height);
            
            const info = window.geotechnicalDrawingInfo;
            const boundaries = info.boundaries;
            
            if (x >= info.paddingLeft && x <= info.paddingLeft + info.drawW) {
                let hoverIndex = -1;
                for (let i = 0; i < boundaries.length; i++) {
                    if (Math.abs(y - boundaries[i].y) <= 8) {
                        hoverIndex = i;
                        break;
                    }
                }
                
                if (hoverIndex !== -1) {
                    isDragging = true;
                    dragBoundaryIndex = hoverIndex;
                    dragStartY = y;
                    
                    const boundary = boundaries[hoverIndex];
                    const layerId = boundary.id;
                    const layerIdx = appState.soilLayers.findIndex(l => l.id === layerId);
                    
                    initialThickness = appState.soilLayers[layerIdx].thickness;
                    if (layerIdx < appState.soilLayers.length - 1) {
                        initialNextThickness = appState.soilLayers[layerIdx + 1].thickness;
                    } else {
                        initialNextThickness = 0;
                    }
                    
                    e.preventDefault();
                }
            }
        });
        
        const stopDrag = () => {
            if (isDragging) {
                isDragging = false;
                dragBoundaryIndex = -1;
                
                renderLayersTable();
                runAnalysis();
                
                showNotification("Đã cập nhật chiều dày lớp đất bằng kéo thả!", "success");
            }
        };
        
        canvasSoil.addEventListener("mouseup", stopDrag);
        canvasSoil.addEventListener("mouseleave", stopDrag);
    }

    // Bind mouse events for charts hover interaction
    const canvasStress = document.getElementById("chart-stress");
    if (canvasStress) {
        canvasStress.addEventListener("mousemove", (e) => {
            if (window.lastSublayers) {
                const rect = canvasStress.getBoundingClientRect();
                const x = (e.clientX - rect.left) * (canvasStress.width / rect.width);
                const y = (e.clientY - rect.top) * (canvasStress.height / rect.height);
                drawStressChart(window.lastSublayers, x, y);
            }
        });
        canvasStress.addEventListener("mouseleave", () => {
            if (window.lastSublayers) {
                drawStressChart(window.lastSublayers);
            }
        });
    }

    const canvasTime = document.getElementById("chart-time");
    if (canvasTime) {
        canvasTime.addEventListener("mousemove", (e) => {
            if (window.lastSettlementTimeData) {
                const rect = canvasTime.getBoundingClientRect();
                const x = (e.clientX - rect.left) * (canvasTime.width / rect.width);
                const y = (e.clientY - rect.top) * (canvasTime.height / rect.height);
                drawTimeCurve(window.lastSettlementTimeData, window.lastTConst, window.lastTEnd, x, y);
            }
        });
        canvasTime.addEventListener("mouseleave", () => {
            if (window.lastSettlementTimeData) {
                drawTimeCurve(window.lastSettlementTimeData, window.lastTConst, window.lastTEnd);
            }
        });
    }

    const canvasDepth = document.getElementById("chart-depth");
    if (canvasDepth) {
        canvasDepth.addEventListener("mousemove", (e) => {
            if (window.lastSublayers) {
                const rect = canvasDepth.getBoundingClientRect();
                const x = (e.clientX - rect.left) * (canvasDepth.width / rect.width);
                const y = (e.clientY - rect.top) * (canvasDepth.height / rect.height);
                drawDepthSettlementCurve(window.lastSublayers, x, y);
            }
        });
        canvasDepth.addEventListener("mouseleave", () => {
            if (window.lastSublayers) {
                drawDepthSettlementCurve(window.lastSublayers);
            }
        });
    }

    // Run first analysis
    runAnalysis();
}

let _analysisDebounceTimer = null;
function scheduleAnalysis(delay = 250) {
    clearTimeout(_analysisDebounceTimer);
    _analysisDebounceTimer = setTimeout(() => runAnalysis(), delay);
}

// Bind standard input values from DOM to state and trigger analysis
function bindInputs() {
    const inputIds = [
                { id: "emb-height", statePath: ["embankment", "height"], type: "float" },
        { id: "emb-staged-toggle", statePath: ["embankment", "staged"], type: "boolean" },
        { id: "staged-h1", statePath: ["embankment", "stagedH1"], type: "float" },
        { id: "staged-t1", statePath: ["embankment", "stagedT1"], type: "float" },
        { id: "staged-h2", statePath: ["embankment", "stagedH2"], type: "float" },
        { id: "emb-gamma", statePath: ["embankment", "gamma"], type: "float" },
        { id: "emb-width", statePath: ["embankment", "width"], type: "float" },
        { id: "emb-slope", statePath: ["embankment", "slope"], type: "float" },
        { id: "gwt-depth", statePath: ["foundation", "gwt"], type: "float" },
        { id: "traffic-load", statePath: ["foundation", "surcharge"], type: "float" },
        { id: "design-life", statePath: ["foundation", "life"], type: "float" },
        { id: "t-construction", statePath: ["foundation", "tConstruction"], type: "float" },
        { id: "allowable-remaining-settlement", statePath: ["foundation", "allowable"], type: "float" },
        { id: "emb-m-factor", statePath: ["foundation", "mFactor"], type: "float" },
        { id: "drainage-boundary", statePath: ["foundation", "drainage"], type: "string" },
        { id: "shear-analysis-mode", statePath: ["foundation", "shearMode"], type: "string" },
        
        { id: "treatment-method", statePath: ["treatment", "method"], type: "string" },
        
        { id: "pvd-standard", statePath: ["pvd", "standard"], type: "string" },
        { id: "pvd-spacing", statePath: ["pvd", "spacing"], type: "float" },
        { id: "pvd-pattern", statePath: ["pvd", "pattern"], type: "string" },
        { id: "pvd-depth", statePath: ["pvd", "depth"], type: "float" },
        { id: "pvd-width", statePath: ["pvd", "width"], type: "float" },
        { id: "pvd-thickness", statePath: ["pvd", "thickness"], type: "float" },
        { id: "pvd-ch-ratio", statePath: ["pvd", "chRatio"], type: "float" },
        { id: "pvd-ds-dw", statePath: ["pvd", "dsDw"], type: "float" },
        { id: "pvd-kh-ks", statePath: ["pvd", "khKs"], type: "float" },
                { id: "pvd-qw", statePath: ["pvd", "qw"], type: "float" },
        { id: "vacuum-pressure", statePath: ["pvd", "vacuumPressure"], type: "float" },
        { id: "vacuum-efficiency", statePath: ["pvd", "vacuumEfficiency"], type: "float" },
        
        { id: "cdm-spacing", statePath: ["cdm", "spacing"], type: "float" },
        { id: "cdm-pattern", statePath: ["cdm", "pattern"], type: "string" },
        { id: "cdm-depth", statePath: ["cdm", "depth"], type: "float" },
        { id: "cdm-diameter", statePath: ["cdm", "diameter"], type: "float" },
        { id: "cdm-qu", statePath: ["cdm", "qu"], type: "float" },
        { id: "cdm-emodulus-ratio", statePath: ["cdm", "emodulusRatio"], type: "float" },
        { id: "cdm-t-ult", statePath: ["cdm", "tUlt"], type: "float" },
        { id: "cdm-rf", statePath: ["cdm", "rf"], type: "float" },
        { id: "cdm-phi", statePath: ["cdm", "phi"], type: "float" },

        { id: "sd-spacing", statePath: ["sandDrain", "spacing"], type: "float" },
        { id: "sd-pattern", statePath: ["sandDrain", "pattern"], type: "string" },
        { id: "sd-depth", statePath: ["sandDrain", "depth"], type: "float" },
        { id: "sd-diameter", statePath: ["sandDrain", "diameter"], type: "float" },
        { id: "sd-ch-ratio", statePath: ["sandDrain", "chRatio"], type: "float" },

        { id: "sc-spacing", statePath: ["sandColumn", "spacing"], type: "float" },
        { id: "sc-pattern", statePath: ["sandColumn", "pattern"], type: "string" },
        { id: "sc-depth", statePath: ["sandColumn", "depth"], type: "float" },
        { id: "sc-diameter", statePath: ["sandColumn", "diameter"], type: "float" },
        { id: "sc-modulus", statePath: ["sandColumn", "modulus"], type: "float" },
        { id: "sc-ch-ratio", statePath: ["sandColumn", "chRatio"], type: "float" },
        
        { id: "bp-spacing", statePath: ["bambooPile", "spacing"], type: "float" },
        { id: "bp-pattern", statePath: ["bambooPile", "pattern"], type: "string" },
        { id: "bp-depth", statePath: ["bambooPile", "depth"], type: "float" },
        { id: "bp-diameter", statePath: ["bambooPile", "diameter"], type: "float" },
        { id: "bp-modulus", statePath: ["bambooPile", "modulus"], type: "float" },
        { id: "bp-phi", statePath: ["bambooPile", "phi"], type: "float" },

        { id: "replace-depth", statePath: ["excavationReplacement", "depth"], type: "float" },
        { id: "replace-gamma", statePath: ["excavationReplacement", "gamma"], type: "float" },
        { id: "replace-gammasat", statePath: ["excavationReplacement", "gammaSat"], type: "float" },
        { id: "replace-modulus", statePath: ["excavationReplacement", "E"], type: "float" },
        { id: "replace-phi", statePath: ["excavationReplacement", "phi"], type: "float" },
        { id: "replace-c", statePath: ["excavationReplacement", "c"], type: "float" }
    ];

    inputIds.forEach(item => {
        const el = document.getElementById(item.id);
        if (el) {
            // Set initial value
            let val = getNestedValue(appState, item.statePath);
            if (el.type === "checkbox") {
                el.checked = val;
            } else {
                el.value = val;
            }

            // Input event listener
            el.addEventListener("input", (e) => {
                let value;
                if (el.type === "checkbox") {
                    value = el.checked;
                } else {
                    value = item.type === "float" ? parseFloat(el.value) : el.value;
                }
                
                if (!isNaN(value) || item.type === "string") {
                    // Validate zero values for treatment parameters that would cause division by zero
                    if (item.type === "float" && value === 0) {
                        let zeroForbiddenIds = [
                             "pvd-spacing", "pvd-width", "pvd-thickness", "pvd-depth", "pvd-ch-ratio", "pvd-ds-dw", "pvd-kh-ks", "pvd-qw",
                             "cdm-spacing", "cdm-diameter", "cdm-depth", "cdm-qu", "cdm-emodulus-ratio", "cdm-t-ult", "cdm-rf",
                            "sd-spacing", "sd-diameter", "sd-depth", "sd-ch-ratio",
                            "sc-spacing", "sc-diameter", "sc-depth", "sc-modulus", "sc-ch-ratio",
                            "bp-spacing", "bp-diameter", "bp-depth", "bp-modulus",
                            "replace-gamma", "replace-gammasat", "replace-modulus",
                            "emb-height", "emb-gamma", "emb-width", "emb-slope",
                            "design-life", "t-construction", "emb-m-factor"
                        ];
                        if (zeroForbiddenIds.includes(item.id)) {
                            el.style.borderColor = "var(--accent-danger, #ef4444)";
                            el.style.boxShadow = "0 0 0 2px rgba(239, 68, 68, 0.3)";
                            markAnalysisInvalid("Giá trị 0 không hợp lệ tại ô " + item.id + ".");
                            showValidationToast("Giá trị 0 không hợp lệ tại ô " + item.id + ".");
                            return; // Do NOT update state or run analysis
                        }
                    }
                    // Clear error styling if valid
                    el.style.borderColor = "";
                    el.style.boxShadow = "";
                    setNestedValue(appState, item.statePath, value);
                    scheduleAnalysis();
                } else {
                    el.style.borderColor = "var(--accent-danger, #ef4444)";
                    markAnalysisInvalid("Ô " + item.id + " đang trống hoặc không phải là số hợp lệ.");
                }
            });
        }
    });
}

function getNestedValue(obj, path) {
    return path.reduce((prev, curr) => prev ? prev[curr] : null, obj);
}

function setNestedValue(obj, path, value) {
    let current = obj;
    for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]];
    }
    current[path[path.length - 1]] = value;
}

// Render dynamic soil layers table
function renderLayersTable() {
    const tbody = document.getElementById("soil-table-body");
    tbody.innerHTML = "";

    appState.soilLayers.forEach((layer, idx) => {
        const tr = document.createElement("tr");
        let isGranular = layer.type === 'granular';
        tr.innerHTML = `
            <td style="text-align: center;">
                <span class="color-indicator" style="background-color: ${sanitizeColor(layer.color)};"></span>
            </td>
            <td>
                <input type="text" value="${escapeHtml(layer.name)}" 
                    onchange="updateLayerField(${layer.id}, 'name', this.value)">
            </td>
            <td>
                <select style="padding: 2px 4px; font-size: 0.75rem; width: 100%;" onchange="updateLayerField(${layer.id}, 'type', this.value)">
                    <option value="cohesive" ${!isGranular ? 'selected' : ''}>Đất dính</option>
                    <option value="granular" ${isGranular ? 'selected' : ''}>Đất rời</option>
                </select>
            </td>
            <td>
                <input type="number" step="0.1" min="0.1" value="${layer.thickness}" style="text-align:right;" 
                    onchange="updateLayerField(${layer.id}, 'thickness', parseFloat(this.value))">
            </td>
            <td>
                <div style="display:flex; gap:4px;">
                    <input type="number" step="0.1" min="10" value="${layer.gamma}" style="text-align:right;" 
                        title="Dung trọng tự nhiên &gamma;" onchange="updateLayerField(${layer.id}, 'gamma', parseFloat(this.value))">
                    <input type="number" step="0.1" min="10" value="${layer.gammaSat}" style="text-align:right;" 
                        title="Dung trọng bão hòa &gamma;sat" onchange="updateLayerField(${layer.id}, 'gammaSat', parseFloat(this.value))">
                </div>
            </td>
            <td>
                <input type="number" step="0.05" min="0.2" value="${layer.e0}" style="text-align:right;" 
                    ${isGranular ? 'disabled style="opacity: 0.5;"' : ''}
                    onchange="updateLayerField(${layer.id}, 'e0', parseFloat(this.value))">
            </td>
            <td>
                <div style="display:flex; gap:4px;">
                    <input type="number" step="0.001" min="0.001" value="${layer.Cc}" style="text-align:right;" 
                        ${isGranular ? 'disabled style="opacity: 0.5;"' : ''}
                        title="Chỉ số nén Cc" onchange="updateLayerField(${layer.id}, 'Cc', parseFloat(this.value))">
                    <input type="number" step="0.005" min="0.000" value="${layer.Cr}" style="text-align:right;" 
                        ${isGranular ? 'disabled style="opacity: 0.5;"' : ''}
                        title="Chỉ số nén lại Cr" onchange="updateLayerField(${layer.id}, 'Cr', parseFloat(this.value))">
                </div>
            </td>
            <td>
                <input type="number" step="1" min="1" value="${layer.sigmaP}" style="text-align:right;" 
                    ${isGranular ? 'disabled style="opacity: 0.5;"' : ''}
                    onchange="updateLayerField(${layer.id}, 'sigmaP', parseFloat(this.value))">
            </td>
            <td>
                <input type="number" step="100" min="100" value="${layer.E || 3000}" style="text-align:right;" 
                    title="Mô đun biến dạng Es (kPa)" onchange="updateLayerField(${layer.id}, 'E', parseFloat(this.value))">
            </td>
            <td>
                <div style="display:flex; gap:4px;">
                    <input type="number" step="1" min="0" value="${layer.c !== undefined ? layer.c : 10}" style="text-align:right;" 
                        title="Lực dính c (kPa)" onchange="updateLayerField(${layer.id}, 'c', parseFloat(this.value))">
                    <input type="number" step="1" min="0" max="45" value="${layer.phi !== undefined ? layer.phi : 5}" style="text-align:right;" 
                        title="Góc ma sát trong phi (độ)" onchange="updateLayerField(${layer.id}, 'phi', parseFloat(this.value))">
                </div>
            </td>
            <td>
                <div style="display:flex; gap:4px;">
                    <input type="number" step="0.01" min="0.01" value="${layer.Cv}" style="text-align:right;" 
                        ${isGranular ? 'disabled style="opacity: 0.5;"' : ''}
                        title="Hệ số cố kết Cv (m2/năm)" onchange="updateLayerField(${layer.id}, 'Cv', parseFloat(this.value))">
                    <input type="number" step="0.001" min="0" value="${layer.Ca}" style="text-align:right;" 
                        ${isGranular ? 'disabled style="opacity: 0.5;"' : ''}
                        title="Chỉ số từ biến Ca" onchange="updateLayerField(${layer.id}, 'Ca', parseFloat(this.value))">
                </div>
            </td>
            <td style="text-align: center;">
                <button class="btn btn-danger btn-sm" style="padding: 4px 8px; font-size: 10px;" onclick="deleteLayer(${layer.id})">X</button>
            </td>
        `;
        tbody.appendChild(tr);
    });

    // Update legend elements on canvas container
    renderLegend();
}

function updateLayerField(id, field, value) {
    const layer = appState.soilLayers.find(l => l.id === id);
    if (layer && (!isNaN(value) || typeof value === "string")) {
        layer[field] = value;
        if (field === 'type') {
            renderLayersTable(); // Re-render to disable/enable inputs
        }
        runAnalysis();
    }
}

function deleteLayer(id) {
    if (appState.soilLayers.length <= 1) {
        alert("Phải có ít nhất một lớp đất!");
        return;
    }
    appState.soilLayers = appState.soilLayers.filter(l => l.id !== id);
    renderLayersTable();
    runAnalysis();
}

function addNewLayer() {
    const newId = appState.soilLayers.length > 0 ? Math.max(...appState.soilLayers.map(l => l.id)) + 1 : 1;
    const color = SOIL_COLORS[newId % SOIL_COLORS.length];
    
    appState.soilLayers.push({
        id: newId,
        name: `Lớp đất mới ${newId}`,
        type: "cohesive",
        thickness: 5.0,
        gamma: 16.5,
        gammaSat: 17.2,
        e0: 1.2,
        Cc: 0.35,
        Cr: 0.05,
        sigmaP: 60.0,
        Cv: 2.0,
        Ca: 0.01,
        E: 3500,
        c: 15.0,
        phi: 10.0,
        color: color
    });

    renderLayersTable();
    runAnalysis();
}

function checkBoundaryError() {
    const method = appState.treatment.method;
    if (method === 'cdm' && appState.cdm.depth < 1.0) {
        return {
            hasError: true,
            msg: `Phát hiện lỗi tiệm cận đường biên: Chiều sâu cọc gia cố CDM L<sub>c</sub> = ${appState.cdm.depth.toFixed(2)} m quá nhỏ (< 1m). Mô hình tự động suy biến về nền tự nhiên (Không xử lý), loại bỏ các thành phần lún phụ trợ và ảnh hưởng từ vải địa kỹ thuật.`
        };
    }
    if ((method === 'excavation_replacement' || method === 'bamboo_replacement') && appState.excavationReplacement.depth <= 0.3) {
        return {
            hasError: true,
            msg: `Phát hiện lỗi tiệm cận đường biên: Chiều sâu đào thay đất = ${appState.excavationReplacement.depth.toFixed(2)} m quá nhỏ (&le; 0.3m). Mô hình tự động suy biến về nền tự nhiên (Không xử lý), loại bỏ các thành phần lún đàn hồi của đệm cát.`
        };
    }
    return { hasError: false, msg: "" };
}

function getEffectiveSoilLayers() {
    let hRep = appState.excavationReplacement.depth;
    let treatment = appState.treatment.method;
    
    // Boundary logic check: if depth <= 0.3m, do not substitute sand cushion
    let isDegenerate = (treatment === 'excavation_replacement' || treatment === 'bamboo_replacement') && hRep <= 0.3;
    
    if ((treatment !== 'excavation_replacement' && treatment !== 'bamboo_replacement') || hRep <= 0 || isDegenerate) {
        return appState.soilLayers.map(l => ({ ...l }));
    }
    
    let newLayers = [];
    
    // Add the replacement sand layer
    newLayers.push({
        id: 999, // special ID
        name: "Cát đệm thay thế (Sand Cushion)",
        type: "granular",
        thickness: hRep,
        gamma: appState.excavationReplacement.gamma,
        gammaSat: appState.excavationReplacement.gammaSat,
        e0: 0.65,
        Cc: 0,
        Cr: 0,
        sigmaP: 100.0,
        Cv: 100.0,
        Ca: 0,
        E: appState.excavationReplacement.E,
        c: appState.excavationReplacement.c,
        phi: appState.excavationReplacement.phi,
        color: "#c2b280" // sandy color
    });
    
    let currentDepth = 0;
    appState.soilLayers.forEach(layer => {
        let top = currentDepth;
        let bottom = currentDepth + layer.thickness;
        
        if (bottom <= hRep) {
            // Completely excavated
        } else if (top < hRep && bottom > hRep) {
            // Partially excavated
            let remainingThickness = bottom - hRep;
            newLayers.push({
                ...layer,
                thickness: remainingThickness
            });
        } else {
            // Completely below replacement depth
            newLayers.push({
                ...layer
            });
        }
        currentDepth = bottom;
    });
    
    return newLayers;
}

function renderExcavationReplacementVerification(circular) {
    const container = document.getElementById("cdm-verification-content");
    if (!container) return;
    
    let isPass = circular.FS >= 1.2;
    
    let html = `
        <div class="verification-section">
            <div class="verification-section-title">
                <span>1. Kiểm Toán Ổn Định Trượt Trụ Tròn (Fellenius - Ordinary Method of Slices)</span>
                <span class="verification-status ${isPass ? 'pass' : 'fail'}">${isPass ? 'ĐẠT (FS = ' + circular.FS.toFixed(3) + ' &ge; 1.2)' : 'KHÔNG ĐẠT (FS = ' + circular.FS.toFixed(3) + ' < 1.2)'}</span>
            </div>
            <p><strong>Phương pháp:</strong> Phân tố Fellenius (Ordinary Method of Slices - Swedish Circle), chia cung trượt thành 20 phân tố, tính toán ổn định trượt sâu qua nền đất đã thay thế cát đệm ở phía trên:</p>
            <div class="verification-formula">
                F<sub>s</sub> = &Sigma;[c<sub>i</sub> &middot; l<sub>i</sub> + W<sub>i</sub> &middot; cos(&alpha;<sub>i</sub>) &middot; tan(&phi;<sub>i</sub>)] / &Sigma;[W<sub>i</sub> &middot; sin(&alpha;<sub>i</sub>)]
            </div>
            <p><strong>Thông số lớp đệm cát thay thế:</strong></p>
            <ul>
                <li>Chiều sâu đào thay thế: H<sub>đào</sub> = <b>${appState.excavationReplacement.depth.toFixed(1)} m</b></li>
                <li>Dung trọng đệm cát: &gamma; = <b>${appState.excavationReplacement.gamma.toFixed(1)} kN/m³</b>, &gamma;<sub>sat</sub> = <b>${appState.excavationReplacement.gammaSat.toFixed(1)} kN/m³</b></li>
                <li>Lực dính: c<sub>cát</sub> = <b>${appState.excavationReplacement.c.toFixed(1)} kPa</b>, Góc ma sát: &phi;<sub>cát</sub> = <b>${appState.excavationReplacement.phi.toFixed(1)}°</b></li>
            </ul>
            <p><strong>Kết quả tìm kiếm cung trượt nguy hiểm nhất:</strong></p>
            <div class="verification-formula">
                F<sub>s,min</sub> = <strong>${circular.FS.toFixed(3)}</strong> &ge; [F<sub>s</sub>] = 1.2
            </div>
            <p style="font-size: 0.775rem; color: var(--text-secondary); margin-top: 5px;">
                Tâm cung trượt nguy hiểm nhất: <b>X<sub>c</sub> = ${circular.Xc.toFixed(2)} m</b> (tính từ tim đường), 
                cao trình tâm: <b>Z<sub>c</sub> = ${circular.Zc.toFixed(2)} m</b> (trên mặt đất tự nhiên), 
                bán kính <b>R = ${circular.R.toFixed(2)} m</b>. 
                Cung trượt đi qua lớp cát đệm và các lớp đất yếu bên dưới, được thể hiện bằng nét đứt đỏ trên sơ đồ 2D.
            </p>
        </div>
    `;
    
    container.innerHTML = html;
}

function renderLegend() {
    const legend = document.getElementById("canvas-legend");
    legend.innerHTML = "";
    let effectiveLayers = getEffectiveSoilLayers();
    effectiveLayers.forEach(layer => {
        const div = document.createElement("div");
        div.className = "legend-item";
        div.innerHTML = `
            <span class="color-indicator" style="background-color: ${sanitizeColor(layer.color)};"></span>
            <span>${escapeHtml(layer.name)} (H = ${layer.thickness.toFixed(1)}m)</span>
        `;
        legend.appendChild(div);
    });
}

// Analytical vertical stress calculation based on Osterberg (1957)
// Calculates stress under the vertical face of a half-trapezoid of crest width b, slope width a.
function osterbergHalfInfluence(a, b, z) {
    if (globalThis.NendatYeuCore) return globalThis.NendatYeuCore.osterbergHalfInfluence(a, b, z);
    if (z <= 0) return 0.5;
    
    // If slope width a is 0, it reduces to a rectangular loading of width b
    if (a < 0.001) {
        let theta = Math.atan(b / z);
        return (1 / Math.PI) * (theta + (b * z) / (b * b + z * z));
    }
    
    let aPlusB = a + b;
    let alpha1 = Math.atan(aPlusB / z) - Math.atan(b / z);
    let alpha2 = Math.atan(b / z);
    
    let Term1 = (aPlusB / a) * (alpha1 + alpha2);
    let Term2 = (b / a) * alpha2;
    
    return (1 / Math.PI) * (Term1 - Term2);
}

// Traffic Surcharge stress calculation (Centerline of symmetric strip load)
function stripLoadInfluenceCenterline(B, z) {
    if (globalThis.NendatYeuCore) return globalThis.NendatYeuCore.stripLoadInfluenceCenterline(B, z);
    if (z <= 0) return 1.0;
    let b = B / 2;
    let theta = 2 * Math.atan(b / z);
    return (1 / Math.PI) * (theta + Math.sin(theta));
}

// Helper to format numbers with 2 decimal places and comma separator (standard Vietnamese notation)
function formatDecimal(val, decimals = 2) {
    if (globalThis.NendatYeuCore) return globalThis.NendatYeuCore.formatDecimal(val, decimals);
    if (!Number.isFinite(Number(val))) {
        return "—";
    }
    return val.toFixed(decimals).replace('.', ',');
}

// CORE ANALYSIS ROUTINE
function runAnalysis() {
    window.analysisSolverErrors = [];
    // Validate critical parameters to prevent division by zero and infinite loops
    if (!validateTreatmentInputs()) {
        return; // Abort analysis if inputs are invalid
    }
    let treatment = appState.treatment.method;
    
    // Boundary logic error warning check
    let boundary = checkBoundaryError();
    const boundaryBanner = document.getElementById("boundary-error-banner");
    const boundaryText = document.getElementById("boundary-error-text");
    if (boundary.hasError) {
        if (boundaryBanner && boundaryText) {
            boundaryText.innerHTML = `<strong>⚠️ CẢNH BÁO TIỆM CẬN ĐƯỜNG BIÊN:</strong> ${boundary.msg}`;
            boundaryBanner.style.display = "flex";
        }
    } else {
        if (boundaryBanner) {
            boundaryBanner.style.display = "none";
        }
    }
    
    let activeTreatment = boundary.hasError ? 'none' : treatment;

    // Toggle conditional PVD inputs panel
    const pvdTcvnPanel = document.getElementById("pvd-tcvn-only");
    if (pvdTcvnPanel) {
        pvdTcvnPanel.style.display = (appState.pvd.standard === 'tcvn_9355' && (treatment === 'pvd' || treatment === 'vacuum_pvd')) ? 'grid' : 'none';
    }
    
    let hRep = (activeTreatment === 'excavation_replacement' || activeTreatment === 'bamboo_replacement') ? appState.excavationReplacement.depth : 0;

    // 1. Subdivide layers into smaller sublayers (thickness delta_z <= 1.0 m)
    let sublayers = [];
    let currentDepth = 0;
    let effectiveLayers = getEffectiveSoilLayers();

    effectiveLayers.forEach((layer) => {
        let thickness = layer.thickness;
        let numSub = Math.ceil(thickness / 1.0);
        let dz = thickness / numSub;

        for (let i = 0; i < numSub; i++) {
            sublayers.push({
                layerId: layer.id,
                layerName: layer.name,
                layerColor: layer.color,
                type: layer.type || (layer.Cc <= 0.05 ? 'granular' : 'cohesive'),
                thickness: dz,
                depthTop: currentDepth,
                depthCenter: currentDepth + dz / 2,
                depthBottom: currentDepth + dz,
                
                // Soil geotechnical parameters
                gamma: layer.gamma,
                gammaSat: layer.gammaSat,
                e0: layer.e0,
                Cc: layer.Cc,
                Cr: layer.Cr,
                sigmaP: layer.sigmaP,
                Cv: layer.Cv,
                Ca: layer.Ca,
                E: layer.E || 3000,
                c: layer.c !== undefined ? layer.c : 10.0,
                phi: layer.phi !== undefined ? layer.phi : 5.0,
                
                // Stress outputs
                sigmaV0: 0,
                deltaSigmaZ: 0,
                
                // Settlement outputs
                Si: 0,
                Sc: 0,
                Ss: 0,
                Stotal: 0
            });
            currentDepth += dz;
        }
    });

    const totalDepth = currentDepth;

    // 2. Calculate initial vertical effective stress (sigma'_v0)
    let cumulativeOverburden = 0;
    sublayers.forEach((sub) => {
        const gwt = appState.foundation.gwt;
        sub.sigmaV0 = cumulativeOverburden +
            effectiveStressIncrement(sub, sub.depthTop, sub.depthCenter, gwt);
        cumulativeOverburden +=
            effectiveStressIncrement(sub, sub.depthTop, sub.depthBottom, gwt);
    });

    // 3. Calculate additional vertical stress (delta_sigma_z)
    let hEmb = appState.embankment.height;
    let gammaEmb = appState.embankment.gamma;
    let bCrest = appState.embankment.width;
    let mSlope = appState.embankment.slope;
    
    let isStaged = appState.embankment.staged;
    let h1 = appState.embankment.stagedH1 || 2.0;
    let t1_yr = (appState.embankment.stagedT1 || 6) / 12;
    let h2 = appState.embankment.stagedH2 || 2.5;

    let qEmb = hEmb * gammaEmb;
    let aSlope = mSlope * hEmb; // Horizontal slope width
    let bHalf = bCrest / 2;     // Half crest width

    let qSurcharge = appState.foundation.surcharge;

    sublayers.forEach((sub) => {
        let z = sub.depthCenter;
        
        // Vertical stress due to embankment (centerline is the sum of two symmetrical halves)
        let I_emb = 2 * osterbergHalfInfluence(aSlope, bHalf, z);
        let deltaSigmaEmb = qEmb * I_emb;

        // Stage 1 stress
        let deltaSigmaEmb1 = 0;
        if (isStaged) {
            let B1 = bCrest + 2 * mSlope * (hEmb - h1);
            let bHalf1 = B1 / 2;
            let aSlope1 = mSlope * h1;
            let qEmb1 = h1 * gammaEmb;
            let I_emb1 = 2 * osterbergHalfInfluence(aSlope1, bHalf1, z);
            deltaSigmaEmb1 = qEmb1 * I_emb1;
        } else {
            deltaSigmaEmb1 = deltaSigmaEmb;
        }

        // Vertical stress due to traffic surcharge at top of embankment
        // The surcharge acts at the surface of embankment, so depth is z + hEmb
        let I_surch = stripLoadInfluenceCenterline(bCrest, z + hEmb);
        let deltaSigmaSurch = qSurcharge * I_surch;

        sub.deltaSigmaEmb1 = deltaSigmaEmb1;
        sub.deltaSigmaEmb = deltaSigmaEmb;
        sub.deltaSigmaSurch = deltaSigmaSurch;
        sub.deltaSigmaZ = deltaSigmaEmb + deltaSigmaSurch;
    });

    // TCCS 41: only include sublayers inside the compression influence zone.
    // For roads, the limiting depth is taken where delta_sigma_z <= 0.15 sigma'_v0.
    const qualifyingSublayers = sublayers.filter(sub =>
        Number.isFinite(sub.deltaSigmaZ) && Number.isFinite(sub.sigmaV0) &&
        sub.deltaSigmaZ > 0.15 * sub.sigmaV0
    );
    const compressionDepth = qualifyingSublayers.length > 0
        ? Math.max(...qualifyingSublayers.map(sub => sub.depthBottom))
        : 0;
    sublayers.forEach(sub => {
        sub.inSettlementZone = compressionDepth > 0 && sub.depthCenter <= compressionDepth;
    });
    if (compressionDepth <= 0) {
        markAnalysisInvalid("Không xác định được vùng chịu nén Ha từ điều kiện ứng suất.");
        return;
    }

    // 4. Calculate primary consolidation (Sc), immediate (Si), and secondary creep (Ss) settlements
    let totalSc = 0;
    let totalSi = 0;
    let totalSs = 0;
    let designLife = appState.foundation.life;
    let tConst = appState.foundation.tConstruction; // Assume 1.0 year of consolidation preloading
    treatment = activeTreatment;

    sublayers.forEach((sub) => {
        if (!sub.inSettlementZone) {
            sub.Sc = 0;
            sub.Si = 0;
            sub.Ss = 0;
            sub.Stotal = 0;
            sub.Sc_perm = 0;
            sub.Sc_preload = 0;
            sub.Sc_preload1 = 0;
            sub.Sc_preload2 = 0;
            return;
        }

        let s0 = sub.sigmaV0;
        let ds = sub.deltaSigmaZ;
        let sp = sub.sigmaP;
        let e0 = sub.e0;
        let Cc = sub.Cc;
        let Cr = sub.Cr;
        let Ca = sub.Ca;
        let h = sub.thickness; // meters

        let sc = 0;
        let si = 0;
        let ss = 0;

        if (sub.type === 'granular') {
            // Simplified elastic estimate for granular soil; preliminary use only (TCVN 9362 excludes road design)
            let beta = 0.8;
            si = beta * (ds / sub.E) * h;
            sc = 0;
            ss = 0;
        } else if (treatment === 'cdm' && sub.depthCenter <= appState.cdm.depth) {
            // Soil-cement column composite ground (TCVN 9403:2012; TCVN 9906 only when its scope applies)
            let d_cdm = appState.cdm.diameter;
            let spacing = appState.cdm.spacing;
            let pattern = appState.cdm.pattern;
            let q_u = appState.cdm.qu * 1000; // kPa
            let E_p = appState.cdm.emodulusRatio * q_u; // kPa
            let E_s = sub.E || 3000; // kPa
            
            // Area replacement ratio m
            let m_area = pattern === 'triangular' ? 
                (Math.PI * d_cdm * d_cdm) / (2 * Math.sqrt(3) * spacing * spacing) : 
                (Math.PI * d_cdm * d_cdm) / (4 * spacing * spacing);
            
            // Composite Modulus E_composite
            let E_comp = m_area * E_p + (1 - m_area) * E_s;
            sub.E_comp = E_comp;
            
            // Settlement in reinforced zone (elastic deformation of composite ground)
            // S_composite = (deltaSigmaZ / E_composite) * thickness
            let s_comp = (ds / E_comp) * h;
            
            sc = s_comp; // Treat composite elastic settlement as consolidation settlement
            si = 0;      // Already accounted in elastic composite modulus
            ss = 0;      // Soil cement column ground has negligible secondary compression in reinforced zone
        } else if (treatment === 'sand_column' && sub.depthCenter <= appState.sandColumn.depth) {
            // Sand column / granular column composite ground (TCCS 41:2022)
            let d_sc = appState.sandColumn.diameter;
            let spacing = appState.sandColumn.spacing;
            let pattern = appState.sandColumn.pattern;
            let E_p = appState.sandColumn.modulus * 1000; // MPa to kPa
            let E_s = sub.E || 3000; // kPa
            
            // Area replacement ratio m
            let m_area = pattern === 'triangular' ? 
                (Math.PI * d_sc * d_sc) / (2 * Math.sqrt(3) * spacing * spacing) : 
                (Math.PI * d_sc * d_sc) / (4 * spacing * spacing);
            
            // Composite Modulus E_composite
            let E_comp = m_area * E_p + (1 - m_area) * E_s;
            sub.E_comp = E_comp;
            
            // Settlement in reinforced zone (elastic deformation of composite ground)
            let s_comp = (ds / E_comp) * h;
            
            sc = s_comp; // Sand column settlement is composite elastic based
            si = 0;
            ss = 0;
        } else if (
            (treatment === 'bamboo_pile' && sub.depthCenter <= appState.bambooPile.depth) ||
            (treatment === 'bamboo_replacement' && sub.depthCenter > hRep && sub.depthCenter <= hRep + appState.bambooPile.depth)
        ) {
            // Bamboo pile composite ground
            let d_bp = appState.bambooPile.diameter;
            let spacing = appState.bambooPile.spacing;
            let pattern = appState.bambooPile.pattern;
            let E_p = appState.bambooPile.modulus * 1000; // MPa to kPa
            let E_s = sub.E || 3000; // kPa
            
            // Area replacement ratio m
            let m_area = pattern === 'triangular' ? 
                (Math.PI * d_bp * d_bp) / (2 * Math.sqrt(3) * spacing * spacing) : 
                (Math.PI * d_bp * d_bp) / (4 * spacing * spacing);
            
            // Composite Modulus E_composite
            let E_comp = m_area * E_p + (1 - m_area) * E_s;
            sub.E_comp = E_comp;
            
            // Settlement in reinforced zone (elastic deformation of composite ground)
            let s_comp = (ds / E_comp) * h;
            
            sc = s_comp;
            si = 0;
            ss = 0;
        } else {
                    // A. Primary Consolidation Settlement (Sc)
            let isVacuum = (treatment === 'vacuum_pvd');
            let pvac = isVacuum ? appState.pvd.vacuumPressure : 0;
            let etavac = isVacuum ? appState.pvd.vacuumEfficiency : 0;
            let pvac_eff = pvac * etavac;

            let ds_perm = sub.deltaSigmaZ;
            let ds_preload = (sub.deltaSigmaEmb || 0) + (isVacuum && (sub.depthCenter <= appState.pvd.depth) ? pvac_eff : 0);
            let ds_preload1 = (sub.deltaSigmaEmb1 || 0) + (isVacuum && (sub.depthCenter <= appState.pvd.depth) ? pvac_eff : 0);
            
            sub.deltaSigmaPreload = ds_preload;

            let sc_perm_val = calculateSc(s0, sp, e0, Cc, Cr, h, ds_perm);
            let sc_preload_val = calculateSc(s0, sp, e0, Cc, Cr, h, ds_preload);
            let sc_preload1_val = calculateSc(s0, sp, e0, Cc, Cr, h, ds_preload1);
            
            sub.Sc_perm = sc_perm_val * 100;
            sub.Sc_preload = sc_preload_val * 100;
            sub.Sc_preload1 = sc_preload1_val * 100;
            sub.Sc_preload2 = Math.max(0, sub.Sc_preload - sub.Sc_preload1);

            sc = sc_perm_val;

            // B. Immediate settlement inferred from the empirical factor m.
            si = Math.max(0, appState.foundation.mFactor - 1) * sc_perm_val;

            // C. Secondary Creep Settlement (Ss)
            let deltaE = (sc_preload_val * (1 + e0)) / h;
            let ep = e0 - deltaE;
            if (ep < 0.1) ep = 0.1;

            let tEnd = tConst + designLife;
            let tPrimary = 1.0;
            if (tEnd > tPrimary && Ca > 0) {
                ss = (Ca / (1 + ep)) * h * Math.log10(tEnd / tPrimary);
            }
        }

        const scCm = sc * 100;
        if (!Number.isFinite(sub.Sc_perm)) sub.Sc_perm = scCm;
        if (!Number.isFinite(sub.Sc_preload)) sub.Sc_preload = scCm;
        if (!Number.isFinite(sub.Sc_preload1)) sub.Sc_preload1 = scCm;
        if (!Number.isFinite(sub.Sc_preload2)) sub.Sc_preload2 = 0;

        sub.Sc = scCm; // Convert to cm
        totalSc += sub.Sc;

        sub.Si = si * 100; // Convert to cm
        totalSi += sub.Si;

        sub.Ss = ss * 100; // Convert to cm
        totalSs += sub.Ss;

        sub.Stotal = sub.Si + sub.Sc + sub.Ss;
    });

    // 5. Degree of Consolidation & Time Rate Calculations
    let clayThickness = 0;
    let totalWeightedCv = 0;
    let clayThicknessBelow = 0;
    let totalWeightedCvBelow = 0;
    
    let colDepth = 0;
    if (treatment === 'cdm') colDepth = appState.cdm.depth;
    else if (treatment === 'sand_column') colDepth = appState.sandColumn.depth;
    else if (treatment === 'bamboo_pile') colDepth = appState.bambooPile.depth;
    else if (treatment === 'bamboo_replacement') colDepth = hRep + appState.bambooPile.depth;
    else if (treatment === 'pvd' || treatment === 'vacuum_pvd') colDepth = appState.pvd.depth;
    else if (treatment === 'sand_drain') colDepth = appState.sandDrain.depth;

    sublayers.forEach(sub => {
        if (sub.inSettlementZone && (sub.type === 'cohesive' || sub.Cc > 0.05)) {
            clayThickness += sub.thickness;
            totalWeightedCv += sub.Cv * sub.thickness;
            if (sub.depthCenter > colDepth) {
                clayThicknessBelow += sub.thickness;
                totalWeightedCvBelow += sub.Cv * sub.thickness;
            }
        }
    });

    let avgCv = clayThickness > 0 ? (totalWeightedCv / clayThickness) : 2.0;
    let avgCvBelow = clayThicknessBelow > 0 ? (totalWeightedCvBelow / clayThicknessBelow) : avgCv;

    let H_d = clayThickness > 0 ? getDrainagePath(clayThickness) : 5.0;
    let H_d_below = clayThicknessBelow > 0 ? getDrainagePath(clayThicknessBelow) : 5.0;

    function updateSublayerU(time_t) {
        if (time_t <= 0) {
            sublayers.forEach(sub => { sub.currentU = 0; });
            return;
        }
        let H_d_path = H_d;
        let H_d_below_path = H_d_below;

        let Tv = (avgCv * time_t) / (H_d_path * H_d_path);
        let U_v = 0;
        if (Tv <= 0.283) {
            U_v = Math.sqrt((4 * Tv) / Math.PI);
        } else {
            U_v = 1 - (8 / (Math.PI * Math.PI)) * Math.exp(-(Math.PI * Math.PI * Tv) / 4);
        }
        if (U_v > 1.0) U_v = 1.0;
        if (U_v < 0) U_v = 0;

        let U_below = 0;
        if (clayThicknessBelow > 0) {
            let Tv_below = (avgCvBelow * time_t) / (H_d_below_path * H_d_below_path);
            if (Tv_below <= 0.283) {
                U_below = Math.sqrt((4 * Tv_below) / Math.PI);
            } else {
                U_below = 1 - (8 / (Math.PI * Math.PI)) * Math.exp(-(Math.PI * Math.PI * Tv_below) / 4);
            }
            if (U_below > 1.0) U_below = 1.0;
            if (U_below < 0) U_below = 0;
        } else {
            U_below = 1.0;
        }

        let U_above = 0;
        if (treatment === 'bamboo_pile' || treatment === 'bamboo_replacement') {
            let clayThicknessAbove = 0;
            let totalWeightedCvAbove = 0;
            sublayers.forEach(sub => {
                if (sub.depthCenter <= colDepth && sub.Cc > 0.05) {
                    clayThicknessAbove += sub.thickness;
                    totalWeightedCvAbove += sub.Cv * sub.thickness;
                }
            });
            let avgCvAbove = clayThicknessAbove > 0 ? (totalWeightedCvAbove / clayThicknessAbove) : avgCv;
            let H_d_above = clayThicknessAbove > 0 ? getDrainagePath(clayThicknessAbove) : 5.0;
            let Tv_above = (avgCvAbove * time_t) / (H_d_above * H_d_above);
            if (Tv_above <= 0.283) {
                U_above = Math.sqrt((4 * Tv_above) / Math.PI);
            } else {
                U_above = 1 - (8 / (Math.PI * Math.PI)) * Math.exp(-(Math.PI * Math.PI * Tv_above) / 4);
            }
            if (U_above > 1.0) U_above = 1.0;
            if (U_above < 0) U_above = 0;
        }

        let U_combined = 0;
        if ((treatment === 'pvd' || treatment === 'vacuum_pvd' || treatment === 'sand_drain') && clayThickness > 0) {
            let spacing, pattern, dw, Ch;
            if (treatment === 'pvd' || treatment === 'vacuum_pvd') {
                spacing = appState.pvd.spacing;
                pattern = appState.pvd.pattern;
                let bb = appState.pvd.width / 1000;
                let tb = appState.pvd.thickness / 1000;
                dw = (2 * (bb + tb)) / Math.PI;
                Ch = avgCv * appState.pvd.chRatio;
            } else {
                spacing = appState.sandDrain.spacing;
                pattern = appState.sandDrain.pattern;
                dw = appState.sandDrain.diameter;
                Ch = avgCv * appState.sandDrain.chRatio;
            }
            
            let de = pattern === 'triangular' ? (1.05 * spacing) : (1.13 * spacing);
            let nRatio = de / dw;
            if (nRatio < 1.01) nRatio = 1.01;
            
            let Fn = 0;
            let Fs = 0;
            let Fr = 0;
            if (treatment === 'pvd' || treatment === 'vacuum_pvd') {
                let isTcvn = (appState.pvd.standard === 'tcvn_9355');
                if (isTcvn) {
                    Fn = (Math.pow(nRatio, 2) / (Math.pow(nRatio, 2) - 1)) * Math.log(nRatio) - (3 * Math.pow(nRatio, 2) - 1) / (4 * Math.pow(nRatio, 2));
                    let dsDw = appState.pvd.dsDw || 2.5;
                    let khKs = appState.pvd.khKs || 3.0;
                    Fs = (khKs - 1) * Math.log(dsDw);
                    
                    let avgEs = 3000;
                    if (appState.soilLayers && appState.soilLayers.length > 0) {
                        let cohesiveLayers = appState.soilLayers.filter(l => l.type === 'cohesive' || l.Cc > 0.05);
                        if (cohesiveLayers.length > 0) {
                            avgEs = cohesiveLayers.reduce((acc, l) => acc + (l.E || 3000), 0) / cohesiveLayers.length;
                        }
                    }
                    if (avgEs <= 0) avgEs = 3000;
                    let kh_year = Ch * (1.0 / avgEs) * 9.81;
                    let kh_s = kh_year / (3.1536 * 1e7);
                    let qw_m3s = (appState.pvd.qw || 60.0) * 1e-6;
                    Fr = (2.0 / 3.0) * Math.PI * Math.pow(appState.pvd.depth || 15.0, 2) * (kh_s / qw_m3s);
                } else {
                    Fn = Math.log(nRatio) - 0.75;
                }
            } else {
                Fn = Math.log(nRatio) - 0.75;
            }
            
            let F_total = Fn + Fs + Fr;
            let Tr = (Ch * time_t) / (de * de);
            let U_r = 1 - Math.exp((-8 * Tr) / F_total);
            if (U_r < 0) U_r = 0;

            U_combined = 1 - (1 - U_v) * (1 - U_r);
        }

        sublayers.forEach(sub => {
            if (treatment === 'cdm') {
                sub.currentU = (sub.depthCenter <= colDepth) ? 1.0 : U_below;
            } else if (treatment === 'sand_column') {
                sub.currentU = (sub.depthCenter <= colDepth) ? 1.0 : U_below;
            } else if (treatment === 'bamboo_pile' || treatment === 'bamboo_replacement') {
                sub.currentU = (sub.depthCenter <= colDepth) ? U_above : U_below;
            } else if (treatment === 'pvd' || treatment === 'vacuum_pvd' || treatment === 'sand_drain') {
                sub.currentU = (sub.depthCenter <= colDepth) ? U_combined : U_v;
            } else {
                sub.currentU = U_v;
            }
        });
    }

    // Precalculate degree of consolidation and Sc achieved at handover (tConst)
    // Note: isStaged, t1_yr, h1, h2 already declared in Section 3 (stress calc)

    if (isStaged) {
        if (tConst <= t1_yr) {
            updateSublayerU(tConst);
            sublayers.forEach(sub => {
                sub.U_const_layer = sub.currentU;
                if (sub.type === 'granular' || (treatment === 'cdm' && sub.depthCenter <= colDepth) || (treatment === 'sand_column' && sub.depthCenter <= colDepth)) {
                    sub.s_c_const_layer = sub.Sc_preload1;
                } else {
                    sub.s_c_const_layer = sub.Sc_preload1 * sub.currentU;
                }
            });
        } else {
            updateSublayerU(tConst);
            sublayers.forEach(sub => { sub.U_const_layer1 = sub.currentU; });
            
            updateSublayerU(tConst - t1_yr);
            sublayers.forEach(sub => { sub.U_const_layer2 = sub.currentU; });
            
            sublayers.forEach(sub => {
                if (sub.type === 'granular' || (treatment === 'cdm' && sub.depthCenter <= colDepth) || (treatment === 'sand_column' && sub.depthCenter <= colDepth)) {
                    sub.s_c_const_layer = sub.Sc_preload;
                } else {
                    sub.s_c_const_layer = sub.Sc_preload1 * sub.U_const_layer1 + sub.Sc_preload2 * sub.U_const_layer2;
                }
                sub.U_const_layer = sub.Sc_preload > 0 ? (sub.s_c_const_layer / sub.Sc_preload) : 1.0;
            });
        }
    } else {
        updateSublayerU(tConst);
        sublayers.forEach(sub => {
            sub.U_const_layer = sub.currentU;
            if (sub.type === 'granular' || (treatment === 'cdm' && sub.depthCenter <= colDepth) || (treatment === 'sand_column' && sub.depthCenter <= colDepth)) {
                sub.s_c_const_layer = sub.Sc_preload;
            } else {
                sub.s_c_const_layer = sub.Sc_preload * sub.currentU;
            }
        });
    }

    // Time array for curves
    let timeSteps = [];
    let settlementTimeData = [];
    let maxTime = Math.max(50, tConst + designLife + 10);
    
    for (let t = 0; t <= maxTime; t += 0.5) {
        let s_t = 0;
        
        let u_t1 = [];
        let u_t2 = [];
        
        if (isStaged && t > t1_yr) {
            updateSublayerU(t);
            sublayers.forEach((sub, idx) => { u_t1[idx] = sub.currentU; });
            
            updateSublayerU(t - t1_yr);
            sublayers.forEach((sub, idx) => { u_t2[idx] = sub.currentU; });
        } else {
            updateSublayerU(t);
            sublayers.forEach((sub, idx) => { u_t1[idx] = sub.currentU; });
        }

        sublayers.forEach((sub, idx) => {
            let s_c_t = 0;
            let s_i_t = sub.Si; 
            
            if (sub.type === 'granular' || (treatment === 'cdm' && sub.depthCenter <= colDepth) || (treatment === 'sand_column' && sub.depthCenter <= colDepth)) {
                s_c_t = sub.Sc_perm;
            } else {
                if (t <= tConst) {
                    if (isStaged) {
                        if (t <= t1_yr) {
                            s_c_t = sub.Sc_preload1 * u_t1[idx];
                        } else {
                            s_c_t = sub.Sc_preload1 * u_t1[idx] + sub.Sc_preload2 * u_t2[idx];
                        }
                    } else {
                        s_c_t = sub.Sc_preload * u_t1[idx];
                    }
                } else {
                    let s_c_const = sub.s_c_const_layer;
                    if (s_c_const >= sub.Sc_perm) {
                        s_c_t = sub.Sc_perm;
                    } else {
                        let u_curr = u_t1[idx];
                        let u_after = (u_curr - sub.U_const_layer) / (1 - sub.U_const_layer);
                        if (u_after < 0) u_after = 0;
                        if (u_after > 1) u_after = 1;
                        s_c_t = s_c_const + (sub.Sc_perm - s_c_const) * u_after;
                    }
                }
            }

            // creep settlement s_s_t
            let s_s_t = 0;
            let tPrimary = 1.0;
            if (t > tPrimary && sub.Ss > 0) {
                let deltaE = ((sub.s_c_const_layer / 100) * (1 + sub.e0)) / sub.thickness;
                let ep = sub.e0 - deltaE;
                if (ep < 0.1) ep = 0.1;
                let ss_val = (sub.Ca / (1 + ep)) * sub.thickness * Math.log10(t / tPrimary);
                s_s_t = ss_val * 100; // in cm
            }

            s_t += s_i_t + s_c_t + s_s_t;
        });

        // Restore active sublayer U representation for stability checks
        sublayers.forEach((sub, idx) => { sub.currentU = u_t1[idx]; });

        let sumScU = sublayers.reduce((acc, sub) => acc + (sub.Sc * sub.currentU), 0);
        let U_total_step = totalSc > 0 ? (sumScU / totalSc) : 1.0;
        if (U_total_step > 1.0) U_total_step = 1.0;

        timeSteps.push(t);
        settlementTimeData.push({
            t: t,
            U: U_total_step,
            S: s_t
        });
    }

    // 6. Internally consistent total: every consumer uses the same numeric result.
    let sEmpirical = totalSi + totalSc + totalSs;

    // Remaining Settlement calculation at t_construction (end of preloading/pavement laying)
    // S_remaining = S(t_end) - S(t_construction)
    let tEnd = tConst + designLife;
    let sAtConst = getSettlementAtTime(tConst, settlementTimeData);
    let sAtEnd = getSettlementAtTime(tEnd, settlementTimeData);
    let remainingSettlement = sAtEnd - sAtConst;
    let uAtConst = getUAtTime(tConst, settlementTimeData) * 100; // %

    // 7. Update metrics cards on DOM
    document.getElementById("val-s-total").innerText = formatDecimal(sEmpirical, 2);
    document.getElementById("val-s-c").innerText = formatDecimal(totalSc, 2);
    document.getElementById("val-s-i").innerText = formatDecimal(totalSi, 2);
    document.getElementById("val-s-s").innerText = formatDecimal(totalSs, 2);
    document.getElementById("val-u-handover").innerText = formatDecimal(uAtConst, 2) + "%";
    document.getElementById("val-s-remaining").innerText = formatDecimal(remainingSettlement, 2);

    // 8. Allowable Settlement Compliance Banner
    const banner = document.getElementById("compliance-banner");
    const bannerText = document.getElementById("compliance-text");
    const valRemaining = document.getElementById("val-s-remaining");
    const limit = appState.foundation.allowable;

    const requiresPvdU90 = (treatment === "pvd" || treatment === "vacuum_pvd") &&
        appState.pvd.standard === "tcvn_9355";
    const isPvdConsolidationPass = !requiresPvdU90 || uAtConst >= 90;
    let isSettlementPass = remainingSettlement <= limit && isPvdConsolidationPass;
    let isCdmPass = true;
    let cdmCheckMsg = "";

    if (treatment === 'cdm') {
        let q_u = appState.cdm.qu * 1000; // kPa
        let E_p = appState.cdm.emodulusRatio * q_u; // kPa
        let E_s_top = appState.soilLayers[0] ? (appState.soilLayers[0].E || 3000) : 2000; // kPa
        
        let d_cdm = appState.cdm.diameter;
        let spacing = appState.cdm.spacing;
        let pattern = appState.cdm.pattern;
        let m_area = pattern === 'triangular' ? 
            (Math.PI * d_cdm * d_cdm) / (2 * Math.sqrt(3) * spacing * spacing) : 
            (Math.PI * d_cdm * d_cdm) / (4 * spacing * spacing);
            
        let E_comp_top = m_area * E_p + (1 - m_area) * E_s_top;
        let q_top = (appState.embankment.height * appState.embankment.gamma) + appState.foundation.surcharge;
        
        let sigma_p_max = q_top * (E_p / E_comp_top); // kPa
        let FS_pile = sigma_p_max > 0 ? (q_u / sigma_p_max) : 999;
        isCdmPass = FS_pile >= 1.2;
        
        cdmCheckMsg = `<br><strong>Kiểm toán sơ bộ thân cọc (TCVN 9403:2012):</strong> Ứng suất đầu cọc &sigma;<sub>p,max</sub> = ${formatDecimal(sigma_p_max, 2)} kPa. Hệ số an toàn vật liệu cọc F<sub>s</sub> = ${formatDecimal(FS_pile, 2)} (${isCdmPass ? "ĐẠT &ge; 1.2" : "KHÔNG ĐẠT &lt; 1.2"}).`;
    }

    if (isSettlementPass && isCdmPass) {
        banner.style.background = "rgba(16, 185, 129, 0.15)";
        banner.style.borderColor = "rgba(16, 185, 129, 0.4)";
        let msg = `<strong>ĐẠT YÊU CẦU:</strong> Độ lún dư còn lại S<sub>dư</sub> = ${formatDecimal(remainingSettlement, 2)} cm &le; [S] = ${formatDecimal(limit, 2)} cm (Quy định TCCS 41:2022).`;
        if (treatment === 'cdm') {
            msg += cdmCheckMsg;
        }
        bannerText.innerHTML = msg;
        bannerText.style.color = "#a7f3d0";
        valRemaining.className = "metric-value";
        valRemaining.style.color = "var(--accent-success)";
    } else {
        banner.style.background = "rgba(239, 68, 68, 0.15)";
        banner.style.borderColor = "rgba(239, 68, 68, 0.4)";
        
        let msg = "";
        if (remainingSettlement > limit) {
            msg += "<strong>KHÔNG ĐẠT LÚN DƯ:</strong> S<sub>dư</sub> = " +
                formatDecimal(remainingSettlement, 2) + " cm &gt; [S] = " + formatDecimal(limit, 2) + " cm.";
        } else {
            msg += "<strong>ĐẠT LÚN DƯ:</strong> S<sub>dư</sub> = " +
                formatDecimal(remainingSettlement, 2) + " cm &le; [S] = " + formatDecimal(limit, 2) + " cm.";
        }
        if (!isPvdConsolidationPass) {
            msg += " <strong>KHÔNG ĐẠT ĐỘ CỐ KẾT:</strong> U(t<sub>ck</sub>) = " +
                formatDecimal(uAtConst, 2) + "% &lt; 90% theo TCVN 9355:2012.";
        }

        if (treatment === 'cdm') {
            msg += cdmCheckMsg;
            if (!isCdmPass) {
                msg += " <span style='color: #fca5a5; font-weight: bold;'>Cảnh báo: Cường độ cọc không đủ an toàn!</span>";
            }
        } else {
            msg += " Cần tăng thời gian gia tải hoặc dùng giải pháp gia cố mạnh hơn!";
        }
        
        bannerText.innerHTML = msg;
        bannerText.style.color = "#fca5a5";
        valRemaining.className = "metric-value danger";
        valRemaining.style.color = "var(--accent-danger)";
    }

    // Detailed stability & geotextile verifications
    const cdmVerifyCard = document.getElementById("cdm-verification-card");
    const verifTitle = document.getElementById("verification-card-title");
    
    if (cdmVerifyCard) cdmVerifyCard.style.display = "block";

    const slipCircleContainer = document.getElementById("slip-circle-canvas-container");
    if (slipCircleContainer) {
        let showSlip = (treatment === 'cdm' || treatment === 'bamboo_pile' || treatment === 'bamboo_replacement' || treatment === 'excavation_replacement' || ((treatment === 'pvd' || treatment === 'vacuum_pvd') && appState.pvd.standard === 'tcvn_9355'));
        slipCircleContainer.style.display = showSlip ? "flex" : "none";
    }

    if (treatment === 'none') {
        if (verifTitle) verifTitle.innerText = "Thuyết Minh Lý Thuyết Tính Lún Tự Nhiên & Cố Kết Terzaghi";
        renderNoneVerification();
        appState.lastVerification = null;
    } else if (treatment === 'pvd' || treatment === 'vacuum_pvd') {
        if (verifTitle) verifTitle.innerText = treatment === "vacuum_pvd" ? "Thuyết Minh Kiểm Toán Ổn Định & Cố Kết Bấc Thấm + Chân Không" : "Thuyết Minh Kiểm Toán Ổn Định & Cố Kết Bấc Thấm (PVD)";
        
        // 1. Calculate consolidation at t_construction for each sublayer
        let clayThickness = 0;
        appState.soilLayers.forEach(layer => {
            if (layer.type === 'cohesive' || layer.Cc > 0.05) {
                clayThickness += layer.thickness;
            }
        });
        let H_d = clayThickness > 0 ? getDrainagePath(clayThickness) : 5.0;
        
        let avgCv = getThicknessWeightedCv(appState.soilLayers);
        
        let tConst = appState.foundation.tConstruction;
        let Tv_tConst = (avgCv * tConst) / (H_d * H_d);
        let U_v_tConst = 0;
        if (Tv_tConst <= 0.283) {
            U_v_tConst = Math.sqrt((4 * Tv_tConst) / Math.PI);
        } else {
            U_v_tConst = 1 - (8 / (Math.PI * Math.PI)) * Math.exp(-(Math.PI * Math.PI * Tv_tConst) / 4);
        }
        if (U_v_tConst > 1.0) U_v_tConst = 1.0;
        
        let pvd = appState.pvd;
        let spacing = pvd.spacing;
        let pattern = pvd.pattern;
        let bb = pvd.width / 1000;
        let tb = pvd.thickness / 1000;
        let dw = (2 * (bb + tb)) / Math.PI;
        let Ch = avgCv * pvd.chRatio;
        let de = pattern === 'triangular' ? (1.05 * spacing) : (1.13 * spacing);
        let nRatio = de / dw;
        if (nRatio < 1.01) nRatio = 1.01;
        
        let F_total = 0;
        let Fn = 0;
        let isTcvn = (pvd.standard === 'tcvn_9355');
        
        if (isTcvn) {
            Fn = (Math.pow(nRatio, 2) / (Math.pow(nRatio, 2) - 1)) * Math.log(nRatio) - (3 * Math.pow(nRatio, 2) - 1) / (4 * Math.pow(nRatio, 2));
            let dsDw = pvd.dsDw || 2.5;
            let khKs = pvd.khKs || 3.0;
            let Fs = (khKs - 1) * Math.log(dsDw);
            
            let avgEs = 3000;
            if (appState.soilLayers && appState.soilLayers.length > 0) {
                let cohesiveLayers = appState.soilLayers.filter(l => l.type === 'cohesive' || l.Cc > 0.05);
                if (cohesiveLayers.length > 0) {
                    avgEs = cohesiveLayers.reduce((acc, l) => acc + (l.E || 3000), 0) / cohesiveLayers.length;
                } else {
                    avgEs = appState.soilLayers.reduce((acc, l) => acc + (l.E || 3000), 0) / appState.soilLayers.length;
                }
            }
            if (avgEs <= 0) avgEs = 3000;
            
            let kh_year = Ch * (1.0 / avgEs) * WATER_UNIT_WEIGHT;
            let kh_s = kh_year / (3.1536 * 1e7);
            let qw_m3s = (pvd.qw || 50.0) * 1e-6;
            let Fr = (2.0 / 3.0) * Math.PI * Math.pow(pvd.depth, 2) * (kh_s / qw_m3s);
            F_total = Fn + Fs + Fr;
        } else {
            Fn = Math.log(nRatio) - 0.75;
            F_total = Fn;
        }
        
        let Tr_tConst = (Ch * tConst) / (de * de);
        let U_r_tConst = 1 - Math.exp(-(8 * Tr_tConst) / F_total);
        if (U_r_tConst < 0) U_r_tConst = 0;
        
        let U_combined_tConst = 1 - (1 - U_v_tConst) * (1 - U_r_tConst);
        if (U_combined_tConst > 1.0) U_combined_tConst = 1.0;
        
        sublayers.forEach(sub => {
            sub.U_tConst = (sub.depthCenter <= pvd.depth) ? U_combined_tConst : U_v_tConst;
        });
        
                // 2. Perform stability checks (initial and consolidated) if TCVN 9355 is selected
        if (isTcvn) {
            let circularSlipResultInitial = checkCircularSlip(sublayers, false);
            let circularSlipResultConsolidated = checkCircularSlip(sublayers, true);
            
            let stagedStabilityHtml = "";
            if (isStaged) {
                appState.embankment.height = h1;
                sublayers.forEach(sub => {
                    sub.deltaSigmaPreload = (sub.deltaSigmaEmb1 || 0) + (isVacuum && (sub.depthCenter <= appState.pvd.depth) ? pvac_eff : 0);
                });
                
                updateSublayerU(t1_yr);
                let u1_avg_calc = sublayers.reduce((sum, sub) => sum + (sub.currentU * sub.thickness), 0) / sublayers.reduce((sum, sub) => sum + sub.thickness, 0);
                let sub_U_t1 = sublayers.map(sub => sub.currentU);
                
                sublayers.forEach((sub, idx) => { sub.U_tConst = sub_U_t1[idx]; });
                let fs1_init = checkCircularSlip(sublayers, false).FS;
                let fs1_cons = checkCircularSlip(sublayers, true).FS;
                
                appState.embankment.height = hEmb;
                sublayers.forEach(sub => {
                    sub.deltaSigmaPreload = (sub.deltaSigmaEmb || 0) + (isVacuum && (sub.depthCenter <= appState.pvd.depth) ? pvac_eff : 0);
                    sub.U_tConst = sub.U_const_layer;
                });
                let fs2_cons = circularSlipResultConsolidated.FS;
                
                let u2_avg_calc = sublayers.reduce((sum, sub) => sum + (sub.currentU * sub.thickness), 0) / sublayers.reduce((sum, sub) => sum + sub.thickness, 0);

                stagedStabilityHtml = `
                    <div style="margin-top: 15px; background: var(--bg-card); padding: 12px; border: 1px solid var(--border-color); border-radius: 8px;">
                        <p style="font-weight: bold; margin-bottom: 8px; color: var(--text-accent);">BẢNG KIỂM TOÁN ỔN ĐỊNH THEO PHÂN KỲ ĐẮP ĐẤT:</p>
                        <table style="width: 100%; border-collapse: collapse; text-align: center; font-size: 0.8rem;">
                            <thead>
                                <tr style="background: var(--bg-secondary); font-weight: bold; color: var(--text-accent);">
                                    <th style="border: 1px solid var(--border-color); padding: 6px;">Giai đoạn</th>
                                    <th style="border: 1px solid var(--border-color); padding: 6px;">Chiều cao đắp (m)</th>
                                    <th style="border: 1px solid var(--border-color); padding: 6px;">Độ cố kết U (%)</th>
                                    <th style="border: 1px solid var(--border-color); padding: 6px;">Hệ số an toàn FS</th>
                                    <th style="border: 1px solid var(--border-color); padding: 6px;">Yêu cầu [FS]</th>
                                    <th style="border: 1px solid var(--border-color); padding: 6px;">Trạng thái</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style="border: 1px solid var(--border-color); padding: 6px; text-align: left;">Đầu Đợt 1 (t = 0)</td>
                                    <td style="border: 1px solid var(--border-color); padding: 6px;">${h1.toFixed(1)}</td>
                                    <td style="border: 1px solid var(--border-color); padding: 6px;">0.0%</td>
                                    <td style="border: 1px solid var(--border-color); padding: 6px; font-weight: bold;">${fs1_init.toFixed(2)}</td>
                                    <td style="border: 1px solid var(--border-color); padding: 6px;">&ge; 1.20</td>
                                    <td style="border: 1px solid var(--border-color); padding: 6px; font-weight: bold; color: ${fs1_init >= 1.2 ? '#10b981' : '#ef4444'};">${fs1_init >= 1.2 ? 'ĐẠT' : 'KHÔNG ĐẠT'}</td>
                                </tr>
                                <tr>
                                    <td style="border: 1px solid var(--border-color); padding: 6px; text-align: left;">Cuối Đợt 1 chờ (t = t₁)</td>
                                    <td style="border: 1px solid var(--border-color); padding: 6px;">${h1.toFixed(1)}</td>
                                    <td style="border: 1px solid var(--border-color); padding: 6px;">${(u1_avg_calc * 100).toFixed(1)}%</td>
                                    <td style="border: 1px solid var(--border-color); padding: 6px; font-weight: bold;">${fs1_cons.toFixed(2)}</td>
                                    <td style="border: 1px solid var(--border-color); padding: 6px;">&ge; 1.20</td>
                                    <td style="border: 1px solid var(--border-color); padding: 6px; font-weight: bold; color: ${fs1_cons >= 1.2 ? '#10b981' : '#ef4444'};">${fs1_cons >= 1.2 ? 'ĐẠT' : 'KHÔNG ĐẠT'}</td>
                                </tr>
                                <tr>
                                    <td style="border: 1px solid var(--border-color); padding: 6px; text-align: left;">Cuối Đợt 2 bàn giao (t = tck)</td>
                                    <td style="border: 1px solid var(--border-color); padding: 6px;">${(h1 + h2).toFixed(1)}</td>
                                    <td style="border: 1px solid var(--border-color); padding: 6px;">${(u2_avg_calc * 100).toFixed(1)}%</td>
                                    <td style="border: 1px solid var(--border-color); padding: 6px; font-weight: bold;">${fs2_cons.toFixed(2)}</td>
                                    <td style="border: 1px solid var(--border-color); padding: 6px;">&ge; 1.40</td>
                                    <td style="border: 1px solid var(--border-color); padding: 6px; font-weight: bold; color: ${fs2_cons >= 1.4 ? '#10b981' : '#ef4444'};">${fs2_cons >= 1.4 ? 'ĐẠT' : 'KHÔNG ĐẠT'}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                `;
            }
            
            renderPvdVerification(circularSlipResultInitial, circularSlipResultConsolidated, stagedStabilityHtml);
            
            appState.lastVerification = {
                circular: circularSlipResultConsolidated,
                circularInitial: circularSlipResultInitial
            };
        } else {
            renderPvdVerification(null, null);
            appState.lastVerification = null;
        }
    } else if (treatment === 'sand_drain') {
        if (verifTitle) verifTitle.innerText = "Thuyết Minh Lý Thuyết Tính Lún & Cố Kết Giếng Cát (Sand Drain)";
        renderSandDrainVerification();
        appState.lastVerification = null;
    } else if (treatment === 'cdm') {
        if (verifTitle) verifTitle.innerText = "Thuyết Minh Kiểm Toán Ổn Định & Vải Địa Kỹ Thuật (Gia cố CDM)";
        
        let circularSlipResult = checkCircularSlip(sublayers);
        let planarSlidingResult = checkPlanarSliding(sublayers);
        let geotextileResult = checkGeotextile(sublayers);
        
        let delta_h1 = sublayers.filter(s => s.depthCenter <= appState.cdm.depth).reduce((acc, s) => acc + s.Stotal, 0);
        let delta_h2 = sublayers.filter(s => s.depthCenter > appState.cdm.depth).reduce((acc, s) => acc + s.Stotal, 0);
        
        renderCdmVerificationDetails(circularSlipResult, planarSlidingResult, geotextileResult, delta_h1, delta_h2);
        
        appState.lastVerification = {
            circular: circularSlipResult,
            planar: planarSlidingResult,
            geotextile: geotextileResult,
            delta_h1: delta_h1,
            delta_h2: delta_h2
        };
    } else if (treatment === 'sand_column') {
        if (verifTitle) verifTitle.innerText = "Thuyết Minh Lý Thuyết Tính Lún Nền Composite Cọc Cát";
        
        let delta_h1 = sublayers.filter(s => s.depthCenter <= appState.sandColumn.depth).reduce((acc, s) => acc + s.Stotal, 0);
        let delta_h2 = sublayers.filter(s => s.depthCenter > appState.sandColumn.depth).reduce((acc, s) => acc + s.Stotal, 0);
        
        renderSandColumnVerification(delta_h1, delta_h2);
        
        appState.lastVerification = {
            delta_h1: delta_h1,
            delta_h2: delta_h2
        };
    } else if (treatment === 'bamboo_pile' || treatment === 'bamboo_replacement') {
        let isReplacement = treatment === 'bamboo_replacement' && hRep > 0;
        if (verifTitle) {
            verifTitle.innerText = isReplacement ? 
                "Thuyết Minh Kiểm Toán Ổn Định & Tính Lún Đóng Cọc Tre + Thay Đất" : 
                "Thuyết Minh Kiểm Toán Ổn Định & Tính Lún Nền Cọc Tre";
        }
        
        let circularSlipResult = checkCircularSlip_bamboo(sublayers);
        let planarSlidingResult = checkPlanarSliding_bamboo(sublayers);
        
        let limitDepth = isReplacement ? (hRep + appState.bambooPile.depth) : appState.bambooPile.depth;
        
        let delta_cushion = isReplacement ? sublayers.filter(s => s.depthCenter <= hRep).reduce((acc, s) => acc + s.Stotal, 0) : 0;
        let delta_h1 = isReplacement ? 
            sublayers.filter(s => s.depthCenter > hRep && s.depthCenter <= limitDepth).reduce((acc, s) => acc + s.Stotal, 0) : 
            sublayers.filter(s => s.depthCenter <= limitDepth).reduce((acc, s) => acc + s.Stotal, 0);
        let delta_h2 = sublayers.filter(s => s.depthCenter > limitDepth).reduce((acc, s) => acc + s.Stotal, 0);
        
        renderBambooPileVerification(circularSlipResult, planarSlidingResult, delta_h1, delta_h2, delta_cushion);
        
        appState.lastVerification = {
            circular: circularSlipResult,
            planar: planarSlidingResult,
            delta_h1: delta_h1,
            delta_h2: delta_h2,
            delta_cushion: delta_cushion
        };
    } else if (treatment === 'excavation_replacement') {
        if (verifTitle) verifTitle.innerText = "Thuyết Minh Kiểm Toán Ổn Định & Tính Lún Đào Thay Đất";
        
        let circularSlipResult = checkCircularSlip(sublayers);
        
        renderExcavationReplacementVerification(circularSlipResult);
        
        appState.lastVerification = {
            circular: circularSlipResult
        };
    } else {
        if (cdmVerifyCard) cdmVerifyCard.style.display = "none";
        appState.lastVerification = null;
    }

    const solverErrors = window.analysisSolverErrors || [];
    const numericResults = [sEmpirical, totalSc, totalSi, totalSs, remainingSettlement, uAtConst, compressionDepth];
    const numericValid = numericResults.every(Number.isFinite);
    appState.lastAnalysis = {
        valid: numericValid && solverErrors.length === 0,
        total: sEmpirical,
        consolidation: totalSc,
        immediate: totalSi,
        secondary: totalSs,
        remaining: remainingSettlement,
        uHandover: uAtConst,
        compressionDepth: compressionDepth,
        drainagePath: getDrainagePath(
            sublayers.filter(s => s.inSettlementZone && (s.type === "cohesive" || s.Cc > 0.05))
                .reduce((sum, s) => sum + s.thickness, 0)
        ),
        standard: "TCCS 41:2022/TCĐBVN + Sửa đổi 1:2022",
        solverErrors: solverErrors
    };
    const analysisMeta = document.getElementById("analysis-meta");
    if (analysisMeta) {
        analysisMeta.textContent = "Ha = " + formatDecimal(compressionDepth, 2) + " m; thoát nước " +
            (appState.foundation.drainage === "double" ? "hai mặt" : "một mặt") +
            "; H = " + formatDecimal(appState.lastAnalysis.drainagePath, 2) + " m; " +
            appState.lastAnalysis.standard + ".";
    }
    if (!appState.lastAnalysis.valid) {
        markAnalysisInvalid(solverErrors[0] || "Kết quả chứa giá trị không hữu hạn.");
    }

    // 9. Generate detailed table report
    generateReportTable(sublayers, effectiveLayers);

    // 10. Draw Canvas Graphics
    drawGeotechnicalSection(totalDepth);
    
    // Store variables globally for hover events
    window.lastSublayers = sublayers;
    window.lastSettlementTimeData = settlementTimeData;
    window.lastTConst = tConst;
    window.lastTEnd = tEnd;

    drawStressChart(sublayers);
    drawTimeCurve(settlementTimeData, tConst, tEnd);
    drawDepthSettlementCurve(sublayers);
}

// Generate report rows
function generateReportTable(sublayers, effectiveLayers) {
    const container = document.getElementById("detailed-tables-container");
    if (!container) return;
    container.innerHTML = "";

    if (!effectiveLayers) {
        effectiveLayers = getEffectiveSoilLayers();
    }

    let sublayerGlobalIdx = 0;
    
    // Array to hold summaries for the final summary table
    let layerSummaries = [];

    // Helper to check if a sublayer is in a reinforced zone
    function isSublayerReinforced(sub) {
        const treatment = appState.treatment.method;
        if (treatment === 'cdm' && sub.depthCenter <= appState.cdm.depth) return true;
        if (treatment === 'sand_column' && sub.depthCenter <= appState.sandColumn.depth) return true;
        if (treatment === 'bamboo_pile' && sub.depthCenter <= appState.bambooPile.depth) return true;
        return false;
    }

    effectiveLayers.forEach((layer) => {
        // Find sublayers belonging to this parent layer
        const layerSublayers = sublayers.filter(sub => sub.layerId === layer.id);
        if (layerSublayers.length === 0) return;

        // Partition into granular, reinforced cohesive, and natural cohesive
        let granularSubs = [];
        let reinforcedSubs = [];
        let naturalSubs = [];

        layerSublayers.forEach(sub => {
            if (sub.type === 'granular') {
                granularSubs.push(sub);
            } else if (isSublayerReinforced(sub)) {
                reinforcedSubs.push(sub);
            } else {
                naturalSubs.push(sub);
            }
        });

        // Helper to render a specific partition table
        function renderPartitionTable(subs, type, titleSuffix) {
            if (subs.length === 0) return;

            // 1. Title for this partition table
            const titleDiv = document.createElement("div");
            titleDiv.className = "table-title";
            titleDiv.innerHTML = `
                <span class="color-indicator" style="background-color: ${sanitizeColor(layer.color)}; display: inline-block; width: 12px; height: 12px; border-radius: 2px;"></span>
                Lớp đất: ${escapeHtml(layer.name)} ${titleSuffix}
            `;
            container.appendChild(titleDiv);

            // 2. Create table
            const table = document.createElement("table");
            table.className = "report-table";
            
            // Build thead based on type
            let theadHTML = "";
            if (type === "granular") {
                theadHTML = `
                    <thead>
                        <tr>
                            <th style="text-align: left;">Phân lớp</th>
                            <th>Chiều sâu z (m)</th>
                            <th>Dày &Delta;z (m)</th>
                            <th>&sigma;'<sub>v0</sub> (kPa)</th>
                            <th>&Delta;&sigma;<sub>z</sub> (kPa)</th>
                            <th>Mô đun E<sub>s</sub> (kPa)</th>
                            <th>S<sub>i</sub> (cm)</th>
                            <th>S<sub>tổng</sub> (cm)</th>
                        </tr>
                    </thead>
                `;
            } else if (type === "reinforced") {
                theadHTML = `
                    <thead>
                        <tr>
                            <th style="text-align: left;">Phân lớp</th>
                            <th>Chiều sâu z (m)</th>
                            <th>Dày &Delta;z (m)</th>
                            <th>&Delta;&sigma;<sub>z</sub> (kPa)</th>
                            <th>Mô đun E<sub>comp</sub> (kPa)</th>
                            <th>Lún gia cố S<sub>c</sub> (cm)</th>
                            <th>S<sub>tổng</sub> (cm)</th>
                        </tr>
                    </thead>
                `;
            } else { // natural cohesive
                theadHTML = `
                    <thead>
                        <tr>
                            <th style="text-align: left;">Phân lớp</th>
                            <th>Chiều sâu z (m)</th>
                            <th>Dày &Delta;z (m)</th>
                            <th>&sigma;'<sub>v0</sub> (kPa)</th>
                            <th>&Delta;&sigma;<sub>z</sub> (kPa)</th>
                            <th>&sigma;'<sub>p</sub> (kPa)</th>
                            <th>Trạng thái</th>
                            <th>S<sub>i</sub> (cm)</th>
                            <th>S<sub>c</sub> (cm)</th>
                            <th>S<sub>s</sub> (cm)</th>
                            <th>S<sub>tổng</sub> (cm)</th>
                        </tr>
                    </thead>
                `;
            }
            table.innerHTML = theadHTML;

            const tbody = document.createElement("tbody");
            
            let sumSi = 0;
            let sumSc = 0;
            let sumSs = 0;
            let sumStotal = 0;
            let sumThickness = 0;

            // Populate rows
            subs.forEach((sub, subIdx) => {
                sublayerGlobalIdx++;
                let isOC = sub.sigmaV0 < sub.sigmaP;
                let stateText = isOC ? "Quá cố kết (OC)" : "Cố kết thường (NC)";
                let stateColor = isOC ? "#f59e0b" : "#10b981";

                let trHTML = "";
                if (type === "granular") {
                    trHTML = `
                        <td style="text-align: left; display:flex; align-items:center; gap:6px;">
                            <span class="color-indicator" style="background-color: ${sub.layerColor}; width:8px; height:8px;"></span>
                            <span style="font-size:0.75rem;">Phần lớp ${sublayerGlobalIdx} (lớp ${subIdx + 1})</span>
                        </td>
                        <td>${sub.depthCenter.toFixed(2)}</td>
                        <td>${sub.thickness.toFixed(2)}</td>
                        <td>${sub.sigmaV0.toFixed(1)}</td>
                        <td>${sub.deltaSigmaZ.toFixed(1)}</td>
                        <td>${(sub.E || 3000).toFixed(0)}</td>
                        <td>${sub.Si.toFixed(2)}</td>
                        <td style="font-weight: 600; color:#cbd5e1;">${sub.Stotal.toFixed(2)}</td>
                    `;
                } else if (type === "reinforced") {
                    trHTML = `
                        <td style="text-align: left; display:flex; align-items:center; gap:6px;">
                            <span class="color-indicator" style="background-color: ${sub.layerColor}; width:8px; height:8px;"></span>
                            <span style="font-size:0.75rem;">Phần lớp ${sublayerGlobalIdx} (lớp ${subIdx + 1})</span>
                        </td>
                        <td>${sub.depthCenter.toFixed(2)}</td>
                        <td>${sub.thickness.toFixed(2)}</td>
                        <td>${sub.deltaSigmaZ.toFixed(1)}</td>
                        <td>${(sub.E_comp || sub.E || 3000).toFixed(0)}</td>
                        <td>${sub.Sc.toFixed(2)}</td>
                        <td style="font-weight: 600; color:#cbd5e1;">${sub.Stotal.toFixed(2)}</td>
                    `;
                } else { // natural cohesive
                    trHTML = `
                        <td style="text-align: left; display:flex; align-items:center; gap:6px;">
                            <span class="color-indicator" style="background-color: ${sub.layerColor}; width:8px; height:8px;"></span>
                            <span style="font-size:0.75rem;">Phần lớp ${sublayerGlobalIdx} (lớp ${subIdx + 1})</span>
                        </td>
                        <td>${sub.depthCenter.toFixed(2)}</td>
                        <td>${sub.thickness.toFixed(2)}</td>
                        <td>${sub.sigmaV0.toFixed(1)}</td>
                        <td>${sub.deltaSigmaZ.toFixed(1)}</td>
                        <td>${sub.sigmaP.toFixed(1)}</td>
                        <td style="text-align: center; color: ${stateColor}; font-weight: 500;">${stateText}</td>
                        <td>${sub.Si.toFixed(2)}</td>
                        <td>${sub.Sc.toFixed(2)}</td>
                        <td>${sub.Ss.toFixed(2)}</td>
                        <td style="font-weight: 600; color:#cbd5e1;">${sub.Stotal.toFixed(2)}</td>
                    `;
                }

                const tr = document.createElement("tr");
                tr.className = "sublayer-row";
                tr.innerHTML = trHTML;
                tbody.appendChild(tr);

                sumSi += sub.Si;
                sumSc += sub.Sc;
                sumSs += sub.Ss;
                sumStotal += sub.Stotal;
                sumThickness += sub.thickness;
            });

            // Table total row
            let totalTrHTML = "";
            if (type === "granular") {
                totalTrHTML = `
                    <tr class="layer-total-row">
                        <td style="text-align: left;">Cộng phân lớp: Đất rời</td>
                        <td>-</td>
                        <td>${sumThickness.toFixed(2)}</td>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td>${sumSi.toFixed(2)}</td>
                        <td style="font-weight: bold; font-size: 0.85rem;">${sumStotal.toFixed(2)}</td>
                    </tr>
                `;
            } else if (type === "reinforced") {
                totalTrHTML = `
                    <tr class="layer-total-row">
                        <td style="text-align: left;">Cộng phân lớp: Gia cố</td>
                        <td>-</td>
                        <td>${sumThickness.toFixed(2)}</td>
                        <td>-</td>
                        <td>-</td>
                        <td>${sumSc.toFixed(2)}</td>
                        <td style="font-weight: bold; font-size: 0.85rem;">${sumStotal.toFixed(2)}</td>
                    </tr>
                `;
            } else {
                totalTrHTML = `
                    <tr class="layer-total-row">
                        <td style="text-align: left;">Cộng phân lớp: Tự nhiên</td>
                        <td>-</td>
                        <td>${sumThickness.toFixed(2)}</td>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td>${sumSi.toFixed(2)}</td>
                        <td>${sumSc.toFixed(2)}</td>
                        <td>${sumSs.toFixed(2)}</td>
                        <td style="font-weight: bold; font-size: 0.85rem;">${sumStotal.toFixed(2)}</td>
                    </tr>
                `;
            }
            tbody.insertAdjacentHTML("beforeend", totalTrHTML);
            table.appendChild(tbody);
            container.appendChild(table);

            // Add summary data for final summary table
            let displayName = layer.name;
            if (titleSuffix) displayName += ` ${titleSuffix}`;
            layerSummaries.push({
                name: displayName,
                color: layer.color,
                thickness: sumThickness,
                si: type === "reinforced" ? 0 : sumSi, // Si is 0 in reinforced zone
                sc: sumSc,
                ss: type === "reinforced" ? 0 : sumSs, // Ss is 0 in reinforced zone
                stotal: sumStotal
            });
        }

        // Render each partition table
        renderPartitionTable(granularSubs, "granular", "(Đất rời)");
        let reinforcementText = "(Gia cố)";
        const treatment = appState.treatment.method;
        if (treatment === 'cdm') reinforcementText = "(Gia cố CDM)";
        else if (treatment === 'sand_column') reinforcementText = "(Gia cố Cọc cát)";
        else if (treatment === 'bamboo_pile') reinforcementText = "(Gia cố Cọc tre)";
        
        renderPartitionTable(reinforcedSubs, "reinforced", reinforcementText);
        renderPartitionTable(naturalSubs, "natural", (reinforcedSubs.length > 0) ? "(Phần dưới mũi cọc - Tự nhiên)" : "(Tự nhiên)");
    });

    // 5. Render Final Summary Table
    if (layerSummaries.length > 0) {
        const titleDiv = document.createElement("div");
        titleDiv.className = "table-title";
        titleDiv.style.marginTop = "1.5rem";
        titleDiv.innerHTML = `
            <span style="font-weight: bold; color: #38bdf8; text-transform: uppercase;">BẢNG TỔNG HỢP ĐỘ LÚN CÁC LỚP ĐẤT NỀN</span>
        `;
        container.appendChild(titleDiv);

        const summaryTable = document.createElement("table");
        summaryTable.className = "report-table";
        summaryTable.innerHTML = `
            <thead>
                <tr>
                    <th style="text-align: left;">Lớp đất / Phân vùng</th>
                    <th>Bề dày H (m)</th>
                    <th>Lún đàn hồi S<sub>i</sub> (cm)</th>
                    <th>Lún cố kết S<sub>c</sub> (cm)</th>
                    <th>Lún từ biến S<sub>s</sub> (cm)</th>
                    <th>Tổng cộng S<sub>tổng</sub> (cm)</th>
                </tr>
            </thead>
        `;

        const tbody = document.createElement("tbody");
        let grandH = 0;
        let grandSi = 0;
        let grandSc = 0;
        let grandSs = 0;
        let grandStotal = 0;

        layerSummaries.forEach(sum => {
            const tr = document.createElement("tr");
            tr.className = "sublayer-row";
            tr.innerHTML = `
                <td style="text-align: left; display:flex; align-items:center; gap:6px;">
                    <span class="color-indicator" style="background-color: ${sum.color}; width:10px; height:10px; border-radius:2px;"></span>
                    <span style="font-weight: 500;">${sum.name}</span>
                </td>
                <td>${sum.thickness.toFixed(2)}</td>
                <td>${sum.si > 0.001 ? sum.si.toFixed(2) : "-"}</td>
                <td>${sum.sc > 0.001 ? sum.sc.toFixed(2) : "-"}</td>
                <td>${sum.ss > 0.001 ? sum.ss.toFixed(2) : "-"}</td>
                <td style="font-weight: 600; color:#cbd5e1;">${sum.stotal.toFixed(2)}</td>
            `;
            tbody.appendChild(tr);

            grandH += sum.thickness;
            grandSi += sum.si;
            grandSc += sum.sc;
            grandSs += sum.ss;
            grandStotal += sum.stotal;
        });

        // Add Grand Total row
        const grandTr = document.createElement("tr");
        grandTr.className = "grand-total-row";
        grandTr.innerHTML = `
            <td style="text-align: left;">TỔNG CỘNG NỀN ĐƯỜNG ĐẮP</td>
            <td>${grandH.toFixed(2)}</td>
            <td>${grandSi.toFixed(2)}</td>
            <td>${grandSc.toFixed(2)}</td>
            <td>${grandSs.toFixed(2)}</td>
            <td style="font-size: 0.95rem;">${grandStotal.toFixed(2)}</td>
        `;
        tbody.appendChild(grandTr);

        summaryTable.appendChild(tbody);
        container.appendChild(summaryTable);
    }
}

// Tính toán bố trí cọc tối ưu đảm bảo bao phủ chân khay taluy và rộng hơn tối đa overWidthLimit (1m)
function getColumnLayout(toeX, spacing, overWidthLimit = 1.0) {
    let B_day = 2 * toeX;
    let N = Math.ceil(B_day / spacing);
    
    let offsets = [];
    let type = '';
    
    if (N % 2 === 0) {
        type = 'centered';
        let k = N / 2;
        for (let i = 0; i <= k; i++) {
            offsets.push(i * spacing);
        }
    } else {
        type = 'shifted';
        let k = (N + 1) / 2;
        for (let i = 0; i < k; i++) {
            offsets.push((i + 0.5) * spacing);
        }
    }
    
    let max = offsets[offsets.length - 1];
    return { type, offsets, max };
}

// Visual drawing of cross-section
function drawGeotechnicalSection(totalSoilDepth) {
    const canvas = document.getElementById("soil-canvas");
    const ctx = canvas.getContext("2d");
    
    // Clear
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Define bounds
    let paddingLeft = 60;
    let paddingRight = 60;
    let paddingTop = 50;
    let paddingBottom = 20;
    
    let drawW = canvas.width - paddingLeft - paddingRight;
    let drawH = canvas.height - paddingTop - paddingBottom;
    
    // Draw background
    ctx.fillStyle = isPrinting ? "#ffffff" : "#0c101b";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Embankment properties
    let hEmb = appState.embankment.height;
    let bCrest = appState.embankment.width;
    let mSlope = appState.embankment.slope;
    
    // Draw scales
    // Let's scale dimensions to fit drawing area
    let scaleX = drawW / (bCrest + 2 * mSlope * hEmb + 10); // px per meter
    let scaleY = drawH / (totalSoilDepth + hEmb);          // px per meter
    
    // Store scale in indicator label
    document.getElementById("scale-indicator").innerText = `Tỷ lệ vẽ: 1m dọc = ${scaleY.toFixed(0)}px | 1m ngang = ${scaleX.toFixed(0)}px`;

    // Coordinates of center ground surface
    let xCenter = paddingLeft + drawW / 2;
    let yGround = paddingTop + hEmb * scaleY;

    // Draw Soil Layers
    let yCurrent = yGround;
    let effectiveLayers = getEffectiveSoilLayers();
    effectiveLayers.forEach((layer) => {
        let dy = layer.thickness * scaleY;
        ctx.fillStyle = layer.color;
        ctx.fillRect(paddingLeft, yCurrent, drawW, dy);
        
        // Border lines
        ctx.strokeStyle = isPrinting ? "rgba(0,0,0,0.15)" : "rgba(255,255,255,0.06)";
        ctx.lineWidth = 1;
        ctx.strokeRect(paddingLeft, yCurrent, drawW, dy);
        
        yCurrent += dy;
    });

    // Draw GWT Line (Ground Water Table)
    let gwtDepth = appState.foundation.gwt;
    let yGwt = yGround + gwtDepth * scaleY;
    
    ctx.strokeStyle = "#3b82f6"; // Blue
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.moveTo(paddingLeft, yGwt);
    ctx.lineTo(paddingLeft + drawW, yGwt);
    ctx.stroke();
    ctx.setLineDash([]); // Reset
    
    // Water level icon
    ctx.fillStyle = "#3b82f6";
    ctx.beginPath();
    ctx.moveTo(paddingLeft + 15, yGwt);
    ctx.lineTo(paddingLeft + 25, yGwt);
    ctx.lineTo(paddingLeft + 20, yGwt + 6);
    ctx.closePath();
    ctx.fill();
    ctx.font = "9px Inter";
    ctx.fillText("MNN", paddingLeft + 30, yGwt - 3);

    let boundary = checkBoundaryError();
    let treatment = boundary.hasError ? 'none' : appState.treatment.method;
    let toeX = bCrest / 2 + mSlope * hEmb;
    
    let spacing = 1.5;
    if (treatment === 'pvd') spacing = appState.pvd.spacing;
    else if (treatment === 'cdm') spacing = appState.cdm.spacing;
    else if (treatment === 'sand_drain') spacing = appState.sandDrain.spacing;
    else if (treatment === 'sand_column') spacing = appState.sandColumn.spacing;
    else if (treatment === 'bamboo_pile' || treatment === 'bamboo_replacement') spacing = appState.bambooPile.spacing;

    let overWidth = 1.0;
    if (treatment === 'cdm') {
        overWidth = appState.cdm.overWidth !== undefined ? appState.cdm.overWidth : 1.0;
    }
    
    let layout = getColumnLayout(toeX, spacing, overWidth);
    let maxReinforcementX = layout.max * scaleX;

    if (treatment === 'pvd') {
        let pvdDepth = appState.pvd.depth;
        
        ctx.strokeStyle = "#f43f5e"; // Pink/Red PVD lines
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);

        layout.offsets.forEach(xOffset => {
            ctx.beginPath();
            ctx.moveTo(xCenter + xOffset * scaleX, yGround);
            ctx.lineTo(xCenter + xOffset * scaleX, yGround + Math.min(pvdDepth, totalSoilDepth) * scaleY);
            ctx.stroke();

            if (xOffset > 0) {
                ctx.beginPath();
                ctx.moveTo(xCenter - xOffset * scaleX, yGround);
                ctx.lineTo(xCenter - xOffset * scaleX, yGround + Math.min(pvdDepth, totalSoilDepth) * scaleY);
                ctx.stroke();
            }
        });
        ctx.setLineDash([]); // Reset
        
        ctx.fillStyle = "#f43f5e";
        ctx.font = "8px Inter";
        ctx.fillText(`Bấc thấm PVD L=${pvdDepth}m (S=${spacing}m)`, xCenter + 35, yGround + 15);
    } else if (treatment === 'sand_drain') {
        let sdDepth = appState.sandDrain.depth;
        let d_sd = appState.sandDrain.diameter;
        
        ctx.fillStyle = "rgba(245, 158, 11, 0.25)"; // Translucent sandy color
        ctx.strokeStyle = "rgba(217, 119, 6, 0.5)";
        ctx.lineWidth = 1;

        let pileWidthPx = d_sd * scaleX;
        
        layout.offsets.forEach(xOffset => {
            ctx.fillRect(xCenter + xOffset * scaleX - pileWidthPx/2, yGround, pileWidthPx, Math.min(sdDepth, totalSoilDepth) * scaleY);
            ctx.strokeRect(xCenter + xOffset * scaleX - pileWidthPx/2, yGround, pileWidthPx, Math.min(sdDepth, totalSoilDepth) * scaleY);

            if (xOffset > 0) {
                ctx.fillRect(xCenter - xOffset * scaleX - pileWidthPx/2, yGround, pileWidthPx, Math.min(sdDepth, totalSoilDepth) * scaleY);
                ctx.strokeRect(xCenter - xOffset * scaleX - pileWidthPx/2, yGround, pileWidthPx, Math.min(sdDepth, totalSoilDepth) * scaleY);
            }
        });
        
        ctx.fillStyle = isPrinting ? "#b45309" : "#f59e0b";
        ctx.font = "8px Inter";
        ctx.fillText(`Giếng cát d=${d_sd}m, L=${sdDepth}m (S=${spacing}m)`, xCenter + 35, yGround + 15);
    } else if (treatment === 'cdm') {
        let cdmDepth = appState.cdm.depth;
        let d_cdm = appState.cdm.diameter;
        
        ctx.fillStyle = "rgba(16, 185, 129, 0.35)"; // Soft green solid columns
        ctx.strokeStyle = "#10b981"; // Emerald border
        ctx.lineWidth = 1;

        let pileWidthPx = d_cdm * scaleX;
        
        layout.offsets.forEach(xOffset => {
            ctx.fillRect(xCenter + xOffset * scaleX - pileWidthPx/2, yGround, pileWidthPx, Math.min(cdmDepth, totalSoilDepth) * scaleY);
            ctx.strokeRect(xCenter + xOffset * scaleX - pileWidthPx/2, yGround, pileWidthPx, Math.min(cdmDepth, totalSoilDepth) * scaleY);

            if (xOffset > 0) {
                ctx.fillRect(xCenter - xOffset * scaleX - pileWidthPx/2, yGround, pileWidthPx, Math.min(cdmDepth, totalSoilDepth) * scaleY);
                ctx.strokeRect(xCenter - xOffset * scaleX - pileWidthPx/2, yGround, pileWidthPx, Math.min(cdmDepth, totalSoilDepth) * scaleY);
            }
        });
        
        ctx.fillStyle = isPrinting ? "#047857" : "#10b981";
        ctx.font = "8px Inter";
        ctx.fillText(`Cọc đất xi măng (CDM) d=${d_cdm}m, L=${cdmDepth}m (S=${spacing}m)`, xCenter + 35, yGround + 15);
    } else if (treatment === 'sand_column') {
        let scDepth = appState.sandColumn.depth;
        let d_sc = appState.sandColumn.diameter;
        
        ctx.fillStyle = "rgba(245, 158, 11, 0.35)"; // Solid sand color
        ctx.strokeStyle = "#d97706"; // Amber border
        ctx.lineWidth = 1;

        let pileWidthPx = d_sc * scaleX;
        
        layout.offsets.forEach(xOffset => {
            ctx.fillRect(xCenter + xOffset * scaleX - pileWidthPx/2, yGround, pileWidthPx, Math.min(scDepth, totalSoilDepth) * scaleY);
            ctx.strokeRect(xCenter + xOffset * scaleX - pileWidthPx/2, yGround, pileWidthPx, Math.min(scDepth, totalSoilDepth) * scaleY);

            if (xOffset > 0) {
                ctx.fillRect(xCenter - xOffset * scaleX - pileWidthPx/2, yGround, pileWidthPx, Math.min(scDepth, totalSoilDepth) * scaleY);
                ctx.strokeRect(xCenter - xOffset * scaleX - pileWidthPx/2, yGround, pileWidthPx, Math.min(scDepth, totalSoilDepth) * scaleY);
            }
        });
        
        ctx.fillStyle = isPrinting ? "#b45309" : "#f59e0b";
        ctx.font = "8px Inter";
        ctx.fillText(`Cọc cát d=${d_sc}m, L=${scDepth}m (S=${spacing}m)`, xCenter + 35, yGround + 15);
    } else if (treatment === 'bamboo_pile' || treatment === 'bamboo_replacement') {
        let bpDepth = appState.bambooPile.depth;
        let d_bp = appState.bambooPile.diameter;
        let hRep = treatment === 'bamboo_replacement' ? appState.excavationReplacement.depth : 0;
        
        ctx.fillStyle = "rgba(132, 204, 22, 0.35)"; // Soft lime green columns
        ctx.strokeStyle = "#84cc16"; // Lime border
        ctx.lineWidth = 1;

        let pileWidthPx = d_bp * scaleX;
        if (pileWidthPx < 2) pileWidthPx = 2; // Đảm bảo nhìn thấy được
        
        layout.offsets.forEach(xOffset => {
            let startY = yGround + hRep * scaleY;
            let endY = yGround + Math.min(hRep + bpDepth, totalSoilDepth) * scaleY;
            ctx.fillRect(xCenter + xOffset * scaleX - pileWidthPx/2, startY, pileWidthPx, endY - startY);
            ctx.strokeRect(xCenter + xOffset * scaleX - pileWidthPx/2, startY, pileWidthPx, endY - startY);

            if (xOffset > 0) {
                ctx.fillRect(xCenter - xOffset * scaleX - pileWidthPx/2, startY, pileWidthPx, endY - startY);
                ctx.strokeRect(xCenter - xOffset * scaleX - pileWidthPx/2, startY, pileWidthPx, endY - startY);
            }
        });
        
        ctx.fillStyle = isPrinting ? "#4d7c0f" : "#a3e635";
        ctx.font = "8px Inter";
        ctx.fillText(`Cọc tre d=${d_bp}m, L=${bpDepth}m (S=${spacing}m)`, xCenter + 35, yGround + hRep * scaleY + 15);
    }
    
    if (treatment === 'excavation_replacement' || treatment === 'bamboo_replacement') {
        let hRep = appState.excavationReplacement.depth;
        if (hRep > 0) {
            ctx.fillStyle = isPrinting ? "#78350f" : "#f59e0b";
            ctx.font = "bold 9px Inter";
            ctx.fillText(`Đào thay đất Hđào = ${hRep.toFixed(1)}m (Đệm cát đầm chặt)`, xCenter - 100, yGround + (hRep * scaleY) / 2 + 3);
        }
    }

    // Draw Embankment (Trapezoid)
    let wCrestPx = bCrest * scaleX;
    let wSlopePx = mSlope * hEmb * scaleX;
    let hEmbPx = hEmb * scaleY;

    ctx.fillStyle = "#78716c"; // Gray/Brown soil color
    ctx.beginPath();
    ctx.moveTo(xCenter - wCrestPx / 2 - wSlopePx, yGround); // Left toe
    ctx.lineTo(xCenter - wCrestPx / 2, yGround - hEmbPx);   // Left shoulder
    ctx.lineTo(xCenter + wCrestPx / 2, yGround - hEmbPx);   // Right shoulder
    ctx.lineTo(xCenter + wCrestPx / 2 + wSlopePx, yGround); // Right toe
    ctx.closePath();
    ctx.fill();

    // Outline
    ctx.strokeStyle = isPrinting ? "#1e293b" : "#d6d3d1";
    ctx.lineWidth = 2;
    ctx.stroke();

    // Text indicators
    ctx.fillStyle = isPrinting ? "#000000" : "#f3f4f6";
    ctx.font = "bold 10px Inter";
    ctx.textAlign = "center";
    ctx.fillText(`Nền đắp H = ${hEmb}m`, xCenter, yGround - hEmbPx / 2 + 3);
    
    // Dimension lines
    ctx.strokeStyle = isPrinting ? "rgba(0,0,0,0.4)" : "rgba(255,255,255,0.4)";
    ctx.lineWidth = 1;
    // Crest width line
    ctx.beginPath();
    ctx.moveTo(xCenter - wCrestPx / 2, yGround - hEmbPx - 10);
    ctx.lineTo(xCenter + wCrestPx / 2, yGround - hEmbPx - 10);
    ctx.stroke();
    ctx.font = "9px Inter";
    ctx.fillStyle = isPrinting ? "#000000" : "#ffffff";
    ctx.fillText(`B = ${bCrest}m`, xCenter, yGround - hEmbPx - 15);

        // Reset align
    ctx.textAlign = "left";

    // Store drawing scale and boundary information for drag helper
    window.geotechnicalDrawingInfo = {
        scaleY: scaleY,
        yGround: yGround,
        drawW: drawW,
        paddingLeft: paddingLeft,
        scaleX: scaleX,
        boundaries: []
    };
    
    let yBound = yGround;
    effectiveLayers.forEach((layer, idx) => {
        yBound += layer.thickness * scaleY;
        window.geotechnicalDrawingInfo.boundaries.push({
            layerIndex: idx,
            y: yBound,
            name: layer.name,
            id: layer.id,
            thickness: layer.thickness
        });
    });
}

function drawSlipCircleChart(circular, sublayers) {
    const canvas = document.getElementById("slip-circle-canvas");
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Nền cho canvas tùy theo chế độ in
    ctx.fillStyle = isPrinting ? "#ffffff" : "#0f172a";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    let hEmb = appState.embankment.height;
    let bCrest = appState.embankment.width;
    let mSlope = appState.embankment.slope;
    
    let totalSoilDepth = 0;
    appState.soilLayers.forEach(layer => {
        totalSoilDepth += layer.thickness;
    });
    
    // Tỷ lệ vẽ (scale)
    let paddingLeft = 50;
    let paddingRight = 50;
    let paddingTop = 60;
    let paddingBottom = 40;
    
    let drawW = canvas.width - paddingLeft - paddingRight;
    let drawH = canvas.height - paddingTop - paddingBottom;
    
    let xCenter = paddingLeft + drawW / 2;
    let yGround = paddingTop + 60; // Mặt đất tự nhiên
    
    // scaleX và scaleY
    let maxXReal = bCrest * 1.5 + mSlope * hEmb * 2;
    if (circular) {
        // Đảm bảo maxXReal đủ lớn để chứa toàn bộ cung trượt (tránh trượt ra ngoài biên vẽ)
        let maxCircleX = Math.abs(circular.Xc) + circular.R;
        maxXReal = Math.max(maxXReal, maxCircleX * 2 + 6.0); // Thêm 6m biên an toàn hai bên
    }
    let scaleX = drawW / maxXReal;
    let scaleY = drawH / (hEmb + totalSoilDepth + 5);
    
    // 1. Vẽ các lớp đất nền
    let currentDepth = 0;
    appState.soilLayers.forEach(layer => {
        let yTop = yGround + currentDepth * scaleY;
        let hPx = layer.thickness * scaleY;
        ctx.fillStyle = layer.color || "#475569";
        ctx.fillRect(paddingLeft, yTop, drawW, hPx);
        
        // Vẽ tên lớp
        ctx.fillStyle = isPrinting ? "rgba(0, 0, 0, 0.8)" : "rgba(255, 255, 255, 0.7)";
        ctx.font = "9px Inter";
        ctx.fillText(`${layer.name} (H = ${layer.thickness.toFixed(1)}m)`, paddingLeft + 10, yTop + 15);
        
        currentDepth += layer.thickness;
    });
    
    // Đường phân cách các lớp
    currentDepth = 0;
    ctx.strokeStyle = isPrinting ? "rgba(0, 0, 0, 0.2)" : "rgba(255, 255, 255, 0.15)";
    ctx.lineWidth = 1;
    appState.soilLayers.forEach(layer => {
        currentDepth += layer.thickness;
        let y = yGround + currentDepth * scaleY;
        ctx.beginPath();
        ctx.moveTo(paddingLeft, y);
        ctx.lineTo(paddingLeft + drawW, y);
        ctx.stroke();
    });
    
    let boundary = checkBoundaryError();
    let treatment = boundary.hasError ? 'none' : appState.treatment.method;
    let toeX = bCrest / 2 + mSlope * hEmb;
    
    let spacing = 1.5;
    if (treatment === 'pvd') spacing = appState.pvd.spacing;
    else if (treatment === 'cdm') spacing = appState.cdm.spacing;
    else if (treatment === 'sand_drain') spacing = appState.sandDrain.spacing;
    else if (treatment === 'sand_column') spacing = appState.sandColumn.spacing;
    else if (treatment === 'bamboo_pile' || treatment === 'bamboo_replacement') spacing = appState.bambooPile.spacing;

    let overWidth = 1.0;
    if (treatment === 'cdm') {
        overWidth = appState.cdm.overWidth !== undefined ? appState.cdm.overWidth : 1.0;
    }
    
    let layout = getColumnLayout(toeX, spacing, overWidth);
    let maxReinforcementX = layout.max * scaleX;
    
    if (treatment === 'cdm') {
        let cdmDepth = appState.cdm.depth;
        let d_cdm = appState.cdm.diameter;
        ctx.fillStyle = "rgba(16, 185, 129, 0.25)";
        ctx.strokeStyle = "rgba(16, 185, 129, 0.5)";
        ctx.lineWidth = 1;
        let pileWidthPx = d_cdm * scaleX;
        
        layout.offsets.forEach(xOffset => {
            ctx.fillRect(xCenter + xOffset * scaleX - pileWidthPx/2, yGround, pileWidthPx, Math.min(cdmDepth, totalSoilDepth) * scaleY);
            ctx.strokeRect(xCenter + xOffset * scaleX - pileWidthPx/2, yGround, pileWidthPx, Math.min(cdmDepth, totalSoilDepth) * scaleY);
            if (xOffset > 0) {
                ctx.fillRect(xCenter - xOffset * scaleX - pileWidthPx/2, yGround, pileWidthPx, Math.min(cdmDepth, totalSoilDepth) * scaleY);
                ctx.strokeRect(xCenter - xOffset * scaleX - pileWidthPx/2, yGround, pileWidthPx, Math.min(cdmDepth, totalSoilDepth) * scaleY);
            }
        });
    } else if (treatment === 'pvd' || treatment === 'vacuum_pvd') {
        let pvdDepth = appState.pvd.depth;
        ctx.strokeStyle = isPrinting ? "#0891b2" : "#06b6d4";
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);
        layout.offsets.forEach(xOffset => {
            ctx.beginPath();
            ctx.moveTo(xCenter + xOffset * scaleX, yGround);
            ctx.lineTo(xCenter + xOffset * scaleX, yGround + Math.min(pvdDepth, totalSoilDepth) * scaleY);
            ctx.stroke();
            if (xOffset > 0) {
                ctx.beginPath();
                ctx.moveTo(xCenter - xOffset * scaleX, yGround);
                ctx.lineTo(xCenter - xOffset * scaleX, yGround + Math.min(pvdDepth, totalSoilDepth) * scaleY);
                ctx.stroke();
            }
        });
        ctx.setLineDash([]);
    } else if (treatment === 'bamboo_pile' || treatment === 'bamboo_replacement') {
        let bpDepth = appState.bambooPile.depth;
        let d_bp = appState.bambooPile.diameter;
        let hRep = treatment === 'bamboo_replacement' ? appState.excavationReplacement.depth : 0;
        
        ctx.fillStyle = "rgba(132, 204, 22, 0.25)";
        ctx.strokeStyle = "rgba(132, 204, 22, 0.5)";
        ctx.lineWidth = 1;
        let pileWidthPx = d_bp * scaleX;
        if (pileWidthPx < 1.5) pileWidthPx = 1.5;
        
        layout.offsets.forEach(xOffset => {
            let startY = yGround + hRep * scaleY;
            let endY = yGround + Math.min(hRep + bpDepth, totalSoilDepth) * scaleY;
            ctx.fillRect(xCenter + xOffset * scaleX - pileWidthPx/2, startY, pileWidthPx, endY - startY);
            ctx.strokeRect(xCenter + xOffset * scaleX - pileWidthPx/2, startY, pileWidthPx, endY - startY);
            if (xOffset > 0) {
                ctx.fillRect(xCenter - xOffset * scaleX - pileWidthPx/2, startY, pileWidthPx, endY - startY);
                ctx.strokeRect(xCenter - xOffset * scaleX - pileWidthPx/2, startY, pileWidthPx, endY - startY);
            }
        });
    }
    
    // 2. Vẽ nền đường đắp (Trapezoid)
    let wCrestPx = bCrest * scaleX;
    let wSlopePx = mSlope * hEmb * scaleX;
    let hEmbPx = hEmb * scaleY;
    
    ctx.fillStyle = isPrinting ? "#e2e8f0" : "#475569";
    ctx.strokeStyle = isPrinting ? "#475569" : "#94a3b8";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(xCenter - wCrestPx / 2 - wSlopePx, yGround);
    ctx.lineTo(xCenter - wCrestPx / 2, yGround - hEmbPx);
    ctx.lineTo(xCenter + wCrestPx / 2, yGround - hEmbPx);
    ctx.lineTo(xCenter + wCrestPx / 2 + wSlopePx, yGround);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    
    // Tim đường
    ctx.strokeStyle = isPrinting ? "rgba(0, 0, 0, 0.3)" : "rgba(255, 255, 255, 0.3)";
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(xCenter, yGround - hEmbPx - 20);
    ctx.lineTo(xCenter, yGround + totalSoilDepth * scaleY);
    ctx.stroke();
    ctx.setLineDash([]);
    
    // Mặt đất tự nhiên
    ctx.strokeStyle = isPrinting ? "#475569" : "#cbd5e1";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(paddingLeft, yGround);
    ctx.lineTo(paddingLeft + drawW, yGround);
    ctx.stroke();
    
    // 3. Vẽ cung trượt tròn
    if (circular) {
        let cx = xCenter + circular.Xc * scaleX;
        let cy = yGround - circular.Zc * scaleY;
        let Dc = -circular.Zc;
        let R = circular.R;
        
        let rX = R * scaleX;
        let rY = R * scaleY;
        
        let bHalf = bCrest / 2;
        function getSurfaceDepthVal(xReal) {
            let absX = Math.abs(xReal);
            if (absX <= bHalf) {
                return -hEmb;
            } else if (absX <= bHalf + mSlope * hEmb) {
                let dist = absX - bHalf;
                return -hEmb + dist / mSlope;
            } else {
                return 0;
            }
        }
        
        let xLeft = circular.Xc - R;
        let xRight = circular.Xc + R;
        let scanSteps = 100;
        let xStart = null;
        let xEnd = null;
        let dx_scan = (xRight - xLeft) / scanSteps;
        
        for (let i = 0; i <= scanSteps; i++) {
            let xReal = xLeft + i * dx_scan;
            let radVal = R * R - (xReal - circular.Xc) * (xReal - circular.Xc);
            if (radVal < 0) continue;
            
            let dCircle = Dc + Math.sqrt(radVal);
            let dSurface = getSurfaceDepthVal(xReal);
            if (dCircle > dSurface) {
                if (xStart === null) xStart = xReal;
                xEnd = xReal;
            }
        }
        
        if (xStart !== null && xEnd !== null && xEnd > xStart) {
            ctx.strokeStyle = "#ef4444";
            ctx.lineWidth = 2.5;
            ctx.setLineDash([6, 4]);
            
            ctx.beginPath();
            let first = true;
            let stepDrawing = (xEnd - xStart) / 100;
            for (let xReal = xStart; xReal <= xEnd; xReal += stepDrawing) {
                let radVal = R * R - (xReal - circular.Xc) * (xReal - circular.Xc);
                if (radVal < 0) continue;
                let yReal = Dc + Math.sqrt(radVal);
                
                let px = xCenter + xReal * scaleX;
                let py = yGround + yReal * scaleY;
                if (first) {
                    ctx.moveTo(px, py);
                    first = false;
                } else {
                    ctx.lineTo(px, py);
                }
            }
            ctx.stroke();
            ctx.setLineDash([]);
            
            ctx.strokeStyle = "#ef4444";
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.moveTo(cx - 6, cy); ctx.lineTo(cx + 6, cy);
            ctx.moveTo(cx, cy - 6); ctx.lineTo(cx, cy + 6);
            ctx.stroke();
            
            ctx.fillStyle = isPrinting ? "#000000" : "#ef4444";
            ctx.font = "9px Inter";
            ctx.fillText(`Tâm Cung Trượt (${circular.Xc.toFixed(1)}, ${circular.Zc.toFixed(1)})`, cx + 8, cy - 4);
            
            let textFS = `Hệ số ổn định trượt Fs = ${circular.FS.toFixed(3)}`;
            ctx.font = "bold 11px Inter";
            ctx.fillStyle = circular.FS >= 1.2 ? "#10b981" : "#ef4444";
            
            let midX = (xStart + xEnd) / 2;
            let midYDepth = Dc + Math.sqrt(R * R - (midX - circular.Xc) * (midX - circular.Xc));
            let midPx = xCenter + midX * scaleX;
            let midPy = yGround + midYDepth * scaleY;
            
            ctx.textAlign = "center";
            ctx.fillText(textFS, midPx, midPy + 16);
            ctx.fillText(circular.FS >= 1.2 ? "ĐẠT YÊU CẦU (FS ≥ 1.2)" : "KHÔNG ĐẠT (FS < 1.2)", midPx, midPy + 30);
            ctx.textAlign = "left";
        }
    }
}

// Tooltip drawing helper for HTML5 canvas
function drawTooltip(ctx, x, y, title, lines, canvasW, canvasH) {
    ctx.save();
    ctx.font = "10px Inter";
    
    // Calculate width of tooltip
    let maxW = ctx.measureText(title).width;
    lines.forEach(l => {
        let w = ctx.measureText(l.text).width;
        if (w > maxW) maxW = w;
    });
    let tooltipW = maxW + 20;
    let tooltipH = 16 + lines.length * 14 + 10;
    
    // Position tooltip to avoid edges
    let tx = x + 15;
    let ty = y - tooltipH / 2;
    if (tx + tooltipW > canvasW - 10) {
        tx = x - tooltipW - 15;
    }
    if (ty < 10) {
        ty = 10;
    }
    if (ty + tooltipH > canvasH - 10) {
        ty = canvasH - tooltipH - 10;
    }
    
    // Draw background
    ctx.fillStyle = "rgba(15, 23, 42, 0.95)";
    ctx.strokeStyle = "rgba(99, 102, 241, 0.6)";
    ctx.lineWidth = 1;
    
    // Rounded rect
    ctx.beginPath();
    if (ctx.roundRect) {
        ctx.roundRect(tx, ty, tooltipW, tooltipH, 6);
    } else {
        ctx.rect(tx, ty, tooltipW, tooltipH);
    }
    ctx.fill();
    ctx.stroke();
    
    // Draw Title
    ctx.fillStyle = "#a5b4fc";
    ctx.font = "bold 10px Inter";
    ctx.textAlign = "left";
    ctx.fillText(title, tx + 10, ty + 18);
    
    // Draw divider
    ctx.strokeStyle = "rgba(255, 255, 255, 0.1)";
    ctx.beginPath();
    ctx.moveTo(tx + 10, ty + 24);
    ctx.lineTo(tx + tooltipW - 10, ty + 24);
    ctx.stroke();
    
    // Draw Lines
    let currY = ty + 36;
    ctx.font = "9px Inter";
    lines.forEach(l => {
        ctx.fillStyle = l.color || "#cbd5e1";
        ctx.fillText(l.text, tx + 10, currY);
        currY += 14;
    });
    ctx.restore();
}

function drawStressChart(sublayers, mouseX = null, mouseY = null) {
    const canvas = document.getElementById("chart-stress");
    const ctx = canvas.getContext("2d");
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    let w = canvas.width;
    let h = canvas.height;
    
    let padL = 45;
    let padR = 20;
    let padT = 20;
    let padB = 35;
    
    let drawW = w - padL - padR;
    let drawH = h - padT - padB;
    
    // Chart bg
    ctx.fillStyle = isPrinting ? "#ffffff" : "#0a0c10";
    ctx.fillRect(0, 0, w, h);
    
    // Draw Axis lines
    ctx.strokeStyle = isPrinting ? "#9ca3af" : "#374151";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(padL, padT);
    ctx.lineTo(padL, padT + drawH);
    ctx.lineTo(padL + drawW, padT + drawH);
    ctx.stroke();
    
    // Max depth
    let maxDepth = sublayers[sublayers.length - 1].depthBottom;
    // Max stress
    let maxStress = Math.max(...sublayers.map(s => s.sigmaV0 + s.deltaSigmaZ), ...sublayers.map(s => s.sigmaP)) * 1.15;
    if (maxStress < 100) maxStress = 100;
    
    let scaleX = drawW / maxStress;
    let scaleY = drawH / maxDepth;
    
    // Draw grid & labels
    ctx.fillStyle = isPrinting ? "#1f2937" : "#9ca3af";
    ctx.font = "9px Inter";
    ctx.textAlign = "center";
    
    // X axis ticks (Stress)
    let xStep = Math.ceil(maxStress / 5 / 10) * 10;
    for (let s = 0; s <= maxStress; s += xStep) {
        let x = padL + s * scaleX;
        ctx.strokeStyle = isPrinting ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.05)";
        ctx.beginPath();
        ctx.moveTo(x, padT);
        ctx.lineTo(x, padT + drawH);
        ctx.stroke();
        
        ctx.fillText(s.toFixed(0), x, padT + drawH + 14);
    }
    ctx.fillText("Ứng suất (kPa)", padL + drawW/2, padT + drawH + 28);
    
    // Y axis ticks (Depth)
    ctx.textAlign = "right";
    let yStep = Math.ceil(maxDepth / 5);
    if (yStep < 1) yStep = 1;
    for (let d = 0; d <= maxDepth; d += yStep) {
        let y = padT + d * scaleY;
        ctx.strokeStyle = isPrinting ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.05)";
        ctx.beginPath();
        ctx.moveTo(padL, y);
        ctx.lineTo(padL + drawW, y);
        ctx.stroke();
        
        ctx.fillText(d.toFixed(0), padL - 6, y + 3);
    }
    
    // Y label (Rotated text)
    ctx.save();
    ctx.translate(12, padT + drawH/2);
    ctx.rotate(-Math.PI/2);
    ctx.textAlign = "center";
    ctx.fillText("Chiều sâu z (m)", 0, 0);
    ctx.restore();

    // 1. Draw sigma'v0 curve (Red)
    ctx.strokeStyle = "#f87171";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(padL + 0 * scaleX, padT + 0 * scaleY);
    sublayers.forEach(sub => {
        ctx.lineTo(padL + sub.sigmaV0 * scaleX, padT + sub.depthCenter * scaleY);
    });
    ctx.stroke();

    // 2. Draw sigma'p curve (Yellow/Amber)
    ctx.strokeStyle = "#fbbf24";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    let prevP = -1;
    sublayers.forEach(sub => {
        let x = padL + sub.sigmaP * scaleX;
        let y = padT + sub.depthCenter * scaleY;
        if (prevP === -1) {
            ctx.moveTo(x, y);
        } else {
            ctx.lineTo(x, y);
        }
        prevP = sub.sigmaP;
    });
    ctx.stroke();
    ctx.setLineDash([]);

    // 3. Draw sigma'v0 + delta_sigma_z curve (Blue)
    ctx.strokeStyle = "#60a5fa";
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    // At surface z=0
    let qEmb = appState.embankment.height * appState.embankment.gamma + appState.foundation.surcharge;
    ctx.moveTo(padL + qEmb * scaleX, padT + 0 * scaleY);
    sublayers.forEach(sub => {
        ctx.lineTo(padL + (sub.sigmaV0 + sub.deltaSigmaZ) * scaleX, padT + sub.depthCenter * scaleY);
    });
    ctx.stroke();
    
    // Add simple Legend inside chart
    ctx.textAlign = "left";
    ctx.font = "8px Inter";
    
    ctx.fillStyle = "#f87171";
    ctx.fillRect(padL + drawW - 75, padT + 10, 8, 8);
    ctx.fillStyle = isPrinting ? "#000000" : "#ffffff";
    ctx.fillText("σ'v0", padL + drawW - 63, padT + 17);
    
    ctx.fillStyle = "#fbbf24";
    ctx.fillRect(padL + drawW - 75, padT + 22, 8, 8);
    ctx.fillStyle = isPrinting ? "#000000" : "#ffffff";
    ctx.fillText("σ'p", padL + drawW - 63, padT + 29);
    
    ctx.fillStyle = "#60a5fa";
    ctx.fillRect(padL + drawW - 75, padT + 34, 8, 8);
    ctx.fillStyle = isPrinting ? "#000000" : "#ffffff";
    ctx.fillText("σ'v0 + Δσz", padL + drawW - 63, padT + 41);

    // Draw hover details
    if (mouseX !== null && mouseY !== null && !isPrinting) {
        if (mouseX >= padL && mouseX <= w - padR && mouseY >= padT && mouseY <= h - padB) {
            let mouseDepth = (mouseY - padT) / scaleY;
            let hoveredSub = null;
            let minDiff = Infinity;
            sublayers.forEach(sub => {
                let diff = Math.abs(sub.depthCenter - mouseDepth);
                if (diff < minDiff) {
                    minDiff = diff;
                    hoveredSub = sub;
                }
            });
            
            if (hoveredSub) {
                let hy = padT + hoveredSub.depthCenter * scaleY;
                let xRed = padL + hoveredSub.sigmaV0 * scaleX;
                let xYellow = padL + hoveredSub.sigmaP * scaleX;
                let xBlue = padL + (hoveredSub.sigmaV0 + hoveredSub.deltaSigmaZ) * scaleX;
                
                // Draw horizontal line
                ctx.strokeStyle = "rgba(255, 255, 255, 0.25)";
                ctx.lineWidth = 1;
                ctx.setLineDash([3, 3]);
                ctx.beginPath();
                ctx.moveTo(padL, hy);
                ctx.lineTo(w - padR, hy);
                ctx.stroke();
                ctx.setLineDash([]);
                
                // Draw markers
                ctx.lineWidth = 1.5;
                ctx.strokeStyle = isPrinting ? "#000" : "#fff";
                
                // Red marker
                ctx.fillStyle = "#f87171";
                ctx.beginPath(); ctx.arc(xRed, hy, 4, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
                
                // Yellow marker
                ctx.fillStyle = "#fbbf24";
                ctx.beginPath(); ctx.arc(xYellow, hy, 4, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
                
                // Blue marker
                ctx.fillStyle = "#60a5fa";
                ctx.beginPath(); ctx.arc(xBlue, hy, 4, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
                
                // Tooltip lines
                let tooltipLines = [
                    { text: `σ'v0 (Bản thân): ${hoveredSub.sigmaV0.toFixed(1)} kPa`, color: "#f87171" },
                    { text: `σ'p (Tiền cố kết): ${hoveredSub.sigmaP.toFixed(1)} kPa`, color: "#fbbf24" },
                    { text: `σ'v0 + Δσz (Tổng): ${(hoveredSub.sigmaV0 + hoveredSub.deltaSigmaZ).toFixed(1)} kPa`, color: "#60a5fa" }
                ];
                
                drawTooltip(ctx, mouseX, mouseY, `Chiều sâu z = ${hoveredSub.depthCenter.toFixed(2)} m`, tooltipLines, w, h);
            }
        }
    }
}

function drawTimeCurve(settlementTimeData, tConst, tEnd, mouseX = null, mouseY = null) {
    const canvas = document.getElementById("chart-time");
    const ctx = canvas.getContext("2d");
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    let w = canvas.width;
    let h = canvas.height;
    
    let padL = 40;
    let padR = 20;
    let padT = 20;
    let padB = 35;
    
    let drawW = w - padL - padR;
    let drawH = h - padT - padB;
    
    ctx.fillStyle = isPrinting ? "#ffffff" : "#0a0c10";
    ctx.fillRect(0, 0, w, h);
    
    ctx.strokeStyle = isPrinting ? "#9ca3af" : "#374151";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(padL, padT);
    ctx.lineTo(padL, padT + drawH);
    ctx.lineTo(padL + drawW, padT + drawH);
    ctx.stroke();
    
    // Bounds
    let maxTime = settlementTimeData[settlementTimeData.length - 1].t;
    let maxS = Math.max(...settlementTimeData.map(d => d.S)) * 1.1;
    if (maxS < 10) maxS = 10;
    
    let scaleX = drawW / maxTime;
    let scaleY = drawH / maxS;
    
    // X ticks (Time)
    ctx.fillStyle = isPrinting ? "#1f2937" : "#9ca3af";
    ctx.font = "9px Inter";
    ctx.textAlign = "center";
    let xStep = Math.ceil(maxTime / 6);
    for (let t = 0; t <= maxTime; t += xStep) {
        let x = padL + t * scaleX;
        ctx.strokeStyle = isPrinting ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.05)";
        ctx.beginPath();
        ctx.moveTo(x, padT);
        ctx.lineTo(x, padT + drawH);
        ctx.stroke();
        
        ctx.fillText(t.toFixed(0), x, padT + drawH + 14);
    }
    ctx.fillText("Thời gian t (năm)", padL + drawW/2, padT + drawH + 28);
    
    // Y ticks (Settlement)
    ctx.textAlign = "right";
    let yStep = Math.ceil(maxS / 5);
    for (let s = 0; s <= maxS; s += yStep) {
        // Draw inverted scale because settlement goes downwards!
        let y = padT + s * scaleY;
        ctx.strokeStyle = isPrinting ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.05)";
        ctx.beginPath();
        ctx.moveTo(padL, y);
        ctx.lineTo(padL + drawW, y);
        ctx.stroke();
        
        ctx.fillText(s.toFixed(0), padL - 6, y + 3);
    }
    
    // Y label
    ctx.save();
    ctx.translate(12, padT + drawH/2);
    ctx.rotate(-Math.PI/2);
    ctx.textAlign = "center";
    ctx.fillText("Độ lún S (cm)", 0, 0);
    ctx.restore();

    // Draw Curve S(t) (Emerald green)
    ctx.strokeStyle = "#34d399";
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(padL, padT);
    settlementTimeData.forEach(d => {
        ctx.lineTo(padL + d.t * scaleX, padT + d.S * scaleY);
    });
    ctx.stroke();

    // Draw vertical dotted line at t_construction (End of preloading)
    let xConst = padL + tConst * scaleX;
    ctx.strokeStyle = "rgba(244, 63, 94, 0.6)"; // Red/Pink
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(xConst, padT);
    ctx.lineTo(xConst, padT + drawH);
    ctx.stroke();
    
    // Draw vertical dotted line at t_end (Design life end)
    let xEnd = padL + tEnd * scaleX;
    ctx.strokeStyle = "rgba(96, 165, 250, 0.6)"; // Blue
    ctx.beginPath();
    ctx.moveTo(xEnd, padT);
    ctx.lineTo(xEnd, padT + drawH);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw text labels for key milestones
    ctx.fillStyle = "#f43f5e";
    ctx.font = "8px Inter";
    ctx.textAlign = "left";
    ctx.fillText("Thảm nhựa", xConst + 4, padT + 12);
    
    ctx.fillStyle = "#60a5fa";
    ctx.fillText("Kết thúc khai thác", xEnd - 70, padT + 24);

    // Draw hover details
    if (mouseX !== null && mouseY !== null && !isPrinting) {
        if (mouseX >= padL && mouseX <= w - padR && mouseY >= padT && mouseY <= h - padB) {
            let mouseTime = (mouseX - padL) / scaleX;
            let hoveredPt = null;
            let minDiff = Infinity;
            settlementTimeData.forEach(pt => {
                let diff = Math.abs(pt.t - mouseTime);
                if (diff < minDiff) {
                    minDiff = diff;
                    hoveredPt = pt;
                }
            });
            
            if (hoveredPt) {
                let hx = padL + hoveredPt.t * scaleX;
                let hy = padT + hoveredPt.S * scaleY;
                
                // Draw vertical line
                ctx.strokeStyle = "rgba(255, 255, 255, 0.25)";
                ctx.lineWidth = 1;
                ctx.setLineDash([3, 3]);
                ctx.beginPath();
                ctx.moveTo(hx, padT);
                ctx.lineTo(hx, h - padB);
                ctx.stroke();
                ctx.setLineDash([]);
                
                // Draw marker
                ctx.lineWidth = 1.5;
                ctx.strokeStyle = isPrinting ? "#000" : "#fff";
                ctx.fillStyle = "#34d399";
                ctx.beginPath(); ctx.arc(hx, hy, 4.5, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
                
                // Tooltip lines
                let tooltipLines = [
                    { text: `Độ lún S: ${hoveredPt.S.toFixed(2)} cm`, color: "#34d399" },
                    { text: `Độ cố kết U: ${(hoveredPt.U * 100).toFixed(1)}%`, color: "#60a5fa" }
                ];
                
                drawTooltip(ctx, mouseX, mouseY, `Thời gian t = ${hoveredPt.t.toFixed(1)} năm`, tooltipLines, w, h);
            }
        }
    }
}

function drawDepthSettlementCurve(sublayers, mouseX = null, mouseY = null) {
    const canvas = document.getElementById("chart-depth");
    const ctx = canvas.getContext("2d");
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    let w = canvas.width;
    let h = canvas.height;
    
    let padL = 40;
    let padR = 20;
    let padT = 20;
    let padB = 35;
    
    let drawW = w - padL - padR;
    let drawH = h - padT - padB;
    
    ctx.fillStyle = isPrinting ? "#ffffff" : "#0a0c10";
    ctx.fillRect(0, 0, w, h);
    
    ctx.strokeStyle = isPrinting ? "#9ca3af" : "#374151";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(padL, padT);
    ctx.lineTo(padL, padT + drawH);
    ctx.lineTo(padL + drawW, padT + drawH);
    ctx.stroke();
    
    // Max depth
    let maxDepth = sublayers[sublayers.length - 1].depthBottom;
    // Max settlement (cumulative from bottom to top)
    let settlements = [];
    let cumulativeS = 0;
    
    // Cumulative settlement is plotted from bottom (0 lún) to top (full lún)
    for (let i = sublayers.length - 1; i >= 0; i--) {
        cumulativeS += sublayers[i].Stotal;
        settlements.unshift({
            depth: sublayers[i].depthCenter,
            S: cumulativeS
        });
    }

    let maxS = cumulativeS * 1.1;
    if (maxS < 10) maxS = 10;
    
    let scaleX = drawW / maxS;
    let scaleY = drawH / maxDepth;
    
    // X ticks (Settlement)
    ctx.fillStyle = isPrinting ? "#1f2937" : "#9ca3af";
    ctx.font = "9px Inter";
    ctx.textAlign = "center";
    let xStep = Math.ceil(maxS / 5);
    for (let s = 0; s <= maxS; s += xStep) {
        let x = padL + s * scaleX;
        ctx.strokeStyle = isPrinting ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.05)";
        ctx.beginPath();
        ctx.moveTo(x, padT);
        ctx.lineTo(x, padT + drawH);
        ctx.stroke();
        
        ctx.fillText(s.toFixed(0), x, padT + drawH + 14);
    }
    ctx.fillText("Độ lún tích lũy S (cm)", padL + drawW/2, padT + drawH + 28);
    
    // Y ticks (Depth)
    ctx.textAlign = "right";
    let yStep = Math.ceil(maxDepth / 5);
    if (yStep < 1) yStep = 1;
    for (let d = 0; d <= maxDepth; d += yStep) {
        let y = padT + d * scaleY;
        ctx.strokeStyle = isPrinting ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.05)";
        ctx.beginPath();
        ctx.moveTo(padL, y);
        ctx.lineTo(padL + drawW, y);
        ctx.stroke();
        
        ctx.fillText(d.toFixed(0), padL - 6, y + 3);
    }
    
    // Y label
    ctx.save();
    ctx.translate(12, padT + drawH/2);
    ctx.rotate(-Math.PI/2);
    ctx.textAlign = "center";
    ctx.fillText("Chiều sâu z (m)", 0, 0);
    ctx.restore();

    // Draw Curve S(z) (Indigo/Purple)
    ctx.strokeStyle = "#818cf8";
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(padL + cumulativeS * scaleX, padT); // surface
    settlements.forEach(pt => {
        ctx.lineTo(padL + pt.S * scaleX, padT + pt.depth * scaleY);
    });
    ctx.lineTo(padL + 0, padT + maxDepth * scaleY); // bottom
    ctx.stroke();

    // Draw hover details
    if (mouseX !== null && mouseY !== null && !isPrinting) {
        if (mouseX >= padL && mouseX <= w - padR && mouseY >= padT && mouseY <= h - padB) {
            let mouseDepth = (mouseY - padT) / scaleY;
            let hoveredPt = null;
            let minDiff = Infinity;
            settlements.forEach(pt => {
                let diff = Math.abs(pt.depth - mouseDepth);
                if (diff < minDiff) {
                    minDiff = diff;
                    hoveredPt = pt;
                }
            });
            
            if (hoveredPt) {
                let hx = padL + hoveredPt.S * scaleX;
                let hy = padT + hoveredPt.depth * scaleY;
                
                // Draw horizontal line
                ctx.strokeStyle = "rgba(255, 255, 255, 0.25)";
                ctx.lineWidth = 1;
                ctx.setLineDash([3, 3]);
                ctx.beginPath();
                ctx.moveTo(padL, hy);
                ctx.lineTo(w - padR, hy);
                ctx.stroke();
                ctx.setLineDash([]);
                
                // Draw marker
                ctx.lineWidth = 1.5;
                ctx.strokeStyle = isPrinting ? "#000" : "#fff";
                ctx.fillStyle = "#818cf8";
                ctx.beginPath(); ctx.arc(hx, hy, 4.5, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
                
                // Tooltip lines
                let tooltipLines = [
                    { text: `Lún tích lũy S: ${hoveredPt.S.toFixed(2)} cm`, color: "#818cf8" }
                ];
                
                drawTooltip(ctx, mouseX, mouseY, `Chiều sâu z = ${hoveredPt.depth.toFixed(2)} m`, tooltipLines, w, h);
            }
        }
    }
}

// Load a typical Mekong Delta clay sample project
function loadSampleProject() {
    appState = {
        schemaVersion: 2,
        embankment: {
            height: 4.5,
            gamma: 18.5,
            width: 24.0,
            slope: 1.5,
            staged: false,
            stagedH1: 2.0,
            stagedT1: 6,
            stagedH2: 2.5
        },
        foundation: {
            gwt: 1.0,
            surcharge: 20.0,
            life: 15,
            allowable: 10.0,
            mFactor: 1.3,
            tConstruction: 1.0,
            drainage: "single",
            shearMode: "undrained"
        },
        treatment: {
            method: 'pvd'
        },
                pvd: {
            spacing: 1.2,
            pattern: 'triangular',
            depth: 15.0,
            width: 100,
            thickness: 4,
            chRatio: 2.0,
            dsDw: 2.5,
            khKs: 3.0,
            qw: 60.0,
            standard: 'tccs_41',
            vacuumPressure: 80.0,
            vacuumEfficiency: 0.85
        },
        cdm: {
            spacing: 1.5,
            pattern: 'square',
            depth: 12.0,
            diameter: 0.8,
            qu: 1.5,
            emodulusRatio: 150,
            tUlt: 100.0,
            rf: 2.0,
            phi: 30.0
        },
        sandDrain: {
            spacing: 1.5,
            pattern: 'triangular',
            depth: 15.0,
            diameter: 0.4,
            chRatio: 2.0
        },
        sandColumn: {
            spacing: 1.8,
            pattern: 'triangular',
            depth: 12.0,
            diameter: 0.8,
            modulus: 25.0,
            chRatio: 2.0
        },
        bambooPile: {
            spacing: 0.2,
            pattern: 'square',
            depth: 4.0,
            diameter: 0.08,
            modulus: 100.0,
            phi: 30.0
        },
        excavationReplacement: {
            depth: 2.0,
            gamma: 18.0,
            gammaSat: 19.0,
            E: 15000.0,
            phi: 30.0,
            c: 0.0
        },
        soilLayers: [
            {
                id: 1,
                name: "Sét bùn hữu cơ yếu (Rất lún)",
                type: "cohesive",
                thickness: 8.0,
                gamma: 14.2,
                gammaSat: 14.8,
                e0: 1.95,
                Cc: 0.72,
                Cr: 0.09,
                sigmaP: 30.0,
                Cv: 1.2,
                Ca: 0.025,
                E: 1800,
                c: 10.0,          // kPa
                phi: 3.0,         // degrees
                color: "#4a3525"
            },
            {
                id: 2,
                name: "Sét mềm xám đen (Lún vừa)",
                type: "cohesive",
                thickness: 8.0,
                gamma: 16.5,
                gammaSat: 17.2,
                e0: 1.20,
                Cc: 0.32,
                Cr: 0.05,
                sigmaP: 75.0,
                Cv: 2.8,
                Ca: 0.012,
                E: 4200,
                c: 16.0,          // kPa
                phi: 8.0,         // degrees
                color: "#6b5e53"
            },
            {
                id: 3,
                name: "Cát pha hạt mịn (Thải nước tốt)",
                type: "granular",
                thickness: 10.0,
                gamma: 18.5,
                gammaSat: 19.5,
                e0: 0.60,
                Cc: 0.015,
                Cr: 0.003,
                sigmaP: 250.0,
                Cv: 55.0,
                Ca: 0.001,
                E: 25000,
                c: 2.0,           // kPa
                phi: 30.0,        // degrees
                color: "#c2b280"
            }
        ]
    };

    // Update DOM inputs to match loaded project
    updateInputsFromState();
    renderLayersTable();
    runAnalysis();
}

function updateInputsFromState() {
    const inputMappings = [
                { id: "emb-height", val: appState.embankment.height },
        { id: "emb-staged-toggle", val: appState.embankment.staged },
        { id: "staged-h1", val: appState.embankment.stagedH1 },
        { id: "staged-t1", val: appState.embankment.stagedT1 },
        { id: "staged-h2", val: appState.embankment.stagedH2 },
        { id: "emb-gamma", val: appState.embankment.gamma },
        { id: "emb-width", val: appState.embankment.width },
        { id: "emb-slope", val: appState.embankment.slope },
        { id: "gwt-depth", val: appState.foundation.gwt },
        { id: "traffic-load", val: appState.foundation.surcharge },
        { id: "design-life", val: appState.foundation.life },
        { id: "t-construction", val: appState.foundation.tConstruction },
        { id: "allowable-remaining-settlement", val: appState.foundation.allowable },
        { id: "emb-m-factor", val: appState.foundation.mFactor },
        { id: "drainage-boundary", val: appState.foundation.drainage || "single" },
        { id: "shear-analysis-mode", val: appState.foundation.shearMode || "undrained" },
        
        { id: "treatment-method", val: appState.treatment.method },
        
        { id: "pvd-standard", val: appState.pvd.standard || "tcvn_9355" },
        { id: "pvd-spacing", val: appState.pvd.spacing },
        { id: "pvd-pattern", val: appState.pvd.pattern },
        { id: "pvd-depth", val: appState.pvd.depth },
        { id: "pvd-width", val: appState.pvd.width },
        { id: "pvd-thickness", val: appState.pvd.thickness },
        { id: "pvd-ch-ratio", val: appState.pvd.chRatio },
        { id: "pvd-ds-dw", val: appState.pvd.dsDw || 2.5 },
        { id: "pvd-kh-ks", val: appState.pvd.khKs || 3.0 },
                { id: "pvd-qw", val: appState.pvd.qw || 60.0 },
        { id: "vacuum-pressure", val: appState.pvd.vacuumPressure || 80.0 },
        { id: "vacuum-efficiency", val: appState.pvd.vacuumEfficiency || 0.85 },
        
        { id: "cdm-spacing", val: appState.cdm.spacing },
        { id: "cdm-pattern", val: appState.cdm.pattern },
        { id: "cdm-depth", val: appState.cdm.depth },
        { id: "cdm-diameter", val: appState.cdm.diameter },
        { id: "cdm-qu", val: appState.cdm.qu },
        { id: "cdm-emodulus-ratio", val: appState.cdm.emodulusRatio },
        { id: "cdm-t-ult", val: appState.cdm.tUlt },
        { id: "cdm-rf", val: appState.cdm.rf },
        { id: "cdm-phi", val: appState.cdm.phi },

        { id: "sd-spacing", val: appState.sandDrain.spacing },
        { id: "sd-pattern", val: appState.sandDrain.pattern },
        { id: "sd-depth", val: appState.sandDrain.depth },
        { id: "sd-diameter", val: appState.sandDrain.diameter },
        { id: "sd-ch-ratio", val: appState.sandDrain.chRatio },

        { id: "sc-spacing", val: appState.sandColumn.spacing },
        { id: "sc-pattern", val: appState.sandColumn.pattern },
        { id: "sc-depth", val: appState.sandColumn.depth },
        { id: "sc-diameter", val: appState.sandColumn.diameter },
        { id: "sc-modulus", val: appState.sandColumn.modulus },
        { id: "sc-ch-ratio", val: appState.sandColumn.chRatio },

        { id: "bp-spacing", val: appState.bambooPile.spacing },
        { id: "bp-pattern", val: appState.bambooPile.pattern },
        { id: "bp-depth", val: appState.bambooPile.depth },
        { id: "bp-diameter", val: appState.bambooPile.diameter },
        { id: "bp-modulus", val: appState.bambooPile.modulus },
        { id: "bp-phi", val: appState.bambooPile.phi },
        { id: "replace-depth", val: appState.excavationReplacement.depth },
        { id: "replace-gamma", val: appState.excavationReplacement.gamma },
        { id: "replace-gammasat", val: appState.excavationReplacement.gammaSat },
        { id: "replace-modulus", val: appState.excavationReplacement.E },
        { id: "replace-phi", val: appState.excavationReplacement.phi },
        { id: "replace-c", val: appState.excavationReplacement.c }
    ];

    inputMappings.forEach(mapping => {
        const el = document.getElementById(mapping.id);
        if (el) {
            if (el.type === "checkbox") el.checked = Boolean(mapping.val);
            else el.value = mapping.val;
        }
    });

    const method = appState.treatment.method;
    document.getElementById("pvd-settings").style.display = (method === "pvd" || method === "vacuum_pvd") ? "block" : "none";
    document.getElementById("pvd-vacuum-settings").style.display = method === "vacuum_pvd" ? "block" : "none";
    document.getElementById("cdm-settings").style.display = method === "cdm" ? "block" : "none";
    document.getElementById("sand-drain-settings").style.display = method === "sand_drain" ? "block" : "none";
    document.getElementById("sand-column-settings").style.display = method === "sand_column" ? "block" : "none";
    document.getElementById("bamboo-pile-settings").style.display = (method === "bamboo_pile" || method === "bamboo_replacement") ? "block" : "none";
    document.getElementById("excavation-replacement-settings").style.display = (method === "excavation_replacement" || method === "bamboo_replacement") ? "block" : "none";
}

// JSON Export
// ==========================================
// NEW PROJECT
// ==========================================
function newProject() {
    if (!confirm("Bạn có chắc chắn muốn tạo dự án mới?\nTất cả dữ liệu chưa lưu sẽ bị mất.")) {
        return;
    }
    // Reset to default state
    appState.embankment = { height: 4.5, gamma: 18.5, width: 24.0, slope: 1.5 };
    appState.foundation = { gwt: 1.0, surcharge: 20.0, life: 15, allowable: 10.0, mFactor: 1.3, tConstruction: 1.0, drainage: "single", shearMode: "undrained" };
    appState.treatment = { method: 'pvd' };
    appState.pvd = { spacing: 1.2, pattern: 'triangular', depth: 15.0, width: 100.0, thickness: 4.0, chRatio: 2.0, dsDw: 2.5, khKs: 3.0, qw: 60.0, standard: 'tccs_41' };
    appState.cdm = { spacing: 1.5, pattern: 'square', depth: 12.0, diameter: 0.8, qu: 1.5, emodulusRatio: 150, tUlt: 100.0, rf: 2.0, phi: 30.0, overWidth: 1.0 };
    appState.sandDrain = { spacing: 1.5, pattern: 'triangular', depth: 15.0, diameter: 0.4, chRatio: 2.0 };
    appState.sandColumn = { spacing: 1.8, pattern: 'triangular', depth: 12.0, diameter: 0.8, modulus: 25.0, chRatio: 2.0 };
    appState.bambooPile = { spacing: 0.2, pattern: 'square', depth: 4.0, diameter: 0.08, modulus: 100.0, phi: 30.0 };
    appState.excavationReplacement = { depth: 2.0, gamma: 18.0, gammaSat: 19.0, E: 15000.0, phi: 30.0, c: 0.0 };
    appState.soilLayers = [
        {
            id: 1, name: "Lớp đất yếu 1", type: "cohesive", thickness: 10.0,
            gamma: 15.0, gammaSat: 16.0, e0: 1.5, Cc: 0.5, Cr: 0.06,
            sigmaP: 40.0, Cv: 1.5, Ca: 0.02, E: 2500, c: 10.0, phi: 5.0, color: "#4a3525"
        },
        {
            id: 2, name: "Lớp đất yếu 2", type: "cohesive", thickness: 5.0,
            gamma: 17.0, gammaSat: 17.8, e0: 1.0, Cc: 0.25, Cr: 0.04,
            sigmaP: 80.0, Cv: 3.0, Ca: 0.008, E: 4500, c: 15.0, phi: 8.0, color: "#6b5e53"
        },
        {
            id: 3, name: "Lớp đất tốt", type: "granular", thickness: 8.0,
            gamma: 18.5, gammaSat: 19.2, e0: 0.65, Cc: 0.02, Cr: 0.004,
            sigmaP: 200.0, Cv: 50.0, Ca: 0.001, E: 20000, c: 2.0, phi: 30.0, color: "#a69575"
        }
    ];
    updateInputsFromState();
    renderLayersTable();
    runAnalysis();
}

// ==========================================
// VALIDATION FUNCTIONS
// ==========================================
let _validationToastTimeout = null;
function showNotification(message, type = "error") {
    let toast = document.getElementById("validation-toast");
    if (!toast) {
        toast = document.createElement("div");
        toast.id = "validation-toast";
        document.body.appendChild(toast);
    }
    toast.style.cssText = "position: fixed; top: 20px; left: 50%; transform: translateX(-50%); z-index: 100000; color: white; padding: 12px 28px; border-radius: 10px; font-size: 15px; font-weight: 600; box-shadow: 0 8px 32px rgba(0,0,0,0.25); opacity: 0; transition: opacity 0.35s; pointer-events: none; letter-spacing: 0.5px;";
    
    if (type === "success") {
        toast.style.background = "linear-gradient(135deg, #10b981, #059669)";
    } else if (type === "info") {
        toast.style.background = "linear-gradient(135deg, #3b82f6, #2563eb)";
    } else {
        toast.style.background = "linear-gradient(135deg, #ef4444, #dc2626)";
    }
    
    toast.textContent = message;
    toast.style.opacity = "1";
    if (_validationToastTimeout) clearTimeout(_validationToastTimeout);
    _validationToastTimeout = setTimeout(() => { toast.style.opacity = "0"; }, type === "info" ? 7000 : 2500);
}

function showValidationToast(message) {
    showNotification(message, "error");
}

function validateTreatmentInputs(silent = false) {
    if (globalThis.NendatYeuCore) {
        const coreValidation = globalThis.NendatYeuCore.validateProject(appState);
        if (!coreValidation.valid) {
            if (!silent && coreValidation.issues[0]) showValidationToast(coreValidation.issues[0].message);
            return false;
        }
    }
    const treatment = appState?.treatment?.method;
    const errors = [];
    const finite = (value, label, predicate = () => true) => {
        if (!Number.isFinite(value) || !predicate(value)) errors.push(label);
    };

    if (!appState || !appState.embankment || !appState.foundation || !appState.treatment) {
        errors.push("Cấu trúc dự án không đầy đủ");
    } else {
        finite(appState.embankment.height, "Chiều cao đắp phải > 0", v => v > 0);
        finite(appState.embankment.gamma, "Dung trọng đất đắp phải > 0", v => v > 0);
        finite(appState.embankment.width, "Chiều rộng đỉnh phải > 0", v => v > 0);
        finite(appState.embankment.slope, "Hệ số dốc taluy phải > 0", v => v > 0);
        finite(appState.foundation.gwt, "Mực nước ngầm phải >= 0", v => v >= 0);
        finite(appState.foundation.surcharge, "Hoạt tải phải >= 0", v => v >= 0);
        finite(appState.foundation.life, "Tuổi thọ phải > 0", v => v > 0);
        finite(appState.foundation.tConstruction, "Thời gian chờ cố kết phải > 0", v => v > 0);
        finite(appState.foundation.allowable, "Lún dư cho phép phải >= 0", v => v >= 0);
        finite(appState.foundation.mFactor, "Hệ số m phải trong [1,1; 1,7]", v => v >= 1.1 && v <= 1.7);
        if (!["single", "double"].includes(appState.foundation.drainage || "single")) {
            errors.push("Điều kiện thoát nước không hợp lệ");
        }
        if (!["undrained", "drained"].includes(appState.foundation.shearMode || "undrained")) {
            errors.push("Chế độ phân tích sức kháng cắt không hợp lệ");
        }

        if (appState.embankment.staged) {
            finite(appState.embankment.stagedH1, "Chiều cao đợt 1 phải > 0", v => v > 0);
            finite(appState.embankment.stagedH2, "Chiều cao đợt 2 phải > 0", v => v > 0);
            finite(appState.embankment.stagedT1, "Thời gian chờ đợt 1 phải > 0", v => v > 0);
            if (Math.abs(appState.embankment.stagedH1 + appState.embankment.stagedH2 - appState.embankment.height) > 0.01) {
                errors.push("H1 + H2 phải bằng tổng chiều cao đắp");
            }
        }
    }

    if (!Array.isArray(appState?.soilLayers) || appState.soilLayers.length < 1 || appState.soilLayers.length > 100) {
        errors.push("Số lớp đất phải từ 1 đến 100");
    } else {
        const ids = new Set();
        appState.soilLayers.forEach((layer, index) => {
            const label = "Lớp " + (index + 1);
            if (!Number.isInteger(layer.id) || ids.has(layer.id)) errors.push(label + ": id không hợp lệ/trùng");
            ids.add(layer.id);
            if (!["cohesive", "granular"].includes(layer.type)) errors.push(label + ": loại đất không hợp lệ");
            finite(layer.thickness, label + ": chiều dày phải > 0", v => v > 0);
            finite(layer.gamma, label + ": gamma phải > 0", v => v > 0);
            finite(layer.gammaSat, label + ": gammaSat phải >= gamma", v => v >= layer.gamma);
            finite(layer.E, label + ": E phải > 0", v => v > 0);
            finite(layer.c, label + ": c phải >= 0", v => v >= 0);
            finite(layer.phi, label + ": phi phải trong [0;45]", v => v >= 0 && v <= 45);
            if (layer.type === "cohesive") {
                finite(layer.e0, label + ": e0 phải > 0", v => v > 0);
                finite(layer.Cc, label + ": Cc phải > 0", v => v > 0);
                finite(layer.Cr, label + ": Cr phải trong [0;Cc]", v => v >= 0 && v <= layer.Cc);
                finite(layer.sigmaP, label + ": sigmaP phải > 0", v => v > 0);
                finite(layer.Cv, label + ": Cv phải > 0", v => v > 0);
                finite(layer.Ca, label + ": Ca phải >= 0", v => v >= 0);
            }
        });
    }

    if (treatment === "pvd" || treatment === "vacuum_pvd") {
        const pvd = appState.pvd || {};
        finite(pvd.spacing, "Khoảng cách PVD phải > 0", v => v > 0);
        finite(pvd.depth, "Chiều sâu PVD phải > 0", v => v > 0);
        finite(pvd.width, "Bản rộng PVD phải > 0", v => v > 0);
        finite(pvd.thickness, "Chiều dày PVD phải > 0", v => v > 0);
        finite(pvd.chRatio, "Ch/Cv phải > 0", v => v > 0);
        if (pvd.standard === "tcvn_9355") {
            finite(pvd.spacing, "TCVN 9355: khoảng cách PVD phải từ 1,30 đến 2,20 m", v => v >= 1.3 && v <= 2.2);
            finite(pvd.dsDw, "TCVN 9355: ds/dw phải từ 2 đến 3", v => v >= 2 && v <= 3);
            finite(pvd.khKs, "kh/ks phải >= 1", v => v >= 1);
            finite(pvd.qw, "TCVN 9355: Qw khai báo phải >= 60 cm3/s", v => v >= 60);
        }
        if (treatment === "vacuum_pvd") {
            finite(pvd.vacuumPressure, "Áp lực chân không phải trong [0;100] kPa", v => v >= 0 && v <= 100);
            finite(pvd.vacuumEfficiency, "Hiệu suất chân không phải trong [0;1]", v => v >= 0 && v <= 1);
        }
    }

    if (treatment === "cdm") {
        const cdm = appState.cdm || {};
        finite(cdm.spacing, "Khoảng cách CDM phải > 0", v => v > 0);
        finite(cdm.diameter, "Đường kính CDM phải > 0", v => v > 0);
        finite(cdm.depth, "TCVN 9403: chiều sâu CDM phải >= 3 m", v => v >= 3);
        finite(cdm.qu, "Cường độ nén CDM phải > 0", v => v > 0);
        finite(cdm.emodulusRatio, "Ep/qu phải > 0", v => v > 0);
        finite(cdm.rf, "RF phải > 0", v => v > 0);
        finite(cdm.tUlt, "Cường độ kéo vải phải > 0", v => v > 0);
    }

    const simpleGroups = [
        ["sand_drain", appState.sandDrain, ["spacing", "diameter", "depth", "chRatio"]],
        ["sand_column", appState.sandColumn, ["spacing", "diameter", "depth", "modulus", "chRatio"]],
        ["bamboo_pile", appState.bambooPile, ["spacing", "diameter", "depth", "modulus"]],
        ["bamboo_replacement", appState.bambooPile, ["spacing", "diameter", "depth", "modulus"]]
    ];
    simpleGroups.forEach(([method, group, fields]) => {
        if (treatment === method) fields.forEach(field =>
            finite(group?.[field], method + ": " + field + " phải > 0", v => v > 0)
        );
    });

    if (treatment === "excavation_replacement" || treatment === "bamboo_replacement") {
        const rep = appState.excavationReplacement || {};
        finite(rep.depth, "Chiều sâu đào phải >= 0", v => v >= 0);
        finite(rep.gamma, "Dung trọng cát thay phải > 0", v => v > 0);
        finite(rep.gammaSat, "Dung trọng bão hòa phải >= gamma", v => v >= rep.gamma);
        finite(rep.E, "Mô đun cát thay phải > 0", v => v > 0);
    }

    if (errors.length > 0) {
        const message = errors.slice(0, 4).join("; ") + (errors.length > 4 ? "…" : "");
        if (!silent) {
            showValidationToast(message);
            markAnalysisInvalid(message);
        }
        return false;
    }
    return true;
}

function exportJSON() {
    const blob = new Blob([JSON.stringify(appState, null, 2)], { type: 'application/json;charset=utf-8;' });
    saveFileWithPicker(blob, "xu_ly_dat_yeu_project.json", [
        { description: 'JSON Files', accept: { 'application/json': ['.json'] } }
    ]);
    window.dispatchEvent(new CustomEvent("nendatyeu:saved"));
}

// JSON Import
function normalizeImportedProject(parsed) {
    if (!parsed || typeof parsed !== "object") throw new Error("Nội dung JSON không phải object.");
    const required = ["embankment", "foundation", "treatment", "pvd", "cdm", "sandDrain",
        "sandColumn", "bambooPile", "excavationReplacement", "soilLayers"];
    required.forEach(key => {
        if (parsed[key] === undefined) throw new Error("Thiếu nhóm dữ liệu: " + key);
    });
    if (!Array.isArray(parsed.soilLayers)) throw new Error("soilLayers phải là mảng.");

    const candidate = structuredClone(parsed);
    candidate.schemaVersion = 2;
    delete candidate.lastAnalysis;
    delete candidate.lastVerification;
    candidate.foundation.drainage = candidate.foundation.drainage || "single";
    candidate.foundation.shearMode = candidate.foundation.shearMode || "undrained";
    candidate.embankment.staged = Boolean(candidate.embankment.staged);
    candidate.embankment.stagedH1 = Number(candidate.embankment.stagedH1 ?? candidate.embankment.height);
    candidate.embankment.stagedH2 = Number(candidate.embankment.stagedH2 ?? 0);
    candidate.embankment.stagedT1 = Number(candidate.embankment.stagedT1 ?? 6);
    candidate.pvd.standard = candidate.pvd.standard || "tccs_41";
    candidate.pvd.vacuumPressure = Number(candidate.pvd.vacuumPressure ?? 80);
    candidate.pvd.vacuumEfficiency = Number(candidate.pvd.vacuumEfficiency ?? 0.85);
    candidate.cdm.overWidth = Number(candidate.cdm.overWidth ?? 1);

    const numericFields = ["thickness", "gamma", "gammaSat", "e0", "Cc", "Cr", "sigmaP",
        "Cv", "Ca", "E", "c", "phi"];
    candidate.soilLayers = candidate.soilLayers.map((layer, index) => {
        const normalized = { ...layer };
        normalized.id = index + 1;
        normalized.name = String(layer.name || ("Lớp đất " + (index + 1))).slice(0, 120);
        normalized.type = layer.type === "granular" ? "granular" : "cohesive";
        normalized.color = sanitizeColor(layer.color);
        numericFields.forEach(field => normalized[field] = Number(layer[field]));
        return normalized;
    });
    return candidate;
}

function importJSON(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
        showNotification("File JSON vượt giới hạn 2 MB.", "error");
        e.target.value = "";
        return;
    }

    const reader = new FileReader();
    reader.onload = function(evt) {
        const previousState = appState;
        try {
            const candidate = normalizeImportedProject(JSON.parse(evt.target.result));
            appState = candidate;
            if (!validateTreatmentInputs(true)) {
                throw new Error("Dữ liệu chứa giá trị thiếu, không hữu hạn hoặc ngoài miền cho phép.");
            }
            updateInputsFromState();
            renderLayersTable();
            runAnalysis();
            showNotification("Đã nhập dự án schema v2 thành công.", "success");
        } catch (err) {
            appState = previousState;
            updateInputsFromState();
            renderLayersTable();
            runAnalysis();
            showNotification("Không thể nhập JSON: " + err.message, "error");
        } finally {
            e.target.value = "";
        }
    };
    reader.readAsText(file);
}

// ==========================================
// GEOTECHNICAL DATA IMPORT/EXPORT (XLSX/XLS/CSV)
// ==========================================

/**
 * Save file with location picker (File System Access API).
 * Falls back to traditional download if API is unavailable.
 * @param {Blob} blob - File data
 * @param {string} defaultName - Default filename
 * @param {Array} fileTypes - File type filters for save dialog
 */
async function saveFileWithPicker(blob, defaultName, fileTypes) {
    // Try File System Access API (Chrome/Edge 86+)
    if (window.showSaveFilePicker) {
        try {
            const handle = await window.showSaveFilePicker({
                suggestedName: defaultName,
                types: fileTypes || []
            });
            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();
            return; // Success
        } catch (err) {
            if (err.name === 'AbortError') return; // User cancelled
            // Fall through to legacy download
        }
    }
    // Fallback: traditional download
    showNotification("Lưu ý: File được tải về thư mục mặc định của trình duyệt. Bạn có thể bật tùy chọn 'Hỏi vị trí lưu trước khi tải' trong cài đặt của trình duyệt để tự chọn vị mục lưu.", "info");
    
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", defaultName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

// Export current soil layers as CSV
function exportGeotechnicalCSV() {
    let csvContent = "\ufeff"; // BOM for Vietnamese characters in Excel
    csvContent += "Tên lớp đất,Loại đất,Bề dày H (m),Dung trọng tự nhiên gamma (kN/m3),Dung trọng bão hòa gamma_sat (kN/m3),Hệ số rỗng e0,Chỉ số nén Cc,Chỉ số nén lại Cr,Áp lực tiền cố kết sigma_p (kPa),Mô đun đàn hồi Es (kPa),Hệ số cố kết Cv (m2/năm),Chỉ số từ biến Ca,Lực dính c (kPa),Góc ma sát phi (độ)\n";
    
    appState.soilLayers.forEach(layer => {
        csvContent += `"${layer.name}","${layer.type || 'cohesive'}",${layer.thickness},${layer.gamma},${layer.gammaSat},${layer.e0},${layer.Cc},${layer.Cr},${layer.sigmaP},${layer.E || 3000},${layer.Cv},${layer.Ca},${layer.c !== undefined ? layer.c : 10},${layer.phi !== undefined ? layer.phi : 5}\n`;
    });
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveFileWithPicker(blob, "dia_tang_chi_tieu_co_ly.csv", [
        { description: 'CSV Files', accept: { 'text/csv': ['.csv'] } }
    ]);
}

// ==========================================
// SMART GEOTECHNICAL DATA IMPORT (.xlsx, .xls, .csv)
// ==========================================

/**
 * Parse angle strings like "15°51'" or "15°51" or "32°31'" into decimal degrees.
 * Also handles plain numbers like "15.5" or "15,5".
 */
function parseDegreeMinute(str) {
    if (str === null || str === undefined) return NaN;
    str = String(str).trim();
    if (!str) return NaN;
    
    // Try degree-minute format: 15°51' or 15°51 or 15o51' etc.
    let match = str.match(/(-?\d+)[°oº][\s]*(\d+)['\u2019\u2032]?/);
    if (match) {
        let deg = parseInt(match[1]);
        let min = parseInt(match[2]);
        return deg + min / 60.0;
    }
    
    // Try plain number (replace comma with dot for European format)
    let val = parseFloat(str.replace(',', '.'));
    return val;
}

/**
 * Classify soil type based on TCVN 5746:2024.
 * Returns 'granular' or 'cohesive'.
 */
function classifySoilTCVN(raw) {
    // If IP (plasticity index) is available
    if (raw.IP !== null && raw.IP !== undefined && !isNaN(raw.IP)) {
        // TCVN 5746:2024: IP < 7 → granular/sandy; IP >= 7 → cohesive
        if (raw.IP < 7) return 'granular';
        return 'cohesive';
    }
    // If Cc is available
    if (raw.Cc !== null && raw.Cc !== undefined && !isNaN(raw.Cc)) {
        return raw.Cc <= 0.05 ? 'granular' : 'cohesive';
    }
    // If name contains keywords
    let name = (raw.name || '').toLowerCase();
    if (name.match(/cát|cat|sand|sỏi|cuội|gravel/)) return 'granular';
    if (name.match(/sét|set|clay|bùn|bun|silt|dẻo|deo/)) return 'cohesive';
    // If emax/emin exist (sand property)
    if ((raw.emax && !isNaN(raw.emax)) || (raw.emin && !isNaN(raw.emin))) return 'granular';
    return 'cohesive'; // default
}

/**
 * Estimate missing geotechnical parameters using TCVN 5746:2024 and empirical formulas.
 * Input: raw layer with whatever data was parsed from Excel.
 * Output: complete layer object for appState.soilLayers.
 */
function estimateMissingParams(raw, layerIndex) {
    let type = classifySoilTCVN(raw);
    let name = raw.name || ('Lớp đất ' + (layerIndex + 1));
    
    // --- Thickness ---
    let thickness = (raw.thickness > 0) ? raw.thickness : 5.0;
    
    // --- Dung trọng (Unit Weight) ---
    // raw.gw is in g/cm3, convert to kN/m3 by × 9.81 ≈ ×10
    let gamma = NaN;
    if (raw.gw > 0) {
        gamma = raw.gw * 9.81; // g/cm3 → kN/m3
    } else if (raw.gamma_kN > 0) {
        gamma = raw.gamma_kN; // already in kN/m3
    }
    if (isNaN(gamma) || gamma <= 0) {
        gamma = type === 'granular' ? 18.0 : 16.0; // default
    }
    
    // --- Dung trọng khô (Dry Density) ---
    let gc = raw.gc > 0 ? raw.gc : NaN; // g/cm3
    
    // --- Tỷ trọng hạt (Specific Gravity) ---
    let Gs = raw.Gs > 0 ? raw.Gs : 2.70; // default
    
    // --- Hệ số rỗng (Void Ratio) ---
    let e0 = NaN;
    if (raw.e0 > 0) {
        e0 = raw.e0;
    } else if (gc > 0 && Gs > 0) {
        // e0 = Gs * γw / γd - 1 (γw=1 g/cm3 for g/cm3 units)
        e0 = (Gs / gc) - 1;
    } else if (gamma > 0 && Gs > 0) {
        // Estimate from γ: γ = Gs × γw × (1 + W/100) / (1 + e0)
        // Simplified: e0 ≈ Gs × 10 / γ - 1
        e0 = (Gs * 9.81) / gamma * (1 + (raw.W || 30) / 100) - 1;
    }
    if (isNaN(e0) || e0 <= 0) {
        e0 = type === 'granular' ? 0.65 : 1.2;
    }
    
    // --- Dung trọng bão hòa (Saturated Unit Weight) ---
    let gammaSat = NaN;
    if (raw.gammaSat_kN > 0) {
        gammaSat = raw.gammaSat_kN;
    } else if (Gs > 0 && e0 > 0) {
        // γsat = (Gs + e0) / (1 + e0) × γw (γw = 9.81 kN/m3)
        gammaSat = ((Gs + e0) / (1 + e0)) * 9.81;
    }
    if (isNaN(gammaSat) || gammaSat <= 0) {
        gammaSat = gamma + 1.5; // fallback
    }
    
    // --- Cc (Compression Index) ---
    let Cc = NaN;
    if (raw.Cc > 0) {
        Cc = raw.Cc;
    } else if (raw.WL > 0) {
        // Terzaghi-Peck: Cc = 0.009 × (WL - 10)
        Cc = 0.009 * (raw.WL - 10);
    } else if (raw.a1_2 > 0 && e0 > 0) {
        // From a1-2: Cc ≈ a1_2 × (1 + e0) / 2.303 × log10(2/1)
        // Simplified: Cc ≈ a1_2 × (1 + e0) × 0.4343
        Cc = raw.a1_2 * (1 + e0) * 0.4343;
    }
    if (isNaN(Cc) || Cc <= 0) {
        Cc = type === 'granular' ? 0.02 : 0.35;
    }
    
    // --- Cr (Recompression Index) ---
    let Cr = NaN;
    if (raw.Cr > 0) {
        Cr = raw.Cr;
    } else {
        Cr = Cc / 6.0; // Empirical: Cr ≈ Cc/5 to Cc/10
    }
    if (isNaN(Cr) || Cr <= 0) {
        Cr = Cc / 6.0;
    }
    
    // --- E (Elastic Modulus, kPa) ---
    let E = NaN;
    if (raw.E_kPa > 0) {
        E = raw.E_kPa;
    } else if (raw.mv > 0) {
        // E = 1/mv (kG/cm2) × 100 → kPa
        E = (1 / raw.mv) * 100;
    } else if (raw.a1_2 > 0 && e0 > 0) {
        // E = (1 + e0) / a1_2 (kG/cm2) × 100 → kPa
        E = ((1 + e0) / raw.a1_2) * 100;
    } else if (raw.av > 0 && e0 > 0) {
        // mv = av / (1 + e0), then E = 1/mv × 100
        let mv = raw.av / (1 + e0);
        E = (1 / mv) * 100;
    }
    if (isNaN(E) || E <= 0) {
        E = type === 'granular' ? 20000 : 3000;
    }
    
    // --- Cv (Coefficient of Consolidation) ---
    // raw.Cv is in 10^-3 cm2/s, convert to m2/year
    // 1 cm2/s = 3153600 cm2/year = 315.36 m2/year
    // So 10^-3 cm2/s = 0.31536 m2/year
    let Cv = NaN;
    if (raw.Cv_raw > 0) {
        Cv = raw.Cv_raw * 0.31536; // 10^-3 cm2/s → m2/year
    } else if (raw.Cv_m2year > 0) {
        Cv = raw.Cv_m2year;
    }
    if (isNaN(Cv) || Cv <= 0) {
        Cv = type === 'granular' ? 50.0 : 2.0;
    }
    
    // --- Ca (Secondary Compression Index) ---
    let Ca = NaN;
    if (raw.Ca > 0) {
        Ca = raw.Ca;
    } else {
        // Mesri & Godlewski: Ca ≈ 0.04 × Cc
        Ca = 0.04 * Cc;
    }
    if (isNaN(Ca) || Ca <= 0) {
        Ca = type === 'granular' ? 0.001 : 0.015;
    }
    
    // --- c (Cohesion, kPa) ---
    // raw.C is in kG/cm2, convert to kPa: × 98.0665
    let c = NaN;
    if (raw.C_kgcm2 > 0 || raw.C_kgcm2 === 0) {
        c = raw.C_kgcm2 * 98.0665; // kG/cm2 → kPa
    } else if (raw.c_kPa >= 0) {
        c = raw.c_kPa;
    }
    if (isNaN(c)) {
        c = type === 'granular' ? 2.0 : 12.0;
    }
    
    // --- φ (Angle of internal friction, degrees) ---
    let phi = NaN;
    if (raw.phi_deg !== null && raw.phi_deg !== undefined && !isNaN(raw.phi_deg) && raw.phi_deg >= 0) {
        phi = raw.phi_deg;
    }
    if (isNaN(phi) || phi < 0) {
        phi = type === 'granular' ? 30.0 : 5.0;
    }
    
    // --- σ'p (Preconsolidation Pressure, kPa) ---
    let sigmaP = NaN;
    if (raw.sigmaP_kPa > 0) {
        sigmaP = raw.sigmaP_kPa;
    } else {
        // Estimate from LI (Liquidity Index)
        // Approximate σ'v0 from γ × depth (use cumulative depth estimate)
        let estimatedDepth = thickness / 2 + layerIndex * thickness;
        let sigma_v0 = gamma * estimatedDepth;
        if (sigma_v0 < 10) sigma_v0 = 20; // minimum
        
        let OCR = 1.0;
        if (raw.LI !== null && raw.LI !== undefined && !isNaN(raw.LI)) {
            if (raw.LI < 0) OCR = 4.0;       // Very stiff, heavily OC
            else if (raw.LI < 0.25) OCR = 3.0;
            else if (raw.LI < 0.5) OCR = 2.0; // Stiff, lightly OC
            else OCR = 1.0;                    // NC
        } else {
            OCR = 1.2; // slight OC default
        }
        sigmaP = OCR * sigma_v0;
    }
    if (isNaN(sigmaP) || sigmaP <= 0) {
        sigmaP = 50.0;
    }
    
    return {
        name: name,
        type: type,
        thickness: thickness,
        gamma: Math.round(gamma * 100) / 100,
        gammaSat: Math.round(gammaSat * 100) / 100,
        e0: Math.round(e0 * 1000) / 1000,
        Cc: Math.round(Cc * 1000) / 1000,
        Cr: Math.round(Cr * 1000) / 1000,
        sigmaP: Math.round(sigmaP * 10) / 10,
        E: Math.round(E),
        Cv: Math.round(Cv * 100) / 100,
        Ca: Math.round(Ca * 1000) / 1000,
        c: Math.round(c * 10) / 10,
        phi: Math.round(phi * 10) / 10
    };
}

/**
 * Known header keywords mapping for geotechnical Excel files.
 * Each key is a normalized keyword; value is the field it maps to.
 */
const HEADER_KEYWORDS = {
    // Thickness / Depth
    'h': 'thickness', 'thickness': 'thickness', 'chieu day': 'thickness', 'be day': 'thickness',
    'chieusau': 'thickness', 'day': 'thickness',
    // Natural moisture content
    'w': 'W', 
    // Atterberg limits
    'wl': 'WL', 'wll': 'WL', 'liquid limit': 'WL', 'gioi han chay': 'WL',
    'wp': 'WP', 'plastic limit': 'WP', 'gioi han deo': 'WP',
    'ip': 'IP', 'pi': 'IP', 'plastic index': 'IP', 'chi so deo': 'IP',
    'li': 'LI', 'liquidity index': 'LI', 'do set': 'LI',
    // Density
    'gw': 'gw', 'gamma w': 'gw', 'wet density': 'gw', 'dung trong': 'gw', 'tu nhien': 'gw',
    'gc': 'gc', 'dry density': 'gc', 'dung trong kho': 'gc', 'kho': 'gc',
    // Specific gravity
    'd': 'Gs', 'gs': 'Gs', 'specific gravity': 'Gs', 'khoi luong rieng': 'Gs', 'ty trong': 'Gs',
    // Void ratio
    'eo': 'e0', 'e0': 'e0', 'void ratio': 'e0', 'he so rong': 'e0',
    'emax': 'emax', 'e max': 'emax',
    'emin': 'emin', 'e min': 'emin',
    // Porosity / Saturation
    'n': 'n', 'porosity': 'n', 'do lo rong': 'n',
    's': 'S_degree', 'sr': 'S_degree', 'degree of saturation': 'S_degree', 'do bao hoa': 'S_degree',
    // Shear strength
    'ak': 'phi_dry', 'an': 'phi_wet',
    'phi': 'phi', 'j': 'phi', 'angle of internal friction': 'phi', 'goc ma sat': 'phi', 'goc ma sat trong': 'phi',
    'c': 'C', 'cohesion': 'C', 'luc dinh': 'C', 'luc dinh ket': 'C',
    // Compressibility
    'a1-2': 'a1_2', 'a12': 'a1_2', 'a1_2': 'a1_2', 'coef. of compression': 'a1_2', 'he so nen lun': 'a1_2',
    'av': 'av', 'he so nen': 'av',
    'mv': 'mv', 'coef. of volume': 'mv', 'he so bien doi': 'mv',
    // Consolidation
    'cv': 'Cv', 'coef. of consolidation': 'Cv', 'he so co ket': 'Cv',
    // Permeability
    'k': 'K_perm', 'he so tham': 'K_perm', 'coef. of permeability': 'K_perm',
    // Compression/Recompression Index
    'cc': 'Cc', 'compression index': 'Cc', 'chi so nen': 'Cc',
    'cr': 'Cr', 're-compression index': 'Cr', 'chi so nen lai': 'Cr',
};

/**
 * Normalize a header string for matching:
 * - lowercase, remove diacritical marks, remove special chars
 */
function normalizeHeader(str) {
    if (!str) return '';
    str = String(str).toLowerCase().trim();
    // Remove Vietnamese diacritical marks
    str = str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    // Replace đ/Đ
    str = str.replace(/đ/g, 'd').replace(/Đ/g, 'D');
    // Remove non-alphanumeric except spaces and hyphens
    str = str.replace(/[^a-z0-9\s\-_]/g, '').trim();
    return str;
}

/**
 * Scan Excel data to find the header row and map columns to geotechnical fields.
 * Returns { headerRowIndex, columnMap: { fieldName: colIndex, ... } }
 */
function detectHeadersAndColumns(sheetData) {
    let bestRow = -1;
    let bestMap = {};
    let bestScore = 0;
    
    // Scan first 15 rows for header candidates
    let maxScan = Math.min(sheetData.length, 15);
    for (let r = 0; r < maxScan; r++) {
        let row = sheetData[r];
        if (!row) continue;
        
        let colMap = {};
        let score = 0;
        let usedFields = new Set();
        
        for (let c = 0; c < row.length; c++) {
            let cellVal = normalizeHeader(row[c]);
            if (!cellVal) continue;
            
            // Try exact match first
            if (HEADER_KEYWORDS[cellVal] && !usedFields.has(HEADER_KEYWORDS[cellVal])) {
                colMap[HEADER_KEYWORDS[cellVal]] = c;
                usedFields.add(HEADER_KEYWORDS[cellVal]);
                score++;
                continue;
            }
            
            // Try partial match (keyword contained in cell)
            for (let [keyword, field] of Object.entries(HEADER_KEYWORDS)) {
                if (!usedFields.has(field) && keyword.length > 1 && cellVal.includes(keyword)) {
                    colMap[field] = c;
                    usedFields.add(field);
                    score++;
                    break;
                }
            }
        }
        
        if (score > bestScore) {
            bestScore = score;
            bestRow = r;
            bestMap = colMap;
        }
    }
    
    return { headerRowIndex: bestRow, columnMap: bestMap, score: bestScore };
}

/**
 * Find data rows containing soil layer information.
 * Typically rows after the header that have a text in the first column starting with "Lớp" or containing soil description.
 */
function extractDataRows(sheetData, headerRowIndex, columnMap) {
    let rawLayers = [];
    
    // Find the name column (usually column A / index 0)
    let nameColIdx = 0; // default to first column
    
    // Scan rows after header
    for (let r = headerRowIndex + 1; r < sheetData.length; r++) {
        let row = sheetData[r];
        if (!row) continue;
        
        // Check if first column has content (layer name)
        let firstCell = String(row[nameColIdx] || '').trim();
        if (!firstCell) continue;
        
        // Skip unit rows (rows that have %, g/cm3, kG/cm2 etc)
        let firstLower = firstCell.toLowerCase();
        if (firstLower.match(/^[%\s]*$/) || firstLower.match(/^g\/cm|^kg|^cm2|^10/)) continue;
        
        // Skip if it looks like a continued header or unit row
        let hasNumericData = false;
        for (let c = 1; c < row.length && c < 30; c++) {
            let val = row[c];
            if (val !== null && val !== undefined && val !== '' && !isNaN(parseFloat(String(val).replace(',', '.')))) {
                hasNumericData = true;
                break;
            }
        }
        if (!hasNumericData) continue;
        
        // Extract raw values from mapped columns
        let raw = { name: firstCell };
        
        for (let [field, colIdx] of Object.entries(columnMap)) {
            let cellVal = row[colIdx];
            if (cellVal === null || cellVal === undefined || cellVal === '') {
                raw[field] = null;
                continue;
            }
            
            let strVal = String(cellVal).trim();
            
            // Parse angle fields (may contain ° symbols)
            if (field === 'phi' || field === 'phi_dry' || field === 'phi_wet') {
                raw[field] = parseDegreeMinute(strVal);
            } else if (field === 'thickness') {
                raw[field] = parseFloat(strVal.replace(',', '.')) || null;
            } else {
                // Parse as number, handling comma decimal separator
                raw[field] = parseFloat(strVal.replace(',', '.'));
                if (isNaN(raw[field])) raw[field] = null;
            }
        }
        
        rawLayers.push(raw);
    }
    
    return rawLayers;
}

/**
 * Convert raw extracted layer data to appState format with unit conversions
 * and estimation of missing parameters.
 */
function convertRawToAppLayer(raw, layerIndex) {
    // Map raw fields to the format expected by estimateMissingParams
    let mapped = {
        name: raw.name || ('Lớp đất ' + (layerIndex + 1)),
        thickness: raw.thickness || null,
        W: raw.W || null,
        WL: raw.WL || null,
        WP: raw.WP || null,
        IP: raw.IP || null,
        LI: raw.LI || null,
        gw: raw.gw || null,           // g/cm3
        gc: raw.gc || null,           // g/cm3
        Gs: raw.Gs || null,
        e0: raw.e0 || null,
        emax: raw.emax || null,
        emin: raw.emin || null,
        Cc: raw.Cc || null,
        Cr: raw.Cr || null,
        a1_2: raw.a1_2 || null,       // cm2/kG
        av: raw.av || null,           // cm2/kG
        mv: raw.mv || null,           // cm2/kG
        Cv_raw: raw.Cv || null,       // 10^-3 cm2/s
        Ca: null,                     // Not typically in Excel, will be estimated
        C_kgcm2: raw.C || null,       // kG/cm2
        K_perm: raw.K_perm || null,
        phi_deg: null,
        sigmaP_kPa: null
    };
    
    // Determine φ from various possible sources
    if (raw.phi !== null && raw.phi !== undefined && !isNaN(raw.phi)) {
        mapped.phi_deg = raw.phi;
    } else if (raw.phi_dry !== null && raw.phi_dry !== undefined && !isNaN(raw.phi_dry)) {
        mapped.phi_deg = raw.phi_dry;
    } else if (raw.phi_wet !== null && raw.phi_wet !== undefined && !isNaN(raw.phi_wet)) {
        mapped.phi_deg = raw.phi_wet;
    }
    
    return estimateMissingParams(mapped, layerIndex);
}

/**
 * Main import handler — reads .xlsx, .xls, or .csv files.
 */
function importGeotechnicalData(e) {
    const file = e.target.files[0];
    if (!file) return;
    
    const fileName = file.name.toLowerCase();
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
    
    if (isExcel) {
        // Use SheetJS to read Excel
        if (typeof XLSX === 'undefined') {
            alert("Thư viện đọc Excel (SheetJS) chưa được tải. Vui lòng kiểm tra kết nối internet và thử lại.");
            e.target.value = "";
            return;
        }
        
        const reader = new FileReader();
        reader.onload = function(evt) {
            try {
                const data = new Uint8Array(evt.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                
                // Use the first sheet
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                
                // Convert to 2D array
                const sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
                
                processGeotechnicalData(sheetData, file.name);
            } catch (err) {
                alert("Lỗi khi đọc file Excel: " + err.message);
            }
        };
        reader.readAsArrayBuffer(file);
    } else {
        // CSV fallback
        const reader = new FileReader();
        reader.onload = function(evt) {
            try {
                const text = evt.target.result;
                const lines = text.split(/\r?\n/);
                let sheetData = lines.map(line => {
                    // Parse CSV with quote handling
                    let parts = [];
                    let inQuotes = false;
                    let current = '';
                    for (let i = 0; i < line.length; i++) {
                        let ch = line[i];
                        if (ch === '"') {
                            inQuotes = !inQuotes;
                        } else if ((ch === ',' || ch === ';' || ch === '\t') && !inQuotes) {
                            parts.push(current.trim());
                            current = '';
                        } else {
                            current += ch;
                        }
                    }
                    parts.push(current.trim());
                    return parts;
                });
                
                processGeotechnicalData(sheetData, file.name);
            } catch (err) {
                alert("Lỗi khi đọc file CSV: " + err.message);
            }
        };
        reader.readAsText(file);
    }
    
    e.target.value = ""; // reset to allow uploading the same file
}

/**
 * Process parsed sheet data (2D array) into soil layers.
 */
function processGeotechnicalData(sheetData, fileName) {
    if (!sheetData || sheetData.length < 2) {
        alert("File trống hoặc không chứa dữ liệu hợp lệ!");
        return;
    }
    
    // Step 1: Detect headers and column mapping
    let { headerRowIndex, columnMap, score } = detectHeadersAndColumns(sheetData);
    
    if (score < 3) {
        alert("Không thể nhận diện được cấu trúc bảng chỉ tiêu cơ lý!\n\nFile cần có các cột ký hiệu như: W, WL, WP, IP, e0, Cc, Cv, φ, C...\n\nVui lòng kiểm tra lại file Excel.");
        return;
    }
    
    // Step 2: Extract data rows
    let rawLayers = extractDataRows(sheetData, headerRowIndex, columnMap);
    
    if (rawLayers.length === 0) {
        alert("Không tìm thấy dữ liệu lớp đất nào trong file!\n\nDữ liệu lớp đất cần nằm ở các hàng phía dưới hàng header có ký hiệu (W, WL, e0, Cc...).");
        return;
    }
    
    // Step 3: Convert and estimate missing parameters
    let newLayers = [];
    let estimatedFields = [];
    
    for (let i = 0; i < rawLayers.length; i++) {
        let converted = convertRawToAppLayer(rawLayers[i], i);
        converted.id = i + 1;
        converted.color = SOIL_COLORS[(i + 1) % SOIL_COLORS.length];
        newLayers.push(converted);
        
        // Track which fields were estimated
        let estimated = [];
        let raw = rawLayers[i];
        if (!raw.thickness && !columnMap.thickness) estimated.push("Bề dày");
        if (!raw.Cc && !raw.a1_2) estimated.push("Cc");
        if (!raw.Cr) estimated.push("Cr");
        if (!raw.Cv) estimated.push("Cv");
        if (raw.C === null && raw.C === undefined) {
            // C was not found
        }
        if (!columnMap.Cc && !raw.Cc) estimated.push("Cc");
        if (estimated.length > 0) {
            estimatedFields.push(`${converted.name}: ${estimated.join(', ')}`);
        }
    }
    
    // Step 4: Apply to appState
    appState.soilLayers = newLayers;
    renderLayersTable();
    runAnalysis();
    
    // Step 5: Show import summary
    let summary = `✅ Đã nhập thành công ${newLayers.length} lớp đất từ file "${fileName}"!\n\n`;
    summary += `📊 Nhận diện được ${score} cột chỉ tiêu cơ lý.\n`;
    
    // List detected columns
    let detectedNames = Object.keys(columnMap);
    summary += `📋 Các cột đã nhận diện: ${detectedNames.join(', ')}\n\n`;
    
    // Show unit conversions applied
    summary += `🔄 Đã tự động chuyển đổi đơn vị:\n`;
    summary += `   • γ: g/cm³ → kN/m³ (×9.81)\n`;
    summary += `   • C: kG/cm² → kPa (×98.07)\n`;
    summary += `   • Cv: 10⁻³ cm²/s → m²/năm (×0.3154)\n`;
    summary += `   • φ: Độ°Phút' → Số thập phân\n\n`;
    
    if (estimatedFields.length > 0) {
        summary += `⚠️ Một số chỉ tiêu đã được ước tính bằng công thức thực nghiệm:\n`;
        summary += `   (TCVN 5746:2024, Terzaghi-Peck, Mesri & Godlewski)\n`;
        summary += `   Vui lòng kiểm tra và điều chỉnh nếu cần.\n`;
    }
    
    if (!columnMap.thickness) {
        summary += `\n⚠️ Không tìm thấy cột bề dày lớp đất → đã gán mặc định 5.0m.\n   Vui lòng nhập bề dày thực tế cho từng lớp.`;
    }
    
    alert(summary);
}

const slopeWorkerCache = new Map();
const slopeWorkerPending = new Set();

function buildSlopeWorkerInput(sublayers, useConsolidation) {
    return {
        embankment: {
            height: appState.embankment.height,
            width: appState.embankment.width,
            slope: appState.embankment.slope,
            gamma: appState.embankment.gamma
        },
        foundation: {
            surcharge: appState.foundation.surcharge,
            gwt: appState.foundation.gwt,
            shearMode: appState.foundation.shearMode || "undrained"
        },
        treatment: appState.treatment.method,
        cdm: {
            diameter: appState.cdm.diameter,
            spacing: appState.cdm.spacing,
            pattern: appState.cdm.pattern,
            depth: appState.cdm.depth,
            qu: appState.cdm.qu,
            phi: appState.cdm.phi,
            overWidth: appState.cdm.overWidth
        },
        layers: sublayers.map(sub => ({
            type: sub.type,
            depthTop: sub.depthTop,
            depthBottom: sub.depthBottom,
            gamma: sub.gamma,
            gammaSat: sub.gammaSat,
            c: sub.c,
            phi: sub.phi,
            U_tConst: sub.U_tConst,
            deltaSigmaPreload: sub.deltaSigmaPreload,
            deltaSigmaZ: sub.deltaSigmaZ
        })),
        useConsolidation: Boolean(useConsolidation)
    };
}

function requestSlopeWorkerResult(sublayers, useConsolidation) {
    const core = globalThis.NendatYeuCore;
    if (!core || typeof core.searchCriticalCircleAsync !== "function") return null;
    const input = buildSlopeWorkerInput(sublayers, useConsolidation);
    const key = JSON.stringify(input);
    if (slopeWorkerCache.has(key)) return { handled: true, result: slopeWorkerCache.get(key) };
    if (!slopeWorkerPending.has(key)) {
        slopeWorkerPending.add(key);
        core.searchCriticalCircleAsync(input).then(result => {
            slopeWorkerPending.delete(key);
            slopeWorkerCache.set(key, result || { Xc: NaN, Zc: NaN, R: NaN, FS: NaN, solverError: true });
            if (slopeWorkerCache.size > 32) slopeWorkerCache.delete(slopeWorkerCache.keys().next().value);
            scheduleAnalysis(0);
        }).catch(error => {
            slopeWorkerPending.delete(key);
            slopeWorkerCache.set(key, { Xc: NaN, Zc: NaN, R: NaN, FS: NaN, solverError: true, error: error.message });
            scheduleAnalysis(0);
        });
    }
    return {
        handled: true,
        result: { Xc: NaN, Zc: NaN, R: NaN, FS: NaN, solverPending: true }
    };
}

// ==========================================
// CDM SOLUTIONS GEOTECHNICAL CHECKS
// ==========================================
function checkCircularSlip(sublayers, useConsolidation = false) {
    const workerState = requestSlopeWorkerResult(sublayers, useConsolidation);
    if (workerState && workerState.handled) {
        const result = workerState.result;
        if (result.solverPending) {
            window.analysisSolverErrors = window.analysisSolverErrors || [];
            window.analysisSolverErrors.push("Đang tìm cung trượt tới hạn trong Web Worker.");
        } else if (result.solverError) {
            window.analysisSolverErrors = window.analysisSolverErrors || [];
            window.analysisSolverErrors.push(result.error || "Không tìm được cung trượt tròn hợp lệ trong miền tìm kiếm.");
        }
        return result;
    }
    // ================================================================
    // KIỂM TOÁN ỔN ĐỊNH TRƯỢT TRỤ TRÒN - PHƯƠNG PHÁP FELLENIUS
    // (Ordinary Method of Slices / Swedish Circle Method)
    // Hệ tọa độ: x = ngang (0 tại tim đường), depth = chiều sâu từ mặt đất tự nhiên (dương hướng xuống)
    // Mặt đất tự nhiên: depth = 0
    // Đỉnh nền đắp: depth = -hEmb (nằm trên mặt đất)
    // ================================================================
    let bCrest = appState.embankment.width;
    let hEmb = appState.embankment.height;
    let mSlope = appState.embankment.slope;
    let gammaEmb = appState.embankment.gamma;
    let qSurcharge = appState.foundation.surcharge;
    
    let bHalf = bCrest / 2;
    let toeX = bHalf + mSlope * hEmb; // x tại chân taluy
    
    // Hàm xác định cao trình mặt đất/nền đắp theo depth
    // depth < 0 = trên mặt đất (trong nền đắp), depth > 0 = dưới mặt đất
    // Trả về depth tại mặt nền đắp/mặt đất tại vị trí x
    function getSurfaceDepth(x) {
        let absX = Math.abs(x);
        if (absX <= bHalf) {
            return -hEmb; // đỉnh nền đắp
        } else if (absX <= toeX) {
            return -hEmb + (absX - bHalf) / mSlope; // mái dốc taluy
        } else {
            return 0; // mặt đất tự nhiên
        }
    }
    
    // Tổng chiều dày nền đất yếu
    let totalSoilDepth = 0;
    sublayers.forEach(sub => {
        if (sub.depthBottom > totalSoilDepth) totalSoilDepth = sub.depthBottom;
    });
    
    let bestFS = 999;
    let bestCircle = null;
    
    // ================================================================
    // LƯỚI TÌM KIẾM TÂM CUNG TRƯỢT
    // Tâm cung trượt (Xc, Dc) nằm phía trên mặt đất
    // Dc = depth tâm (giá trị âm = trên mặt đất)
    // Cung trượt nguy hiểm nhất thường có tâm ở phía trên và lệch về 
    // phía mái taluy
    // ================================================================
    
    // Pass 1: Lưới thô
    let xMin_s = bHalf * 0.3;
    let xMax_s = toeX + hEmb * 2;
    let dMin_s = -hEmb * 4.0;   // tâm cung ở rất cao trên mặt đắp
    let dMax_s = -hEmb * 0.5;   // tâm cung gần đỉnh đắp
    
    let steps1 = 18;
    
    for (let i = 0; i <= steps1; i++) {
        let Xc = xMin_s + (xMax_s - xMin_s) * (i / steps1);
        for (let j = 0; j <= steps1; j++) {
            let Dc = dMin_s + (dMax_s - dMin_s) * (j / steps1);
            
            // Bán kính phải đủ lớn để cung trượt cắt sâu xuống đất nền ít nhất 2.0m
            let R_min_val = Math.abs(Dc) + 2.0; 
            // R_max: cung trượt không nên quá sâu
            let R_max_val = Math.abs(Dc) + Math.min(totalSoilDepth, hEmb * 1.8);
            if (R_min_val >= R_max_val) continue;
            
            for (let k = 0; k <= steps1; k++) {
                let R = R_min_val + (R_max_val - R_min_val) * (k / steps1);
                
                let fs = calculateFSForCircle_v2(Xc, Dc, R, getSurfaceDepth, sublayers, useConsolidation);
                if (fs > 0.2 && fs < bestFS) {
                    bestFS = fs;
                    bestCircle = { Xc: Xc, Dc: Dc, R: R, FS: fs };
                }
            }
        }
    }
    
    // Pass 2: Lưới tinh quanh kết quả pass 1
    if (bestCircle) {
        let refXc = bestCircle.Xc;
        let refDc = bestCircle.Dc;
        let refR  = bestCircle.R;
        
        let dX = (xMax_s - xMin_s) / steps1;
        let dD = (dMax_s - dMin_s) / steps1;
        let dR_range = (Math.abs(refDc) + totalSoilDepth - Math.abs(refDc) * 0.8) / steps1;
        
        let steps2 = 10;
        
        for (let i = 0; i <= steps2; i++) {
            let Xc = refXc - dX + 2 * dX * (i / steps2);
            for (let j = 0; j <= steps2; j++) {
                let Dc = refDc - dD + 2 * dD * (j / steps2);
                if (Dc >= 0) continue; // tâm phải trên mặt đất
                for (let k = 0; k <= steps2; k++) {
                    let R = refR - dR_range + 2 * dR_range * (k / steps2);
                    if (R <= Math.abs(Dc) * 0.3) continue;
                    
                    let fs = calculateFSForCircle_v2(Xc, Dc, R, getSurfaceDepth, sublayers, useConsolidation);
                    if (fs > 0.2 && fs < bestFS) {
                        bestFS = fs;
                        bestCircle = { Xc: Xc, Dc: Dc, R: R, FS: fs };
                    }
                }
            }
        }
    }
    
    // Chuyển đổi kết quả sang hệ tọa độ hiển thị (Zc = -Dc cho 2D canvas)
    if (bestCircle) {
        return { 
            Xc: bestCircle.Xc, 
            Zc: -bestCircle.Dc, // Zc dương = phía trên mặt đất (cho vẽ 2D)
            R: bestCircle.R, 
            FS: bestCircle.FS 
        };
    }
    
    window.analysisSolverErrors = window.analysisSolverErrors || [];
    window.analysisSolverErrors.push("Không tìm được cung trượt tròn hợp lệ trong miền tìm kiếm.");
    return { Xc: NaN, Zc: NaN, R: NaN, FS: NaN, solverError: true };
}

function calculateFSForCircle_v2(Xc, Dc, R, getSurfaceDepth, sublayers, useConsolidation = false) {
    if (Dc + R < 2.0) return 999;
    // ================================================================
    // TÍNH HỆ SỐ AN TOÀN CHO MỘT CUNG TRƯỢT THEO FELLENIUS
    // 
    // Hệ tọa độ: 
    //   x = ngang, depth = chiều sâu (dương hướng xuống)
    //   Tâm cung: (Xc, Dc), bán kính R
    //   Dc < 0 (tâm cung nằm trên mặt đất)
    //
    // Phương trình cung tròn: (x - Xc)² + (d - Dc)² = R²
    //   => d = Dc + sqrt(R² - (x-Xc)²)  [nửa dưới cung tròn]
    //   => d = Dc - sqrt(R² - (x-Xc)²)  [nửa trên - không dùng]
    //
    // Công thức Fellenius:
    //   FS = Σ[c_i · l_i + W_i · cos(α_i) · tan(φ_i)] / Σ[W_i · sin(α_i)]
    //
    //   Trong đó:
    //   - W_i: trọng lượng phân tố thứ i (kN/m)
    //   - α_i: góc nghiêng đáy phân tố so với phương ngang
    //          = arcsin((x_i - Xc) / R)
    //   - c_i: lực dính tại đáy phân tố (kPa)
    //   - φ_i: góc ma sát trong tại đáy phân tố (độ)
    //   - l_i: chiều dài đáy phân tố = b / cos(α_i)
    //   - b:   chiều rộng phân tố
    // ================================================================
    
    let hEmb = appState.embankment.height;
    let gammaEmb = appState.embankment.gamma;
    let qSurcharge = appState.foundation.surcharge;
    let bHalf = appState.embankment.width / 2;
    let mSlope = appState.embankment.slope;
    let toeX = bHalf + mSlope * hEmb;
    let gwt = appState.foundation.gwt;
    
    let cdm_d = appState.cdm.diameter;
    let cdm_s = appState.cdm.spacing;
    let cdm_p = appState.cdm.pattern;
    let cdm_depth = appState.cdm.depth;
    let cdm_qu = appState.cdm.qu * 1000; // kPa
    let cdm_c = cdm_qu / 2; // cường độ kháng cắt không thoát nước của cọc XMĐ
    
    let m_cdm = cdm_p === 'triangular' ? 
        (Math.PI * cdm_d * cdm_d) / (2 * Math.sqrt(3) * cdm_s * cdm_s) : 
        (Math.PI * cdm_d * cdm_d) / (4 * cdm_s * cdm_s);
    
    // Xác định phạm vi x mà cung trượt cắt qua mặt đất/nền đắp
    let xLeft = Xc - R;
    let xRight = Xc + R;
    
    // Tìm các điểm giao giữa cung tròn và mặt đất/nền đắp
    let scanSteps = 80;
    let xStart = null;
    let xEnd = null;
    let dx_scan = (xRight - xLeft) / scanSteps;
    
    for (let i = 0; i <= scanSteps; i++) {
        let x = xLeft + i * dx_scan;
        let radVal = R * R - (x - Xc) * (x - Xc);
        if (radVal < 0) continue;
        
        // Điểm dưới cùng của cung tròn tại x (depth lớn nhất)
        let dCircle = Dc + Math.sqrt(radVal);
        // Cao trình mặt đất/nền đắp tại x
        let dSurface = getSurfaceDepth(x);
        
        // Cung tròn nằm dưới mặt đất/nền đắp
        if (dCircle > dSurface) {
            if (xStart === null) xStart = x;
            xEnd = x;
        }
    }
    
    let weakSoilDepth = 0;
    sublayers.forEach(sub => {
        if (sub.type === 'cohesive') {
            if (sub.depthBottom > weakSoilDepth) weakSoilDepth = sub.depthBottom;
        }
    });
    if (weakSoilDepth === 0) {
        sublayers.forEach(sub => {
            if (sub.depthBottom > weakSoilDepth) weakSoilDepth = sub.depthBottom;
        });
    }
    if (xStart === null || xEnd === null || (xEnd - xStart) < 0.5) return 0;
    if (xEnd > toeX + Math.max(0.5 * weakSoilDepth, 0.8 * hEmb)) return 999;
    
    // Chia thành các phân tố (slices)
    let numSlices = 20;
    let b = (xEnd - xStart) / numSlices;
    let sumDriving = 0;
    let sumResisting = 0;
    
    for (let j = 0; j < numSlices; j++) {
        let xj = xStart + (j + 0.5) * b; // tâm phân tố
        let radVal = R * R - (xj - Xc) * (xj - Xc);
        if (radVal < 0) continue;
        
        // Đáy cung trượt (depth) tại phân tố j
        let dBase = Dc + Math.sqrt(radVal);
        // Mặt trên phân tố (mặt đất hoặc mặt đắp)
        let dTop = getSurfaceDepth(xj);
        
        // Chiều cao phân tố
        let hSlice = dBase - dTop;
        if (hSlice <= 0) continue;
        
        // Góc nghiêng đáy phân tố
        let sinAlpha = (Xc - xj) / R;
        if (sinAlpha > 1) sinAlpha = 1;
        if (sinAlpha < -1) sinAlpha = -1;
        let alpha = Math.asin(sinAlpha);
        let cosAlpha = Math.cos(alpha);
        let lj = b / cosAlpha; // chiều dài đáy phân tố
        
        // ============================================================
        // TÍNH TRỌNG LƯỢNG PHÂN TỐ W_j
        // ============================================================
        let Wj = 0;
        
        // Phần nền đắp (depth < 0)
        if (dTop < 0) {
            let embTop = dTop;
            let embBot = Math.min(0, dBase); // đáy phần đắp = min(mặt đất, đáy cung)
            let hEmbSlice = embBot - embTop;
            if (hEmbSlice > 0) {
                Wj += gammaEmb * hEmbSlice * b;
            }
        }
        
        // Tải trọng xe cộ (chỉ tác dụng trên đỉnh nền đắp)
        if (dTop <= -hEmb + 0.01 && Math.abs(xj) <= bHalf) {
            Wj += qSurcharge * b;
        }
        
        // Phần nền đất (depth >= 0)
        if (dBase > 0) {
            let soilTop = Math.max(0, dTop); // bắt đầu từ mặt đất hoặc cao hơn
            sublayers.forEach(sub => {
                // Giao giữa phân tố và lớp đất
                let layerTop = Math.max(soilTop, sub.depthTop);
                let layerBot = Math.min(dBase, sub.depthBottom);
                if (layerBot > layerTop) {
                    let thk = layerBot - layerTop;
                    // Dùng γ_sat nếu dưới mực nước ngầm
                    let midDepth = (layerTop + layerBot) / 2;
                    let gammaSoil = (midDepth >= gwt) ? sub.gammaSat : sub.gamma;
                    Wj += gammaSoil * thk * b;
                }
            });
        }
        
        // ============================================================
        // XÁC ĐỊNH c, φ TẠI ĐÁY PHÂN TỐ
        // ============================================================
        let c_base = 10; // mặc định
        let phi_base = 5; // mặc định (độ)
        
        if (dBase > 0) {
            // Đáy nằm trong nền đất
            for (let idx = 0; idx < sublayers.length; idx++) {
                let sub = sublayers[idx];
                if (dBase >= sub.depthTop && dBase <= sub.depthBottom + 0.01) {
                    c_base = sub.c !== undefined ? sub.c : 10;
                    phi_base = sub.phi !== undefined ? sub.phi : 5;
                    
                    if (useConsolidation) {
                        let U_val = sub.U_tConst || 0;
                        let deltaSigma = sub.deltaSigmaPreload || sub.deltaSigmaZ || 0;
                        let tanPhi = Math.tan(phi_base * Math.PI / 180);
                        let deltaC = U_val * deltaSigma * tanPhi;
                        c_base += deltaC;
                    }
                    break;
                }
            }
            
            let treatment = appState.treatment.method;
            let cdm_overWidth = appState.cdm.overWidth !== undefined ? appState.cdm.overWidth : 1.0;
            let cdm_spacing = appState.cdm.spacing;
            let cdm_layout = getColumnLayout(toeX, cdm_spacing, cdm_overWidth);
            let cdm_actualMaxX = cdm_layout.max;
            if (treatment === 'cdm' && dBase <= cdm_depth && Math.abs(xj) <= cdm_actualMaxX) {
                c_base = m_cdm * cdm_c + (1 - m_cdm) * c_base;
                // Góc ma sát composite
                let phi_p = appState.cdm.phi !== undefined ? appState.cdm.phi : 30.0;
                phi_base = Math.atan(m_cdm * Math.tan(phi_p * Math.PI / 180) + (1 - m_cdm) * Math.tan(phi_base * Math.PI / 180)) * 180 / Math.PI;
            }
        } else {
            // Đáy nằm trong nền đắp
            c_base = 5; // đất đắp (kPa)
            phi_base = 30; // đất đắp (độ)
        }
        
        // ============================================================
        // ÁP LỰC NƯỚC LỖ RỖNG (Fellenius tổng ứng suất đơn giản hóa)
        // Với đất yếu bão hòa, dùng phân tích tổng ứng suất (c_u, φ_u=0)
        // nên không trừ áp lực nước riêng
        // ============================================================
        
        const shearMode = appState.foundation.shearMode || "undrained";
        const useUndrained = shearMode === "undrained" && dBase > 0;
        let phiRad = (useUndrained ? 0 : phi_base) * Math.PI / 180;

        // Driving and resisting forces. Effective-stress mode subtracts pore-water force.
        let driving = Wj * Math.sin(alpha);
        const poreWaterForce = (!useUndrained && dBase > gwt)
            ? WATER_UNIT_WEIGHT * (dBase - gwt) * lj
            : 0;
        const effectiveNormal = Math.max(0, Wj * cosAlpha - poreWaterForce);
        let resisting = c_base * lj + effectiveNormal * Math.tan(phiRad);
        
        sumDriving += driving;
        sumResisting += resisting;
    }
    
    if (sumDriving <= 0) return 999;
    return sumResisting / sumDriving;
}

function checkPlanarSliding(sublayers) {
    let hEmb = appState.embankment.height;
    let gammaEmb = appState.embankment.gamma;
    let bCrest = appState.embankment.width;
    let mSlope = appState.embankment.slope;
    let qSurcharge = appState.foundation.surcharge;
    
    let bHalf = bCrest / 2;
    let toeX = bHalf + mSlope * hEmb;
    let cdm_spacing = appState.cdm.spacing;
    let cdm_overWidth = appState.cdm.overWidth !== undefined ? appState.cdm.overWidth : 1.0;
    let cdm_layout = getColumnLayout(toeX, cdm_spacing, cdm_overWidth);
    let B_block = cdm_layout.max; // Chiều rộng của nửa khối đắp / vùng trượt phẳng gia cố
    
    let phi_emb = 30;
    let Ka_emb = Math.tan((45 - phi_emb/2) * Math.PI / 180)**2;
    
    // 1. Áp lực chủ động của đất đắp tác dụng lên mặt đứng tại tim đường:
    let Pa_emb = 0.5 * Ka_emb * gammaEmb * hEmb * hEmb + Ka_emb * qSurcharge * hEmb;
    
    // 2. Trọng lượng nửa khối đắp W_E:
    let areaEmb = bHalf * hEmb + 0.5 * mSlope * hEmb * hEmb;
    let WE = areaEmb * gammaEmb + bHalf * qSurcharge;
    
    // CDM column parameters
    let cdm_d = appState.cdm.diameter;
    let cdm_s = appState.cdm.spacing;
    let cdm_p = appState.cdm.pattern;
    let cdm_depth = appState.cdm.depth;
    
    let m = cdm_p === 'triangular' ? 
        (Math.PI * cdm_d * cdm_d) / (2 * Math.sqrt(3) * cdm_s * cdm_s) : 
        (Math.PI * cdm_d * cdm_d) / (4 * cdm_s * cdm_s);
    
    // --- 3. KIỂM TOÁN TẠI MẶT TIẾP GIÁP BÊN DƯỚI ĐÁY ĐẮP (z = 0) ---
    // Sử dụng công thức E.3 cho góc ma sát trong tương đương dọc mặt trượt:
    // Phi_tđ = m * Phi_p + (1 - m) * Phi_s.
    let phi_p = appState.cdm.phi !== undefined ? appState.cdm.phi : 30.0;
    let phi_soil_0 = sublayers[0] ? (sublayers[0].phi !== undefined ? sublayers[0].phi : 5) : 5;
    let phi_comp_0 = m * phi_p + (1 - m) * phi_soil_0; 
    let phiRad_0 = phi_comp_0 * Math.PI / 180;
    
    let WC = 0;
    let Pa_soil = 0;
    let Pp_soil = 0;
    let gwt = appState.foundation.gwt;
    
    sublayers.forEach(sub => {
        if (sub.depthBottom <= cdm_depth) {
            let midDepth = sub.depthCenter;
            let gammaSoil = (midDepth >= gwt) ? sub.gammaSat : sub.gamma;
            WC += gammaSoil * sub.thickness * B_block;
            
            let phi_rad = (sub.phi !== undefined ? sub.phi : 5) * Math.PI / 180;
            let Ka = Math.tan(Math.PI/4 - phi_rad/2)**2;
            let c_val = sub.c !== undefined ? sub.c : 10;
            
            let sigma_v = hEmb * gammaEmb + qSurcharge + sub.sigmaV0;
            let pa_z = Ka * sigma_v - 2 * c_val * Math.sqrt(Ka);
            if (pa_z < 0) pa_z = 0;
            Pa_soil += pa_z * sub.thickness;
            
            let Kp = Math.tan(Math.PI/4 + phi_rad/2)**2;
            let sigma_v_pass = sub.sigmaV0;
            let pp_z = Kp * sigma_v_pass + 2 * c_val * Math.sqrt(Kp);
            Pp_soil += pp_z * sub.thickness;
        }
    });
    
    let P_drive_0 = Pa_emb + Pa_soil;
    let R_resist_0 = Pp_soil + (WE + WC) * Math.tan(phiRad_0);
    let FS_0 = P_drive_0 > 0 ? (R_resist_0 / P_drive_0) : 999;
    
    return {
        B_block: B_block,
        WE: WE,
        WC: WC,
        Pa_emb: Pa_emb,
        Pa_soil: Pa_soil,
        Pp_soil: Pp_soil,
        FS_0: FS_0,
        R_resist_0: R_resist_0,
        P_drive_0: P_drive_0,
        FS_L: FS_0,
        R_resist_L: R_resist_0,
        P_drive_L: P_drive_0,
        FS_min: FS_0
    };
}

function checkGeotextile(sublayers) {
    let hEmb = appState.embankment.height;
    let gammaEmb = appState.embankment.gamma;
    let mSlope = appState.embankment.slope;
    let bCrest = appState.embankment.width;
    let qSurcharge = appState.foundation.surcharge;
    
    // ========================================================
    // 1. LỰC KÉO DO LỰC XÔ NGANG MÁI TALUY (T_sr)
    // Theo lý thuyết cân bằng giới hạn Rankine
    // ========================================================
    let phi_emb = 30; // Góc ma sát trong của đất đắp (độ) 
    let Ka_emb = Math.tan((45 - phi_emb / 2) * Math.PI / 180) ** 2;
    
    // Áp lực đất chủ động tại đáy nền đắp (Rankine)
    // Pa = 0.5 * Ka * γ * H² + Ka * q * H
    let Pa_total = 0.5 * Ka_emb * gammaEmb * hEmb * hEmb + Ka_emb * qSurcharge * hEmb;
    
    // Áp lực bị động phía hạ lưu (lấy gần đúng = 0 vì đất yếu)
    // T_sr = Pa (lực kéo yêu cầu để cân bằng lực xô ngang)
    let T_sr = Pa_total;
    
    // ========================================================
    // 2. LỰC KÉO DO HIỆU ỨNG VÒM ĐẤT GIỮA CÁC CỌC (T_arching)  
    // Theo BS 8006-1:2010, Mục 8.3.3 - Hewlett & Randolph
    // ========================================================
    let cdm_d = appState.cdm.diameter;
    let cdm_s = appState.cdm.spacing;
    let cdm_p = appState.cdm.pattern;
    
    // Diện tích đầu cọc tương đương a (kích thước cạnh vuông tương đương)
    let a = cdm_d * Math.sqrt(Math.PI / 4); // a = d * sqrt(π/4) cho cọc tròn
    
    // Khoảng cách thông thủy giữa các cọc (s - a)
    let s_clear = cdm_s - a;
    if (s_clear < 0) s_clear = 0.01;
    
    // Hệ số thay thế diện tích
    let m_ratio = cdm_p === 'triangular' ? 
        (Math.PI * cdm_d * cdm_d) / (2 * Math.sqrt(3) * cdm_s * cdm_s) : 
        (Math.PI * cdm_d * cdm_d) / (4 * cdm_s * cdm_s);
    
    // Tổng tải trọng trên mặt nền (q_total)
    let q_total = hEmb * gammaEmb + qSurcharge;
    
    // Hệ số vòm Cc theo BS 8006-1:2010, Bảng 9
    // Cc = 1.95 * H/a - 0.18 cho trường hợp cọc cuối đắp (end-bearing)  
    // Cc phụ thuộc tỷ số H/a
    let H_over_a = hEmb / a;
    let Cc_arch;
    if (H_over_a >= 1.4) {
        Cc_arch = 1.95 * H_over_a - 0.18;
    } else {
        Cc_arch = 1.5 * H_over_a + 0.5; // Nội suy cho H/a thấp
    }
    
    // Tỷ lệ tải trọng truyền qua vòm xuống đất giữa cọc  
    // Theo phương pháp Hewlett & Randolph (BS 8006)
    // Phần trăm tải truyền trực tiếp lên cọc: E (Efficacy)
    // E = 1 - [Cc * a / (H + a)]^2 (đơn giản hóa)
    // Tải trọng truyền lên đất giữa cọc:
    // σ_s = f_fs * σ_v (σ_v = γ*H + q)
    // Trong đó f_fs = [(s^2 - a^2) / s^2] * [s^2 / (Cc * a)^2] khi H/a > 1.4
    
    // Tính tỷ số tải truyền lên đất giữa cọc theo BS 8006 đơn giản hóa
    let s2 = cdm_s * cdm_s;
    let a2 = a * a;

    // Phần tải phân bố lên đất giữa cọc (phương pháp BS 8006 đơn giản hóa)
    let ratio_load_soil;
    if (Cc_arch * a > 0) {
        ratio_load_soil = (s2 - a2) / (Cc_arch * a) / (Cc_arch * a);
        if (ratio_load_soil > 1.0) ratio_load_soil = 1.0;
        if (ratio_load_soil < 0) ratio_load_soil = 0;
    } else {
        ratio_load_soil = 1.0;
    }
    
    // Tải trọng trên dải vải địa giữa các cọc (kN/m² per unit strip)
    let sigma_s = ratio_load_soil * q_total;
    
    // Lực kéo màng (membrane tension) theo lý thuyết dây xích (catenary)
    // BS 8006-1:2010, Equation 26:
    // T_rp = W_T * (s - a) / (2*a) (giản lược cho membrane effect)
    // Với W_T = σ_s * (s - a) là tải trọng tổng trên dải vải giữa cọc
    let W_T = sigma_s * s_clear; // kN/m per unit run
    
    // Lực kéo do treo vòm (tension from load spreading to pile caps)
    // T = W_T * (s-a) / (2*a) theo BS 8006 simplified
    // Giới hạn dưới: nếu a rất nhỏ thì T rất lớn -> giới hạn bằng hệ số 6% sag
    let sag_ratio = 0.06; // 6% tỷ lệ võng cho phép vải địa  
    let T_arching;
    if (a > 0.01) {
        T_arching = W_T * s_clear / (2 * a);
        // Kiểm tra giới hạn dưới theo lý thuyết dây xích (catenary check)
        let T_catenary = sigma_s * s_clear * s_clear / (8 * sag_ratio * s_clear);
        // Lấy giá trị lớn hơn để an toàn
        T_arching = Math.max(T_arching, T_catenary);
    } else {
        // Fallback: catenary formula T = w*L² / (8*f)
        T_arching = sigma_s * s_clear / (8 * sag_ratio);
    }
    
    // ========================================================
    // 3. TỔNG LỰC KÉO YÊU CẦU VÀ KIỂM TOÁN
    // ========================================================
    let T_req = T_sr + T_arching;
    
    let T_ult = appState.cdm.tUlt || 100.0;
    let RF = appState.cdm.rf || 2.0;
    let T_allow = T_ult / RF;
    
    let FS = T_allow / T_req;
    
    return {
        // Kết quả lực kéo ngang
        Ka_emb: Ka_emb,
        phi_emb: phi_emb,
        Pa_total: Pa_total,
        T_sr: T_sr,
        // Kết quả vòm đất
        a: a,
        s_clear: s_clear,
        m_ratio: m_ratio,
        H_over_a: H_over_a,
        Cc_arch: Cc_arch,
        ratio_load_soil: ratio_load_soil,
        sigma_s: sigma_s,
        W_T: W_T,
        sag_ratio: sag_ratio,
        T_arching: T_arching,
        // Tổng
        T_req: T_req,
        T_ult: T_ult,
        RF: RF,
        T_allow: T_allow,
        FS: FS,
        Pass: FS >= 1.0
    };
}

function renderNoneVerification() {
    const container = document.getElementById("cdm-verification-content");
    if (!container) return;
    let html = `
        <div class="verification-section">
            <div class="verification-section-title" style="background: rgba(96, 165, 250, 0.15); color: #60a5fa; border: 1px solid rgba(96, 165, 250, 0.3);">
                <span style="font-weight: bold; text-transform: uppercase;">A. Phương pháp tính toán lún phân tầng cộng lún (TCCS 41:2022)</span>
            </div>
            <p>Độ lún tổng cộng cuối cùng được xác định theo công thức:</p>
            <div class="verification-formula">
                S = m &middot; (S<sub>i</sub> + S<sub>c</sub> + S<sub>s</sub>)
            </div>
            <p>Trong đó:</p>
            <ul>
                <li><strong>S<sub>i</sub>:</strong> Độ lún tức thời (đàn hồi) của nền đất dính và đất rời.</li>
                <li><strong>S<sub>c</sub>:</strong> Độ lún cố kết sơ cấp tính theo chỉ số nén C<sub>c</sub> và chỉ số nén lại C<sub>r</sub> tùy thuộc trạng thái NC hoặc OC.</li>
                <li><strong>S<sub>s</sub>:</strong> Độ lún từ biến thứ cấp tính theo chỉ số nén thứ cấp C<sub>&alpha;</sub>.</li>
                <li><strong>m:</strong> Hệ số kinh nghiệm điều chỉnh độ lún (lấy m = ${appState.foundation.mFactor}).</li>
            </ul>

            <div class="verification-section-title" style="background: rgba(96, 165, 250, 0.15); color: #60a5fa; border: 1px solid rgba(96, 165, 250, 0.3); margin-top: 15px;">
                <span style="font-weight: bold; text-transform: uppercase;">B. Lý thuyết cố kết Terzaghi 1 chiều (Thoát nước đứng)</span>
            </div>
            <p>Độ cố kết đứng trung bình U<sub>v</sub> tại thời điểm t được tính theo T<sub>v</sub> = C<sub>v</sub> &middot; t / H<sub>d</sub>²:</p>
            <ul>
                <li>Nếu T<sub>v</sub> &le; 0.283: U<sub>v</sub> = 2 &middot; &radic;(T<sub>v</sub> / &pi;)</li>
                <li>Nếu T<sub>v</sub> &gt; 0.283: U<sub>v</sub> = 1 - 8/&pi;² &middot; exp(-&pi;² &middot; T<sub>v</sub> / 4)</li>
            </ul>
            <p>Độ cố kết này quyết định tốc độ phát triển độ lún theo thời gian dưới tải trọng đắp.</p>
        </div>
    `;
    container.innerHTML = html;
}

function renderPvdVerification(circInit, circCons, stagedStabilityHtml = "") {
    const container = document.getElementById("cdm-verification-content");
    if (!container) return;
    let pvd = appState.pvd;
    let isVacuum = (appState.treatment.method === 'vacuum_pvd');
    let vacuumInfoHtml = "";
    if (isVacuum) {
        vacuumInfoHtml = `
            <p><strong>Thông số hút chân không (Vacuum Preloading):</strong></p>
            <ul>
                <li>Áp lực hút chân không P<sub>vac</sub> = <strong>${pvd.vacuumPressure} kPa</strong>.</li>
                <li>Hệ số hiệu quả hút chân không &eta;<sub>vac</sub> = <strong>${pvd.vacuumEfficiency}</strong>.</li>
                <li>Áp lực hút hữu hiệu truyền vào bấc thấm P<sub>vac,eff</sub> = P<sub>vac</sub> &middot; &eta;<sub>vac</sub> = <strong>${(pvd.vacuumPressure * pvd.vacuumEfficiency).toFixed(1)} kPa</strong> (tương đương tải trọng đắp tăng thêm khoảng ${(pvd.vacuumPressure * pvd.vacuumEfficiency / 18.5).toFixed(2)} m chiều cao đắp).</li>
                <li>Áp lực này tác dụng làm tăng tốc độ và độ lớn của cố kết hữu hiệu trong suốt chiều sâu cắm bấc thấm L = ${pvd.depth.toFixed(1)} m.</li>
            </ul>
        `;
    }
    let d_w = (2 * (pvd.width + pvd.thickness)) / 1000 / Math.PI; // m
    let d_e = pvd.pattern === 'triangular' ? (1.05 * pvd.spacing) : (1.13 * pvd.spacing); // m
    let nRatio = d_e / d_w;
    if (nRatio < 1.01) nRatio = 1.01;
    
    let isTcvn = (pvd.standard === 'tcvn_9355');
    
    let avgCv = getThicknessWeightedCv(appState.soilLayers);
    let avgEs = 3000;
    if (appState.soilLayers && appState.soilLayers.length > 0) {
        let cohesiveLayers = appState.soilLayers.filter(l => l.type === 'cohesive' || l.Cc > 0.05);
        if (cohesiveLayers.length > 0) {
            avgEs = cohesiveLayers.reduce((acc, l) => acc + (l.E || 3000), 0) / cohesiveLayers.length;
        } else {
            avgEs = appState.soilLayers.reduce((acc, l) => acc + (l.E || 3000), 0) / appState.soilLayers.length;
        }
    }
    if (avgEs <= 0) avgEs = 3000;
    let Ch = avgCv * pvd.chRatio;
    
    let Fn = 0;
    let Fs = 0;
    let Fr = 0;
    let F_total = 0;
    let kh_s = 0;
    
    let dsDw = pvd.dsDw || 2.5;
    let khKs = pvd.khKs || 3.0;
    let pvdDepth = pvd.depth || 15.0;
    let qw = pvd.qw || 50.0;

    if (isTcvn) {
        Fn = (Math.pow(nRatio, 2) / (Math.pow(nRatio, 2) - 1)) * Math.log(nRatio) - (3 * Math.pow(nRatio, 2) - 1) / (4 * Math.pow(nRatio, 2));
        Fs = (khKs - 1) * Math.log(dsDw);
        let kh_year = Ch * (1.0 / avgEs) * WATER_UNIT_WEIGHT;
        kh_s = kh_year / (3.1536 * 1e7);
        let qw_m3s = qw * 1e-6;
        Fr = (2.0 / 3.0) * Math.PI * Math.pow(pvdDepth, 2) * (kh_s / qw_m3s);
        F_total = Fn + Fs + Fr;
    } else {
        Fn = Math.log(nRatio) - 0.75;
        F_total = Fn;
    }

        let html = "";
    if (isTcvn) {
        html = `
            <div class="verification-section">
                ${vacuumInfoHtml}
                <div class="verification-section-title" style="background: var(--bg-section-title); color: var(--text-section-title); border: 1px solid var(--border-section-title);">
                    <span style="font-weight: bold; text-transform: uppercase;">A. Lý thuyết cố kết hướng tâm Barron - Hansbo (TCVN 9355:2012)</span>
                </div>
                <p>Khi sử dụng bấc thấm (PVD), độ cố kết đồng thời theo phương đứng và hướng tâm được kết hợp theo công thức của Carillo:</p>
                <div class="verification-formula">
                    U(t) = 1 - (1 - U<sub>v</sub>) &middot; (1 - U<sub>r</sub>)
                </div>
                <p>Độ cố kết hướng tâm U<sub>r</sub> do bấc thấm phát triển theo thời gian:</p>
                <div class="verification-formula">
                    U<sub>r</sub> = 1 - exp(-8 &middot; T<sub>r</sub> / F)
                </div>
                <p>Trong đó hệ số cản tổng hợp F = F<sub>n</sub> + F<sub>s</sub> + F<sub>r</sub> theo TCVN 9355:2012:</p>
                <ul>
                    <li><strong>Hệ số khoảng cách F<sub>n</sub>:</strong> F<sub>n</sub> = [n²/(n²-1)]&middot;ln(n) - (3n²-1)/(4n²) = <strong>${Fn.toFixed(4)}</strong> (với n = d<sub>e</sub>/d<sub>w</sub> = <strong>${nRatio.toFixed(2)}</strong>).</li>
                    <li><strong>Hệ số xáo động F<sub>s</sub> (smear zone):</strong> F<sub>s</sub> = (k<sub>h</sub>/k<sub>s</sub> - 1)&middot;ln(d<sub>s</sub>/d<sub>w</sub>) = <strong>${Fs.toFixed(4)}</strong> (với k<sub>h</sub>/k<sub>s</sub> = <strong>${khKs.toFixed(1)}</strong>, d<sub>s</sub>/d<sub>w</sub> = <strong>${dsDw.toFixed(1)}</strong>).</li>
                    <li><strong>Trở lực giếng bấc F<sub>r</sub>:</strong> F<sub>r</sub> = (2/3)&middot;&pi;&middot;L²&middot;(k<sub>h</sub>/q<sub>w</sub>) = <strong>${Fr.toFixed(4)}</strong> (với L = <strong>${pvdDepth.toFixed(1)} m</strong>, q<sub>w</sub> = <strong>${qw.toFixed(1)} cm³/s</strong>, k<sub>h</sub> &approx; <strong>${kh_s.toExponential(3)} m/s</strong>).</li>
                    <li><strong>Hệ số cản tổng cộng F:</strong> F = F<sub>n</sub> + F<sub>s</sub> + F<sub>r</sub> = <strong>${F_total.toFixed(4)}</strong>.</li>
                </ul>
                <p>Các thông số hình học khác:</p>
                <ul>
                    <li>Bản rộng bấc thấm b = <strong>${pvd.width} mm</strong>, dày t = <strong>${pvd.thickness} mm</strong>.</li>
                    <li>Đường kính tương đương d<sub>w</sub> = 2(b+t)/&pi; = <strong>${(d_w * 100).toFixed(2)} cm</strong>.</li>
                    <li>Đường kính vùng ảnh hưởng d<sub>e</sub> = <strong>${d_e.toFixed(3)} m</strong> (Lưới ${pvd.pattern === 'triangular' ? 'Tam giác de=1.05S' : 'Ô cờ de=1.13S'}, S = ${pvd.spacing} m).</li>
                    <li>Hệ số cố kết ngang C<sub>h</sub> = ${pvd.chRatio} &middot; C<sub>v</sub> = <strong>${Ch.toFixed(2)} m²/năm</strong> (C<sub>v,tb</sub> &approx; ${avgCv.toFixed(2)} m²/năm).</li>
                    <li>Nhân tố thời gian hướng tâm T<sub>r</sub> = C<sub>h</sub> &middot; t / d<sub>e</sub>².</li>
                </ul>
                <p><strong>Ảnh hưởng chiều sâu cắm bấc thấm (L = ${pvdDepth.toFixed(1)} m):</strong></p>
                <ul>
                    <li><strong>Phía trên mũi bấc (z &le; L):</strong> Đất nền cố kết nhanh theo chế độ thoát nước kết hợp U<sub>combined</sub>.</li>
                    <li><strong>Phía dưới mũi bấc (z &gt; L):</strong> Đất nền cố kết chậm theo chế độ thoát nước đứng tự nhiên U<sub>v</sub>.</li>
                </ul>
            </div>
        `;
        } else {
        html = `
            <div class="verification-section">
                ${vacuumInfoHtml}
                <div class="verification-section-title" style="background: var(--bg-section-title); color: var(--text-section-title); border: 1px solid var(--border-section-title);">
                    <span style="font-weight: bold; text-transform: uppercase;">A. Lý thuyết cố kết hướng tâm (TCCS 41:2022/TCĐBVN)</span>
                </div>
                <p>Khi sử dụng bấc thấm (PVD) theo TCCS 41:2022, độ cố kết đồng thời theo phương đứng và hướng tâm được kết hợp theo công thức của Carillo:</p>
                <div class="verification-formula">
                    U(t) = 1 - (1 - U<sub>v</sub>) &middot; (1 - U<sub>r</sub>)
                </div>
                <p>Độ cố kết hướng tâm U<sub>r</sub> do bấc thấm phát triển theo thời gian:</p>
                <div class="verification-formula">
                    U<sub>r</sub> = 1 - exp(-8 &middot; T<sub>r</sub> / F<sub>n</sub>)
                </div>
                <p>Trong đó hệ số khoảng cách F<sub>n</sub>:</p>
                <ul>
                    <li><strong>Hệ số khoảng cách F<sub>n</sub>:</strong> F<sub>n</sub> = ln(d<sub>e</sub>/d<sub>w</sub>) - 0.75 = <strong>${Fn.toFixed(4)}</strong> (với d<sub>e</sub>/d<sub>w</sub> = <strong>${nRatio.toFixed(2)}</strong>).</li>
                </ul>
                <p>Các thông số hình học khác:</p>
                <ul>
                    <li>Bản rộng bấc thấm b = <strong>${pvd.width} mm</strong>, dày t = <strong>${pvd.thickness} mm</strong>.</li>
                    <li>Đường kính tương đương bấc thấm d<sub>w</sub> = 2(b+t)/&pi; = <strong>${(d_w * 100).toFixed(2)} cm</strong>.</li>
                    <li>Đường kính vùng ảnh hưởng d<sub>e</sub> = <strong>${d_e.toFixed(3)} m</strong> (Lưới ${pvd.pattern === 'triangular' ? 'Tam giác de=1.05S' : 'Ô cờ de=1.13S'}, S = ${pvd.spacing} m).</li>
                    <li>Hệ số cố kết ngang C<sub>h</sub> = ${pvd.chRatio} &middot; C<sub>v</sub> = <strong>${Ch.toFixed(2)} m²/năm</strong> (C<sub>v,tb</sub> &approx; ${avgCv.toFixed(2)} m²/năm).</li>
                    <li>Nhân tố thời gian hướng tâm T<sub>r</sub> = C<sub>h</sub> &middot; t / d<sub>e</sub>².</li>
                </ul>
                <p><strong>Ảnh hưởng chiều sâu cắm bấc thấm (L = ${pvdDepth.toFixed(1)} m):</strong></p>
                <ul>
                    <li><strong>Phía trên mũi bấc (z &le; L):</strong> Đất nền cố kết nhanh theo chế độ thoát nước kết hợp U<sub>combined</sub>.</li>
                    <li><strong>Phía dưới mũi bấc (z &gt; L):</strong> Đất nền cố kết chậm theo chế độ thoát nước đứng tự nhiên U<sub>v</sub>.</li>
                </ul>
            </div>
        `;
    }
    
    // Thêm mục B. Kiểm tra ổn định nền đắp trên đất yếu
    if (circInit && circCons) {
        html += `
            <div class="verification-section" style="margin-top: 20px; border-top: 1px dashed var(--border-color); padding-top: 15px;">
                <div class="verification-section-title" style="background: rgba(16, 185, 129, 0.15); color: #10b981; border: 1px solid rgba(16, 185, 129, 0.3);">
                    <span style="font-weight: bold; text-transform: uppercase;">B. Kiểm tra ổn định nền đắp trên đất yếu (Phụ lục C - TCVN 9355:2012)</span>
                </div>
                <p>Ổn định tổng thể chống trượt sâu của nền đắp được tính toán theo phương pháp phân tích lát cắt Fellenius. Dưới tác dụng của cố kết, lực dính không thoát nước tăng lên theo thời gian:</p>
                <div class="verification-formula">
                    c<sub>t</sub> = c<sub>0</sub> + U &middot; &Delta;&sigma;<sub>v</sub> &middot; tan(&phi;)
                </div>
                <p>Với các thông số kết quả tính toán hệ số an toàn F<sub>s</sub>:</p>
                <ul>
                    <li><strong>Hệ số an toàn lúc chưa cố kết (t = 0, U = 0):</strong> F<sub>s,0</sub> = <strong>${circInit.FS.toFixed(3)}</strong>.</li>
                    <li><strong>Hệ số an toàn tại thời điểm t = t<sub>ck</sub> (${appState.foundation.tConstruction} năm):</strong> F<sub>s,t</sub> = <strong>${circCons.FS.toFixed(3)}</strong>.</li>
                    <li><strong>Hệ số an toàn yêu cầu [F<sub>s</sub>]:</strong> <strong>1.20</strong>.</li>
                </ul>
                <p>Đánh giá kết quả kiểm toán:</p>
                <ul>
                    <li>Lúc bắt đầu đắp (t = 0): F<sub>s,0</sub> = <strong>${circInit.FS.toFixed(3)}</strong> &rarr; 
                    <strong>${circInit.FS >= 1.2 ? '<span style="color:var(--accent-success);font-weight:bold;">ĐẠT</span>' : '<span style="color:var(--accent-danger);font-weight:bold;">KHÔNG ĐẠT (Cần đắp phân kỳ hoặc chờ cố kết)</span>'}</strong>.</li>
                                        <li>Sau khi kết thúc thời gian chờ cố kết (t = ${appState.foundation.tConstruction} năm): F<sub>s,t</sub> = <strong>${circCons.FS.toFixed(3)}</strong> &rarr; 
                    <strong>${circCons.FS >= 1.2 ? '<span style="color:var(--accent-success);font-weight:bold;">ĐẠT YÊU CẦU</span>' : '<span style="color:var(--accent-danger);font-weight:bold;">KHÔNG ĐẠT YÊU CẦU</span>'}</strong>.</li>
                </ul>
            </div>
            ${stagedStabilityHtml}
        `;
    }
    
    container.innerHTML = html;
    
    // Gọi vẽ biểu đồ cung trượt tròn ổn định sau cố kết cho PVD
    if (circCons) {
        setTimeout(() => {
            drawSlipCircleChart(circCons, getEffectiveSoilLayers());
        }, 50);
    }
}

function renderSandDrainVerification() {
    const container = document.getElementById("cdm-verification-content");
    if (!container) return;
    let sd = appState.sandDrain;
    let d_w = sd.diameter; // m
    let d_e = sd.pattern === 'triangular' ? (1.05 * sd.spacing) : (1.13 * sd.spacing); // m
    let Fn = Math.log(d_e / d_w) - 0.75;
    let html = `
        <div class="verification-section">
            <div class="verification-section-title" style="background: rgba(96, 165, 250, 0.15); color: #60a5fa; border: 1px solid rgba(96, 165, 250, 0.3);">
                <span style="font-weight: bold; text-transform: uppercase;">A. Lý thuyết cố kết hướng tâm Barron - Hansbo (Giếng cát)</span>
            </div>
            <p>Giếng cát tăng tốc độ cố kết tương tự bấc thấm nhưng có đường kính thoát nước lớn hơn. Độ cố kết tổng hợp U(t) được tính theo công thức:</p>
            <div class="verification-formula">
                U(t) = 1 - (1 - U<sub>v</sub>) &middot; (1 - U<sub>r</sub>)
            </div>
            <p>Độ cố kết hướng tâm U<sub>r</sub> do giếng cát:</p>
            <div class="verification-formula">
                U<sub>r</sub> = 1 - exp(-8 &middot; T<sub>r</sub> / F<sub>n</sub>)
            </div>
            <p>Với các thông số tính toán cụ thể từ dự án:</p>
            <ul>
                <li>Đường kính giếng cát d<sub>w</sub> = <strong>${(d_w * 100).toFixed(0)} cm</strong>.</li>
                <li>Đường kính vùng ảnh hưởng d<sub>e</sub> = <strong>${d_e.toFixed(3)} m</strong> (Lưới ${sd.pattern === 'triangular' ? 'Tam giác S=1.05S' : 'Ô cờ S=1.13S'}, S = ${sd.spacing} m).</li>
                <li>Hệ số vùng ảnh hưởng F<sub>n</sub> = ln(d<sub>e</sub>/d<sub>w</sub>) - 0.75 = <strong>${Fn.toFixed(3)}</strong>.</li>
                <li>Hệ số cố kết ngang C<sub>h</sub> = ${sd.chRatio} &middot; C<sub>v</sub> = <strong>${(sd.chRatio * 1.5).toFixed(2)} m²/năm</strong> (C<sub>v,tb</sub> &approx; 1.5 m²/năm).</li>
                <li>Nhân tố thời gian hướng tâm T<sub>r</sub> = C<sub>h</sub> &middot; t / d<sub>e</sub>².</li>
            </ul>
            <p><strong>Ảnh hưởng chiều sâu cắm giếng cát (L = ${sd.depth} m):</strong></p>
            <ul>
                <li><strong>Phía trên mũi giếng (z &le; L):</strong> Đất nền cố kết nhanh theo chế độ thoát nước kết hợp U<sub>combined</sub>.</li>
                <li><strong>Phía dưới mũi giếng (z &gt; L):</strong> Đất nền cố kết chậm theo chế độ thoát nước đứng tự nhiên U<sub>v</sub>.</li>
            </ul>
        </div>
    `;
    container.innerHTML = html;
}

function renderSandColumnVerification(delta_h1, delta_h2) {
    const container = document.getElementById("cdm-verification-content");
    if (!container) return;
    let sc = appState.sandColumn;
    let d_sc = sc.diameter;
    let spacing = sc.spacing;
    let pattern = sc.pattern;
    let m = pattern === 'triangular' ? 
        (Math.PI * d_sc * d_sc) / (2 * Math.sqrt(3) * spacing * spacing) : 
        (Math.PI * d_sc * d_sc) / (4 * spacing * spacing);
    
    let html = `
        <div class="verification-section">
            <div class="verification-section-title" style="background: rgba(96, 165, 250, 0.15); color: #60a5fa; border: 1px solid rgba(96, 165, 250, 0.3);">
                <span style="font-weight: bold; text-transform: uppercase;">A. Lý thuyết nền liên hợp cọc cát (TCCS 41:2022)</span>
            </div>
            <p>Cọc cát đóng vai trò là cấu kiện gia cường có độ cứng lớn hơn đất yếu, đồng thời làm nhiệm vụ giếng cát thoát nước ngang. Mô hình tính lún composite:</p>
            <ul>
                <li>Tỷ lệ diện tích thay thế m = &pi;&middot;d² / (4&middot;S²) (cho lưới vuông) = <strong>${(m * 100).toFixed(2)}%</strong> (d = ${d_sc} m, S = ${spacing} m).</li>
                <li>Mô đun biến dạng tương đương nền composite E<sub>comp</sub> = m &middot; E<sub>p</sub> + (1 - m) &middot; E<sub>s</sub>.</li>
                <li>Trong đó: E<sub>p</sub> (cọc cát) = <strong>${sc.modulus} MPa</strong> (tức ${(sc.modulus * 1000).toLocaleString()} kPa), E<sub>s</sub> (đất yếu) &approx; 3000 kPa.</li>
            </ul>
            <p><strong>Tính lún phân vùng:</strong></p>
            <ul>
                <li><strong>Vùng gia cố (z &le; L<sub>c</sub>):</strong> Lún composite đàn hồi &Delta;h₁ = &Sigma;[(&Delta;&sigma;<sub>z</sub> / E<sub>composite</sub>) &middot; &Delta;z] = <strong>${delta_h1.toFixed(2)} cm</strong>.</li>
                <li><strong>Vùng dưới mũi cọc (z &gt; L<sub>c</sub>):</strong> Tính lún phân tầng tự nhiên &Delta;h₂ = <strong>${delta_h2.toFixed(2)} cm</strong>.</li>
                <li><strong>Tổng độ lún &Delta;h = &Delta;h₁ + &Delta;h₂ = ${(delta_h1 + delta_h2).toFixed(2)} cm</strong>.</li>
            </ul>
        </div>
    `;
    container.innerHTML = html;
}

function renderBambooPileVerification(circular, planar, delta_h1, delta_h2, delta_cushion = 0) {
    const container = document.getElementById("cdm-verification-content");
    if (!container) return;
    let bp = appState.bambooPile;
    let d_bp = bp.diameter;
    let spacing = bp.spacing;
    let pattern = bp.pattern;
    
    let treatment = appState.treatment.method;
    let hRep = treatment === 'bamboo_replacement' ? appState.excavationReplacement.depth : 0;
    let isReplacement = treatment === 'bamboo_replacement' && hRep > 0;
    let z_slide = hRep;
    
    // Find the original layer index/object that matches depth >= hRep
    let depthAcc = 0;
    let layer_at_slide = appState.soilLayers[0];
    for (let i = 0; i < appState.soilLayers.length; i++) {
        let l = appState.soilLayers[i];
        if (hRep >= depthAcc && hRep < depthAcc + l.thickness) {
            layer_at_slide = l;
            break;
        }
        depthAcc += l.thickness;
    }
    let phi_soil = layer_at_slide ? (layer_at_slide.phi !== undefined ? layer_at_slide.phi : 5) : 5;
    
    let n_density = pattern === 'triangular' ? (1.155 / (spacing * spacing)) : (1 / (spacing * spacing));
    let m = pattern === 'triangular' ? 
        (Math.PI * d_bp * d_bp) / (2 * Math.sqrt(3) * spacing * spacing) : 
        (Math.PI * d_bp * d_bp) / (4 * spacing * spacing);
    let phi_p = appState.bambooPile.phi !== undefined ? appState.bambooPile.phi : 30.0;
    let phi_comp_0 = m * phi_p + (1 - m) * phi_soil;
  
    let html = `
        <div class="verification-section">
            <div class="verification-section-title" style="background: rgba(96, 165, 250, 0.15); color: #60a5fa; border: 1px solid rgba(96, 165, 250, 0.3);">
                <span style="font-weight: bold; text-transform: uppercase;">A. Kiểm toán ổn định nền gia cố cọc tre</span>
            </div>
            
            <h4 style="color: #a5b4fc; margin-top: 10px; margin-bottom: 5px;">1. Kiểm tra ổn định trượt sâu (Fellenius cung trượt composite)</h4>
            <p>Hệ số an toàn trượt sâu F<sub>s</sub> = <strong>${circular.FS.toFixed(3)}</strong> &ge; 1.2 &rarr; <strong>${circular.FS >= 1.2 ? '<span style="color:var(--accent-success);">ĐẠT YÊU CẦU</span>' : '<span style="color:var(--accent-danger);">KHÔNG ĐẠT</span>'}</strong></p>
            <p style="font-size: 0.775rem; color: var(--text-secondary); margin-top: 3px;">
                Tâm trượt: X<sub>c</sub> = ${circular.Xc.toFixed(2)} m, Z<sub>c</sub> = ${circular.Zc.toFixed(2)} m. Bán kính R = ${circular.R.toFixed(2)} m. (Đường cung trượt chi tiết hiển thị trên biểu đồ phía trên).
            </p>
 
            <h4 style="color: #a5b4fc; margin-top: 10px; margin-bottom: 5px;">2. Kiểm tra ổn định trượt phẳng (Mặt cắt tiếp giáp z = ${z_slide.toFixed(1)}m)</h4>
            <div class="verification-formula">
                F<sub>s</sub> = [P<sub>PS</sub> + (W<sub>E</sub> + W<sub>C</sub>) &middot; tan(&phi;<sub>tđ</sub>')] / [P<sub>AE</sub> + P<sub>AS</sub>] &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(E.6)
            </div>
            <p style="margin-left: 10px; font-size: 0.8rem;">
                Trong đó: W<sub>E</sub> = <strong>${planar.WE.toFixed(1)} kN/m</strong>, W<sub>C</sub> = <strong>${planar.WC.toFixed(1)} kN/m</strong>, B<sub>block</sub> = <strong>${planar.B_block.toFixed(1)} m</strong>.<br>
                Lực đẩy chủ động: P<sub>AE</sub> = <strong>${planar.Pa_emb.toFixed(1)} kN/m</strong>, P<sub>AS</sub> = <strong>${planar.Pa_soil.toFixed(1)} kN/m</strong>.<br>
                Lực cản bị động: P<sub>PS</sub> = <strong>${planar.Pp_soil.toFixed(1)} kN/m</strong>.<br>
                &phi;<sub>tđ</sub>': Góc ma sát trong tương đương dọc theo mặt trượt z=${z_slide.toFixed(1)}m (TCVN 9906:2014 công thức E.3):<br>
                &nbsp;&nbsp;&nbsp;&nbsp;&phi;<sub>tđ</sub>' = ap &middot; &phi;<sub>p</sub> + (1 - ap) &middot; &phi;<sub>soil</sub> = ${m.toFixed(3)} &middot; ${phi_p.toFixed(1)}&deg; + (1 - ${m.toFixed(3)}) &middot; ${phi_soil.toFixed(1)}&deg; = <strong>${phi_comp_0.toFixed(2)}&deg;</strong> (với ap = m = ${m.toFixed(3)}, &phi;<sub>p</sub> = ${phi_p.toFixed(1)}&deg;)
            </p>
            <p>Hệ số an toàn trượt phẳng F<sub>s,sliding</sub> = <strong>${planar.FS_min.toFixed(2)}</strong> &ge; 1.2 &rarr; <strong>${planar.FS_min >= 1.2 ? '<span style="color:var(--accent-success);">ĐẠT YÊU CẦU</span>' : '<span style="color:var(--accent-danger);">KHÔNG ĐẠT</span>'}</strong></p>
        </div>
 
        <div class="verification-section">
            <div class="verification-section-title" style="background: rgba(96, 165, 250, 0.15); color: #60a5fa; border: 1px solid rgba(96, 165, 250, 0.3);">
                <span style="font-weight: bold; text-transform: uppercase;">B. Tính toán độ lún nền composite cọc tre${isReplacement ? ' + Thay đất' : ''}</span>
            </div>
            <p>Độ lún trong phạm vi chiều dài cọc tre được tính toán theo mô hình mô đun đàn hồi tương đương E<sub>composite</sub>:</p>
            <ul>
                <li>Mật độ cọc thiết kế: n &approx; <strong>${n_density.toFixed(0)} cọc/m²</strong>.</li>
                <li>Hệ số thay thế diện tích m = <strong>${(m * 100).toFixed(2)}%</strong> (đường kính cọc d = ${d_bp} m, khoảng cách S = ${spacing} m).</li>
                <li>Mô đun đàn hồi tương đương: E<sub>comp</sub> = m &middot; E<sub>p</sub> + (1 - m) &middot; E<sub>s</sub> = <strong>${(m * bp.modulus * 1000 + (1 - m) * 3000).toFixed(0)} kPa</strong> (E<sub>p</sub> = ${bp.modulus} MPa).</li>
            </ul>
            <p><strong>Kết quả độ lún chi tiết:</strong></p>
            <ul>
                ${isReplacement ? `<li>Độ lún đệm cát thay thế (z &le; ${hRep.toFixed(2)} m) &Delta;h<sub>cát</sub> = <strong>${delta_cushion.toFixed(2)} cm</strong>.</li>` : ''}
                <li>Độ lún vùng gia cố cọc tre &Delta;h₁ = <strong>${delta_h1.toFixed(2)} cm</strong> &le; [&Delta;h₁] = 8.0 cm &rarr; <strong>${delta_h1 <= 8.0 ? '<span style="color:var(--accent-success);">ĐẠT YÊU CẦU</span>' : '<span style="color:var(--accent-danger);">KHÔNG ĐẠT (Vượt quá 8.0cm)</span>'}</strong>.</li>
                <li>Độ lún vùng đất tự nhiên dưới mũi cọc &Delta;h₂ = <strong>${delta_h2.toFixed(2)} cm</strong>.</li>
                <li>Tổng độ lún tính toán &Delta;h = ${isReplacement ? '&Delta;h<sub>cát</sub> + ' : ''}&Delta;h₁ + &Delta;h₂ = <strong>${(delta_cushion + delta_h1 + delta_h2).toFixed(2)} cm</strong>.</li>
            </ul>
            <p style="font-size: 0.775rem; color: var(--text-secondary); margin-top: 5px;">
                Lưu ý: Tốc độ cố kết của nền cọc tre chỉ diễn ra theo phương thoát nước đứng tự nhiên của đất nền (U<sub>v</sub>), không có thoát nước ngang như cọc cát hay bấc thấm.
            </p>
        </div>
    `;
    container.innerHTML = html;
    
    setTimeout(() => {
        drawSlipCircleChart(circular, getEffectiveSoilLayers());
    }, 50);
}

function checkCircularSlip_bamboo(sublayers) {
    let bCrest = appState.embankment.width;
    let hEmb = appState.embankment.height;
    let mSlope = appState.embankment.slope;
    let bHalf = bCrest / 2;
    let toeX = bHalf + mSlope * hEmb;
    
    function getSurfaceDepth(x) {
        let absX = Math.abs(x);
        if (absX <= bHalf) return -hEmb;
        else if (absX <= toeX) return -hEmb + (absX - bHalf) / mSlope;
        else return 0;
    }
    
    let totalSoilDepth = 0;
    sublayers.forEach(sub => {
        if (sub.depthBottom > totalSoilDepth) totalSoilDepth = sub.depthBottom;
    });
    
    let bestFS = 999;
    let bestCircle = null;
    
    let xMin_s = bHalf * 0.3;
    let xMax_s = toeX + hEmb * 2;
    let dMin_s = -hEmb * 4.0;
    let dMax_s = -hEmb * 0.5;
    let steps1 = 18;
    
    for (let i = 0; i <= steps1; i++) {
        let Xc = xMin_s + (xMax_s - xMin_s) * (i / steps1);
        for (let j = 0; j <= steps1; j++) {
            let Dc = dMin_s + (dMax_s - dMin_s) * (j / steps1);
            let R_min_val = Math.abs(Dc) + 2.0; 
            let R_max_val = Math.abs(Dc) + Math.min(totalSoilDepth, hEmb * 1.8);
            if (R_min_val >= R_max_val) continue;
            
            for (let k = 0; k <= steps1; k++) {
                let R = R_min_val + (R_max_val - R_min_val) * (k / steps1);
                let fs = calculateFSForCircle_bamboo(Xc, Dc, R, getSurfaceDepth, sublayers);
                if (fs > 0.2 && fs < bestFS) {
                    bestFS = fs;
                    bestCircle = { Xc: Xc, Dc: Dc, R: R, FS: fs };
                }
            }
        }
    }
    
    if (bestCircle) {
        return { 
            Xc: bestCircle.Xc, 
            Zc: -bestCircle.Dc, 
            R: bestCircle.R, 
            FS: bestCircle.FS 
        };
    }
    window.analysisSolverErrors = window.analysisSolverErrors || [];
    window.analysisSolverErrors.push("Không tìm được cung trượt tròn hợp lệ trong miền tìm kiếm.");
    return { Xc: NaN, Zc: NaN, R: NaN, FS: NaN, solverError: true };
}

function calculateFSForCircle_bamboo(Xc, Dc, R, getSurfaceDepth, sublayers) {
    if (Dc + R < 2.0) return 999;
    let hEmb = appState.embankment.height;
    let gammaEmb = appState.embankment.gamma;
    let qSurcharge = appState.foundation.surcharge;
    let bHalf = appState.embankment.width / 2;
    let mSlope = appState.embankment.slope;
    let toeX = bHalf + mSlope * hEmb;
    let gwt = appState.foundation.gwt;
    
    let bp_d = appState.bambooPile.diameter;
    let bp_s = appState.bambooPile.spacing;
    let bp_p = appState.bambooPile.pattern;
    let bp_depth = appState.bambooPile.depth;
    let bp_c = 50; // kPa
    
    let m_bp = bp_p === 'triangular' ? 
        (Math.PI * bp_d * bp_d) / (2 * Math.sqrt(3) * bp_s * bp_s) : 
        (Math.PI * bp_d * bp_d) / (4 * bp_s * bp_s);
    
    let xLeft = Xc - R;
    let xRight = Xc + R;
    let scanSteps = 80;
    let xStart = null;
    let xEnd = null;
    let dx_scan = (xRight - xLeft) / scanSteps;
    
    for (let i = 0; i <= scanSteps; i++) {
        let x = xLeft + i * dx_scan;
        let radVal = R * R - (x - Xc) * (x - Xc);
        if (radVal < 0) continue;
        
        let dCircle = Dc + Math.sqrt(radVal);
        let dSurface = getSurfaceDepth(x);
        if (dCircle > dSurface) {
            if (xStart === null) xStart = x;
            xEnd = x;
        }
    }
    
    let weakSoilDepth = 0;
    sublayers.forEach(sub => {
        if (sub.type === 'cohesive') {
            if (sub.depthBottom > weakSoilDepth) weakSoilDepth = sub.depthBottom;
        }
    });
    if (weakSoilDepth === 0) {
        sublayers.forEach(sub => {
            if (sub.depthBottom > weakSoilDepth) weakSoilDepth = sub.depthBottom;
        });
    }
    if (xStart === null || xEnd === null || (xEnd - xStart) < 0.5) return 0;
    if (xEnd > toeX + Math.max(0.5 * weakSoilDepth, 0.8 * hEmb)) return 999;
    
    let numSlices = 20;
    let b = (xEnd - xStart) / numSlices;
    let sumDriving = 0;
    let sumResisting = 0;
    
    for (let j = 0; j < numSlices; j++) {
        let xj = xStart + (j + 0.5) * b;
        let radVal = R * R - (xj - Xc) * (xj - Xc);
        if (radVal < 0) continue;
        
        let dBase = Dc + Math.sqrt(radVal);
        let dTop = getSurfaceDepth(xj);
        let hSlice = dBase - dTop;
        if (hSlice <= 0) continue;
        
        let sinAlpha = (Xc - xj) / R;
        if (sinAlpha > 1) sinAlpha = 1;
        if (sinAlpha < -1) sinAlpha = -1;
        let alpha = Math.asin(sinAlpha);
        let cosAlpha = Math.cos(alpha);
        let lj = b / cosAlpha;
        
        let Wj = 0;
        if (dTop < 0) {
            let embTop = dTop;
            let embBot = Math.min(0, dBase);
            let hEmbSlice = embBot - embTop;
            if (hEmbSlice > 0) Wj += gammaEmb * hEmbSlice * b;
        }
        if (dTop <= -hEmb + 0.01 && Math.abs(xj) <= bHalf) {
            Wj += qSurcharge * b;
        }
        if (dBase > 0) {
            let soilTop = Math.max(0, dTop);
            sublayers.forEach(sub => {
                let layerTop = Math.max(soilTop, sub.depthTop);
                let layerBot = Math.min(dBase, sub.depthBottom);
                if (layerBot > layerTop) {
                    let thk = layerBot - layerTop;
                    let midDepth = (layerTop + layerBot) / 2;
                    let gammaSoil = (midDepth >= gwt) ? sub.gammaSat : sub.gamma;
                    Wj += gammaSoil * thk * b;
                }
            });
        }
        
        let c_base = 10;
        let phi_base = 5;
        
        if (dBase > 0) {
            for (let idx = 0; idx < sublayers.length; idx++) {
                let sub = sublayers[idx];
                if (dBase >= sub.depthTop && dBase <= sub.depthBottom + 0.01) {
                    c_base = sub.c !== undefined ? sub.c : 10;
                    phi_base = sub.phi !== undefined ? sub.phi : 5;
                    break;
                }
            }
            let treatment = appState.treatment.method;
            let hRep = (treatment === 'bamboo_replacement') ? appState.excavationReplacement.depth : 0;
            let isInsideBamboo = false;
            if (treatment === 'bamboo_pile') {
                isInsideBamboo = (dBase <= bp_depth);
            } else if (treatment === 'bamboo_replacement') {
                isInsideBamboo = (dBase > hRep && dBase <= hRep + bp_depth);
            }
            if (isInsideBamboo && Math.abs(xj) <= toeX + 1.0) {
                c_base = m_bp * bp_c + (1 - m_bp) * c_base;
                let phi_p = appState.bambooPile.phi !== undefined ? appState.bambooPile.phi : 30.0;
                phi_base = Math.atan(m_bp * Math.tan(phi_p * Math.PI / 180) + (1 - m_bp) * Math.tan(phi_base * Math.PI / 180)) * 180 / Math.PI;
            }
        } else {
            c_base = 5;
            phi_base = 30;
        }
        
        const shearMode = appState.foundation.shearMode || "undrained";
        const useUndrained = shearMode === "undrained" && dBase > 0;
        let phiRad = (useUndrained ? 0 : phi_base) * Math.PI / 180;
        let driving = Wj * Math.sin(alpha);
        const poreWaterForce = (!useUndrained && dBase > gwt)
            ? WATER_UNIT_WEIGHT * (dBase - gwt) * lj
            : 0;
        const effectiveNormal = Math.max(0, Wj * cosAlpha - poreWaterForce);
        let resisting = c_base * lj + effectiveNormal * Math.tan(phiRad);
        
        sumDriving += driving;
        sumResisting += resisting;
    }
    
    if (sumDriving <= 0) return 999;
    return sumResisting / sumDriving;
}

function checkPlanarSliding_bamboo(sublayers) {
    let hEmb = appState.embankment.height;
    let gammaEmb = appState.embankment.gamma;
    let bCrest = appState.embankment.width;
    let mSlope = appState.embankment.slope;
    let qSurcharge = appState.foundation.surcharge;
    let treatment = appState.treatment.method;
    let hRep = treatment === 'bamboo_replacement' ? appState.excavationReplacement.depth : 0;
    
    let bHalf = bCrest / 2;
    let toeX = bHalf + mSlope * hEmb;
    let B_block = toeX; // Chiều rộng của nửa khối đắp
    
    let phi_emb = 30;
    let Ka_emb = Math.tan((45 - phi_emb/2) * Math.PI / 180)**2;
    
    let Pa_emb = 0.5 * Ka_emb * gammaEmb * hEmb * hEmb + Ka_emb * qSurcharge * hEmb;
    
    let areaEmb = bHalf * hEmb + 0.5 * mSlope * hEmb * hEmb;
    
    let bp_d = appState.bambooPile.diameter;
    let bp_s = appState.bambooPile.spacing;
    let bp_p = appState.bambooPile.pattern;
    let bp_depth = appState.bambooPile.depth;
    
    let m = bp_p === 'triangular' ? 
        (Math.PI * bp_d * bp_d) / (2 * Math.sqrt(3) * bp_s * bp_s) : 
        (Math.PI * bp_d * bp_d) / (4 * bp_s * bp_s);
        
    let z_slide = treatment === 'bamboo_replacement' ? hRep : 0;

    let WE = areaEmb * gammaEmb + bHalf * qSurcharge;
    let gwt = appState.foundation.gwt;

    // Add weight of soil above z_slide
    sublayers.forEach(sub => {
        if (sub.depthBottom <= z_slide) {
            let midDepth = sub.depthCenter;
            let gammaSoil = (midDepth >= gwt) ? sub.gammaSat : sub.gamma;
            WE += gammaSoil * sub.thickness * B_block;
        }
    });

    let WC = 0;
    let Pa_soil = 0;
    let Pp_soil = 0;

    sublayers.forEach(sub => {
        if (sub.depthCenter > z_slide && sub.depthBottom <= z_slide + bp_depth) {
            let midDepth = sub.depthCenter;
            let gammaSoil = (midDepth >= gwt) ? sub.gammaSat : sub.gamma;
            WC += gammaSoil * sub.thickness * B_block;
            
            let phi_rad = (sub.phi !== undefined ? sub.phi : 5) * Math.PI / 180;
            let Ka = Math.tan(Math.PI/4 - phi_rad/2)**2;
            let c_val = sub.c !== undefined ? sub.c : 10;
            
            let sigma_v = hEmb * gammaEmb + qSurcharge + sub.sigmaV0;
            let pa_z = Ka * sigma_v - 2 * c_val * Math.sqrt(Ka);
            if (pa_z < 0) pa_z = 0;
            Pa_soil += pa_z * sub.thickness;
            
            let Kp = Math.tan(Math.PI/4 + phi_rad/2)**2;
            let sigma_v_pass = sub.sigmaV0;
            let pp_z = Kp * sigma_v_pass + 2 * c_val * Math.sqrt(Kp);
            Pp_soil += pp_z * sub.thickness;
        }
    });

    let Pa_soil_above = 0;
    sublayers.forEach(sub => {
        if (sub.depthBottom <= z_slide) {
            let phi_rad = (sub.phi !== undefined ? sub.phi : 5) * Math.PI / 180;
            let Ka = Math.tan(Math.PI/4 - phi_rad/2)**2;
            let c_val = sub.c !== undefined ? sub.c : 10;
            let sigma_v = hEmb * gammaEmb + qSurcharge + sub.sigmaV0;
            let pa_z = Ka * sigma_v - 2 * c_val * Math.sqrt(Ka);
            if (pa_z < 0) pa_z = 0;
            Pa_soil_above += pa_z * sub.thickness;
        }
    });

    let phi_p = appState.bambooPile.phi !== undefined ? appState.bambooPile.phi : 30.0;
    let sub_at_slide = sublayers.find(sub => sub.depthCenter > z_slide);
    let phi_soil_slide = sub_at_slide ? (sub_at_slide.phi !== undefined ? sub_at_slide.phi : 5) : 5;
    let phi_comp_slide = m * phi_p + (1 - m) * phi_soil_slide;
    let phiRad_slide = phi_comp_slide * Math.PI / 180;

    let P_drive_above = Pa_emb + Pa_soil_above;
    let P_drive_total = P_drive_above + Pa_soil;
    let R_resist_total = Pp_soil + (WE + WC) * Math.tan(phiRad_slide);
    let FS_min = P_drive_total > 0 ? (R_resist_total / P_drive_total) : 999;
    
    return {
        B_block: B_block,
        WE: WE,
        WC: WC,
        Pa_emb: P_drive_above,
        Pa_soil: Pa_soil,
        Pp_soil: Pp_soil,
        FS_0: FS_min,
        R_resist_0: R_resist_total,
        P_drive_0: P_drive_total,
        FS_L: FS_min,
        R_resist_L: R_resist_total,
        P_drive_L: P_drive_total,
        FS_min: FS_min
    };
}

function renderCdmVerificationDetails(circular, planar, geotextile, delta_h1, delta_h2) {
    const container = document.getElementById("cdm-verification-content");
    if (!container) return;
    
    let hEmb = appState.embankment.height;
    let gammaEmb = appState.embankment.gamma;
    let bCrest = appState.embankment.width;
    let mSlope = appState.embankment.slope;
    let qSurcharge = appState.foundation.surcharge;
    
    let cdm_d = appState.cdm.diameter;
    let cdm_s = appState.cdm.spacing;
    let cdm_p = appState.cdm.pattern;
    let cdm_qu = appState.cdm.qu * 1000; // kPa
    
    let m = cdm_p === 'triangular' ? 
        (Math.PI * cdm_d * cdm_d) / (2 * Math.sqrt(3) * cdm_s * cdm_s) : 
        (Math.PI * cdm_d * cdm_d) / (4 * cdm_s * cdm_s);
        
    let E_p = appState.cdm.emodulusRatio * cdm_qu; // kPa
    let E_s_top = appState.soilLayers[0] ? (appState.soilLayers[0].E || 3000) : 2000; // kPa
    let E_comp_top = m * E_p + (1 - m) * E_s_top;
    let q_top = hEmb * gammaEmb + qSurcharge;
    let sigma_p_max = q_top * (E_p / E_comp_top); // kPa
    let FS_pile = sigma_p_max > 0 ? (cdm_qu / sigma_p_max) : 999;
    let isCdmPass = FS_pile >= 1.2;
    
    let c_soil = appState.soilLayers[0] ? (appState.soilLayers[0].c !== undefined ? appState.soilLayers[0].c : 10) : 10;
    let phi_soil = appState.soilLayers[0] ? (appState.soilLayers[0].phi !== undefined ? appState.soilLayers[0].phi : 5) : 5;
    let phi_p = appState.cdm.phi !== undefined ? appState.cdm.phi : 30.0;
    let phi_comp_0 = m * phi_p + (1 - m) * phi_soil;
    let phi_rad = phi_soil * Math.PI / 180;
    let Nq = Math.exp(Math.PI * Math.tan(phi_rad)) * Math.pow(Math.tan(Math.PI/4 + phi_rad/2), 2);
    let Nc = (Nq - 1) / (Math.tan(phi_rad) || 1e-9);
    let R_tc = c_soil * Nc + qSurcharge * Nq; // Giá trị Rtc tính gần đúng
    
    let sigma_s_max = q_top * (E_s_top / E_comp_top); // Ứng suất trên đất nền xung quanh cọc
    
    let toeX = bCrest / 2 + mSlope * hEmb;
    let cdm_overWidth = appState.cdm.overWidth !== undefined ? appState.cdm.overWidth : 1.0;
    let cdm_layout = getColumnLayout(toeX, cdm_s, cdm_overWidth);
    let cdm_actualMaxX = cdm_layout.max;
    let actualOverWidth = cdm_actualMaxX - toeX;

    let html = `
        <div class="verification-section">
            <div class="verification-section-title" style="background: rgba(96, 165, 250, 0.15); color: #60a5fa; border: 1px solid rgba(96, 165, 250, 0.3);">
                <span style="font-weight: bold; text-transform: uppercase;">A. Tính toán theo trạng thái giới hạn 1 (TCVN 9906:2014)</span>
            </div>
            
            <h4 style="color: #a5b4fc; margin-top: 10px; margin-bottom: 5px;">1. Kiểm tra ổn định trượt sâu (Trượt trụ tròn)</h4>
            <p><strong>Bố trí cọc CDM:</strong> Khoảng cách cọc S = <b>${cdm_s.toFixed(1)} m</b>. Phạm vi gia cố cọc thực tế rộng hơn đáy đất đắp mỗi bên b<sub>over, thực tế</sub> = <b>${actualOverWidth.toFixed(2)} m</b> (vị trí cọc ngoài cùng cách tim đường x = <b>${cdm_actualMaxX.toFixed(2)} m</b>, chân taluy x<sub>toe</sub> = <b>${toeX.toFixed(2)} m</b>).</p>
            <p>Hệ số an toàn: F<sub>s</sub> = <strong>${circular.FS.toFixed(3)}</strong> &ge; 1.2 &rarr; <strong>${circular.FS >= 1.2 ? '<span style="color:var(--accent-success);">ĐẠT YÊU CẦU</span>' : '<span style="color:var(--accent-danger);">KHÔNG ĐẠT</span>'}</strong></p>
            <p style="font-size: 0.775rem; color: var(--text-secondary); margin-top: 3px;">
                Tâm trượt nguy hiểm nhất: X<sub>c</sub> = ${circular.Xc.toFixed(2)} m, Z<sub>c</sub> = ${circular.Zc.toFixed(2)} m. Bán kính R = ${circular.R.toFixed(2)} m. (Đường cung trượt chi tiết hiển thị trên biểu đồ phía trên).
            </p>
 
            <h4 style="color: #a5b4fc; margin-top: 10px; margin-bottom: 5px;">2. Kiểm tra ổn định trượt phẳng (Mục E.1.3 - Phụ lục E)</h4>
            <p>Xét tại mặt tiếp giáp dưới đáy nền đắp (z = 0):</p>
            <div class="verification-formula">
                F<sub>s</sub> = [P<sub>PS</sub> + (W<sub>E</sub> + W<sub>C</sub>) &middot; tan(&phi;<sub>tđ</sub>')] / [P<sub>AE</sub> + P<sub>AS</sub>] &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(E.6)
            </div>
            <p style="margin-left: 10px; font-size: 0.8rem;">
                Trong đó:<br>
                - W<sub>E</sub>: Trọng lượng nửa khối đắp đè lên vùng gia cố = <strong>${planar.WE.toFixed(1)} kN/m</strong><br>
                - W<sub>C</sub>: Trọng lượng khối cọc gia cố = <strong>${planar.WC.toFixed(1)} kN/m</strong><br>
                - B<sub>block</sub>: Chiều rộng tính toán nửa khối đập = <strong>${planar.B_block.toFixed(1)} m</strong><br>
                - Lực đẩy chủ động: P<sub>AE</sub> = <strong>${planar.Pa_emb.toFixed(1)} kN/m</strong> (đất đắp), P<sub>AS</sub> = <strong>${planar.Pa_soil.toFixed(1)} kN/m</strong> (nền đất)<br>
                - Lực cản bị động ngoài taluy: P<sub>PS</sub> = <strong>${planar.Pp_soil.toFixed(1)} kN/m</strong><br>
                - &phi;<sub>tđ</sub>': Góc ma sát trong tương đương dọc theo mặt trượt z=0 (TCVN 9906:2014 công thức E.3):<br>
                &nbsp;&nbsp;&nbsp;&nbsp;&phi;<sub>tđ</sub>' = ap &middot; &phi;<sub>p</sub> + (1 - ap) &middot; &phi;<sub>soil</sub> = ${m.toFixed(3)} &middot; ${phi_p.toFixed(1)}&deg; + (1 - ${m.toFixed(3)}) &middot; ${phi_soil.toFixed(1)}&deg; = <strong>${phi_comp_0.toFixed(2)}&deg;</strong> (với ap = m = ${m.toFixed(3)}, &phi;<sub>p</sub> = ${phi_p.toFixed(1)}&deg;)
            </p>
            <p>Hệ số an toàn: F<sub>s,sliding</sub> = <strong>${planar.FS_min.toFixed(2)}</strong> &ge; 1.2 &rarr; <strong>${planar.FS_min >= 1.2 ? '<span style="color:var(--accent-success);">ĐẠT YÊU CẦU</span>' : '<span style="color:var(--accent-danger);">KHÔNG ĐẠT</span>'}</strong></p>
        </div>
 
        <div class="verification-section">
            <div class="verification-section-title" style="background: rgba(96, 165, 250, 0.15); color: #60a5fa; border: 1px solid rgba(96, 165, 250, 0.3);">
                <span style="font-weight: bold; text-transform: uppercase;">B. Tính toán theo trạng thái giới hạn 2</span>
            </div>
            
            <h4 style="color: #a5b4fc; margin-top: 10px; margin-bottom: 5px;">1. Điều kiện an toàn về ứng suất</h4>
            
            <p><strong>1.1. Ứng suất tác dụng vào cọc CDM (Công thức E.18)</strong></p>
            <div class="verification-formula">
                &sigma;<sub>p</sub> &le; [q<sub>u</sub>]
            </div>
            <p style="margin-left: 10px;">
                Trong đó:<br>
                - &sigma;<sub>p</sub>: Ứng suất tác dụng vào cọc CDM (kPa). &sigma;<sub>p</sub> = q<sub>top</sub> &middot; (E<sub>p</sub> / E<sub>comp</sub>) = <strong>${sigma_p_max.toFixed(1)} kPa</strong><br>
                - [q<sub>u</sub>]: Cường độ chịu nén cho phép của cọc CDM (kPa). [q<sub>u</sub>] = q<sub>u</sub> / F<sub>s</sub> = ${cdm_qu.toFixed(0)} / 1.2 = <strong>${(cdm_qu/1.2).toFixed(1)} kPa</strong><br>
                &rarr; <strong>${sigma_p_max <= cdm_qu/1.2 ? '<span style="color:var(--accent-success);">ĐẠT YÊU CẦU</span>' : '<span style="color:var(--accent-danger);">KHÔNG ĐẠT</span>'}</strong>
            </p>
 
            <p><strong>1.2. Ứng suất tác dụng vào đất nền xung quanh cọc (Công thức E.19)</strong></p>
            <div class="verification-formula">
                &sigma;<sub>s</sub> &le; R<sub>tc</sub>
            </div>
            <p style="margin-left: 10px;">
                Trong đó:<br>
                - &sigma;<sub>s</sub>: Ứng suất tác dụng vào đất nền (kPa). &sigma;<sub>s</sub> = q<sub>top</sub> &middot; (E<sub>s</sub> / E<sub>comp</sub>) = <strong>${sigma_s_max.toFixed(1)} kPa</strong><br>
                - R<sub>tc</sub>: Cường độ chịu tải tiêu chuẩn của đất nền xung quanh cọc (kPa) được tính theo Phụ lục C - TCVN 9362:2012:<br>
                <div class="verification-formula">
                    R<sub>tc</sub> = c &middot; N<sub>c</sub> + q &middot; N<sub>q</sub> + 0.5 &middot; &gamma; &middot; b &middot; N<sub>&gamma;</sub>
                </div>
                &nbsp;&nbsp;&nbsp;&nbsp;+ c: Lực dính của đất nền = <b>${c_soil.toFixed(1)} kPa</b><br>
                &nbsp;&nbsp;&nbsp;&nbsp;+ q: Tải trọng phụ bên trên mặt đất tự nhiên = hoạt tải xe = <b>${qSurcharge.toFixed(1)} kPa</b><br>
                &nbsp;&nbsp;&nbsp;&nbsp;+ &gamma;: Dung trọng tự nhiên của đất nền = <b>${appState.soilLayers[0]?.gamma?.toFixed(1) || 16.0} kN/m³</b><br>
                &nbsp;&nbsp;&nbsp;&nbsp;+ N<sub>c</sub>, N<sub>q</sub>, N<sub>&gamma;</sub>: Các hệ số sức chịu tải phụ thuộc góc ma sát trong &phi; = ${phi_soil.toFixed(1)}&deg;:<br>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;N<sub>q</sub> = e<sup>&pi;&middot;tan(&phi;)</sup> &middot; tan²(45&deg; + &phi;/2) = <b>${Nq.toFixed(2)}</b><br>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;N<sub>c</sub> = (N<sub>q</sub> - 1) / tan(&phi;) = <b>${Nc.toFixed(2)}</b><br>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;N<sub>&gamma;</sub> = 1.5 &middot; (N<sub>q</sub> - 1) &middot; tan(&phi;) = <b>${(1.5 * (Nq - 1) * Math.tan(phi_rad)).toFixed(2)}</b><br>
                &nbsp;&nbsp;&nbsp;&nbsp;+ b: Chiều rộng móng quy ước (bỏ qua thành phần kích thước móng b trong tính gần đúng)<br>
                &nbsp;&nbsp;&nbsp;&nbsp;&rArr; R<sub>tc</sub> &approx; c &middot; N<sub>c</sub> + q &middot; N<sub>q</sub> = <b>${R_tc.toFixed(1)} kPa</b><br>
                &rarr; <strong>${sigma_s_max <= R_tc ? '<span style="color:var(--accent-success);">ĐẠT YÊU CẦU</span>' : '<span style="color:var(--accent-danger);">KHÔNG ĐẠT</span>'}</strong>
            </p>
 
            <p><strong>1.3. Lực kéo tác dụng vào vải địa kỹ thuật (Công thức E.20)</strong></p>
            <div class="verification-formula">
                T<sub>rp</sub> &le; [T]<sub>rp</sub>
            </div>
            <p style="margin-left: 10px;">
                Trong đó:<br>
                - T<sub>rp</sub>: Lực kéo lớn nhất phát sinh trong vải địa kỹ thuật (kN/m). T<sub>rp</sub> = T<sub>sr</sub> + T<sub>arching</sub><br>
                &nbsp;&nbsp;&nbsp;&nbsp;+ T<sub>sr</sub>: Lực kéo do lực xô ngang của mái taluy hình thang:<br>
                <div class="verification-formula">
                    T<sub>sr</sub> = P<sub>a</sub> = 0.5 &middot; K<sub>a</sub> &middot; &gamma;<sub>đắp</sub> &middot; H<sub>đắp</sub>² + K<sub>a</sub> &middot; q &middot; H<sub>đắp</sub>
                </div>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Với K<sub>a</sub> = tan²(45&deg; - &phi;<sub>đắp</sub>/2) = tan²(45&deg; - 30&deg;/2) = <b>${geotextile.Ka_emb.toFixed(3)}</b><br>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&rArr; T<sub>sr</sub> = <b>${geotextile.T_sr.toFixed(1)} kN/m</b><br>
                &nbsp;&nbsp;&nbsp;&nbsp;+ T<sub>arching</sub>: Lực kéo do hiệu ứng vòm đất giữa các cọc (Hewlett & Randolph / BS 8006):<br>
                <div class="verification-formula">
                    T<sub>arching</sub> = [W<sub>T</sub> &middot; (s - a)] / (2 &middot; a)
                </div>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Với W<sub>T</sub> = &sigma;<sub>s</sub> &middot; (s - a) = <b>${(sigma_s_max * (cdm_s - cdm_d * Math.sqrt(Math.PI/4))).toFixed(1)} kN/m</b>, s = tim cọc = <b>${cdm_s.toFixed(1)} m</b>, a = đầu cọc tương đương = <b>${(cdm_d * Math.sqrt(Math.PI/4)).toFixed(3)} m</b><br>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&rArr; T<sub>arching</sub> = <b>${geotextile.T_arching.toFixed(1)} kN/m</b><br>
                &nbsp;&nbsp;&nbsp;&nbsp;&rArr; Tổng lực kéo yêu cầu: T<sub>rp</sub> = <b>${geotextile.T_req.toFixed(1)} kN/m</b><br>
                - [T]<sub>rp</sub>: Lực kéo cho phép của vải địa kỹ thuật. [T]<sub>rp</sub> = T<sub>ult</sub> / RF = ${geotextile.T_ult.toFixed(0)} / ${geotextile.RF.toFixed(1)} = <strong>${geotextile.T_allow.toFixed(1)} kN/m</strong><br>
                &rarr; <strong>${geotextile.T_req <= geotextile.T_allow ? '<span style="color:var(--accent-success);">ĐẠT YÊU CẦU</span>' : '<span style="color:var(--accent-danger);">KHÔNG ĐẠT</span>'}</strong>
            </p>
 
            <h4 style="color: #a5b4fc; margin-top: 15px; margin-bottom: 5px;">2. Tính toán độ lún (Công thức E.25 - E.28)</h4>
            <div class="verification-formula">
                &Delta;h = &Delta;h<sub>1</sub> + &Delta;h<sub>2</sub> &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(E.25)
            </div>
            
            <p><strong>2.1. Phần lớp đất được gia cố (&Delta;h<sub>1</sub>)</strong></p>
            <div class="verification-formula">
                &Delta;h<sub>1</sub> = (q &middot; H) / (a<sub>p</sub> &middot; E<sub>p</sub> + (1 - a<sub>p</sub>) &middot; E<sub>s</sub>) &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(E.26)
            </div>
            <p style="margin-left: 10px;">
                Trong đó:<br>
                - q: Tải trọng đơn vị tác dụng (kPa)<br>
                - H: Chiều dày lớp đất yếu được gia cố (m)<br>
                - a<sub>p</sub>: Tỷ lệ diện tích gia cố (%) = m = ${m.toFixed(3)}<br>
                - E<sub>p</sub>, E<sub>s</sub>: Mô đun biến dạng của cọc và đất nền (kPa)<br>
                &rarr; &Delta;h<sub>1</sub> = <strong>${delta_h1.toFixed(2)} cm</strong> &le; [&Delta;h<sub>1</sub>] = 8.0 cm &rarr; <strong>${delta_h1 <= 8.0 ? '<span style="color:var(--accent-success);">ĐẠT YÊU CẦU</span>' : '<span style="color:var(--accent-danger);">KHÔNG ĐẠT (Vượt quá 8.0cm)</span>'}</strong>
            </p>
 
            <p><strong>2.2. Phần lớp đất bên dưới lớp gia cố (&Delta;h<sub>2</sub>)</strong></p>
            <div class="verification-formula">
                &Delta;h<sub>2</sub> = &Sigma; [ (Q<sub>c</sub> / (1 + e<sub>0</sub>)) &middot; H' &middot; lg((&sigma;'<sub>0</sub> + q') / &sigma;'<sub>0</sub>) ] &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(E.27)
            </div>
            <div class="verification-formula">
                q' = (q &middot; B) / (B + H'/2) &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(E.28)
            </div>
            <p style="margin-left: 10px;">
                Trong đó:<br>
                - Q<sub>c</sub>, e<sub>0</sub>: Chỉ số nén và hệ số rỗng của lớp đất yếu dưới mũi cọc<br>
                - H': Chiều dày phân lớp đất yếu không được gia cố (m)<br>
                - &sigma;'<sub>0</sub>: Ứng suất địa tầng hữu hiệu (kPa)<br>
                - q': Tải trọng tác dụng lên lớp đất yếu dưới mũi cọc (kPa)<br>
                - B: Chiều rộng đáy khối đắp (m)<br>
                &rarr; &Delta;h<sub>2</sub> = <strong>${delta_h2.toFixed(2)} cm</strong>
            </p>
            <p style="font-size: 1rem; color: #a5b4fc; text-align: center; margin-top: 10px; border-top: 1px dashed var(--border-color); padding-top: 10px;">
                <strong>Tổng độ lún &Delta;h = ${delta_h1.toFixed(2)} + ${delta_h2.toFixed(2)} = ${(delta_h1 + delta_h2).toFixed(2)} cm</strong>
            </p>
        </div>
    `;
    
    // Convert backslashes representation for clean inline reading
    html = html.replace(/\\frac{([^}]+)}{([^}]+)}/g, "($1)/($2)");
    html = html.replace(/\\cdot/g, "&middot;");
    html = html.replace(/\\sigma/g, "&sigma;");
    html = html.replace(/\\gamma/g, "&gamma;");
    html = html.replace(/\\pi/g, "&pi;");
    html = html.replace(/\\ge/g, "&ge;");
    html = html.replace(/\\le/g, "&le;");
    html = html.replace(/\\times/g, "&times;");
    html = html.replace(/\\approx/g, "&approx;");
    html = html.replace(/\\rarr/g, "&rarr;");
    
    container.innerHTML = html;
    
    // Gọi vẽ biểu đồ ổn định cung trượt tròn riêng
    setTimeout(() => {
        drawSlipCircleChart(circular, getEffectiveSoilLayers());
    }, 50);
}


// ==========================================
// WORD DOCUMENT REPORT GENERATOR (.doc)
// ==========================================
function exportWord() {
    let title = "BÁO CÁO KẾT QUẢ TÍNH TOÁN LÚN VÀ GIA CỐ";
    
    function formatReportTable(rows) {
        let html = '<table border="0" cellspacing="0" cellpadding="4" style="width:100%; border:none; font-family:\'Times New Roman\'; font-size:11pt; margin-bottom:12px; border-collapse:collapse;">';
        rows.forEach(row => {
            if (row.isHeader) {
                html += `
                    <tr style="background-color:#f1f5f9; font-weight:bold;">
                        <td colspan="2" style="text-align:left; padding:6px 10px; border:1px solid #cbd5e1; font-size:11pt; color:#1e293b;">${row.label}</td>
                    </tr>
                `;
            } else {
                html += `
                    <tr style="border-bottom:1px dotted #cbd5e1;">
                        <td style="text-align:left; padding:5px 8px; border:none; color:#334155; font-size:11pt;">${row.label}</td>
                        <td style="text-align:right; padding:5px 8px; border:none; font-weight:bold; color:#0f172a; white-space:nowrap; font-size:11pt;">${row.value}</td>
                    </tr>
                `;
            }
        });
        html += '</table>';
        return html;
    }
    
    function formatSymbolTable(rows) {
        let html = '<table border="1" cellspacing="0" cellpadding="5" style="border-collapse:collapse; width:100%; font-family:\'Times New Roman\'; font-size:10pt; margin-bottom:12px;">';
        html += `
            <thead>
                <tr style="background-color:#f1f5f9; font-weight:bold; font-size:10pt;">
                    <th style="width:15%; text-align:center;">Ký hiệu</th>
                    <th style="width:15%; text-align:center;">Thứ nguyên</th>
                    <th style="width:50%; text-align:left;">Ý nghĩa ký hiệu</th>
                    <th style="width:20%; text-align:right;">Giá trị</th>
                </tr>
            </thead>
            <tbody>
        `;
        rows.forEach(row => {
            html += `
                <tr>
                    <td style="text-align:center; font-weight:bold; font-size:10pt;">${row.symbol}</td>
                    <td style="text-align:center; font-size:10pt;">${row.unit}</td>
                    <td style="text-align:left; font-size:10pt;">${row.meaning}</td>
                    <td style="text-align:right; font-weight:bold; font-size:10pt;">${row.value}</td>
                </tr>
            `;
        });
        html += '</tbody></table>';
        return html;
    }
    
    // Temporarily switch to white-background printing mode
    isPrinting = true;
    runAnalysis(); // Synchronously redraws the 4 main charts with white background
    
    // Draw the slip circle chart synchronously with isPrinting = true if needed.
    if (appState.lastVerification && appState.lastVerification.circular) {
        drawSlipCircleChart(appState.lastVerification.circular, getEffectiveSoilLayers());
    }
    
    let soilCanvas = document.getElementById("soil-canvas");
    let stressCanvas = document.getElementById("chart-stress");
    let timeCanvas = document.getElementById("chart-time");
    let depthCanvas = document.getElementById("chart-depth");
    let slipCanvas = document.getElementById("slip-circle-canvas");

    let soilImg = soilCanvas ? soilCanvas.toDataURL("image/png") : "";
    let stressImg = stressCanvas ? stressCanvas.toDataURL("image/png") : "";
    let timeImg = timeCanvas ? timeCanvas.toDataURL("image/png") : "";
    let depthImg = depthCanvas ? depthCanvas.toDataURL("image/png") : "";
    let treatment = appState.treatment.method;
    let isPvdTcvn = ((treatment === 'pvd' || treatment === 'vacuum_pvd') && appState.pvd.standard === 'tcvn_9355');
    let slipImg = (slipCanvas && (treatment === 'cdm' || treatment === 'bamboo_pile' || treatment === 'bamboo_replacement' || treatment === 'excavation_replacement' || isPvdTcvn)) ? slipCanvas.toDataURL("image/png") : "";

    // Restore original view
    isPrinting = false;
    runAnalysis(); // Redraws everything back to the dark theme screen display

    // Build soil layers table HTML
    let soilTableHTML = `
        <table border="1" cellspacing="0" cellpadding="5" style="border-collapse:collapse; width:100%; font-family:'Times New Roman'; font-size:11pt; margin-top:10px;">
            <thead>
                <tr style="background-color:#e2e8f0; font-weight:bold;">
                    <th>Tên lớp đất yếu</th>
                    <th>Loại đất</th>
                    <th>Dày H (m)</th>
                    <th>&gamma; (kN/m³)</th>
                    <th>&gamma;<sub>sat</sub> (kN/m³)</th>
                    <th>e<sub>0</sub></th>
                    <th>C<sub>c</sub></th>
                    <th>C<sub>r</sub></th>
                    <th>&sigma;'<sub>p</sub> (kPa)</th>
                    <th>E<sub>s</sub> (kPa)</th>
                    <th>c (kPa)</th>
                    <th>&phi; (°)</th>
                    <th>C<sub>v</sub> (m²/năm)</th>
                    <th>C<sub>&alpha;</sub></th>
                </tr>
            </thead>
            <tbody>
    `;
    appState.soilLayers.forEach(layer => {
        let isGranular = layer.type === 'granular';
        soilTableHTML += `
            <tr>
                <td>${escapeHtml(layer.name)}</td>
                <td>${isGranular ? 'Đất rời' : 'Đất dính'}</td>
                <td style="text-align:right;">${layer.thickness.toFixed(2)}</td>
                <td style="text-align:right;">${layer.gamma.toFixed(1)}</td>
                <td style="text-align:right;">${layer.gammaSat.toFixed(1)}</td>
                <td style="text-align:right;">${isGranular ? '-' : layer.e0.toFixed(2)}</td>
                <td style="text-align:right;">${isGranular ? '-' : layer.Cc.toFixed(3)}</td>
                <td style="text-align:right;">${isGranular ? '-' : layer.Cr.toFixed(3)}</td>
                <td style="text-align:right;">${isGranular ? '-' : layer.sigmaP.toFixed(1)}</td>
                <td style="text-align:right;">${layer.E || 3000}</td>
                <td style="text-align:right;">${layer.c !== undefined ? layer.c : 10}</td>
                <td style="text-align:right;">${layer.phi !== undefined ? layer.phi : 5}</td>
                <td style="text-align:right;">${isGranular ? '-' : layer.Cv.toFixed(2)}</td>
                <td style="text-align:right;">${isGranular ? '-' : layer.Ca.toFixed(4)}</td>
            </tr>
        `;
    });
    soilTableHTML += `</tbody></table>`;

    // Summary results
    const reportResult = appState.lastAnalysis;
    if (!reportResult || !reportResult.valid) {
        showNotification("Không thể xuất Word khi kết quả tính chưa hợp lệ.", "error");
        return;
    }
    let s_total = formatDecimal(reportResult.total, 2);
    let s_c = formatDecimal(reportResult.consolidation, 2);
    let s_i = formatDecimal(reportResult.immediate, 2);
    let s_s = formatDecimal(reportResult.secondary, 2);
    let u_t = formatDecimal(reportResult.uHandover, 2) + "%";
    let s_rem = formatDecimal(reportResult.remaining, 2);
    let allowable = appState.foundation.allowable;
    
    treatment = appState.treatment.method;
    let treatmentText = "Không xử lý (Để tự nhiên)";
    if (treatment === 'pvd' || treatment === 'vacuum_pvd') treatmentText = "Cắm bấc thấm (PVD)";
    else if (treatment === 'sand_drain') treatmentText = "Giếng cát (Sand Drain)";
    else if (treatment === 'cdm') treatmentText = "Cọc xi măng đất (CDM)";
    else if (treatment === 'sand_column') treatmentText = "Cọc cát / Cọc vật liệu rời";
    else if (treatment === 'bamboo_pile') treatmentText = "Gia cố cọc tre (Bamboo Pile)";
    else if (treatment === 'excavation_replacement') treatmentText = "Đào thay đất (Thay đệm cát)";
    else if (treatment === 'bamboo_replacement') treatmentText = "Đóng cọc tre + Thay đất";

    let clayThickness = 0;
    appState.soilLayers.forEach(layer => {
        if (layer.type === 'cohesive' || layer.Cc > 0.05) {
            clayThickness += layer.thickness;
        }
    });
    let H_d = clayThickness > 0 ? getDrainagePath(clayThickness) : 5.0;

    let verificationHTML = "";
    if (treatment === 'none') {
        verificationHTML = `
            <h2>4. THUYẾT MINH LÝ THUYẾT TÍNH LÚN TỰ NHIÊN & CỐ KẾT (TCCS 41:2022)</h2>
            <p>Độ lún tổng cộng cuối cùng được xác định theo phương pháp cộng lún phân tầng:</p>
            <p align="center"><b>S = m &middot; (S<sub>i</sub> + S<sub>c</sub> + S<sub>s</sub>)</b></p>
            <p>Trong đó:</p>
            ${formatReportTable([
                { label: "- Độ lún tức thời (đàn hồi) (S<sub>i</sub>)", value: `${s_i} cm` },
                { label: "- Độ lún cố kết sơ cấp (S<sub>c</sub>)", value: `${s_c} cm` },
                { label: "- Độ lún từ biến thứ cấp (S<sub>s</sub>)", value: `${s_s} cm` },
                { label: "- Hệ số kinh nghiệm hiệu chỉnh độ lún (m)", value: appState.foundation.mFactor.toFixed(2) }
            ])}
            <h3>4.1. Lý thuyết cố kết Terzaghi 1 chiều (Thoát nước đứng)</h3>
            <p>Độ cố kết đứng trung bình U<sub>v</sub> tại thời điểm t được tính dựa trên nhân tố thời gian đứng T<sub>v</sub> = C<sub>v</sub> &middot; t / H<sub>d</sub>²:</p>
            ${formatReportTable([
                { label: "Chiều dài đường thoát nước H<sub>d</sub> = H / 2 (thoát nước 2 phía)", value: `${H_d.toFixed(2)} m` }
            ])}
        `;
    } else if (treatment === 'pvd' || treatment === 'vacuum_pvd') {
        let pvd = appState.pvd;
        let d_w = (2 * (pvd.width + pvd.thickness)) / 1000 / Math.PI; // m
        let d_e = pvd.pattern === 'triangular' ? (1.05 * pvd.spacing) : (1.13 * pvd.spacing); // m
        let nRatio = d_e / d_w;
        if (nRatio < 1.01) nRatio = 1.01;
        
        let isTcvn = (pvd.standard === 'tcvn_9355');
        let Fn = 0;
        let Fs = 0;
        let Fr = 0;
        let F_total = 0;
        let kh_s = 0;
        
        let dsDw = pvd.dsDw || 2.5;
        let khKs = pvd.khKs || 3.0;
        
        let avgCv = getThicknessWeightedCv(appState.soilLayers);
        let Ch = avgCv * pvd.chRatio;
        
        if (isTcvn) {
            Fn = (Math.pow(nRatio, 2) / (Math.pow(nRatio, 2) - 1)) * Math.log(nRatio) - (3 * Math.pow(nRatio, 2) - 1) / (4 * Math.pow(nRatio, 2));
            Fs = (khKs - 1) * Math.log(dsDw);
            
            let avgEs = 3000;
            if (appState.soilLayers && appState.soilLayers.length > 0) {
                let cohesiveLayers = appState.soilLayers.filter(l => l.type === 'cohesive' || l.Cc > 0.05);
                if (cohesiveLayers.length > 0) {
                    avgEs = cohesiveLayers.reduce((acc, l) => acc + (l.E || 3000), 0) / cohesiveLayers.length;
                } else {
                    avgEs = appState.soilLayers.reduce((acc, l) => acc + (l.E || 3000), 0) / appState.soilLayers.length;
                }
            }
            if (avgEs <= 0) avgEs = 3000;
            
            let kh_year = Ch * (1.0 / avgEs) * WATER_UNIT_WEIGHT;
            kh_s = kh_year / (3.1536 * 1e7); // m/s
            let qw_m3s = (pvd.qw || 50.0) * 1e-6; // cm3/s to m3/s
            Fr = (2.0 / 3.0) * Math.PI * Math.pow(pvd.depth, 2) * (kh_s / qw_m3s);
            F_total = Fn + Fs + Fr;
        } else {
            Fn = Math.log(nRatio) - 0.75;
            F_total = Fn;
        }

        if (isTcvn) {
            verificationHTML = `
                <h2>4. THUYẾT MINH LÝ THUYẾT TÍNH LÚN & CỐ KẾT BẤC THẤM (PVD) (TCVN 9355:2012)</h2>
                <p>Khi sử dụng biện pháp cắm bấc thấm, nước lỗ rỗng thoát ra theo cả hai phương: đứng và hướng tâm. Độ cố kết tổng hợp U(t) tại thời điểm t được tính theo công thức Barron - Hansbo:</p>
                <p align="center"><b>U(t) = 1 - (1 - U<sub>v</sub>) &middot; (1 - U<sub>r</sub>)</b></p>
                <p>Độ cố kết hướng tâm U<sub>r</sub> do bấc thấm phát triển theo thời gian:</p>
                <p align="center"><b>U<sub>r</sub> = 1 - exp(-8 &middot; T<sub>r</sub> / F)</b></p>
                <p>Với hệ số cản tổng hợp F = F<sub>n</sub> + F<sub>s</sub> + F<sub>r</sub> theo TCVN 9355:2012:</p>
                ${formatReportTable([
                    { label: "Bản rộng bấc b", value: `${pvd.width} mm` },
                    { label: "Độ dày bấc t", value: `${pvd.thickness} mm` },
                    { label: "Đường kính tương đương d<sub>w</sub> = 2&middot;(b+t)/&pi;", value: `${(d_w * 100).toFixed(2)} cm` },
                    { label: `Kiểu bố trí lưới (${pvd.pattern === 'triangular' ? 'Tam giác d<sub>e</sub> = 1.05&middot;S' : 'Ô cờ d<sub>e</sub> = 1.13&middot;S'})`, value: `${pvd.pattern === 'triangular' ? 'Tam giác' : 'Ô cờ'}` },
                    { label: "Khoảng cách bấc S", value: `${pvd.spacing} m` },
                    { label: "Đường kính vùng ảnh hưởng d<sub>e</sub>", value: `${d_e.toFixed(3)} m` },
                    { label: "Hệ số khoảng cách F<sub>n</sub> (Hansbo)", value: Fn.toFixed(4) },
                    { label: "Hệ số xáo động F<sub>s</sub> (k<sub>h</sub>/k<sub>s</sub> = " + khKs.toFixed(1) + ", d<sub>s</sub>/d<sub>w</sub> = " + dsDw.toFixed(1) + ")", value: Fs.toFixed(4) },
                    { label: "Hệ số trở lực giếng bấc F<sub>r</sub> (q<sub>w</sub> = " + pvd.qw.toFixed(1) + " cm³/s, k<sub>h</sub> &approx; " + kh_s.toExponential(3) + " m/s)", value: Fr.toFixed(4) },
                    { label: "Hệ số cản tổng cộng F = F<sub>n</sub> + F<sub>s</sub> + F<sub>r</sub>", value: F_total.toFixed(4) },
                    { label: "Tỷ lệ hệ số cố kết ngang và đứng C<sub>h</sub>/C<sub>v</sub>", value: pvd.chRatio.toFixed(1) }
                ])}
                <p>Ảnh hưởng chiều sâu cắm bấc thấm L = ${pvd.depth} m:</p>
                <ul>
                    <li>Phía trên mũi bấc (z &le; L): Thoát nước kết hợp U<sub>combined</sub>.</li>
                    <li>Phía dưới mũi bấc (z &gt; L): Thoát nước đứng tự nhiên U<sub>v</sub>.</li>
                </ul>
            `;
        } else {
            verificationHTML = `
                <h2>4. THUYẾT MINH LÝ THUYẾT TÍNH LÚN & CỐ KẾT BẤC THẤM (PVD) (TCCS 41:2022)</h2>
                <p>Khi sử dụng biện pháp cắm bấc thấm theo TCCS 41:2022, nước lỗ rỗng thoát ra theo cả hai phương: đứng và hướng tâm. Độ cố kết tổng hợp U(t) tại thời điểm t được tính theo công thức:</p>
                <p align="center"><b>U(t) = 1 - (1 - U<sub>v</sub>) &middot; (1 - U<sub>r</sub>)</b></p>
                <p>Độ cố kết hướng tâm U<sub>r</sub> do bấc thấm phát triển theo thời gian:</p>
                <p align="center"><b>U<sub>r</sub> = 1 - exp(-8 &middot; T<sub>r</sub> / F<sub>n</sub>)</b></p>
                <p>Với các thông số tính toán:</p>
                ${formatReportTable([
                    { label: "Bản rộng bấc b", value: `${pvd.width} mm` },
                    { label: "Độ dày bấc t", value: `${pvd.thickness} mm` },
                    { label: "Đường kính tương đương d<sub>w</sub> = 2&middot;(b+t)/&pi;", value: `${(d_w * 100).toFixed(2)} cm` },
                    { label: `Kiểu bố trí lưới (${pvd.pattern === 'triangular' ? 'Tam giác d<sub>e</sub> = 1.05&middot;S' : 'Ô cờ d<sub>e</sub> = 1.13&middot;S'})`, value: `${pvd.pattern === 'triangular' ? 'Tam giác' : 'Ô cờ'}` },
                    { label: "Khoảng cách bấc S", value: `${pvd.spacing} m` },
                    { label: "Đường kính vùng ảnh hưởng d<sub>e</sub>", value: `${d_e.toFixed(3)} m` },
                    { label: "Hệ số khoảng cách F<sub>n</sub>", value: Fn.toFixed(4) },
                    { label: "Tỷ lệ hệ số cố kết ngang và đứng C<sub>h</sub>/C<sub>v</sub>", value: pvd.chRatio.toFixed(1) }
                ])}
                <p>Ảnh hưởng chiều sâu cắm bấc thấm L = ${pvd.depth} m:</p>
                <ul>
                    <li>Phía trên mũi bấc (z &le; L): Thoát nước kết hợp U<sub>combined</sub>.</li>
                    <li>Phía dưới mũi bấc (z &gt; L): Thoát nước đứng tự nhiên U<sub>v</sub>.</li>
                </ul>
            `;
        }
        
        if (isTcvn) {
            let circCons = appState.lastVerification && appState.lastVerification.circular ? appState.lastVerification.circular : { FS: 1.2, Xc: 0, Zc: 0, R: 10 };
            let circInit = appState.lastVerification && appState.lastVerification.circularInitial ? appState.lastVerification.circularInitial : { FS: 1.0, Xc: 0, Zc: 0, R: 10 };
            
            verificationHTML += `
                <h3>4.2. Kiểm tra ổn định nền đắp trên đất yếu (Phụ lục C - TCVN 9355:2012)</h3>
                <p>Ổn định tổng thể chống trượt sâu của nền đắp được tính toán theo phương pháp phân tích lát cắt Fellenius. Dưới tác dụng của cố kết, lực dính không thoát nước tăng lên theo thời gian:</p>
                <p align="center" style="font-size:12pt; font-weight:bold; margin: 10px 0;">
                    c<sub>t</sub> = c<sub>0</sub> + U &middot; &Delta;&sigma;<sub>v</sub> &middot; tan(&phi;<sub>0</sub>)
                </p>
                <p>Kết quả kiểm toán ổn định chống trượt sâu:</p>
                ${formatSymbolTable([
                    { symbol: "F<sub>s,0</sub>", unit: "Không", meaning: "Hệ số an toàn lúc chưa cố kết (t=0, U=0)", value: circInit.FS.toFixed(3) },
                    { symbol: "F<sub>s,t</sub>", unit: "Không", meaning: "Hệ số an toàn sau cố kết tại thời điểm t = " + appState.foundation.tConstruction + " năm", value: circCons.FS.toFixed(3) },
                    { symbol: "[F<sub>s</sub>]", unit: "Không", meaning: "Hệ số an toàn chống trượt sâu yêu cầu tiêu chuẩn", value: "1.200" }
                ])}
                <p>Đánh giá ổn định: Hệ số an toàn sau cố kết F<sub>s,t</sub> = <b>${circCons.FS.toFixed(3)}</b> &ge; [F<sub>s</sub>] = 1.200 &rarr; <b>${circCons.FS >= 1.2 ? '<span style="color:green;font-weight:bold;">ĐẠT YÊU CẦU</span>' : '<span style="color:red;font-weight:bold;">KHÔNG ĐẠT YÊU CẦU</span>'}</b></p>
                ${slipImg ? `
                <p align="center">
                    <img src="${slipImg}" width="420" style="max-width:100%; height:auto;"><br>
                    <i>Hình 5. Bản vẽ cung trượt tròn ổn định trượt sâu sau cố kết (Gia cố PVD)</i>
                </p>
                ` : ''}
            `;
        }
    } else if (treatment === 'cdm') {
        let cdm_qu = appState.cdm.qu * 1000; // kPa
        let E_p = appState.cdm.emodulusRatio * cdm_qu; // kPa
        let cdm_d = appState.cdm.diameter;
        let cdm_s = appState.cdm.spacing;
        let m = appState.cdm.pattern === 'triangular' ? 
            (Math.PI * cdm_d * cdm_d) / (2 * Math.sqrt(3) * cdm_s * cdm_s) : 
            (Math.PI * cdm_d * cdm_d) / (4 * cdm_s * cdm_s);
        
        let c_soil = appState.soilLayers[0] ? (appState.soilLayers[0].c !== undefined ? appState.soilLayers[0].c : 10) : 10;
        let phi_soil = appState.soilLayers[0] ? (appState.soilLayers[0].phi !== undefined ? appState.soilLayers[0].phi : 5) : 5;
        let E_s_top = 3000; // kPa
        let E_comp_top = m * E_p + (1 - m) * E_s_top;
        
        let q_top = appState.embankment.height * appState.embankment.gamma + appState.foundation.surcharge;
        let sigma_p_max = q_top * (E_p / E_comp_top);
        let sigma_s_max = q_top * (E_s_top / E_comp_top);
        
        // compute Nc, Nq for R_tc
        let phi_rad = phi_soil * Math.PI / 180;
        let Nq = Math.exp(Math.PI * Math.tan(phi_rad)) * Math.pow(Math.tan(Math.PI / 4 + phi_rad / 2), 2);
        let Nc = (Nq - 1) / Math.tan(phi_rad || 0.0001);
        let qSurcharge = appState.foundation.surcharge;
        let R_tc = c_soil * Nc + qSurcharge * Nq; // simplified
        
        let circ = appState.lastVerification && appState.lastVerification.circular ? appState.lastVerification.circular : { FS: 1.2, Xc: 0, Zc: 0, R: 10 };
        let plan = appState.lastVerification && appState.lastVerification.planar ? appState.lastVerification.planar : { FS_min: 1.2, WE: 100, WC: 200, B_block: 5, Pa_emb: 50, Pa_soil: 60, Pp_soil: 30 };
        let geo = appState.lastVerification && appState.lastVerification.geotextile ? appState.lastVerification.geotextile : { T_req: 20, T_allow: 50, T_sr: 10, T_arching: 10, T_ult: 100, RF: 2.0, Ka_emb: 0.3, W_T: 20, a: 0.7 };
        let delta_h1 = appState.lastVerification ? appState.lastVerification.delta_h1 : 0;
        let delta_h2 = appState.lastVerification ? appState.lastVerification.delta_h2 : 0;
        
        let phi_p = appState.cdm.phi !== undefined ? appState.cdm.phi : 30.0;
        let phi_comp_0 = (m * phi_p) + (1 - m) * phi_soil;

        verificationHTML = `
            <h2>4. THUYẾT MINH KIỂM TOÁN SƠ BỘ CỌC XI MĂNG ĐẤT (CDM) THEO TCVN 9403:2012</h2>
            
            <h3>A. TÍNH TOÁN THEO TRẠNG THÁI GIỚI HẠN 1 (ỔN ĐỊNH NỀN GIA CỐ)</h3>
            
            <h4>1. Kiểm tra ổn định trượt sâu (Trượt trụ tròn)</h4>
            <p>Phương pháp phân tích ổn định trượt sâu theo Fellenius. Cung trượt đi qua cả vùng nền gia cố và nền đất yếu dưới mũi cọc được kiểm tra để tìm hệ số an toàn nhỏ nhất:</p>
            <p align="center" style="font-size:12pt; font-weight:bold; margin: 10px 0;">
                F<sub>s</sub> = &Sigma; [ c'<sub>tđ,i</sub> &middot; l<sub>i</sub> + W<sub>i</sub> &middot; cos(&alpha;<sub>i</sub>) &middot; tan(&phi;'<sub>tđ,i</sub>) ] / &Sigma; [ W<sub>i</sub> &middot; sin(&alpha;<sub>i</sub>) ] &ge; [F<sub>s</sub>] = 1.2
            </p>
            <p>Trong đó:</p>
            ${formatSymbolTable([
                { symbol: "F<sub>s</sub>", unit: "Không", meaning: "Hệ số an toàn ổn định chống trượt sâu thực tế thu được", value: circ.FS.toFixed(3) },
                { symbol: "[F<sub>s</sub>]", unit: "Không", meaning: "Hệ số an toàn chống trượt sâu yêu cầu tiêu chuẩn", value: "1.200" },
                { symbol: "W<sub>i</sub>", unit: "kN/m", meaning: "Trọng lượng phân tố thứ i kể cả hoạt tải phụ tải", value: "Tính tự động" },
                { symbol: "&alpha;<sub>i</sub>", unit: "độ", meaning: "Góc nghiêng đáy phân tố thứ i so với phương ngang", value: "Tính tự động" },
                { symbol: "l<sub>i</sub>", unit: "m", meaning: "Chiều dài đáy phân tố thứ i", value: "Tính tự động" },
                { symbol: "c'<sub>tđ,i</sub>", unit: "kPa", meaning: "Lực dính tương đương tại đáy phân tố thứ i: c'<sub>tđ</sub> = m&middot;c<sub>p</sub> + (1-m)&middot;c<sub>s</sub>", value: `${(m * (cdm_qu / 2) + (1 - m) * c_soil).toFixed(1)} (vùng cọc)` },
                { symbol: "&phi;'<sub>tđ,i</sub>", unit: "độ", meaning: "Góc ma sát trong tương đương tại đáy phân tố thứ i: tan(&phi;'<sub>tđ</sub>) = m&middot;tan(&phi;<sub>p</sub>) + (1-m)&middot;tan(&phi;<sub>s</sub>)", value: `${phi_comp_0.toFixed(2)}&deg; (vùng cọc)` },
                { symbol: "m", unit: "Không", meaning: "Hệ số thay thế diện tích cọc CDM", value: m.toFixed(3) },
                { symbol: "c<sub>p</sub>", unit: "kPa", meaning: "Lực dính hiệu dụng cọc CDM (c<sub>p</sub> = q<sub>u</sub>/2)", value: (cdm_qu/2).toFixed(1) },
                { symbol: "c<sub>s</sub>", unit: "kPa", meaning: "Lực dính của đất nền xung quanh cọc", value: c_soil.toFixed(1) },
                { symbol: "&phi;<sub>p</sub>", unit: "độ", meaning: "Góc ma sát trong của cọc CDM", value: phi_p.toFixed(1) },
                { symbol: "&phi;<sub>s</sub>", unit: "độ", meaning: "Góc ma sát trong của đất nền xung quanh cọc", value: phi_soil.toFixed(1) }
            ])}
            <p>Đánh giá: Hệ số an toàn F<sub>s</sub> = <b>${circ.FS.toFixed(3)}</b> &ge; [F<sub>s</sub>] = 1.200 &rarr; <b>${circ.FS >= 1.2 ? '<span style="color:green;font-weight:bold;">ĐẠT YÊU CẦU</span>' : '<span style="color:red;font-weight:bold;">KHÔNG ĐẠT YÊU CẦU</span>'}</b></p>
            ${slipImg ? `
            <p align="center">
                <img src="${slipImg}" width="420" style="max-width:100%; height:auto;"><br>
                <i>Hình 5. Bản vẽ cung trượt tròn ổn định trượt sâu nguy hiểm nhất (Gia cố CDM)</i>
            </p>
            ` : ''}

            <h4>2. Kiểm tra ổn định trượt phẳng (Mục E.1.3 - Phụ lục E)</h4>
            <p>Xét ổn định trượt phẳng của khối gia cố dưới áp lực chủ động ngang tại tiếp diện đáy đắp (z = 0) theo công thức:</p>
            <p align="center" style="font-size:12pt; font-weight:bold; margin: 10px 0;">
                F<sub>s,sliding</sub> = [ P<sub>PS</sub> + (W<sub>E</sub> + W<sub>C</sub>) &middot; tan(&phi;'<sub>tđ</sub>) ] / [ P<sub>AE</sub> + P<sub>AS</sub> ] &ge; [F<sub>s</sub>] = 1.2
            </p>
            <p>Trong đó:</p>
            ${formatSymbolTable([
                { symbol: "F<sub>s,sliding</sub>", unit: "Không", meaning: "Hệ số an toàn ổn định chống trượt phẳng thực tế", value: plan.FS_min.toFixed(2) },
                { symbol: "[F<sub>s</sub>]", unit: "Không", meaning: "Hệ số an toàn chống trượt phẳng yêu cầu tiêu chuẩn", value: "1.20" },
                { symbol: "W<sub>E</sub>", unit: "kN/m", meaning: "Trọng lượng nửa khối đắp đè lên vùng gia cố cọc", value: plan.WE.toFixed(1) },
                { symbol: "W<sub>C</sub>", unit: "kN/m", meaning: "Trọng lượng khối đất nền gia cố cọc CDM", value: plan.WC.toFixed(1) },
                { symbol: "B<sub>block</sub>", unit: "m", meaning: "Chiều rộng nửa khối đắp tính toán (phạm vi gia cố cọc)", value: plan.B_block.toFixed(1) },
                { symbol: "P<sub>AE</sub>", unit: "kN/m", meaning: "Áp lực chủ động ngang của đất đắp tại tim đường", value: plan.Pa_emb.toFixed(1) },
                { symbol: "P<sub>AS</sub>", unit: "kN/m", meaning: "Áp lực chủ động ngang của đất nền ngoài khối gia cố", value: plan.Pa_soil.toFixed(1) },
                { symbol: "P<sub>PS</sub>", unit: "kN/m", meaning: "Áp lực bị động đất nền phía ngoài mái taluy cản trượt", value: plan.Pp_soil.toFixed(1) },
                { symbol: "&phi;'<sub>tđ</sub>", unit: "độ", meaning: "Góc ma sát trong tương đương tại đáy phẳng: &phi;'<sub>tđ</sub> = m&middot;&phi;<sub>p</sub> + (1-m)&middot;&phi;<sub>s</sub>", value: phi_comp_0.toFixed(2) }
            ])}
            <p>Đánh giá: Hệ số an toàn F<sub>s,sliding</sub> = <b>${plan.FS_min.toFixed(2)}</b> &ge; [F<sub>s</sub>] = 1.20 &rarr; <b>${plan.FS_min >= 1.2 ? '<span style="color:green;font-weight:bold;">ĐẠT YÊU CẦU</span>' : '<span style="color:red;font-weight:bold;">KHÔNG ĐẠT YÊU CẦU</span>'}</b></p>
 
            <h3>B. TÍNH TOÁN THEO TRẠNG THÁI GIỚI HẠN 2 (ỨNG SUẤT VÀ BIẾN DẠNG LÚN)</h3>
            
            <h4>1. Điều kiện an toàn về ứng suất</h4>
            
            <p><b>1.1. Ứng suất tác dụng vào cọc CDM (Công thức E.18)</b></p>
            <p align="center" style="font-size:12pt; font-weight:bold; margin: 10px 0;">
                &sigma;<sub>p</sub> = q<sub>top</sub> &middot; ( E<sub>p</sub> / E<sub>comp</sub> ) &le; [q<sub>u</sub>]
            </p>
            <p>Trong đó:</p>
            ${formatSymbolTable([
                { symbol: "&sigma;<sub>p</sub>", unit: "kPa", meaning: "Ứng suất nén lớn nhất tác dụng vào đầu cọc CDM thực tế", value: sigma_p_max.toFixed(1) },
                { symbol: "[q<sub>u</sub>]", unit: "kPa", meaning: "Cường độ nén cho phép của vật liệu cọc CDM ([q<sub>u</sub>] = q<sub>u</sub> / 1.2)", value: (cdm_qu/1.2).toFixed(1) },
                { symbol: "q<sub>u</sub>", unit: "kPa", meaning: "Cường độ nén không hạn chế nở hông cọc đất xi măng tuổi 28 ngày", value: cdm_qu.toFixed(0) },
                { symbol: "q<sub>top</sub>", unit: "kPa", meaning: "Tổng áp lực thẳng đứng phân bố trên mặt nền đất yếu", value: q_top.toFixed(1) },
                { symbol: "E<sub>p</sub>", unit: "kPa", meaning: "Mô đun biến dạng của cọc CDM", value: E_p.toLocaleString() },
                { symbol: "E<sub>comp</sub>", unit: "kPa", meaning: "Mô đun tương đương của nền composite: m&middot;E<sub>p</sub> + (1-m)&middot;E<sub>s</sub>", value: E_comp_top.toLocaleString() }
            ])}
            <p>Đánh giá: Ứng suất đầu cọc &sigma;<sub>p</sub> = <b>${sigma_p_max.toFixed(1)} kPa</b> &le; [q<sub>u</sub>] = <b>${(cdm_qu/1.2).toFixed(1)} kPa</b> &rarr; <b>${sigma_p_max <= cdm_qu/1.2 ? '<span style="color:green;font-weight:bold;">ĐẠT YÊU CẦU</span>' : '<span style="color:red;font-weight:bold;">KHÔNG ĐẠT YÊU CẦU</span>'}</b></p>
 
            <p><b>1.2. Ứng suất tác dụng vào đất nền xung quanh cọc (Công thức E.19)</b></p>
            <p align="center" style="font-size:12pt; font-weight:bold; margin: 10px 0;">
                &sigma;<sub>s</sub> = q<sub>top</sub> &middot; ( E<sub>s</sub> / E<sub>comp</sub> ) &le; R<sub>tc</sub>
            </p>
            <p>Trong đó:</p>
            ${formatSymbolTable([
                { symbol: "&sigma;<sub>s</sub>", unit: "kPa", meaning: "Ứng suất tác dụng lên đất nền xung quanh cọc CDM thực tế", value: sigma_s_max.toFixed(1) },
                { symbol: "R<sub>tc</sub>", unit: "kPa", meaning: "Sức chịu tải tiêu chuẩn của đất nền: c&middot;N<sub>c</sub> + q&middot;N<sub>q</sub> + 0.5&middot;&gamma;&middot;b&middot;N<sub>&gamma;</sub>", value: R_tc.toFixed(1) },
                { symbol: "E<sub>s</sub>", unit: "kPa", meaning: "Mô đun biến dạng của lớp đất yếu xung quanh cọc", value: E_s_top.toFixed(0) },
                { symbol: "c", unit: "kPa", meaning: "Lực dính của đất nền yếu", value: c_soil.toFixed(1) },
                { symbol: "q", unit: "kPa", meaning: "Tải trọng phụ bên ngoài tim đường (hoạt tải xe xe chạy)", value: qSurcharge.toFixed(1) },
                { symbol: "N<sub>q</sub>", unit: "Không", meaning: "Hệ số sức chịu tải N<sub>q</sub> = e<sup>&pi;&middot;tan(&phi;)</sup> &middot; tan²(45&deg; + &phi;/2)", value: Nq.toFixed(2) },
                { symbol: "N<sub>c</sub>", unit: "Không", meaning: "Hệ số sức chịu tải N<sub>c</sub> = (N<sub>q</sub> - 1) / tan(&phi;)", value: Nc.toFixed(2) }
            ])}
            <p>Đánh giá: Ứng suất đất nền &sigma;<sub>s</sub> = <b>${sigma_s_max.toFixed(1)} kPa</b> &le; R<sub>tc</sub> = <b>${R_tc.toFixed(1)} kPa</b> &rarr; <b>${sigma_s_max <= R_tc ? '<span style="color:green;font-weight:bold;">ĐẠT YÊU CẦU</span>' : '<span style="color:red;font-weight:bold;">KHÔNG ĐẠT YÊU CẦU</span>'}</b></p>
 
            <p><b>1.3. Lực kéo tác dụng vào vải địa kỹ thuật gia cường (Công thức E.20)</b></p>
            <p align="center" style="font-size:12pt; font-weight:bold; margin: 10px 0;">
                T<sub>rp</sub> = T<sub>sr</sub> + T<sub>arching</sub> &le; [T]<sub>rp</sub>
            </p>
            <p>Trong đó:</p>
            ${formatSymbolTable([
                { symbol: "T<sub>rp</sub>", unit: "kN/m", meaning: "Tổng lực kéo yêu cầu lớn nhất trong vải địa kỹ thuật thực tế", value: geo.T_req.toFixed(1) },
                { symbol: "[T]<sub>rp</sub>", unit: "kN/m", meaning: "Lực kéo thiết kế cho phép của vải địa kỹ thuật: T<sub>ult</sub> / RF", value: geo.T_allow.toFixed(1) },
                { symbol: "T<sub>sr</sub>", unit: "kN/m", meaning: "Lực kéo xô ngang taluy: 0.5&middot;K<sub>a</sub>&middot;&gamma;&middot;H² + K<sub>a</sub>&middot;q&middot;H", value: geo.T_sr.toFixed(1) },
                { symbol: "T<sub>arching</sub>", unit: "kN/m", meaning: "Lực kéo do hiệu ứng vòm đất treo tải trọng: W<sub>T</sub>&middot;(s-a) / (2&middot;a)", value: geo.T_arching.toFixed(1) },
                { symbol: "T<sub>ult</sub>", unit: "kN/m", meaning: "Cường độ kéo đứt danh định lớn nhất của vải địa kỹ thuật", value: geo.T_ult.toFixed(0) },
                { symbol: "RF", unit: "Không", meaning: "Hệ số giảm cường độ tổng hợp thiết kế", value: geo.RF.toFixed(1) },
                { symbol: "K<sub>a</sub>", unit: "Không", meaning: "Hệ số áp lực chủ động của đất đắp: tan²(45&deg; - &phi;<sub>đắp</sub>/2)", value: geo.Ka_emb.toFixed(3) },
                { symbol: "W<sub>T</sub>", unit: "kN/m", meaning: "Tải trọng dải đất truyền xuống giữa 2 cọc: &sigma;<sub>s</sub>&middot;(s-a)", value: geo.W_T.toFixed(1) },
                { symbol: "s", unit: "m", meaning: "Khoảng cách tim cọc cắm bố trí", value: cdm_s.toFixed(2) },
                { symbol: "a", unit: "m", meaning: "Chiều rộng cạnh đầu cọc tương đương: d&middot;&radic;(&pi;/4)", value: geo.a.toFixed(3) }
            ])}
            <p>Đánh giá: Tổng lực kéo yêu cầu T<sub>rp</sub> = <b>${geo.T_req.toFixed(1)} kN/m</b> &le; [T]<sub>rp</sub> = <b>${geo.T_allow.toFixed(1)} kN/m</b> &rarr; <b>${geo.T_req <= geo.T_allow ? '<span style="color:green;font-weight:bold;">ĐẠT YÊU CẦU</span>' : '<span style="color:red;font-weight:bold;">KHÔNG ĐẠT YÊU CẦU</span>'}</b></p>
 
            <h4>2. Tính toán độ lún biến dạng (Công thức E.25 - E.28)</h4>
            <p>Tổng độ lún nền đất được chia thành 2 phần độc lập theo công thức:</p>
            <p align="center" style="font-size:12pt; font-weight:bold; margin: 10px 0;">
                &Delta;h = &Delta;h<sub>1</sub> + &Delta;h<sub>2</sub>
            </p>
            
            <p><b>2.1. Phần lớp đất được gia cố cọc cắm (&Delta;h<sub>1</sub>) (Công thức E.26)</b></p>
            <p align="center" style="font-size:12pt; font-weight:bold; margin: 10px 0;">
                &Delta;h<sub>1</sub> = ( q &middot; H<sub>c</sub> ) / [ m &middot; E<sub>p</sub> + ( 1 - m ) &middot; E<sub>s</sub> ]
            </p>
            <p>Trong đó:</p>
            ${formatSymbolTable([
                { symbol: "&Delta;h<sub>1</sub>", unit: "cm", meaning: "Độ lún đàn hồi của nền gia cố cọc composite thực tế", value: delta_h1.toFixed(2) },
                { symbol: "q", unit: "kPa", meaning: "Tổng tải trọng đứng tác dụng đầu cọc cắm", value: q_top.toFixed(1) },
                { symbol: "H<sub>c</sub>", unit: "m", meaning: "Chiều sâu cọc đất xi măng gia cố", value: appState.cdm.depth.toFixed(1) },
                { symbol: "m", unit: "Không", meaning: "Hệ số thay thế diện tích cọc CDM", value: m.toFixed(3) },
                { symbol: "E<sub>p</sub>", unit: "kPa", meaning: "Mô đun biến dạng của cọc CDM", value: E_p.toLocaleString() },
                { symbol: "E<sub>s</sub>", unit: "kPa", meaning: "Mô đun biến dạng trung bình lớp đất yếu trong phạm vi cọc", value: E_s_top.toFixed(0) }
            ])}
            <p>Đánh giá: Độ lún phần gia cố &Delta;h<sub>1</sub> = <b>${delta_h1.toFixed(2)} cm</b> &le; [&Delta;h<sub>1</sub>] = 8.00 cm &rarr; <b>${delta_h1 <= 8.0 ? '<span style="color:green;font-weight:bold;">ĐẠT YÊU CẦU</span>' : '<span style="color:red;font-weight:bold;">KHÔNG ĐẠT (Vượt quá 8.00 cm)</span>'}</b></p>
 
            <p><b>2.2. Phần lớp đất bên dưới lớp gia cố (&Delta;h<sub>2</sub>) (Công thức E.27 & E.28)</b></p>
            <p align="center" style="font-size:12pt; font-weight:bold; margin: 10px 0;">
                &Delta;h<sub>2</sub> = &Sigma; [ ( C<sub>c</sub> / (1 + e<sub>0</sub>) ) &middot; H<sub>i</sub> &middot; lg( (&sigma;'<sub>v0</sub> + q') / &sigma;'<sub>v0</sub> ) ]
            </p>
            <p align="center" style="font-size:12pt; font-weight:bold; margin: 10px 0;">
                q' = ( q &middot; B ) / ( B + z )
            </p>
            <p>Trong đó:</p>
            ${formatSymbolTable([
                { symbol: "&Delta;h<sub>2</sub>", unit: "cm", meaning: "Tổng độ lún phần đất nằm phía dưới mũi cọc CDM thực tế", value: delta_h2.toFixed(2) },
                { symbol: "C<sub>c</sub>", unit: "Không", meaning: "Chỉ số nén của phân lớp thứ i dưới mũi cọc", value: "Theo từng lớp" },
                { symbol: "e<sub>0</sub>", unit: "Không", meaning: "Hệ số rỗng tự nhiên phân lớp thứ i dưới mũi cọc", value: "Theo từng lớp" },
                { symbol: "H<sub>i</sub>", unit: "m", meaning: "Bề dày phân lớp thứ i dưới mũi cọc", value: "Tính tự động" },
                { symbol: "&sigma;'<sub>v0</sub>", unit: "kPa", meaning: "Ứng suất hữu hiệu địa tầng bản thân tự nhiên tại tâm phân lớp thứ i", value: "Tính tự động" },
                { symbol: "q'", unit: "kPa", meaning: "Ứng suất truyền thêm lan tỏa tại tâm phân lớp thứ i dưới mũi cọc", value: "Tính tự động" },
                { symbol: "B", unit: "m", meaning: "Chiều rộng đáy khối đắp tính toán quy ước", value: appState.embankment.width.toFixed(1) },
                { symbol: "z", unit: "m", meaning: "Chiều sâu tính từ mũi cọc CDM đến tâm phân lớp thứ i", value: "Tính tự động" }
            ])}
            <p>Độ lún tính toán dưới mũi cọc thu được &Delta;h<sub>2</sub> = <b>${delta_h2.toFixed(2)} cm</b>.</p>
            
            <p align="center" style="font-size: 11pt; color: #1e293b; margin-top: 15px; border-top: 1px dashed black; padding-top: 10px; font-weight: bold;">
                Tổng độ lún nền composite CDM: &Delta;h = &Delta;h<sub>1</sub> + &Delta;h<sub>2</sub> = ${delta_h1.toFixed(2)} + ${delta_h2.toFixed(2)} = ${(delta_h1 + delta_h2).toFixed(2)} cm
            </p>
        `;
    } else if (treatment === 'sand_column' && appState.lastVerification) {
        let sc = appState.sandColumn;
        let d_sc = sc.diameter;
        let spacing = sc.spacing;
        let pattern = sc.pattern;
        let m = pattern === 'triangular' ? 
            (Math.PI * d_sc * d_sc) / (2 * Math.sqrt(3) * spacing * spacing) : 
            (Math.PI * d_sc * d_sc) / (4 * spacing * spacing);
        let delta_h1 = appState.lastVerification.delta_h1 || 0;
        let delta_h2 = appState.lastVerification.delta_h2 || 0;
        verificationHTML = `
            <h2>4. THUYẾT MINH LÝ THUYẾT TÍNH LÚN NỀN LIÊN HỢP CỌC CÁT (TCCS 41:2022)</h2>
            <p>Cọc cát đóng vai trò là cấu kiện gia cường có độ cứng lớn hơn đất yếu, đồng thời làm nhiệm vụ giếng cát thoát nước ngang. Mô hình tính lún composite:</p>
            ${formatReportTable([
                { label: "- Đường kính cọc cát d", value: `${d_sc.toFixed(2)} m` },
                { label: "- Khoảng cách bố trí cọc S", value: `${spacing.toFixed(2)} m` },
                { label: `- Kiểu lưới bố trí (${pattern === 'triangular' ? 'Tam giác' : 'Ô cờ'})`, value: pattern === 'triangular' ? 'Tam giác' : 'Ô cờ' },
                { label: "- Tỷ lệ diện tích thay thế m = &pi;&middot;d² / (4&middot;S²) (hoặc tam giác)", value: `${(m * 100).toFixed(2)}%` },
                { label: "- Mô đun đàn hồi cọc cát E<sub>p</sub>", value: `${sc.modulus.toFixed(1)} MPa (${(sc.modulus * 1000).toLocaleString()} kPa)` },
                { label: "- Mô đun đàn hồi đất yếu xung quanh cọc E<sub>s</sub>", value: `3000 kPa` },
                { label: "<b>&rArr; Mô đun biến dạng tương đương E<sub>comp</sub> = m &middot; E<sub>p</sub> + (1 - m) &middot; E<sub>s</sub></b>", value: `${(m * sc.modulus * 1000 + (1 - m) * 3000).toFixed(0)} kPa` }
            ])}
            <h3>4.1. Tính toán độ lún phân vùng</h3>
            ${formatReportTable([
                { label: "- Độ lún vùng gia cố cọc cát &Delta;h₁ = &Sigma;[(&Delta;&sigma;<sub>z</sub> / E<sub>comp</sub>) &middot; &Delta;z]", value: `${delta_h1.toFixed(2)} cm` },
                { label: "- Độ lún vùng dưới mũi cọc &Delta;h₂", value: `${delta_h2.toFixed(2)} cm` },
                { label: "<b>&rArr; Tổng độ lún tích lũy &Delta;h = &Delta;h₁ + &Delta;h₂</b>", value: `${(delta_h1 + delta_h2).toFixed(2)} cm` }
            ])}
        `;
    } else if ((treatment === 'bamboo_pile' || treatment === 'bamboo_replacement') && appState.lastVerification) {
        let bp = appState.bambooPile;
        let circ = appState.lastVerification.circular;
        let plan = appState.lastVerification.planar;
        let delta_h1 = appState.lastVerification.delta_h1 || 0;
        let delta_h2 = appState.lastVerification.delta_h2 || 0;
        let delta_cushion = appState.lastVerification.delta_cushion || 0;
        
        let hRep = treatment === 'bamboo_replacement' ? appState.excavationReplacement.depth : 0;
        let isReplacement = treatment === 'bamboo_replacement' && hRep > 0;
        let z_slide = hRep;
        
        // Find layer at sliding interface
        let depthAcc = 0;
        let layer_at_slide = appState.soilLayers[0];
        for (let i = 0; i < appState.soilLayers.length; i++) {
            let l = appState.soilLayers[i];
            if (hRep >= depthAcc && hRep < depthAcc + l.thickness) {
                layer_at_slide = l;
                break;
            }
            depthAcc += l.thickness;
        }
        let phi_soil = layer_at_slide ? (layer_at_slide.phi !== undefined ? layer_at_slide.phi : 5) : 5;
        
        let d_bp = bp.diameter;
        let bp_spacing = bp.spacing;
        let pattern = bp.pattern;
        let n_density = pattern === 'triangular' ? (1.155 / (bp_spacing * bp_spacing)) : (1 / (bp_spacing * bp_spacing));
        let m = pattern === 'triangular' ? 
            (Math.PI * d_bp * d_bp) / (2 * Math.sqrt(3) * bp_spacing * bp_spacing) : 
            (Math.PI * d_bp * d_bp) / (4 * bp_spacing * bp_spacing);
            
        let phi_p = bp.phi !== undefined ? bp.phi : 30.0;
        let phi_comp_0 = m * phi_p + (1 - m) * phi_soil;
            
        verificationHTML = `
            <h2>4. THUYẾT MINH KIỂM TOÁN NỀN GIA CỐ CỌC TRE ${isReplacement ? '+ THAY ĐẤT' : ''}</h2>
            
            <h3>A. TÍNH TOÁN THEO TRẠNG THÁI GIỚI HẠN 1 (ỔN ĐỊNH NỀN)</h3>
            
            <h4>1. Kiểm tra ổn định trượt sâu (Cung trượt Fellenius composite)</h4>
            <p>Hệ số an toàn trượt sâu:</p>
            ${formatReportTable([
                { label: "Hệ số an toàn trượt sâu thực tế (F<sub>s</sub>)", value: circ.FS.toFixed(3) },
                { label: "Hệ số an toàn trượt sâu yêu cầu ([F<sub>s</sub>])", value: "1.200" },
                { label: "Tâm trượt nguy hiểm nhất (X<sub>c</sub>, Z<sub>c</sub>)", value: `(${circ.Xc.toFixed(2)} m, ${circ.Zc.toFixed(2)} m)` },
                { label: "Bán kính cung trượt (R)", value: `${circ.R.toFixed(2)} m` },
                { label: "Lực dính hiệu dụng của cọc tre quy ước (c<sub>tre</sub>)", value: "50.0 kPa" },
                { label: "Lực dính của đất nền yếu", value: `${(appState.soilLayers[0] ? (appState.soilLayers[0].c || 10) : 10).toFixed(1)} kPa` },
                { label: "Đánh giá an toàn ổn định trượt sâu", value: circ.FS >= 1.2 ? '<span style="color:green;font-weight:bold;">ĐẠT YÊU CẦU</span>' : '<span style="color:red;font-weight:bold;">KHÔNG ĐẠT YÊU CẦU</span>' }
            ])}
            ${slipImg ? `
            <p align="center">
                <img src="${slipImg}" width="420" style="max-width:100%; height:auto;"><br>
                <i>Hình 5. Bản vẽ cung trượt tròn ổn định trượt sâu nguy hiểm nhất (${isReplacement ? 'Cọc tre + Thay đất' : 'Gia cố cọc tre'})</i>
            </p>
            ` : ''}

            <h4>2. Kiểm tra ổn định trượt phẳng (Trượt ngang tiếp giáp z = ${z_slide.toFixed(1)}m)</h4>
            <p align="center"><b>F<sub>s</sub> = [P<sub>PS</sub> + (W<sub>E</sub> + W<sub>C</sub>) &middot; tan(&phi;<sub>tđ</sub>')] / [P<sub>AE</sub> + P<sub>AS</sub>] &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(E.6)</b></p>
            ${formatReportTable([
                { label: "- Trọng lượng khối đắp đè lên vùng gia cố (W<sub>E</sub>)", value: `${plan.WE.toFixed(1)} kN/m` },
                { label: "- Trọng lượng khối gia cố quy ước (W<sub>C</sub>)", value: `${plan.WC.toFixed(1)} kN/m` },
                { label: "- Chiều rộng tính toán nửa khối đập (B<sub>block</sub>)", value: `${plan.B_block.toFixed(1)} m` },
                { label: "- Lực đẩy ngang chủ động từ bên trên mặt trượt (P<sub>AE</sub>)", value: `${plan.Pa_emb.toFixed(1)} kN/m` },
                { label: "- Lực đẩy ngang chủ động của đất nền trong vùng cọc (P<sub>AS</sub>)", value: `${plan.Pa_soil.toFixed(1)} kN/m` },
                { label: "- Lực cản bị động ngoài taluy (P<sub>PS</sub>)", value: `${plan.Pp_soil.toFixed(1)} kN/m` },
                { label: "- Hệ số thay thế diện tích cọc (m)", value: m.toFixed(3) },
                { label: "- Góc ma sát trong tương đương dọc theo mặt trượt (&phi;<sub>tđ</sub>')", value: `${phi_comp_0.toFixed(2)}&deg;` },
                { label: "<b>&rArr; Hệ số an toàn trượt phẳng thực tế (F<sub>s,sliding</sub>)</b>", value: plan.FS_min.toFixed(2) },
                { label: "Hệ số an toàn yêu cầu ([F<sub>s</sub>])", value: "1.20" },
                { label: "Đánh giá an toàn ổn định trượt phẳng", value: plan.FS_min >= 1.2 ? '<span style="color:green;font-weight:bold;">ĐẠT YÊU CẦU</span>' : '<span style="color:red;font-weight:bold;">KHÔNG ĐẠT YÊU CẦU</span>' }
            ])}
 
            <h3>B. TÍNH TOÁN THEO TRẠNG THÁI GIỚI HẠN 2 (BIẾN DẠNG LÚN)</h3>
            
            <h4>1. Lý thuyết nền liên hợp composite cọc tre</h4>
            <p>Độ lún trong phạm vi cọc tre (z &le; L<sub>c</sub>) được tính toán theo mô hình mô đun tương đương E<sub>composite</sub>:</p>
            ${formatReportTable([
                { label: "- Đường kính cọc tre d", value: `${d_bp.toFixed(2)} m` },
                { label: "- Khoảng cách cọc S", value: `${bp_spacing.toFixed(2)} m` },
                { label: "- Mật độ cọc thiết kế n", value: `&approx; ${n_density.toFixed(0)} cọc/m²` },
                { label: "- Hệ số thay thế diện tích m", value: `${(m * 100).toFixed(2)}%` },
                { label: "- Mô đun đàn hồi cọc tre E<sub>p</sub>", value: `${bp.modulus.toFixed(1)} MPa (${(bp.modulus * 1000).toLocaleString()} kPa)` },
                { label: "- Mô đun đàn hồi đất yếu xung quanh cọc E<sub>s</sub>", value: `3000 kPa` },
                { label: "<b>&rArr; Mô đun đàn hồi tương đương E<sub>comp</sub> = m &middot; E<sub>p</sub> + (1 - m) &middot; E<sub>s</sub></b>", value: `${(m * bp.modulus * 1000 + (1 - m) * 3000).toFixed(0)} kPa` }
            ])}
            
            <h4>2. Kết quả độ lún chi tiết</h4>
            ${formatReportTable([
                ...(isReplacement ? [{ label: "- Độ lún lớp đệm cát thay thế &Delta;h<sub>cát</sub>", value: `${delta_cushion.toFixed(2)} cm` }] : []),
                { label: "- Độ lún vùng gia cố cọc tre &Delta;h₁", value: `${delta_h1.toFixed(2)} cm` },
                { label: "Giới hạn độ lún vùng gia cố [&Delta;h₁]", value: "8.00 cm" },
                { label: "Đánh giá độ lún vùng gia cố", value: delta_h1 <= 8.0 ? '<span style="color:green;font-weight:bold;">ĐẠT YÊU CẦU</span>' : '<span style="color:red;font-weight:bold;">KHÔNG ĐẠT (Vượt quá 8.0cm)</span>' },
                { label: "- Độ lún vùng đất tự nhiên dưới mũi cọc &Delta;h₂", value: `${delta_h2.toFixed(2)} cm` },
                { label: `<b>&rArr; Tổng độ lún tính toán &Delta;h = ${isReplacement ? '&Delta;h<sub>cát</sub> + ' : ''}&Delta;h₁ + &Delta;h₂</b>`, value: `${(delta_cushion + delta_h1 + delta_h2).toFixed(2)} cm` }
            ])}
        `;
    } else if (treatment === 'excavation_replacement' && appState.lastVerification) {
        let circ = appState.lastVerification.circular;
        verificationHTML = `
            <h2>4. THUYẾT MINH KIỂM TOÁN GIA CỐ ĐÀO THAY ĐẤT</h2>
            <h3>4.1. Thông số lớp đệm cát thay thế</h3>
            ${formatReportTable([
                { label: "- Chiều sâu đào thay thế H<sub>đào</sub>", value: `${appState.excavationReplacement.depth.toFixed(1)} m` },
                { label: "- Dung trọng đệm cát tự nhiên &gamma;", value: `${appState.excavationReplacement.gamma.toFixed(1)} kN/m³` },
                { label: "- Dung trọng đệm cát bão hòa &gamma;<sub>sat</sub>", value: `${appState.excavationReplacement.gammaSat.toFixed(1)} kN/m³` },
                { label: "- Mô đun đàn hồi đệm cát E<sub>cát</sub>", value: `${appState.excavationReplacement.E.toFixed(0)} kPa` },
                { label: "- Lực dính đệm cát c<sub>cát</sub>", value: `${appState.excavationReplacement.c.toFixed(1)} kPa` },
                { label: "- Góc ma sát trong đệm cát &phi;<sub>cát</sub>", value: `${appState.excavationReplacement.phi.toFixed(1)}°` }
            ])}
            <h3>4.2. Kiểm toán ổn định trượt sâu trụ tròn (Fellenius Method)</h3>
            ${formatReportTable([
                { label: "Hệ số an toàn trượt sâu thực tế (F<sub>s,min</sub>)", value: circ.FS.toFixed(3) },
                { label: "Hệ số an toàn trượt sâu yêu cầu ([F<sub>s</sub>])", value: "1.200" },
                { label: "Tâm trượt nguy hiểm nhất (X<sub>c</sub>, Z<sub>c</sub>)", value: `(${circ.Xc.toFixed(2)} m, ${circ.Zc.toFixed(2)} m)` },
                { label: "Bán kính cung trượt (R)", value: `${circ.R.toFixed(2)} m` },
                { label: "Đánh giá an toàn ổn định trượt sâu", value: circ.FS >= 1.2 ? '<span style="color:green;font-weight:bold;">ĐẠT YÊU CẦU</span>' : '<span style="color:red;font-weight:bold;">KHÔNG ĐẠT YÊU CẦU</span>' }
            ])}
            ${slipImg ? `
            <p align="center">
                <img src="${slipImg}" width="420" style="max-width:100%; height:auto;"><br>
                <i>Hình 5. Bản vẽ cung trượt tròn ổn định trượt sâu nguy hiểm nhất (Đào thay đất)</i>
            </p>
            ` : ''}
        `;
    }

    // Build detailed table report for sublayers
    let detailedTableHTML = "";
    const containerChildren = document.querySelectorAll("#detailed-tables-container > *");
    containerChildren.forEach(child => {
        if (child.classList.contains("table-title")) {
            detailedTableHTML += `
                <p style="font-family:'Times New Roman'; font-size:11pt; font-weight:bold; color:#1e3a8a; margin-top:20px; margin-bottom:6px;">
                    ${child.innerText}
                </p>
            `;
        } else if (child.tagName.toLowerCase() === "table") {
            detailedTableHTML += `
                <table border="1" cellspacing="0" cellpadding="4" style="border-collapse:collapse; width:100%; font-family:'Times New Roman'; font-size:9.5pt; margin-bottom:15px; border:1px solid #cbd5e1;">
            `;
            
            // th header rows
            const headers = child.querySelectorAll("thead tr");
            headers.forEach(hRow => {
                detailedTableHTML += `<thead><tr style="background-color:#e2e8f0; font-weight:bold;">`;
                hRow.querySelectorAll("th").forEach((th, idx) => {
                    let align = (idx === 0) ? "left" : "right";
                    detailedTableHTML += `
                        <th style="border:1px solid #cbd5e1; padding:5px; text-align:${align}; font-family:'Times New Roman'; font-size:9.5pt;">${th.innerHTML}</th>
                    `;
                });
                detailedTableHTML += `</tr></thead><tbody>`;
            });

            // body rows
            const rows = child.querySelectorAll("tbody tr");
            rows.forEach(row => {
                if (row.classList.contains("layer-total-row")) {
                    let tds = row.querySelectorAll("td");
                    detailedTableHTML += `<tr style="background-color:#faf5ff; font-weight:bold;">`;
                    tds.forEach((td, colIdx) => {
                        let colorStyle = (colIdx === 0 || colIdx === tds.length - 1) ? "color:#6b21a8;" : "color:#111827;";
                        let align = (colIdx === 0) ? "left" : "right";
                        detailedTableHTML += `
                            <td style="border:1px solid #cbd5e1; padding:5px 8px; font-family:'Times New Roman'; font-size:9.5pt; text-align:${align}; ${colorStyle}">${td.innerText}</td>
                        `;
                    });
                    detailedTableHTML += `</tr>`;
                } else if (row.classList.contains("grand-total-row")) {
                    let tds = row.querySelectorAll("td");
                    detailedTableHTML += `<tr style="background-color:#e0f2fe; font-weight:bold; border-top:2px solid #0284c7; border-bottom:2px solid #0284c7;">`;
                    tds.forEach((td, colIdx) => {
                        let align = (colIdx === 0) ? "left" : "right";
                        detailedTableHTML += `
                            <td style="border:1px solid #cbd5e1; padding:6px 8px; font-family:'Times New Roman'; font-size:10pt; text-align:${align}; color:#0369a1;">${td.innerText}</td>
                        `;
                    });
                    detailedTableHTML += `</tr>`;
                } else {
                    let tds = row.querySelectorAll("td");
                    detailedTableHTML += `<tr>`;
                    tds.forEach((td, colIdx) => {
                        let align = (colIdx === 0) ? "left" : "right";
                        if (td.innerText.includes("OC") || td.innerText.includes("NC")) {
                            align = "center";
                        }
                        let isBold = (colIdx === tds.length - 1) ? "font-weight:bold;" : "";
                        detailedTableHTML += `
                            <td style="border:1px solid #cbd5e1; padding:4px 6px; font-family:'Times New Roman'; font-size:9.5pt; text-align:${align}; ${isBold}">${td.innerText}</td>
                        `;
                    });
                    detailedTableHTML += `</tr>`;
                }
            });
            detailedTableHTML += `</tbody></table>`;
        }
    });

    const reportMeta = globalThis.NendatYeuCore
        ? globalThis.NendatYeuCore.createReportMetadata(appState)
        : { version: "3.0.0", checksum: "N/A", generatedAt: new Date().toISOString(), standards: [], assumptions: [] };
    const reportMetaHtml =
        '<div style="border:1px solid #94a3b8; background:#f8fafc; padding:10px; margin:0 0 18px 0; font-size:9.5pt;">' +
        '<b>Phiên bản phần mềm:</b> ' + reportMeta.version + ' &nbsp; | &nbsp; ' +
        '<b>Checksum dữ liệu:</b> ' + reportMeta.checksum + '<br>' +
        '<b>Thời điểm lập:</b> ' + new Date(reportMeta.generatedAt).toLocaleString("vi-VN") + '<br>' +
        '<b>Tiêu chuẩn:</b> ' + reportMeta.standards.join("; ") + '<br>' +
        '<b>Giả định chính:</b> ' + reportMeta.assumptions.join(" ") + '</div>';

    // Complete MS Word HTML wrapper
    let htmlContent = `
        <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
            <title>${title}</title>
            <!--[if gte mso 9]>
            <xml>
                <w:WordDocument>
                    <w:View>Print</w:View>
                    <w:Zoom>100</w:Zoom>
                    <w:DoNotOptimizeForBrowser/>
                </w:WordDocument>
            </xml>
            <![endif]-->
            <style>
                @page {
                    size: A4;
                    margin: 2cm;
                }
                body {
                    font-family: "Times New Roman", Times, serif;
                    font-size: 12pt;
                    line-height: 1.5;
                    color: black;
                }
                h1 {
                    font-size: 15pt;
                    text-align: center;
                    font-weight: bold;
                    margin-bottom: 25px;
                    text-transform: uppercase;
                }
                h2 {
                    font-size: 13pt;
                    font-weight: bold;
                    margin-top: 25px;
                    margin-bottom: 12px;
                    border-bottom: 1px solid black;
                    padding-bottom: 4px;
                }
                h3 {
                    font-size: 12pt;
                    font-weight: bold;
                    margin-top: 18px;
                    margin-bottom: 6px;
                }
                p, li {
                    margin-bottom: 8px;
                    text-align: justify;
                }
                ul {
                    margin-left: 20px;
                    margin-bottom: 10px;
                }
                table {
                    margin-bottom: 15px;
                }
            </style>
        </head>
        <body>
            <h1>BÁO CÁO KẾT QUẢ TÍNH TOÁN LÚN VÀ GIA CỐ NỀN ĐƯỜNG ĐẮP TRÊN ĐẤT YẾU</h1>
            ${reportMetaHtml}
            
            <h2>1. THÔNG TIN CHUNG VỀ NỀN ĐƯỜNG ĐẮP VÀ ĐỊA TẦNG</h2>
            <h3>1.1. Thông số hình học và tải trọng nền đắp</h3>
            ${formatReportTable([
                { label: "- Chiều cao đắp H<sub>đắp</sub>", value: `${appState.embankment.height.toFixed(1)} m` },
                { label: "- Dung trọng đất đắp &gamma;", value: `${appState.embankment.gamma.toFixed(1)} kN/m³` },
                { label: "- Chiều rộng đỉnh nền đắp B<sub>đỉnh</sub>", value: `${appState.embankment.width.toFixed(1)} m` },
                { label: "- Hệ số dốc taluy 1:m", value: `1:${appState.embankment.slope.toFixed(1)}` },
                { label: "- Hoạt tải xe xe chạy q", value: `${appState.foundation.surcharge.toFixed(1)} kPa` },
                { label: "- Mực nước ngầm Z<sub>w</sub>", value: `${appState.foundation.gwt.toFixed(1)} m` }
            ])}

            <h3>1.2. Chỉ tiêu cơ lý các lớp đất nền</h3>
            ${soilTableHTML}

            <h3>1.3. Sơ đồ địa tầng địa chất 2D và mô hình tính toán</h3>
            <p align="center">
                <img src="${soilImg}" width="550" style="max-width:100%; height:auto;"><br>
                <i>Hình 1. Bản vẽ sơ đồ địa tầng 2D và mặt cắt ngang thiết kế</i>
            </p>

            <h2>2. PHƯƠNG ÁN XỬ LÝ NỀN ĐẤT YẾU</h2>
            <p>Phương án gia cố được lựa chọn: <b>${treatmentText}</b></p>
            ${treatment === 'cdm' ? formatReportTable([
                { label: "- Chiều sâu cọc xi măng đất L<sub>c</sub>", value: `${appState.cdm.depth.toFixed(1)} m` },
                { label: "- Đường kính cọc d", value: `${appState.cdm.diameter.toFixed(2)} m` },
                { label: "- Khoảng cách cọc S", value: `${appState.cdm.spacing.toFixed(2)} m` },
                { label: "- Kiểu bố trí lưới", value: appState.cdm.pattern === 'triangular' ? 'Tam giác' : 'Ô cờ (Vuông)' },
                { label: "- Cường độ nén cọc q<sub>u</sub> (tuổi 28 ngày)", value: `${appState.cdm.qu.toFixed(1)} MPa` },
                { label: "- Mô đun đàn hồi cọc E<sub>p</sub> = ratio &middot; q<sub>u</sub>", value: `${(appState.cdm.emodulusRatio * appState.cdm.qu).toFixed(1)} MPa` },
                { label: "- Cường độ kéo đứt vải địa gia cường đáy đắp T<sub>ult</sub>", value: `${appState.cdm.tUlt.toFixed(0)} kN/m` },
                { label: "- Hệ số giảm thiểu cường độ vải địa RF", value: appState.cdm.rf.toFixed(1) },
                { label: "- Góc ma sát trong của cọc CDM &phi;<sub>p</sub>", value: `${appState.cdm.phi.toFixed(1)}°` },
                { label: "- Phạm vi gia cố rộng hơn đáy đắp b<sub>over</sub>", value: "1.00 m (Tự động)" }
            ]) : ''}
            ${treatment === 'pvd' ? (
                (() => {
                    let items = [
                        { label: "- Khoảng cách bấc thấm S<sub>pvd</sub>", value: `${appState.pvd.spacing.toFixed(2)} m` },
                        { label: "- Kiểu bố trí lưới bấc", value: appState.pvd.pattern === 'triangular' ? 'Tam giác' : 'Ô cờ (Vuông)' },
                        { label: "- Chiều sâu cắm bấc thấm L<sub>pvd</sub>", value: `${appState.pvd.depth.toFixed(1)} m` },
                        { label: "- Chiều rộng bản bấc b", value: `${appState.pvd.width.toFixed(0)} mm` },
                        { label: "- Chiều dày bản bấc t", value: `${appState.pvd.thickness.toFixed(1)} mm` },
                        { label: "- Tỷ lệ cố kết hướng tâm/đứng C<sub>h</sub>/C<sub>v</sub>", value: appState.pvd.chRatio.toFixed(1) }
                    ];
                    if (appState.pvd.standard === 'tcvn_9355') {
                        items.push(
                            { label: "- Tỷ lệ xáo động d<sub>s</sub>/d<sub>w</sub>", value: (appState.pvd.dsDw || 2.5).toFixed(1) },
                            { label: "- Tỷ lệ thấm k<sub>h</sub>/k<sub>s</sub>", value: (appState.pvd.khKs || 3.0).toFixed(1) },
                            { label: "- Q<sub>w</sub> thoát nước", value: `${(appState.pvd.qw || 60.0).toFixed(1)} cm³/s` }
                        );
                    }
                    return formatReportTable(items);
                })()
            ) : ''}
            ${treatment === 'sand_drain' ? formatReportTable([
                { label: "- Khoảng cách giếng cát S<sub>sd</sub>", value: `${appState.sandDrain.spacing.toFixed(2)} m` },
                { label: "- Kiểu bố trí lưới giếng", value: appState.sandDrain.pattern === 'triangular' ? 'Tam giác' : 'Ô cờ (Vuông)' },
                { label: "- Chiều sâu cắm giếng cát L<sub>sd</sub>", value: `${appState.sandDrain.depth.toFixed(1)} m` },
                { label: "- Đường kính giếng cát d", value: `${appState.sandDrain.diameter.toFixed(2)} m` },
                { label: "- Tỷ lệ cố kết hướng tâm/đứng C<sub>h</sub>/C<sub>v</sub>", value: appState.sandDrain.chRatio.toFixed(1) }
            ]) : ''}
            ${treatment === 'sand_column' ? formatReportTable([
                { label: "- Khoảng cách cọc cát S<sub>sc</sub>", value: `${appState.sandColumn.spacing.toFixed(2)} m` },
                { label: "- Kiểu bố trí lưới cọc cát", value: appState.sandColumn.pattern === 'triangular' ? 'Tam giác' : 'Ô cờ (Vuông)' },
                { label: "- Chiều sâu cọc cát L<sub>sc</sub>", value: `${appState.sandColumn.depth.toFixed(1)} m` },
                { label: "- Đường kính cọc cát d", value: `${appState.sandColumn.diameter.toFixed(2)} m` },
                { label: "- Mô đun đàn hồi cọc cát E<sub>p</sub>", value: `${appState.sandColumn.modulus.toFixed(1)} MPa` },
                { label: "- Tỷ lệ cố kết hướng tâm/đứng C<sub>h</sub>/C<sub>v</sub>", value: appState.sandColumn.chRatio.toFixed(1) }
            ]) : ''}
            ${(treatment === 'bamboo_pile' || (treatment === 'bamboo_replacement' && appState.excavationReplacement.depth === 0)) ? formatReportTable([
                { label: "- Khoảng cách cọc tre S<sub>bp</sub>", value: `${appState.bambooPile.spacing.toFixed(2)} m` },
                { label: "- Kiểu bố trí lưới cọc tre", value: appState.bambooPile.pattern === 'triangular' ? 'Tam giác' : 'Ô cờ (Vuông)' },
                { label: "- Chiều sâu cọc tre L<sub>bp</sub>", value: `${appState.bambooPile.depth.toFixed(1)} m` },
                { label: "- Đường kính cọc tre d", value: `${appState.bambooPile.diameter.toFixed(2)} m` },
                { label: "- Mô đun đàn hồi cọc tre E<sub>p</sub>", value: `${appState.bambooPile.modulus.toFixed(1)} MPa` },
                { label: "- Góc ma sát trong cọc tre &phi;<sub>p</sub>", value: `${appState.bambooPile.phi.toFixed(1)}°` }
            ]) : ''}
            ${(treatment === 'bamboo_replacement' && appState.excavationReplacement.depth > 0) ? formatReportTable([
                { label: "<b>Thông số Đệm cát thay thế:</b>", value: "" },
                { label: "- Chiều sâu đào thay thế H<sub>đào</sub>", value: `${appState.excavationReplacement.depth.toFixed(1)} m` },
                { label: "- Dung trọng đệm cát tự nhiên &gamma;", value: `${appState.excavationReplacement.gamma.toFixed(1)} kN/m³` },
                { label: "- Dung trọng đệm cát bão hòa &gamma;<sub>sat</sub>", value: `${appState.excavationReplacement.gammaSat.toFixed(1)} kN/m³` },
                { label: "- Mô đun đàn hồi đệm cát E<sub>cát</sub>", value: `${appState.excavationReplacement.E.toFixed(0)} kPa` },
                { label: "- Lực dính đệm cát c<sub>cát</sub>", value: `${appState.excavationReplacement.c.toFixed(1)} kPa` },
                { label: "- Góc ma sát trong đệm cát &phi;<sub>cát</sub>", value: `${appState.excavationReplacement.phi.toFixed(1)}°` },
                { label: "<b>Thông số Cọc tre:</b>", value: "" },
                { label: "- Khoảng cách cọc tre S<sub>bp</sub>", value: `${appState.bambooPile.spacing.toFixed(2)} m` },
                { label: "- Kiểu bố trí lưới cọc tre", value: appState.bambooPile.pattern === 'triangular' ? 'Tam giác' : 'Ô cờ (Vuông)' },
                { label: "- Chiều sâu cọc tre L<sub>bp</sub>", value: `${appState.bambooPile.depth.toFixed(1)} m` },
                { label: "- Đường kính cọc tre d", value: `${appState.bambooPile.diameter.toFixed(2)} m` },
                { label: "- Mô đun đàn hồi cọc tre E<sub>p</sub>", value: `${appState.bambooPile.modulus.toFixed(1)} MPa` },
                { label: "- Góc ma sát trong cọc tre &phi;<sub>p</sub>", value: `${appState.bambooPile.phi.toFixed(1)}°` }
            ]) : ''}
            ${treatment === 'excavation_replacement' ? formatReportTable([
                { label: "- Chiều sâu đào thay thế H<sub>đào</sub>", value: `${appState.excavationReplacement.depth.toFixed(1)} m` },
                { label: "- Dung trọng đệm cát tự nhiên &gamma;", value: `${appState.excavationReplacement.gamma.toFixed(1)} kN/m³` },
                { label: "- Dung trọng đệm cát bão hòa &gamma;<sub>sat</sub>", value: `${appState.excavationReplacement.gammaSat.toFixed(1)} kN/m³` },
                { label: "- Mô đun đàn hồi đệm cát E<sub>cát</sub>", value: `${appState.excavationReplacement.E.toFixed(0)} kPa` },
                { label: "- Lực dính đệm cát c<sub>cát</sub>", value: `${appState.excavationReplacement.c.toFixed(1)} kPa` },
                { label: "- Góc ma sát trong đệm cát &phi;<sub>cát</sub>", value: `${appState.excavationReplacement.phi.toFixed(1)}°` }
            ]) : ''}

            <h2>3. KẾT QUẢ TÍNH TOÁN ĐỘ LÚN VÀ CỐ KẾT (TCCS 41:2022)</h2>
            
            <h3>3.1. Các biểu đồ kết quả phân tích ứng suất đứng và tiến trình lún</h3>
            <table width="100%" style="font-family:'Times New Roman'; font-size:11pt; border:0; margin-top:10px; margin-bottom:15px;">
                <tr>
                    <td width="50%" align="center" style="border:0;">
                        <img src="${stressImg}" width="280" style="max-width:100%; height:auto;"><br>
                        <i>Hình 2. Phân bố ứng suất đứng</i>
                    </td>
                    <td width="50%" align="center" style="border:0;">
                        <img src="${timeImg}" width="280" style="max-width:100%; height:auto;"><br>
                        <i>Hình 3. Lún theo thời gian</i>
                    </td>
                </tr>
                <tr>
                    <td width="100%" colspan="2" align="center" style="border:0; padding-top:15px;">
                        <img src="${depthImg}" width="350" style="max-width:100%; height:auto;"><br>
                        <i>Hình 4. Lún theo chiều sâu</i>
                    </td>
                </tr>
            </table>

            <h3>3.2. Tóm tắt kết quả tính toán độ lún tích lũy và cố kết</h3>
            ${formatReportTable([
                { label: "- Độ lún tổng cộng cuối cùng S = m &middot; (S<sub>i</sub> + S<sub>c</sub> + S<sub>s</sub>)", value: `${s_total} cm` },
                { label: "- Hệ số hiệu chỉnh độ lún kinh nghiệm (m)", value: appState.foundation.mFactor.toFixed(2) },
                { label: "- Độ lún cố kết sơ cấp (S<sub>c</sub>)", value: `${s_c} cm` },
                { label: "- Độ lún tức thời (đàn hồi) (S<sub>i</sub>)", value: `${s_i} cm` },
                { label: "- Độ lún từ biến thứ cấp (S<sub>s</sub>)", value: `${s_s} cm` },
                { label: "- Thời gian chờ cố kết preloading (t<sub>ck</sub>)", value: `${appState.foundation.tConstruction.toFixed(1)} năm` },
                { label: "- Độ cố kết tại thời điểm nghiệm thu U(t<sub>ck</sub>)", value: `${u_t}` },
                { label: "<b>&rArr; Độ lún dư còn lại tại thời điểm nghiệm thu (S<sub>dư</sub>)</b>", value: `${s_rem} cm` },
                { label: "- Độ lún dư cho phép thiết kế [S]", value: `${allowable.toFixed(1)} cm` },
                { label: "<b>&rArr; Đánh giá độ ổn định lún dư nền đường</b>", value: reportResult.remaining <= allowable ? '<span style="color:green;font-weight:bold;">ĐẠT YÊU CẦU</span>' : '<span style="color:red;font-weight:bold;">KHÔNG ĐẠT YÊU CẦU</span>' }
            ])}

            ${verificationHTML}

            <h2>5. BẢNG CHI TIẾT TÍNH TOÁN CỘNG LÚN PHÂN TẦNG</h2>
            ${detailedTableHTML}

            <br><br>
            <table width="100%" style="font-family:'Times New Roman'; font-size:11pt;">
                <tr>
                    <td width="50%" style="text-align:center;">
                        <b>Người lập biểu</b><br><br><br><br>
                        <i>(Ký và ghi rõ họ tên)</i>
                    </td>
                    <td width="50%" style="text-align:center;">
                        <b>Trưởng phòng kỹ thuật</b><br><br><br><br>
                        <i>(Ký và ghi rõ họ tên)</i>
                    </td>
                </tr>
            </table>
        </body>
        </html>
    `;

    const blob = new Blob([htmlContent], { type: 'application/msword' });
    saveFileWithPicker(blob, "Bao_cao_tinh_toan_lun_va_gia_co_TimesNewRoman.doc", [
        { description: 'Word Documents', accept: { 'application/msword': ['.doc'] } }
    ]);
}

function exportTheoryToWord() {
    const activeSubContent = document.querySelector(".theory-sub-content.active");
    if (!activeSubContent) return;

    const h3El = activeSubContent.querySelector("h3");
    const title = h3El ? h3El.innerText : "Thuyet_Minh_Ly_Thuyet";
    const filename = title.replace(/\s+/g, "_") + ".doc";

    let htmlContent = `
        <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
            <title>${title}</title>
            <!--[if gte mso 9]>
            <xml>
                <w:WordDocument>
                    <w:View>Print</w:View>
                    <w:Zoom>100</w:Zoom>
                    <w:DoNotOptimizeForBrowser/>
                </w:WordDocument>
            </xml>
            <![endif]-->
            <style>
                @page {
                    size: A4;
                    margin: 2cm;
                }
                body {
                    font-family: "Times New Roman", Times, serif;
                    font-size: 12pt;
                    line-height: 1.5;
                    color: black;
                }
                h3 {
                    font-size: 14pt;
                    font-weight: bold;
                    margin-top: 18px;
                    margin-bottom: 12px;
                    text-align: center;
                    text-transform: uppercase;
                }
                h4 {
                    font-size: 12.5pt;
                    font-weight: bold;
                    margin-top: 14px;
                    margin-bottom: 6px;
                }
                p, li {
                    margin-bottom: 8px;
                    text-align: justify;
                }
                ul, ol {
                    margin-left: 20px;
                    margin-bottom: 10px;
                }
                .verification-formula {
                    background-color: #f1f5f9;
                    border: 1px solid #cbd5e1;
                    padding: 8px 12px;
                    font-family: Consolas, Courier New, monospace;
                    font-size: 11pt;
                    text-align: center;
                    margin: 10px 0;
                }
                sub, sup {
                    font-size: 8.5pt;
                }
                strong {
                    font-weight: bold;
                }
            </style>
        </head>
        <body>
            ${activeSubContent.innerHTML}
        </body>
        </html>
    `;

    const blob = new Blob([htmlContent], { type: 'application/msword' });
    saveFileWithPicker(blob, filename, [
        { description: 'Word Documents', accept: { 'application/msword': ['.doc'] } }
    ]);
}

function getProjectStateSnapshot() {
    const snapshot = structuredClone(appState);
    delete snapshot.lastAnalysis;
    delete snapshot.lastVerification;
    return snapshot;
}

function replaceProjectStateSnapshot(snapshot) {
    appState = structuredClone(snapshot);
    delete appState.lastAnalysis;
    delete appState.lastVerification;
    updateInputsFromState();
    renderLayersTable();
    runAnalysis();
    window.dispatchEvent(new CustomEvent("nendatyeu:state-replaced"));
}

function evaluateProjectSensitivity(parameter, factors) {
    const original = structuredClone(appState);
    const rows = [];
    try {
        factors.forEach(factor => {
            appState = structuredClone(original);
            if (parameter === "chRatio") {
                appState.pvd.chRatio *= factor;
            } else {
                appState.soilLayers.forEach(layer => {
                    if (layer.type === "cohesive" && Number.isFinite(layer[parameter])) layer[parameter] *= factor;
                });
            }
            runAnalysis();
            const result = structuredClone(appState.lastAnalysis || {});
            result.fs = appState.lastVerification?.circular?.FS;
            rows.push({ factor, result });
        });
    } finally {
        appState = original;
        updateInputsFromState();
        renderLayersTable();
        runAnalysis();
    }
    return rows;
}

window.NendatYeuLegacy = {
    getState: getProjectStateSnapshot,
    replaceState: replaceProjectStateSnapshot,
    getResult: () => appState.lastAnalysis ? structuredClone(appState.lastAnalysis) : null,
    evaluateSensitivity: evaluateProjectSensitivity
};
Object.assign(window, { updateLayerField, deleteLayer });
